import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { CameraPawIcon } from "../components/CameraPawIcon";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { haalAfdrukKeuzes, openAfdruk, type AfdrukKeuze } from "../api/verblijfAfdruk";
import {
  useCreate,
  useCustom,
  useCustomMutation,
  useDelete,
  useGetIdentity,
  useGetLocale,
  useOne,
  useTranslate,
  useUpdate,
} from "@refinedev/core";
import {
  DateField,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  Button,
  ChipRegistrationStatusCard,
  CalendarIcon,
  CameraIcon,
  CheckIcon,
  ClockIcon,
  ChevronRightIcon,
  CombIcon,
  CrosshairsIcon,
  DropletIcon,
  Field,
  FieldShell,
  FileTextIcon,
  IdCardIcon,
  ImagePreview,
  MicrochipIcon,
  Modal,
  ConfirmDialog,
  PaletteIcon,
  RotateLeftIcon,
  RotateRightIcon,
  SelectField,
  PawIcon,
  PencilIcon,
  PinIcon,
  PlusCircleSolidIcon,
  PlusIcon,
  ReadField,
  LogoutIcon,
  SearchIcon,
  ShieldIcon,
  SparklesIcon,
  StatusBadge,
  Tile,
  Tabs,
  ScissorsIcon,
  TagIcon,
  ToggleField,
  TrashIcon,
  UploadCloudIcon,
  UserIcon,
  VenusIcon,
  XIcon,
  FileIcon,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import {
  uploadAnimalPhoto,
  fetchAnimalPhotoBlob,
  analyzeAnimalPhoto,
  fetchPhotoAiAvailability,
  AnimalPhotoApiError,
  type PhotoAiSuggestion,
} from "../api/animalPhoto";
import {
  scanPassport,
  importPassport,
  fetchPassportAiAvailability,
  type PassportSuggestion,
  type PassportImportRow,
  type PassportImportResult,
  type PassportTiterSuggestie,
} from "../api/vaccinationPassport";

import { formatDate, formatDateTime, formatEuro, localDateIso, todayIso } from "../i18n/format";
import { FoundLocationMap } from "../components/FoundLocationMap";
import { useTaakVoorTegel } from "./taakUitvoeren";
import { VoedingsplanOverzicht, type HistoriePlan, type Samenstelling } from "./Voedingsplan";
import { VoedingsschemaVenster } from "./Voedingsschemas";
import { resolveGemeenteId, useFoundLocationGeocoding } from "../components/useFoundLocationGeocoding";
import { uitgevoerdDoor } from "../uitgevoerdDoor";
import { buildAddressQuery, forwardGeocode } from "../api/geocoding";
import {
  deleteMedia,
  downloadMedia,
  fetchMediaBlob,
  updateMedia,
  rotateMedia,
  uploadMedia,
  MediaApiError,
  type MediaItem,
  type MediaTag,
} from "../api/media";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Animal-detail migration: the per-tile content components. Each fetches its own sub-resource
// (GET /api/animals/{id}/<tile>) via Refine's useCustom (relative url — the dataProvider prepends
// /api) and renders ALL of that fragment's fields read-only. Add/edit/delete + file download are a
// flagged follow-up.

// useCustom's generic is constrained to BaseRecord (an object); our tile payloads are objects or
// arrays, so we fetch untyped and cast the unwrapped body to the tile's shape.
function useTile<T>(animalId: string, sub: string) {
  const { data, isLoading, isError, refetch } = useCustom({ url: `animals/${animalId}/${sub}`, method: "get" });
  return { data: data?.data as T | undefined, isLoading, isError, refetch };
}

function num(value: number | null | undefined, locale: string, suffix = "", maxFractionDigits = 3): string {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat(locale, { maximumFractionDigits: maxFractionDigits }).format(value) + suffix;
}

// Strict Jmix-clone parity: the legacy Vaadin tiles render dates with DateFormatters.FORMAL —
// `dd-MM-yyyy` (and `dd-MM-yyyy HH:mm` for datetimes), locale-invariant — and the AppDefaults
// missing-value placeholder is a plain ASCII hyphen "-". This is NOT the app-wide Intl "medium"
// style (`1 mei 2020`) / em-dash used by the shared i18n/format.ts helpers, which stay in place for
// the rest of the SPA. These tile-local helpers reproduce the Jmix look exactly.
function fmtFormalDate(iso: string | null | undefined): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "-";
}
function fmtFormalDateTime(iso: string | null | undefined): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso) : null;
  return m ? `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}` : fmtFormalDate(iso);
}

function Status({ isLoading, isError, empty, t }: { isLoading: boolean; isError: boolean; empty?: boolean; t: (k: string) => string }) {
  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (isError) return <p className="fawkes-error">{t("animalDetailView.tileLoadFailed")}</p>;
  if (empty) return <p className="fawkes-tile-placeholder">{t("animalDetailView.tileEmpty")}</p>;
  return null;
}

// ---- Basis -----------------------------------------------------------------------------------------
// The Basisinformatie tile's EDITABLE form (GET + PUT /api/animals/{id}). A close port of the Jmix
// `basis-info` card (animal-card-default.{xml,java,css}): a hero row (photo left + name/sex/species
// right) over grouped paired rows split by a hairline divider, a live age badge, the cascading
// reference dropdowns (species/breed/coat/colour from /api/animals/reference/*), the Cas-ster +
// Kruising toggles, and the Tags chips. Tags are a structured view over `kenmerken` (the coupling
// lives in the backend); the picker rewrites `kenmerken`, the chips re-parse it. The photo is
// read-only this build (no upload). Reference IDs + the optimistic-lock version round-trip the save.
// Two Jmix sub-features round out parity: a live chip-duplicate check (a complete 15-digit chip is
// looked up via GET /chip-duplicate; an existing owner opens a "open that animal?" dialog) and the
// crossbreed (ras_id_kruising) combo, shown only when the Kruising toggle is on.
export interface AnimalBasis {
  id: number;
  /** Het dierregistratienummer van de opvang (dier.identificatie_nummer_asiel). */
  registratieNummer: number | null;
  naam: string; geslacht: string | null; gecastreerd: boolean; castratieDatum: string | null;
  eartip: boolean; eartipOor: string | null;
  diersoort: string | null; diersoortId: number | null; ras: string | null; rasId: number | null;
  kruising: boolean; rasKruising: string | null; rasKruisingId: number | null; vacht: string | null; vachtId: number | null;
  kleur: string | null; kleurId: number | null; chipPasnummer: string | null; paspoortNummer: string | null;
  stamboomNummer: string | null; kenmerken: string | null; geboortedatum: string | null;
  overledendatum: string | null; overleden: boolean; fotoUrl: string | null; version: number | null;
  tags: { label: string; color: string | null }[];
}

// GET /api/animals/{id}/chip-duplicate?chip=… — whether another animal already carries the chip.
interface ChipDuplicate { duplicate: boolean; id: number | null; naam: string | null }

interface OptionRow { id: number; label: string; color: string | null }

interface BasisForm {
  naam: string; geslacht: string; gecastreerd: boolean; castratieDatum: string;
  eartip: boolean; eartipOor: string;
  diersoortId: string; rasId: string; kruising: boolean; rasKruisingId: string; vachtId: string; kleurId: string;
  chipPasnummer: string; paspoortNummer: string; stamboomNummer: string; kenmerken: string;
  geboortedatum: string; overledendatum: string;
}

const GESLACHT_OPTIONS = ["Mannelijk", "Vrouwelijk"];

function toForm(a: AnimalBasis): BasisForm {
  const idStr = (v: number | null) => (v != null ? String(v) : "");
  return {
    naam: a.naam ?? "", geslacht: a.geslacht ?? "", gecastreerd: a.gecastreerd,
    eartip: a.eartip, eartipOor: a.eartipOor ?? "",
    castratieDatum: a.castratieDatum ?? "", diersoortId: idStr(a.diersoortId), rasId: idStr(a.rasId),
    kruising: a.kruising, rasKruisingId: idStr(a.rasKruisingId), vachtId: idStr(a.vachtId), kleurId: idStr(a.kleurId),
    chipPasnummer: a.chipPasnummer ?? "", paspoortNummer: a.paspoortNummer ?? "",
    stamboomNummer: a.stamboomNummer ?? "", kenmerken: a.kenmerken ?? "",
    geboortedatum: a.geboortedatum ?? "", overledendatum: a.overledendatum ?? "",
  };
}

// Calendar-field difference (years/months), UTC date-only — the basis of the age badge.
function periodYearsMonths(birth: Date, today: Date): { years: number; months: number } {
  let years = today.getUTCFullYear() - birth.getUTCFullYear();
  let months = today.getUTCMonth() - birth.getUTCMonth();
  if (today.getUTCDate() < birth.getUTCDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  return { years, months };
}

// Port of the Jmix formatLeeftijdLabel: ≤7d → "x dag(en)", <4mo → "x wk", else "y j m mnd".
function ageLabel(iso: string, t: (k: string) => string): string | null {
  if (!iso) return null;
  const birth = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const birthDay = new Date(Date.UTC(birth.getUTCFullYear(), birth.getUTCMonth(), birth.getUTCDate()));
  if (birthDay.getTime() > today.getTime()) return null;
  const days = Math.floor((today.getTime() - birthDay.getTime()) / 86_400_000);
  if (days <= 7) return `${days} ${t(days === 1 ? "animalDetailView.basis.age.day" : "animalDetailView.basis.age.days")}`;
  const fourMonths = new Date(Date.UTC(birth.getUTCFullYear(), birth.getUTCMonth() + 4, birth.getUTCDate()));
  if (fourMonths.getTime() > today.getTime()) {
    const weeks = Math.max(1, Math.round(days / 7));
    return `${weeks} ${t("animalDetailView.basis.age.wk")}`;
  }
  const { years, months } = periodYearsMonths(birthDay, today);
  const y = t("animalDetailView.basis.age.y");
  const mo = t("animalDetailView.basis.age.mo");
  if (years > 0 && months > 0) return `${years} ${y} ${months} ${mo}`;
  if (years > 0) return `${years} ${y}`;
  return `${years * 12 + months} ${mo}`;
}

// Tag/kenmerken helpers — mirror the backend AnimalTagCoupling so the live chips match what a save
// produces (split on [,;\n], trim, collapse whitespace, dedupe case-insensitively).
function parseTokens(kenmerken: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of kenmerken.split(/[,;\n]/)) {
    const n = part.trim().replace(/\s+/g, " ");
    if (n && !seen.has(n.toLowerCase())) { seen.add(n.toLowerCase()); out.push(n); }
  }
  return out;
}

function joinTokens(labels: string[]): string {
  return parseTokens(labels.join("\n")).join(", ");
}

function tagColor(label: string, tags: OptionRow[]): string | undefined {
  const m = tags.find((tg) => tg.label.trim().toLowerCase() === label.toLowerCase());
  return m?.color ?? undefined;
}

function useRefList(url: string): OptionRow[] {
  const { data } = useCustom({ url, method: "get" });
  return (data?.data as OptionRow[] | undefined) ?? [];
}

// Build <select> options, guaranteeing the current value is present even before its list has loaded
// (or when it falls outside a species-filtered list). `blank` adds a clearable "—" first option.
function selectOptions(
  rows: { id: number | string; label: string }[],
  currentValue: string,
  currentLabel: string | null,
  blank: boolean,
): { value: string; label: string }[] {
  const opts = rows.map((r) => ({ value: String(r.id), label: r.label }));
  if (currentValue && !opts.some((o) => o.value === currentValue)) {
    opts.unshift({ value: currentValue, label: currentLabel ?? currentValue });
  }
  return blank ? [{ value: "", label: "—" }, ...opts] : opts;
}

// Sliding switch — reuses the @fawkes/ui fui-toggle track/thumb (the checked rule keys off the
// adjacent input), without the inline label (the label sits above, like every other field).
function Switch({ checked, onChange, disabled, ariaLabel }: {
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; ariaLabel: string;
}) {
  return (
    <label className="fui-toggle fawkes-basis__switch">
      <input
        type="checkbox" role="switch" className="fui-toggle__input"
        checked={checked} disabled={disabled} aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="fui-toggle__track" aria-hidden="true"><span className="fui-toggle__thumb" /></span>
    </label>
  );
}

// The "Tags bewerken" picker — a checkbox list of the catalog; confirming joins the chosen labels into
// kenmerken (Jmix applySelectedTagsFromDialog: known-tag labels only, so free-text tokens drop out).
function TagDialog({ open, onClose, catalog, selectedLabels, onConfirm, t }: {
  open: boolean; onClose: () => void; catalog: OptionRow[]; selectedLabels: string[];
  onConfirm: (labels: string[]) => void; t: (k: string) => string;
}) {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open) {
      const lower = new Set(selectedLabels.map((l) => l.toLowerCase()));
      setChecked(new Set(catalog.filter((c) => lower.has(c.label.toLowerCase())).map((c) => c.label)));
    }
  }, [open, catalog, selectedLabels]);

  const toggle = (label: string) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(label)) next.delete(label); else next.add(label);
    return next;
  });

  return (
    <Modal
      open={open}
      title={t("animalDetailView.basis.tagsDialogTitle")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={() => onConfirm([...checked])}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-basis__tag-dialog">
        {catalog.map((tag) => (
          <label key={tag.id} className="fawkes-basis__tag-option">
            <input type="checkbox" checked={checked.has(tag.label)} onChange={() => toggle(tag.label)} />
            <span className="fawkes-basis__tag" style={tag.color ? { backgroundColor: tag.color } : undefined}>
              {tag.label}
            </span>
          </label>
        ))}
      </div>
    </Modal>
  );
}

/**
 * Eén blok binnen de basisinformatie: een gekleurd rondje met icoon, een kop en een ondertitel, en
 * daaronder de velden. De kleur is er om de vier blokken uit elkaar te houden bij het scannen —
 * identiteit groen, kenmerken blauw, registratie paars, overige groen — niet om betekenis te dragen.
 */
function BasisSectie({ kleur, icoon, titel, onder, children }: {
  kleur: "groen" | "accent" | "paars";
  icoon: ReactNode;
  titel: string;
  onder: string;
  children: ReactNode;
}) {
  return (
    <section className={`bi-sectie bi-sectie--${kleur}`}>
      <div className="bi-sectie__kop">
        <span className="bi-sectie__merk" aria-hidden="true">{icoon}</span>
        <span className="bi-sectie__tekst">
          <strong>{titel}</strong>
          <small>{onder}</small>
        </span>
      </div>
      <div className="bi-sectie__velden">{children}</div>
    </section>
  );
}

export function BasisTile({ animalId, onOpenAnimal, hiddenFields }: {
  animalId: string;
  onOpenAnimal?: (id: string) => void;
  /** Per-user hidden Basis field keys (bare AnimalCardDefault.FIELD_* names) — presentation-only. */
  hiddenFields?: Set<string>;
}) {
  const t = useTranslate();
  // Per-user field visibility (the pencil dialog). Hiding is presentation-only: hidden fields keep
  // their form state and still round-trip on save, exactly like the Jmix setVisible(false).
  const show = (fieldKey: string) => !hiddenFields?.has(fieldKey);
  const { data, isLoading, isError, refetch } = useOne<AnimalBasis>({ resource: "animals", id: animalId });
  const a = data?.data;

  const [form, setForm] = useState<BasisForm | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState<"saved" | "error" | "conflict" | "required" | "aiApplied" | null>(null);
  const [aiAppliedCount, setAiAppliedCount] = useState(0);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [heroReload, setHeroReload] = useState(0);
  const [hasPhoto, setHasPhoto] = useState(false);

  // Live chip-duplicate check (Jmix checkDuplicateChipNumberAndOfferNavigation): when a complete
  // 15-digit chip is entered/blurred, ask the API whether another animal already carries it. The
  // query key is the chip itself, so it fires once per distinct value; `dismissedChip` suppresses
  // re-prompting for a chip the operator already chose to keep (mirrors the Jmix prompt cache).
  const [chipToCheck, setChipToCheck] = useState("");
  // Een taak "Chip registreren" uitvoeren: het chipveld in beeld; na opslaan met een chip is de taak af.
  const chipVeld = useRef<HTMLInputElement | null>(null);
  const taak = useTaakVoorTegel(animalId, ["CHIPREGISTRATIE"], () => {
    chipVeld.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    chipVeld.current?.focus();
  }, true);
  const [duplicate, setDuplicate] = useState<{ id: number; naam: string } | null>(null);
  const [dismissedChip, setDismissedChip] = useState<string | null>(null);
  const chipCheckEnabled = /^\d{15}$/.test(chipToCheck);
  const { data: chipDup } = useCustom({
    url: `animals/${animalId}/chip-duplicate?chip=${chipToCheck}`,
    method: "get",
    queryOptions: { enabled: chipCheckEnabled },
  });
  useEffect(() => {
    const hit = chipDup?.data as ChipDuplicate | undefined;
    if (chipCheckEnabled && hit?.duplicate && hit.id != null && chipToCheck !== dismissedChip) {
      setDuplicate({ id: hit.id, naam: hit.naam ?? "" });
    }
  }, [chipDup?.data, chipCheckEnabled, chipToCheck, dismissedChip]);

  // (Re)seed the form when the animal first loads and after a save bumps its version.
  useEffect(() => {
    if (a) { setForm(toForm(a)); setVersion(a.version ?? null); setDirty(false); }
  }, [a?.id, a?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const species = useRefList("animals/reference/species");
  const coats = useRefList("animals/reference/coats");
  const colours = useRefList("animals/reference/colours");
  const tagCatalog = useRefList("animals/reference/tags");
  // Een eartip hoort bij katten: bij een hond betekent een knip in het oor iets heel anders. "Wilde
  // kat" en "TNRC" zijn dezelfde diersoort onder een andere naam en tellen mee -- juist daar kom je
  // een geknipt oor tegen.
  const isKat = species.some(
    (o) => String(o.id) === form?.diersoortId && /kat|tnrc/i.test(o.label),
  );
  const breeds = useRefList(
    form?.diersoortId ? `animals/reference/breeds?species=${form.diersoortId}` : "animals/reference/breeds",
  );

  const { mutate, isLoading: saving } = useUpdate();

  if (isLoading || isError || !a || !form) {
    return <Status isLoading={isLoading} isError={isError} empty={!isLoading && !isError && !a} t={t} />;
  }

  const set = <K extends keyof BasisForm>(key: K, value: BasisForm[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setDirty(true);
    setNotice(null);
  };

  const setGecastreerd = (v: boolean) => {
    setForm((f) => (f ? { ...f, gecastreerd: v, castratieDatum: v ? f.castratieDatum : "" } : f));
    setDirty(true);
    setNotice(null);
  };

  const tokens = parseTokens(form.kenmerken);
  const age = ageLabel(form.geboortedatum, t);

  const applyTags = (labels: string[]) => {
    set("kenmerken", joinTokens(labels));
    setTagDialogOpen(false);
  };

  const reset = () => { setForm(toForm(a)); setVersion(a.version ?? null); setDirty(false); setNotice(null); };

  // Which Basis fields already carry a value — the photo dialog uses this to default-uncheck (and flag
  // "overwrites existing value") AI suggestions for fields the operator has already filled, like Jmix.
  const aiFilled: Record<string, boolean> = {
    GESLACHT: form.geslacht.trim() !== "",
    DIERSOORT: form.diersoortId !== "",
    RAS: form.rasId !== "",
    VACHT: form.vachtId !== "",
    VACHT_KLEUR: form.kleurId !== "",
    KENMERKEN: form.kenmerken.trim() !== "",
    NAAM: form.naam.trim() !== "",
    GEBOORTEDATUM: form.geboortedatum !== "",
    CHIPNUMMER: form.chipPasnummer.trim() !== "",
    PASPOORTNUMMER: form.paspoortNummer.trim() !== "",
  };

  // Merge the operator's selected AI suggestions into the edit form (client-side apply, mirroring the
  // Jmix applySuggestions). Reference fields carry a resolved id; the rest carry their display value.
  const applyAiSuggestions = (selected: PhotoAiSuggestion[]) => {
    const patch: Partial<BasisForm> = {};
    for (const s of selected) {
      const id = s.referenceId != null ? String(s.referenceId) : "";
      switch (s.field) {
        case "GESLACHT": patch.geslacht = s.value; break;
        case "DIERSOORT": if (id) patch.diersoortId = id; break;
        case "RAS": if (id) patch.rasId = id; break;
        case "VACHT": if (id) patch.vachtId = id; break;
        case "VACHT_KLEUR": if (id) patch.kleurId = id; break;
        case "KENMERKEN": patch.kenmerken = s.value; break;
        case "NAAM": patch.naam = s.value; break;
        case "GEBOORTEDATUM": patch.geboortedatum = s.value; break;
        case "CHIPNUMMER": patch.chipPasnummer = s.value; break;
        case "PASPOORTNUMMER": patch.paspoortNummer = s.value; break;
        default: break;
      }
    }
    if (Object.keys(patch).length === 0) return;
    setForm((f) => (f ? { ...f, ...patch } : f));
    setDirty(true);
    setAiAppliedCount(Object.keys(patch).length);
    setNotice("aiApplied");
  };

  // Chip field: edit normally, and the moment a full 15-digit value is entered (or on blur) ask the
  // API whether it already belongs to another animal. A fresh value re-arms the prompt.
  const setChip = (value: string) => {
    set("chipPasnummer", value);
    const trimmed = value.trim();
    if (trimmed !== dismissedChip) setDismissedChip(null);
    if (/^\d{15}$/.test(trimmed)) setChipToCheck(trimmed);
  };
  // "Nee, hier blijven": drop the conflicting chip and keep editing (mirrors the Jmix dialog).
  const dismissDuplicate = () => { setDuplicate(null); setDismissedChip(null); setChipToCheck(""); set("chipPasnummer", ""); };
  // "Ja, open bestaand dier": navigate to the animal that already owns the chip.
  const openDuplicate = () => { const target = duplicate; setDuplicate(null); if (target) onOpenAnimal?.(String(target.id)); };

  const save = () => {
    if (!form.diersoortId) { setNotice("required"); return; }
    const numOrNull = (v: string) => (v ? Number(v) : null);
    const trimOrNull = (v: string) => (v.trim() ? v : null);
    mutate(
      {
        resource: "animals",
        id: animalId,
        values: {
          naam: form.naam,
          geslacht: trimOrNull(form.geslacht),
          gecastreerd: form.gecastreerd,
          castratieDatum: form.gecastreerd ? trimOrNull(form.castratieDatum) : null,
          eartip: form.eartip,
          eartipOor: form.eartip ? form.eartipOor || null : null,
          diersoortId: Number(form.diersoortId),
          rasId: numOrNull(form.rasId),
          kruising: form.kruising,
          rasKruisingId: form.kruising ? numOrNull(form.rasKruisingId) : null,
          dierVachtId: numOrNull(form.vachtId),
          dierKleurId: numOrNull(form.kleurId),
          chipPasnummer: trimOrNull(form.chipPasnummer),
          paspoortNummer: trimOrNull(form.paspoortNummer),
          stamboomNummer: trimOrNull(form.stamboomNummer),
          kenmerken: trimOrNull(form.kenmerken),
          geboortedatum: trimOrNull(form.geboortedatum),
          overledendatum: trimOrNull(form.overledendatum),
          version,
        },
      },
      {
        onSuccess: (res) => {
          const updated = res.data as unknown as AnimalBasis;
          if (updated.chipPasnummer) taak.klaar();
          setForm(toForm(updated));
          setVersion(updated.version ?? null);
          setDirty(false);
          setNotice("saved");
          refetch();
        },
        onError: (err) => {
          setNotice((err as { statusCode?: number }).statusCode === 409 ? "conflict" : "error");
        },
      },
    );
  };

  const noticeText =
    notice === "saved" ? t("animalDetailView.basis.saved")
    : notice === "conflict" ? t("animalDetailView.basis.saveConflict")
    : notice === "required" ? t("animalDetailView.basis.diersoortRequired")
    : notice === "error" ? t("animalDetailView.basis.saveError")
    : notice === "aiApplied" ? t("animalDetailView.basis.aiApplied", { args: [String(aiAppliedCount)] })
    : null;

  // The "change vs upload" label tracks whether ANY photo is *shown* — an api blob OR the external
  // fallback URL (online_dier_afbeelding_url). hasPhoto alone only flags the api blob, so an animal
  // with just an external photo would wrongly read "Foto uploaden" while a photo is visible. Jmix
  // always shows "Foto wijzigen" once a photo exists; this matches that.
  const showsPhoto = hasPhoto || Boolean(a.fotoUrl);

  return (
    <div className="fawkes-basis">
      <div className="bi-kop">
        <span className="bi-kop__merk" aria-hidden="true"><ShieldIcon width={26} height={26} /></span>
        <span className="bi-kop__tekst">
          <strong>{t("animalDetailView.tile.basis")}</strong>
          <small>{t("basis.subtitel")}</small>
        </span>
      </div>

      <BasisSectie kleur="groen" icoon={<UserIcon />}
        titel={t("basis.sectie.identiteit")} onder={t("basis.sectie.identiteitOnder")}>
      <div className="fawkes-basis__hero">
        {show("foto") && (
        <div className="fawkes-basis__photo-col">
          <button
            type="button"
            className="fawkes-basis__photo-button"
            onClick={() => setPhotoDialogOpen(true)}
            aria-label={t(showsPhoto ? "animalDetailView.basis.photoChange" : "animalDetailView.basis.photo")}
          >
            <div className="fawkes-basis__photo-frame">
              <BasisHeroPhoto
                animalId={animalId} fallbackUrl={a.fotoUrl} alt={form.naam}
                reloadKey={heroReload} onHasPhoto={setHasPhoto}
              />
              <span className="fawkes-basis__photo-badge" aria-hidden="true">
                <CameraPawIcon size={16} strokeWidth={2} />
              </span>
            </div>
            <span className="fawkes-basis__photo-hint">
              {t(showsPhoto ? "animalDetailView.basis.photoChange" : "animalDetailView.basis.photo")}
            </span>
          </button>
        </div>
        )}

        <div className="fawkes-basis__top">
          {show("naam") && (
          <FieldShell icon={<UserIcon />} label={t("animalDetailView.field.naam")}>
            <div className="fawkes-basis__name-row">
              <input
                className="fui-field__input" value={form.naam}
                onChange={(e) => set("naam", e.target.value)}
              />
              {age && <span className="fawkes-basis__age-badge">{age}</span>}
            </div>
          </FieldShell>
          )}

          <div className="fawkes-basis__compact-row">
            {show("geslacht") && (
            <FieldShell icon={<VenusIcon />} label={t("animalDetailView.field.geslacht")}>
              <select
                className="fui-field__input fui-select" value={form.geslacht}
                onChange={(e) => set("geslacht", e.target.value)}
              >
                {selectOptions(GESLACHT_OPTIONS.map((g) => ({ id: g, label: g })), form.geslacht, form.geslacht, true)
                  .map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FieldShell>
            )}

            {show("gecastreerd") && (
            <FieldShell label={t("animalDetailView.field.casSter")} className="fawkes-basis__field--switch">
              <Switch
                checked={form.gecastreerd} onChange={setGecastreerd}
                ariaLabel={t("animalDetailView.field.casSter")}
              />
            </FieldShell>
            )}

            {show("castratieDatum") && (
            <FieldShell label={t("animalDetailView.field.castratieDatum")}>
              <DateField className="fui-field__input" value={form.castratieDatum}
                disabled={!form.gecastreerd}
                onChange={(e) => set("castratieDatum", e.target.value)}
              />
            </FieldShell>
            )}
          </div>

          {show("diersoort") && (
          <FieldShell icon={<PawIcon />} label={t("animalDetailView.field.diersoort")} className="fawkes-basis__field--diersoort">
            <select
              className="fui-field__input fui-select" value={form.diersoortId}
              onChange={(e) => set("diersoortId", e.target.value)}
            >
              {selectOptions(species, form.diersoortId, a.diersoort, false)
                .map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </FieldShell>
          )}
        </div>
      </div>

      </BasisSectie>

      <BasisSectie kleur="accent" icoon={<PawIcon />}
        titel={t("basis.sectie.kenmerken")} onder={t("basis.sectie.kenmerkenOnder")}>
      <div className="fawkes-basis__row fawkes-basis__row--breed">
        {show("dierVacht") && (
        <FieldShell icon={<PawIcon />} label={t("animalDetailView.field.ras")}>
          <select
            className="fui-field__input fui-select" value={form.rasId}
            onChange={(e) => set("rasId", e.target.value)}
          >
            {selectOptions(breeds, form.rasId, a.ras, true)
              .map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FieldShell>
        )}
        {show("kruising") && (
        <FieldShell label={t("animalDetailView.field.kruising")} className="fawkes-basis__field--switch fawkes-basis__field--kruising">
          <Switch checked={form.kruising} onChange={(v) => set("kruising", v)} ariaLabel={t("animalDetailView.field.kruising")} />
        </FieldShell>
        )}
      </div>

      {/* Strict Jmix-clone parity: the Jmix Basis card (animal-card-default.xml) has no second
          "Ras kruising" breed selector — only the Kruising checkbox. The field is intentionally not
          rendered here to match :8080; the loaded rasKruisingId still round-trips on save unchanged. */}

      <div className="fawkes-basis__row fawkes-basis__row--2">
        <FieldShell icon={<CombIcon />} label={t("animalDetailView.field.vacht")}>
          <select
            className="fui-field__input fui-select" value={form.vachtId}
            onChange={(e) => set("vachtId", e.target.value)}
          >
            {selectOptions(coats, form.vachtId, a.vacht, true)
              .map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FieldShell>
        <FieldShell icon={<PaletteIcon />} label={t("animalDetailView.field.vachtKleur")}>
          <select
            className="fui-field__input fui-select" value={form.kleurId}
            onChange={(e) => set("kleurId", e.target.value)}
          >
            {selectOptions(colours, form.kleurId, a.kleur, true)
              .map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FieldShell>
      </div>

      {/* Een eartip is een geknipt oorpuntje: het teken dat een zwerfkat gevangen, geholpen en
          teruggezet is. Alleen bij katten, want bij een hond betekent een knip in het oor iets heel
          anders. Dezelfde schakelaar als Kruising hierboven, zodat het ene ja/nee-veld in deze kaart
          er niet anders uitziet dan het andere. Welk oor verschijnt pas als de schakelaar aanstaat --
          leeg mag, want dat je de knip ziet is de melding en niet iedereen noteert welke kant. */}
      {isKat && (
        <div className="fawkes-basis__row fawkes-basis__row--2">
          <FieldShell
            icon={<ScissorsIcon />}
            label={t("animalDetailView.field.eartip")}
            className="fawkes-basis__field--eartip"
          >
            <Switch
              checked={form.eartip}
              onChange={(v) => set("eartip", v)}
              ariaLabel={t("animalDetailView.field.eartip")}
            />
          </FieldShell>
          {form.eartip && (
            <FieldShell icon={<ScissorsIcon />} label={t("animalDetailView.field.eartipOor")}>
              <select
                className="fui-field__input fui-select"
                value={form.eartipOor}
                data-testid="basis-eartip-oor"
                onChange={(e) => set("eartipOor", e.target.value)}
              >
                <option value="">{t("animalDetailView.field.eartipOorLeeg")}</option>
                <option value="LINKS">{t("animalDetailView.field.eartipLinks")}</option>
                <option value="RECHTS">{t("animalDetailView.field.eartipRechts")}</option>
              </select>
            </FieldShell>
          )}
        </div>
      )}

      </BasisSectie>

      <BasisSectie kleur="paars" icoon={<IdCardIcon />}
        titel={t("basis.sectie.registratie")} onder={t("basis.sectie.registratieOnder")}>

      <div className="fawkes-basis__row fawkes-basis__row--3">
        {show("chipPasnummer") && (
        <FieldShell icon={<MicrochipIcon />} label={t("animalDetailView.field.chip")}>
          <input
            ref={chipVeld}
            className="fui-field__input" value={form.chipPasnummer} inputMode="numeric" maxLength={15}
            onChange={(e) => setChip(e.target.value)}
            onBlur={() => setChipToCheck(form.chipPasnummer.trim())}
          />
        </FieldShell>
        )}
        {show("paspoortNummer") && (
        <FieldShell icon={<IdCardIcon />} label={t("animalDetailView.field.paspoort")}>
          <input
            className="fui-field__input" value={form.paspoortNummer}
            onChange={(e) => set("paspoortNummer", e.target.value)}
          />
        </FieldShell>
        )}
        {show("stamboomNummer") && (
        <FieldShell icon={<IdCardIcon />} label={t("animalDetailView.field.stamboom")}>
          <input
            className="fui-field__input" value={form.stamboomNummer} maxLength={30}
            placeholder={t("animalDetailView.basis.stamboomPlaceholder")}
            onChange={(e) => set("stamboomNummer", e.target.value)}
          />
        </FieldShell>
        )}
      </div>

      <div className="fawkes-basis__row fawkes-basis__row--2">
        {show("geboortedatum") && (
        <FieldShell icon={<CalendarIcon />} label={t("animalDetailView.field.geboortedatum")}>
          <DateField className="fui-field__input" value={form.geboortedatum}
            onChange={(e) => set("geboortedatum", e.target.value)}
          />
        </FieldShell>
        )}
        {show("overledendatum") && (
        <FieldShell icon={<CalendarIcon />} label={t("animalDetailView.field.overledendatum")}>
          <DateField className="fui-field__input" value={form.overledendatum}
            onChange={(e) => set("overledendatum", e.target.value)}
          />
        </FieldShell>
        )}
      </div>

      </BasisSectie>

      <BasisSectie kleur="groen" icoon={<FileTextIcon />}
        titel={t("basis.sectie.overige")} onder={t("basis.sectie.overigeOnder")}>
      {show("kenmerken") && (
      <FieldShell icon={<PawIcon />} label={t("animalDetailView.field.kenmerken")}>
        <textarea
          className="fui-field__input fawkes-basis__textarea" value={form.kenmerken} rows={3}
          onChange={(e) => set("kenmerken", e.target.value)}
        />
      </FieldShell>
      )}

      <div className="fawkes-basis__field fawkes-basis__tags">
        <div className="fawkes-basis__tags-head">
          <span className="fawkes-basis__label-text">{t("animalDetailView.basis.tags")}</span>
          <button type="button" className="fawkes-basis__tag-add" onClick={() => setTagDialogOpen(true)}>
            {t("animalDetailView.basis.tagsAdd")}
          </button>
        </div>
        <div className="fawkes-basis__tags-box">
          {tokens.length === 0 ? (
            <span className="fawkes-basis__tags-empty">{t("animalDetailView.basis.tagsEmpty")}</span>
          ) : (
            tokens.map((tok, i) => (
              <span
                key={`${tok}-${i}`} className="fawkes-basis__tag"
                style={tagColor(tok, tagCatalog) ? { backgroundColor: tagColor(tok, tagCatalog) } : undefined}
              >
                {tok}
              </span>
            ))
          )}
        </div>
      </div>

      </BasisSectie>

      <div className="fawkes-basis__actions">
        {noticeText && (
          <span className={`fawkes-basis__notice fawkes-basis__notice--${notice}`} role="status">{noticeText}</span>
        )}
        <Button variant="secondary" onClick={reset} disabled={!dirty || saving}>
          {t("animalDetailView.basis.reset")}
        </Button>
        <Button variant="primary" onClick={save} disabled={!dirty || saving}>
          {t("common.save")}
        </Button>
      </div>

      <TagDialog
        open={tagDialogOpen} onClose={() => setTagDialogOpen(false)} catalog={tagCatalog}
        selectedLabels={tokens} onConfirm={applyTags} t={t}
      />

      <Modal
        open={duplicate != null}
        title={t("animalDetailView.basis.chipDuplicateTitle")}
        onClose={dismissDuplicate}
        footer={
          <>
            <Button variant="secondary" onClick={dismissDuplicate}>{t("animalDetailView.basis.chipDuplicateStay")}</Button>
            <Button variant="primary" onClick={openDuplicate} disabled={!onOpenAnimal}>
              {t("animalDetailView.basis.chipDuplicateOpen")}
            </Button>
          </>
        }
      >
        <p className="fawkes-basis__chip-dup-message">
          {duplicate &&
            t("animalDetailView.basis.chipDuplicateMessage", {
              args: [chipToCheck, duplicate.naam.trim() || t("animalDetailView.basis.chipDuplicateNoName")],
            })}
        </p>
      </Modal>

      <BasisPhotoDialog
        open={photoDialogOpen}
        animalId={animalId}
        hasPhoto={hasPhoto}
        aiFilled={aiFilled}
        onApplySuggestions={applyAiSuggestions}
        onClose={() => setPhotoDialogOpen(false)}
        onSaved={(detail) => {
          // The save cleared any online-photo URL → the form's lock token may have moved; refresh it
          // without clobbering unsaved field edits, and reload the authed hero blob.
          if (detail && typeof detail.version === "number") setVersion(detail.version);
          setHeroReload((k) => k + 1);
          setPhotoDialogOpen(false);
        }}
        t={t}
      />
    </div>
  );
}

// ---- Basisinformatie photo dialog (Foto wijzigen) --------------------------------------------------
// Ports the Jmix AnimalCardDefault.openPhotoChangeDialog: upload OR live camera capture, then a square
// crop with horizontal/vertical nudge + drag-to-position + 90° rotation, saved to the animal's profile
// photo (afbeelding / dier_afbeelding via POST /api/animals/{id}/photo). The crop/rotate is applied
// client-side on a <canvas> — exactly the bytes the Jmix server-side applyPreviewCrop produced — so the
// API just stores what it receives. The "AI-analyse" step (Jmix openAiSuggestionsDialog) sends the
// cropped bytes to the configured provider and applies the chosen suggestions to the Basis form.

// Maps a backend PrefillField name to the Basis field's i18n label, so the AI suggestion rows read with
// the same field names the form uses (falls back to the backend's Dutch label).
function aiFieldLabel(field: string, t: Translate): string {
  const key: string | undefined = {
    GESLACHT: "animalDetailView.field.geslacht",
    DIERSOORT: "animalDetailView.field.diersoort",
    RAS: "animalDetailView.field.ras",
    VACHT: "animalDetailView.field.vacht",
    VACHT_KLEUR: "animalDetailView.field.vachtKleur",
    KENMERKEN: "animalDetailView.field.kenmerken",
    NAAM: "animalDetailView.field.naam",
    GEBOORTEDATUM: "animalDetailView.field.geboortedatum",
    CHIPNUMMER: "animalDetailView.field.chip",
    PASPOORTNUMMER: "animalDetailView.field.paspoort",
  }[field];
  return key ? t(key) : "";
}

// The authenticated hero image: fetch the profile-photo bytes as a blob → object URL (revoked on
// unmount). Falls back to the simple online-photo URL, then ImagePreview's camera placeholder.
function BasisHeroPhoto({
  animalId, fallbackUrl, alt, reloadKey, onHasPhoto,
}: {
  animalId: string;
  fallbackUrl: string | null;
  alt: string;
  reloadKey: number;
  onHasPhoto: (has: boolean) => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    fetchAnimalPhotoBlob(animalId)
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        onHasPhoto(true);
      })
      .catch(() => {
        if (revoked) return;
        setBlobUrl(null);
        onHasPhoto(false);
      });
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [animalId, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return <ImagePreview src={blobUrl ?? fallbackUrl} alt={alt} className="fawkes-basis__photo" />;
}

type PhotoSelection = { url: string; type: string; name: string };
export type Translate = (key: string, options?: Record<string, unknown>) => string;

function loadImageEl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = url;
  });
}

// Mirror of the Jmix applyPreviewCrop + rotateImage: rotate the source 0/90/180/270°, then take a
// centred square crop whose position is driven by the offset percents (−100..100), and re-encode.
async function buildAdjustedPhotoBlob(
  source: PhotoSelection,
  offsetXPercent: number,
  offsetYPercent: number,
  rotationDegrees: number,
): Promise<{ blob: Blob; type: string }> {
  const outputType = source.type === "image/png" ? "image/png" : "image/jpeg";
  const img = await loadImageEl(source.url);
  const rot = ((rotationDegrees % 360) + 360) % 360;

  const swap = rot === 90 || rot === 270;
  const rw = swap ? img.naturalHeight : img.naturalWidth;
  const rh = swap ? img.naturalWidth : img.naturalHeight;
  const rotated = document.createElement("canvas");
  rotated.width = rw;
  rotated.height = rh;
  const rctx = rotated.getContext("2d");
  if (!rctx) throw new Error("canvas unavailable");
  rctx.translate(rw / 2, rh / 2);
  rctx.rotate((rot * Math.PI) / 180);
  rctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

  const cropSize = Math.min(rw, rh);
  const maxX = Math.max(0, rw - cropSize);
  const maxY = Math.max(0, rh - cropSize);
  let x = Math.round(((offsetXPercent + 100) / 200) * maxX);
  let y = Math.round(((offsetYPercent + 100) / 200) * maxY);
  x = Math.max(0, Math.min(maxX, x));
  y = Math.max(0, Math.min(maxY, y));

  const out = document.createElement("canvas");
  out.width = cropSize;
  out.height = cropSize;
  const octx = out.getContext("2d");
  if (!octx) throw new Error("canvas unavailable");
  if (outputType === "image/jpeg") {
    octx.fillStyle = "#ffffff"; // flatten any alpha for JPEG, like the Jmix RGB re-encode
    octx.fillRect(0, 0, cropSize, cropSize);
  }
  octx.drawImage(rotated, x, y, cropSize, cropSize, 0, 0, cropSize, cropSize);

  const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, outputType, 0.92));
  if (!blob) throw new Error("encode failed");
  return { blob, type: outputType };
}

// Live camera capture (getUserMedia → <video> → canvas snapshot), an inline panel inside the photo
// dialog (a nested Modal would fight z-index — see the guide). Mirrors the Jmix openCameraPreview JS.
// Exported for the intake wizard's animal sub-wizard (parity slice 3b), which reuses the same panel.
export function CameraCapture({
  onCapture, onCancel, t,
}: {
  onCapture: (dataUrl: string) => void;
  onCancel: () => void;
  t: Translate;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stop = () => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    let cancelled = false;
    const md = navigator.mediaDevices;
    if (!md || !md.getUserMedia) {
      setError(t("animalDetailView.photo.cameraUnsupported"));
      return;
    }
    md.getUserMedia({
      video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      audio: false,
    })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => undefined);
        }
      })
      .catch(() => {
        if (!cancelled) setError(t("animalDetailView.photo.cameraDenied"));
      });
    return () => {
      cancelled = true;
      stop();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 1280;
    c.height = v.videoHeight || 720;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(v, 0, 0);
    const dataUrl = c.toDataURL("image/jpeg", 0.92);
    stop();
    onCapture(dataUrl);
  };

  const cancel = () => {
    stop();
    onCancel();
  };

  if (error) {
    return (
      <div className="fawkes-photo-dialog__camera fawkes-photo-dialog__camera--error">
        <p className="fawkes-photo-dialog__camera-msg">{error}</p>
        <Button variant="secondary" onClick={cancel}>{t("animalDetailView.photo.cameraBack")}</Button>
      </div>
    );
  }

  return (
    <div className="fawkes-photo-dialog__camera">
      <video ref={videoRef} autoPlay playsInline muted className="fawkes-photo-dialog__video" />
      <div className="fawkes-photo-dialog__camera-bar">
        <Button variant="secondary" onClick={cancel}>{t("animalDetailView.photo.cameraCancel")}</Button>
        <Button variant="primary" onClick={snap}>{t("animalDetailView.photo.cameraSnap")}</Button>
      </div>
    </div>
  );
}

function PhotoAdjustButton({
  label, onClick, children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className="fawkes-photo-dialog__adjust-btn" onClick={onClick} title={label} aria-label={label}>
      {children}
    </button>
  );
}

function BasisPhotoDialog({
  open, animalId, hasPhoto, aiFilled, onApplySuggestions, onClose, onSaved, t,
}: {
  open: boolean;
  animalId: string;
  hasPhoto: boolean;
  aiFilled: Record<string, boolean>;
  onApplySuggestions: (selected: PhotoAiSuggestion[]) => void;
  onClose: () => void;
  onSaved: (detail: { version?: number } | null) => void;
  t: Translate;
}) {
  const [selection, setSelection] = useState<PhotoSelection | null>(null);
  const [offX, setOffX] = useState(0);
  const [offY, setOffY] = useState(0);
  const [rot, setRot] = useState(0);
  const [cameraOn, setCameraOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // AI-analyse step: availability gates the button; the inline panel shows the proposed fields with a
  // checkbox each (mirrors the Jmix openAiSuggestionsDialog), applied to the Basis form behind the dialog.
  const [aiAvailable, setAiAvailable] = useState(false);
  const [aiPanel, setAiPanel] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<PhotoAiSuggestion[]>([]);
  const [aiChecked, setAiChecked] = useState<Record<string, boolean>>({});
  const objectUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; startOffX: number; startOffY: number } | null>(null);

  const revokeCurrent = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  const replaceSelection = (next: PhotoSelection | null, trackObjectUrl: boolean) => {
    revokeCurrent();
    if (next && trackObjectUrl) objectUrlRef.current = next.url;
    setSelection(next);
    setOffX(0);
    setOffY(0);
    setRot(0);
  };

  // On open: reset, then seed the preview from the existing profile photo (if any).
  useEffect(() => {
    if (!open) return;
    setCameraOn(false);
    setBusy(false);
    setError(null);
    setOffX(0);
    setOffY(0);
    setRot(0);
    setAiPanel(false);
    setAiBusy(false);
    setAiSuggestions([]);
    setAiAvailable(false);
    fetchPhotoAiAvailability(animalId).then(setAiAvailable);
    let revoked = false;
    let objectUrl: string | null = null;
    if (hasPhoto) {
      fetchAnimalPhotoBlob(animalId)
        .then((blob) => {
          if (revoked) return;
          objectUrl = URL.createObjectURL(blob);
          objectUrlRef.current = objectUrl;
          setSelection({ url: objectUrl, type: blob.type || "image/jpeg", name: "animal-photo.jpg" });
        })
        .catch(() => {
          if (!revoked) setSelection(null);
        });
    } else {
      setSelection(null);
    }
    return () => {
      revoked = true;
      revokeCurrent();
    };
  }, [open, animalId, hasPhoto]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError(t("animalDetailView.photo.unsupported"));
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError(t("animalDetailView.photo.tooLarge"));
      return;
    }
    setError(null);
    replaceSelection({ url: URL.createObjectURL(file), type: file.type, name: file.name }, true);
  };

  const onCameraCapture = (dataUrl: string) => {
    setCameraOn(false);
    setError(null);
    replaceSelection({ url: dataUrl, type: "image/jpeg", name: `camera_${Date.now()}.jpg` }, false);
  };

  const nudge = (dx: number, dy: number) => {
    if (dx) setOffX((v) => Math.max(-100, Math.min(100, v + dx)));
    if (dy) setOffY((v) => Math.max(-100, Math.min(100, v + dy)));
  };
  const rotate = (delta: number) => setRot((v) => (((v + delta) % 360) + 360) % 360);
  const center = () => { setOffX(0); setOffY(0); setRot(0); };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!selection) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startOffX: offX, startOffY: offY };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const dxPct = ((e.clientX - d.startX) / Math.max(1, rect.width)) * 200;
    const dyPct = ((e.clientY - d.startY) / Math.max(1, rect.height)) * 200;
    setOffX(Math.max(-100, Math.min(100, Math.round(d.startOffX - dxPct))));
    setOffY(Math.max(-100, Math.min(100, Math.round(d.startOffY - dyPct))));
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  const save = async () => {
    if (!selection) {
      setError(t("animalDetailView.photo.empty"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { blob, type } = await buildAdjustedPhotoBlob(selection, offX, offY, rot);
      const ext = type === "image/png" ? "png" : "jpg";
      const baseName = selection.name && selection.name.includes(".")
        ? selection.name.replace(/\.[^.]+$/, "")
        : (selection.name || "animal-photo");
      const file = new File([blob], `${baseName}.${ext}`, { type });
      const detail = (await uploadAnimalPhoto(animalId, file)) as { version?: number } | null;
      onSaved(detail);
    } catch (err) {
      const code = err instanceof AnimalPhotoApiError ? err.statusCode : 0;
      setError(code === 400 ? t("animalDetailView.photo.unsupported") : t("animalDetailView.photo.saveError"));
    } finally {
      setBusy(false);
    }
  };

  // "AI-analyse": send the current (cropped/rotated) selection to the configured provider and show the
  // proposed fields. Defaults each suggestion checked when it's confident (>= 50%) and the field is empty.
  const runAnalyze = async () => {
    if (!selection) return;
    setAiBusy(true);
    setError(null);
    try {
      const { blob, type } = await buildAdjustedPhotoBlob(selection, offX, offY, rot);
      const ext = type === "image/png" ? "png" : "jpg";
      const file = new File([blob], `analyze.${ext}`, { type });
      const res = await analyzeAnimalPhoto(animalId, file);
      if (!res.available) {
        setError(t("animalDetailView.photo.aiUnavailable"));
        return;
      }
      if (res.suggestions.length === 0) {
        setError(res.message || t("animalDetailView.photo.aiNone"));
        return;
      }
      const checked: Record<string, boolean> = {};
      for (const s of res.suggestions) checked[s.field] = !aiFilled[s.field] && s.confidence >= 0.5;
      setAiSuggestions(res.suggestions);
      setAiChecked(checked);
      setAiPanel(true);
    } catch {
      setError(t("animalDetailView.photo.aiError"));
    } finally {
      setAiBusy(false);
    }
  };

  const applyAi = () => {
    onApplySuggestions(aiSuggestions.filter((s) => aiChecked[s.field]));
    setAiPanel(false);
  };

  const objectPosition = `${50 + offX * 0.5}% ${50 + offY * 0.5}%`;
  const transform = rot === 0 ? undefined : `rotate(${rot}deg)`;

  const footer = aiPanel ? (
    <>
      <Button variant="secondary" onClick={() => setAiPanel(false)} disabled={aiBusy}>
        {t("animalDetailView.photo.aiBack")}
      </Button>
      <Button variant="secondary" onClick={runAnalyze} disabled={aiBusy}>
        {t("animalDetailView.photo.aiRetry")}
      </Button>
      <Button variant="primary" onClick={applyAi} disabled={aiBusy}>
        {t("animalDetailView.photo.aiApply")}
      </Button>
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose} disabled={busy}>
        {t("animalDetailView.photo.cancel")}
      </Button>
      <Button variant="primary" onClick={save} disabled={busy || cameraOn || !selection}>
        {t("animalDetailView.photo.save")}
      </Button>
    </>
  );

  return (
    <Modal open={open} title={t("animalDetailView.photo.dialogTitle")} onClose={onClose} footer={footer}>
      <div className="fawkes-photo-dialog">
        <p className="fawkes-photo-dialog__helper">{t("animalDetailView.photo.helper")}</p>

        {aiPanel ? (
          <div className="fawkes-photo-dialog__ai">
            <p className="fawkes-photo-dialog__ai-title">{t("animalDetailView.photo.aiTitle")}</p>
            <p className="fawkes-photo-dialog__ai-helper">{t("animalDetailView.photo.aiHelper")}</p>
            <div className="fawkes-photo-dialog__ai-list">
              {aiSuggestions.map((s) => (
                <label key={s.field} className="fawkes-photo-dialog__ai-row">
                  <input
                    type="checkbox"
                    checked={aiChecked[s.field] ?? false}
                    onChange={(e) => setAiChecked((c) => ({ ...c, [s.field]: e.target.checked }))}
                  />
                  <span className="fawkes-photo-dialog__ai-text">
                    <strong>{aiFieldLabel(s.field, t) || s.label}:</strong> {s.value}{" "}
                    <span className="fawkes-photo-dialog__ai-conf">({Math.round(s.confidence * 100)}%)</span>
                    {aiFilled[s.field] && (
                      <em className="fawkes-photo-dialog__ai-overwrite"> — {t("animalDetailView.photo.aiOverwrites")}</em>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ) : cameraOn ? (
          <CameraCapture onCapture={onCameraCapture} onCancel={() => setCameraOn(false)} t={t} />
        ) : (
          <>
            <div className="fawkes-photo-dialog__source-row">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="fawkes-photo-dialog__file-input"
                onChange={onPickFile}
              />
              <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                <UploadCloudIcon width={16} height={16} /> {t("animalDetailView.photo.choose")}
              </Button>
              <Button variant="secondary" onClick={() => { setError(null); setCameraOn(true); }}>
                <CameraIcon width={16} height={16} /> {t("animalDetailView.photo.camera")}
              </Button>
              {aiAvailable && (
                <Button variant="secondary" onClick={runAnalyze} disabled={!selection || aiBusy}>
                  {aiBusy ? t("animalDetailView.photo.aiAnalyzing") : t("animalDetailView.photo.aiAnalyze")}
                </Button>
              )}
            </div>
            <p className="fawkes-photo-dialog__fallback">{t("animalDetailView.photo.cameraFallback")}</p>

            <div
              className={`fawkes-photo-dialog__preview${selection ? " fawkes-photo-dialog__preview--draggable" : ""}`}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              {selection ? (
                <img
                  src={selection.url}
                  alt={t("animalDetailView.photo.previewAlt")}
                  className="fawkes-photo-dialog__preview-img"
                  style={{ objectPosition, transform }}
                  draggable={false}
                />
              ) : (
                <span className="fawkes-photo-dialog__preview-empty" aria-hidden="true">
                  <CameraPawIcon size={40} />
                </span>
              )}
            </div>

            <div className="fawkes-photo-dialog__adjust-row">
              <span className="fawkes-photo-dialog__adjust-label">{t("animalDetailView.photo.horizontal")}: {offX}</span>
              <PhotoAdjustButton label={t("animalDetailView.photo.left")} onClick={() => nudge(-10, 0)}><ArrowLeftIcon width={16} height={16} /></PhotoAdjustButton>
              <PhotoAdjustButton label={t("animalDetailView.photo.right")} onClick={() => nudge(10, 0)}><ArrowRightIcon width={16} height={16} /></PhotoAdjustButton>
            </div>
            <div className="fawkes-photo-dialog__adjust-row">
              <span className="fawkes-photo-dialog__adjust-label">{t("animalDetailView.photo.vertical")}: {offY}</span>
              <PhotoAdjustButton label={t("animalDetailView.photo.up")} onClick={() => nudge(0, -10)}><ArrowUpIcon width={16} height={16} /></PhotoAdjustButton>
              <PhotoAdjustButton label={t("animalDetailView.photo.down")} onClick={() => nudge(0, 10)}><ArrowDownIcon width={16} height={16} /></PhotoAdjustButton>
              <PhotoAdjustButton label={t("animalDetailView.photo.center")} onClick={center}><CrosshairsIcon width={16} height={16} /></PhotoAdjustButton>
            </div>
            <div className="fawkes-photo-dialog__adjust-row">
              <span className="fawkes-photo-dialog__adjust-label">{t("animalDetailView.photo.rotation")}: {rot}°</span>
              <PhotoAdjustButton label={t("animalDetailView.photo.rotateLeft")} onClick={() => rotate(-90)}><RotateLeftIcon width={16} height={16} /></PhotoAdjustButton>
              <PhotoAdjustButton label={t("animalDetailView.photo.rotateRight")} onClick={() => rotate(90)}><RotateRightIcon width={16} height={16} /></PhotoAdjustButton>
            </div>
          </>
        )}

        {error && <p className="fawkes-photo-dialog__error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

// ---- Gewichten -------------------------------------------------------------------------------------
// The Gewichten tile's EDITABLE form — a close functional port of the Jmix AnimalWeightTileFragment:
// a latest-measurement line + status pill, a hand-drawn SVG trend chart (date-range filter + the
// resolved target-band zone), the measurement history with per-row edit/delete, "Toevoegen", and the
// "Streefbandbreedte" (target band) dialog. Reads GET /api/animals/{id}/weights; writes POST/PUT/DELETE
// /weights[/{id}] + PUT/DELETE /weight-target via useCustomMutation. The resolved band + status + the
// per-date band points (the chart's age-varying zone) are computed server-side (AnimalWeightRangeService).
interface WeightMeting {
  id: number; datum: string | null; gewicht: number | null; opmerking: string | null;
  source: string | null; version: number | null;
}
interface WeightTargetT {
  id: number; minKg: number | null; ideaalKg: number | null; maxKg: number | null;
  opmerking: string | null; changeReason: string | null; version: number | null;
}
interface ResolvedRange {
  minKg: number | null; idealKg: number | null; maxKg: number | null; sourceLabel: string | null; status: string | null;
}
interface RangePoint { datum: string; minKg: number | null; idealKg: number | null; maxKg: number | null }
interface WeightTile {
  metingen: WeightMeting[]; streefgewicht: WeightTargetT | null;
  resolvedRange: ResolvedRange | null; rangePoints: RangePoint[];
}

type WeightNotice = "saved" | "error" | "conflict" | "invalid" | null;

// Status pill from the server-resolved band (override OR breed/age curve). resolvedRange null → no band
// known; status null → band known but no measurement yet. Mirrors Jmix updateRangeLabels + statusOf.
function rangeStatus(resolved: ResolvedRange | null): { tone: StatusTone; key: string } {
  if (!resolved) return { tone: "neutral", key: "noTarget" };
  switch (resolved.status) {
    case "BELOW_RANGE": return { tone: "warning", key: "below" };
    case "ABOVE_RANGE": return { tone: "danger", key: "above" };
    case "WITHIN_RANGE": return { tone: "success", key: "within" };
    default: return { tone: "neutral", key: "pending" };
  }
}

// Accept "17,5" or "17.5"; return a finite number or null (blank/non-numeric → null), WITHOUT a
// positivity constraint. Lets the target dialog mirror the Jmix dialog's staged validation, which
// distinguishes "missing" (→ required) from "<= 0" (→ must be greater than 0).
function parseDecimal(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = Number(raw.trim().replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

const RANGE_KEYS = ["3m", "1m", "all"] as const;
type RangeKey = (typeof RANGE_KEYS)[number];

// ---- the trend chart (inline SVG, ported from Jmix renderTrendChart) -------------------------------
const CHART = { W: 980, H: 320, L: 78, R: 78, T: 14, B: 64, Y_TICKS: 6, MAX_X: 6 };

function fmtNum(v: number): string {
  return v.toFixed(2);
}
function fmtAxis(v: number): string {
  return String(Number(v.toFixed(1)));
}
function isoDate(d: string | null): string | null {
  return d ? d.slice(0, 10) : null;
}

function WeightChart({
  points, resolved, rangeByDate, locale, t,
}: {
  points: WeightMeting[]; resolved: ResolvedRange | null;
  rangeByDate: Map<string, RangePoint>; locale: string; t: (k: string) => string;
}) {
  // Complete, ascending points only.
  const sorted = points
    .filter((m) => m.datum != null && m.gewicht != null)
    .sort((a, b) => (a.datum ?? "").localeCompare(b.datum ?? ""));

  if (sorted.length === 0) {
    return <p className="fawkes-weights__chart-empty">{t("animalDetailView.weights.noChart")}</p>;
  }

  const values = sorted.map((m) => m.gewicht as number);
  let rawMin = Math.min(...values);
  let rawMax = Math.max(...values);
  const hasBand = resolved?.minKg != null && resolved?.maxKg != null;
  if (hasBand) {
    rawMin = Math.min(rawMin, resolved!.minKg as number);
    rawMax = Math.max(rawMax, resolved!.maxKg as number);
  }
  // Ronde stappen op de y-as (26, 28, 30 …) in plaats van 27,3 / 28,5: de kleinste stap die in zes
  // streepjes past, met één stap ruimte onder en boven de metingen en de streefband.
  const stap = [0.5, 1, 2, 5, 10, 20, 50].find((st) => Math.ceil(rawMax / st) - Math.floor(rawMin / st) + 2 <= CHART.Y_TICKS) ?? 100;
  const min = Math.max(0, (Math.floor(rawMin / stap) - 1) * stap);
  const max = (Math.ceil(rawMax / stap) + 1) * stap;
  const range = Math.max(max - min, 1);
  const tikken = Math.round((max - min) / stap);
  const plotW = CHART.W - CHART.L - CHART.R;
  const plotH = CHART.H - CHART.T - CHART.B;
  const n = sorted.length;
  const xAt = (i: number) => (n === 1 ? CHART.L + plotW / 2 : CHART.L + (plotW * i) / (n - 1));
  const yAt = (v: number) => {
    const y = CHART.T + plotH - ((v - min) / range) * plotH;
    return Math.max(CHART.T, Math.min(CHART.T + plotH, y));
  };
  const baselineY = CHART.T + plotH;

  // Gridlines + y labels.
  const grid: ReactNode[] = [];
  for (let i = 0; i <= tikken; i++) {
    const ratio = i / tikken;
    const y = CHART.T + plotH * ratio;
    const labelValue = max - (max - min) * ratio;
    grid.push(
      <line key={`g${i}`} className="fawkes-weights__chart-grid" x1={CHART.L} y1={y} x2={CHART.L + plotW} y2={y}
        strokeWidth={1} strokeDasharray="5 6" />,
      <text key={`gl${i}`} className="fawkes-weights__chart-axis-label" x={CHART.L - 14} y={y + 5}
        textAnchor="end" fontSize={19} fontWeight={500}>{fmtAxis(labelValue).replace(".", locale.startsWith("nl") ? "," : ".")}</text>,
    );
  }

  // X labels (thinned to at most MAX_X).
  const xStep = n <= CHART.MAX_X ? 1 : Math.ceil((n - 1) / (CHART.MAX_X - 1));
  const xLabels: ReactNode[] = [];
  sorted.forEach((m, i) => {
    if (!(n <= CHART.MAX_X || i === 0 || i === n - 1 || i % xStep === 0)) return;
    let x = xAt(i);
    let anchor: "start" | "middle" | "end" = "middle";
    if (n > 1 && i === 0) { anchor = "start"; x += 2; } else if (n > 1 && i === n - 1) { anchor = "end"; x -= 2; }
    const iso = isoDate(m.datum);
    // Ontwerp 2026-09: "1 jul", "15 jul" onder de as; het jaar staat al in "Laatste metingen".
    const label = iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(locale, { day: "numeric", month: "short" }).replace(".", "") : "";
    xLabels.push(
      <text key={`x${i}`} className="fawkes-weights__chart-axis-label" x={x} y={CHART.H - 30}
        textAnchor={anchor} fontSize={19} fontWeight={500}>{label}</text>,
    );
  });

  // Target-band zone: an age-varying polygon when the per-date bands differ across the shown points,
  // else a simple rectangle from the resolved band (mirrors Jmix buildTargetRangeZone).
  const shownBands = sorted.map((m) => rangeByDate.get(isoDate(m.datum) ?? "") ?? null);
  const bandPts = shownBands.filter((b): b is RangePoint => b != null && b.minKg != null && b.maxKg != null);
  const varying = bandPts.length >= 2 && bandPts.length === n
    && bandPts.some((b) => b.minKg !== bandPts[0].minKg || b.maxKg !== bandPts[0].maxKg);

  let zone: ReactNode = null;
  if (hasBand && varying) {
    const top = sorted.map((_m, i) => `${fmtNum(xAt(i))},${fmtNum(yAt((shownBands[i]?.maxKg ?? resolved!.maxKg) as number))}`);
    const bottom = sorted.map((_m, i) => `${fmtNum(xAt(i))},${fmtNum(yAt((shownBands[i]?.minKg ?? resolved!.minKg) as number))}`).reverse();
    const idealValid = shownBands.every((b) => b?.idealKg != null);
    zone = (
      <>
        <polygon className="fawkes-weights__range-zone" points={[...top, ...bottom].join(" ")} fillOpacity={0.18} />
        {idealValid && (
          <polyline className="fawkes-weights__range-ideal" fill="none" strokeWidth={1.4} strokeDasharray="2 4"
            points={sorted.map((_m, i) => `${fmtNum(xAt(i))},${fmtNum(yAt(shownBands[i]!.idealKg as number))}`).join(" ")} />
        )}
      </>
    );
  } else if (hasBand) {
    const yTop = yAt(resolved!.maxKg as number);
    const yBottom = yAt(resolved!.minKg as number);
    const yIdeal = resolved!.idealKg != null ? yAt(resolved!.idealKg) : null;
    zone = (
      <>
        <rect className="fawkes-weights__range-zone" x={CHART.L} y={yTop} width={plotW} height={Math.max(2, yBottom - yTop)}
          rx={4} fillOpacity={0.18} />
        <line className="fawkes-weights__range-edge" x1={CHART.L} y1={yTop} x2={CHART.L + plotW} y2={yTop} strokeWidth={1} strokeDasharray="4 4" />
        <line className="fawkes-weights__range-edge" x1={CHART.L} y1={yBottom} x2={CHART.L + plotW} y2={yBottom} strokeWidth={1} strokeDasharray="4 4" />
        {yIdeal != null && (
          <line className="fawkes-weights__range-ideal" x1={CHART.L} y1={yIdeal} x2={CHART.L + plotW} y2={yIdeal} strokeWidth={1.4} strokeDasharray="2 4" />
        )}
      </>
    );
  }

  const linePoints = sorted.map((m, i) => `${fmtNum(xAt(i))},${fmtNum(yAt(m.gewicht as number))}`).join(" ");

  return (
    <svg className="fawkes-weights__chart-svg" viewBox={`0 0 ${CHART.W} ${CHART.H}`} role="img"
      aria-label={t("animalDetailView.weights.chartTitle")}>
      <rect className="fawkes-weights__chart-bg" x={0} y={0} width={CHART.W} height={CHART.H} rx={10} />
      {grid}
      {zone}
      <line className="fawkes-weights__chart-axis" x1={CHART.L} y1={baselineY} x2={CHART.L + plotW} y2={baselineY} strokeWidth={1.2} />
      <line className="fawkes-weights__chart-axis" x1={CHART.L} y1={CHART.T} x2={CHART.L} y2={baselineY} strokeWidth={1.2} />
      <polyline className="fawkes-weights__chart-line" fill="none" strokeWidth={4} strokeLinecap="round" points={linePoints} />
      {sorted.map((m, i) => {
        // Mirror Jmix buildPointTooltip: date / weight / target band / status / source.
        const band = rangeByDate.get(isoDate(m.datum) ?? "") ?? null;
        const bMin = band?.minKg ?? resolved?.minKg ?? null;
        const bMax = band?.maxKg ?? resolved?.maxKg ?? null;
        const g = m.gewicht as number;
        const lines = [fmtFormalDate(m.datum), num(m.gewicht, locale, " kg")];
        if (bMin != null && bMax != null) {
          lines.push(`${t("animalDetailView.weights.target")}: ${num(bMin, locale)}–${num(bMax, locale)} kg`);
          const key = g < bMin ? "below" : g > bMax ? "above" : "within";
          lines.push(`${t("animalDetailView.weights.tipStatus")}: ${t(`animalDetailView.weights.status.${key}`)}`);
        }
        if (resolved?.sourceLabel) lines.push(`${t("animalDetailView.weights.tipSource")}: ${resolved.sourceLabel}`);
        return (
          <circle key={m.id} className="fawkes-weights__chart-dot" cx={xAt(i)} cy={yAt(m.gewicht as number)} r={7} strokeWidth={3}>
            <title>{lines.join("\n")}</title>
          </circle>
        );
      })}
      {xLabels}
    </svg>
  );
}

// ---- measurement add/edit dialog -------------------------------------------------------------------
// Ontwerp 2026-09: icoon + ondertitel in de kop, een uitleg onder elk veld en "kg" als vakje vóór het
// gewicht. Nog steeds alleen Datum + Gewicht, net als het Jmix-venster.
function MeasurementDialog({ open, row, saving, onClose, onSubmit, eerdereWeegschalen = [], t }: {
  open: boolean; row: WeightMeting | null; saving: boolean;
  onClose: () => void; onSubmit: (v: { datum: string; gewicht: number; opmerking: string | null }) => void;
  /** Wat er bij eerdere wegingen als weegschaal/opmerking stond, als suggestie. */
  eerdereWeegschalen?: string[];
  t: (k: string) => string;
}) {
  const today = todayIso();
  const [datum, setDatum] = useState(today);
  const [gewicht, setGewicht] = useState("");
  // Op welke weegschaal is gewogen (Alma, 2026-10-09): verschillende weegschalen geven verschillen, en dan wil je
  // later kunnen zien waarom een meting afwijkt. Komt in de opmerking van de weging.
  const [opmerking, setOpmerking] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDatum(row?.datum ? row.datum.slice(0, 10) : today);
    setGewicht(row?.gewicht != null ? String(row.gewicht).replace(".", ",") : "");
    // Een nieuwe weging begint met de weegschaal van de vorige: meestal weeg je op dezelfde.
    setOpmerking(row ? (row.opmerking ?? "") : (eerdereWeegschalen[0] ?? ""));
    setErr(null);
  }, [open, row?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    if (!datum) { setErr(t("animalDetailView.weights.err.dateRequired")); return; }
    if (datum > today) { setErr(t("animalDetailView.weights.err.dateFuture")); return; }
    // Mirror Jmix's two distinct weight messages: empty/unparseable -> "verplicht",
    // a parsed but non-positive value -> "moet groter zijn dan 0".
    const w = Number(gewicht.trim().replace(",", "."));
    if (gewicht.trim() === "" || !Number.isFinite(w)) { setErr(t("animalDetailView.weights.err.weightRequired")); return; }
    if (w <= 0) { setErr(t("animalDetailView.weights.err.weight")); return; }
    onSubmit({ datum, gewicht: w, opmerking: opmerking.trim() || null });
  };

  return (
    <Modal
      open={open}
      title={t(row ? "animalDetailView.weights.editTitle" : "animalDetailView.weights.addTitle")}
      subtitle={t(row ? "animalDetailView.weights.editSubtitle" : "animalDetailView.weights.addSubtitle")}
      icon={<WeegGlyph />}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={saving} onClick={submit}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-weights-form vg-form">
        <FieldShell label={t("animalDetailView.weights.col.datum")} required>
          <DateField className="fui-field__input" max={today} value={datum} aria-required="true" onChange={(e) => setDatum(e.target.value)} />
          <span className="vg-form__hint">{t("animalDetailView.weights.dateHint")}</span>
        </FieldShell>
        <FieldShell label={`${t("animalDetailView.weights.col.gewicht")} (kg)`} required>
          <span className="vg-invoer">
            <span className="vg-invoer__eenheid" aria-hidden="true"><KgGlyph /></span>
            <input className="fui-field__input" inputMode="decimal" placeholder={t("animalDetailView.weights.weightPlaceholder")}
              value={gewicht} aria-required="true" autoFocus onChange={(e) => setGewicht(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
          </span>
          <span className="vg-form__hint">{t("animalDetailView.weights.weightHint")}</span>
        </FieldShell>
        <FieldShell label={t("animalDetailView.weights.weegschaal")}>
          <input className="fui-field__input" list="vg-weegschalen" maxLength={1000} value={opmerking}
            placeholder={t("animalDetailView.weights.weegschaalPlaats")} onChange={(e) => setOpmerking(e.target.value)} />
          <datalist id="vg-weegschalen">
            {eerdereWeegschalen.map((w) => <option key={w} value={w} />)}
          </datalist>
          <span className="vg-form__hint">{t("animalDetailView.weights.weegschaalHint")}</span>
        </FieldShell>
        {err && <p className="fawkes-error">{err}</p>}
      </div>
    </Modal>
  );
}

const vgLijn = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" } as const;
/** Een weeggewicht met handvat (kop van de tegel en de vensters). */
function WeegGlyph() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" {...vgLijn}>
      <path d="M9 7.5V6a3 3 0 0 1 6 0v1.5" />
      <path d="M6.2 7.5h11.6l1.9 11.8a1.5 1.5 0 0 1-1.5 1.7H5.8a1.5 1.5 0 0 1-1.5-1.7z" />
      <circle cx="12" cy="13.5" r="2.4" />
    </svg>
  );
}
/** Het "kg"-gewichtje vóór het invoerveld. */
function KgGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9.2 7V5.8a2.8 2.8 0 0 1 5.6 0V7" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
      <path d="M6 7h12l2 12.4A1.4 1.4 0 0 1 18.6 21H5.4A1.4 1.4 0 0 1 4 19.4z" fill="currentColor" />
      <text x="12" y="17.6" textAnchor="middle" fontSize="7.2" fontWeight="700" fill="#fff" fontFamily="system-ui, sans-serif">kg</text>
    </svg>
  );
}
function BelGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" {...vgLijn}>
      <path d="M18 8a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" /><path d="M10.5 20a2 2 0 0 0 3 0" />
    </svg>
  );
}
function OogGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" {...vgLijn}>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function StavenGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" {...vgLijn}>
      <path d="M6 20v-6M10 20V9M14 20v-8M18 20V5" />
    </svg>
  );
}

/** De kop van de gewichtentegel: vierkant icoonvlak en de titel (ontwerp 2026-09). */
export function GewichtTegelKop({ titel }: { titel: string }) {
  return (
    <span className="vd-kop">
      <span className="vd-kop__icoon vg-kop__icoon" aria-hidden="true"><WeegGlyph /></span>
      <span className="vd-kop__tekst"><span className="vd-kop__titel">{titel}</span></span>
    </span>
  );
}

/** "+0,3 kg" / "−0,2 kg" / "0 kg" — het verschil met de vorige meting. */
function verschilTekst(delta: number, locale: string): string {
  const afgerond = Math.round(delta * 10) / 10;
  const abs = Math.abs(afgerond).toLocaleString(locale, { maximumFractionDigits: 1 });
  if (afgerond > 0) return `+${abs} kg`;
  if (afgerond < 0) return `−${abs} kg`;
  return `${abs} kg`;
}

/** Het verschil van elke meting met de meting ervóór (nieuwste eerst; de oudste heeft er geen). */
export function gewichtVerschillen(rows: { id: number; gewicht: number | null }[]): Map<number, number> {
  const metGewicht = rows.filter((r) => r.gewicht != null);
  const uit = new Map<number, number>();
  metGewicht.forEach((r, i) => {
    const vorige = metGewicht[i + 1];
    if (vorige) uit.set(r.id, (r.gewicht as number) - (vorige.gewicht as number));
  });
  return uit;
}

function VerschilCel({ delta, locale }: { delta: number | undefined; locale: string }) {
  if (delta == null) return <span className="vg-verschil vg-verschil--geen">–</span>;
  const richting = Math.round(delta * 10) === 0 ? "gelijk" : delta > 0 ? "op" : "neer";
  return (
    <span className={`vg-verschil vg-verschil--${richting}`}>
      {richting === "op" && <ArrowUpIcon width={16} height={16} aria-hidden="true" />}
      {richting === "neer" && <ArrowDownIcon width={16} height={16} aria-hidden="true" />}
      {verschilTekst(delta, locale)}
    </span>
  );
}

// ---- target-band dialog ----------------------------------------------------------------------------
function TargetDialog({ open, target, saving, onClose, onSubmit, onRemove, t }: {
  open: boolean; target: WeightTargetT | null; saving: boolean; onClose: () => void;
  onSubmit: (v: { minKg: number; ideaalKg: number | null; maxKg: number; opmerking: string | null; changeReason: string | null }) => void;
  onRemove: () => void; t: (k: string) => string;
}) {
  const [minKg, setMin] = useState("");
  const [ideaalKg, setIdeal] = useState("");
  const [maxKg, setMax] = useState("");
  const [opmerking, setOpmerking] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMin(target?.minKg != null ? String(target.minKg) : "");
    setIdeal(target?.ideaalKg != null ? String(target.ideaalKg) : "");
    setMax(target?.maxKg != null ? String(target.maxKg) : "");
    setOpmerking(target?.opmerking ?? "");
    setReason(target?.changeReason ?? "");
    setErr(null);
  }, [open, target?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    // Mirror the Jmix openTargetRangeDialog validation, in the same staged order: required min+max,
    // then all-values-positive, then min < max, then ideal within the band. A non-numeric ideal is
    // ignored (treated as null), exactly as Jmix's parseWeightInput does.
    const mn = parseDecimal(minKg);
    const mx = parseDecimal(maxKg);
    const id = ideaalKg.trim() ? parseDecimal(ideaalKg) : null;
    if (mn == null || mx == null) { setErr(t("animalDetailView.weights.err.minMaxRequired")); return; }
    if (mn <= 0 || mx <= 0 || (id != null && id <= 0)) { setErr(t("animalDetailView.weights.err.weightsPositive")); return; }
    if (mn >= mx) { setErr(t("animalDetailView.weights.err.minLtMax")); return; }
    if (id != null && (id < mn || id > mx)) { setErr(t("animalDetailView.weights.err.idealBetween")); return; }
    onSubmit({ minKg: mn, ideaalKg: id, maxKg: mx, opmerking: opmerking.trim() || null, changeReason: reason.trim() || null });
  };

  return (
    <Modal
      open={open}
      title={t(target ? "animalDetailView.weights.targetEditTitle" : "animalDetailView.weights.targetTitle")}
      subtitle={t("animalDetailView.weights.targetSubtitle")}
      icon={<StavenGlyph />}
      onClose={onClose}
      footer={
        <>
          {target && <Button variant="secondary" disabled={saving} onClick={onRemove}>{t("animalDetailView.weights.remove")}</Button>}
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={saving} onClick={submit}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-weights-form">
        <div className="fawkes-weights-form__row3">
          <FieldShell label={`${t("animalDetailView.weights.min")} (kg)`} required>
            <input className="fui-field__input" inputMode="decimal" required aria-required="true"
              placeholder={t("animalDetailView.weights.minPlaceholder")} value={minKg} onChange={(e) => setMin(e.target.value)} />
          </FieldShell>
          <FieldShell label={`${t("animalDetailView.weights.idealLabel")} (kg, ${t("animalDetailView.weights.optional")})`}>
            <input className="fui-field__input" inputMode="decimal"
              placeholder={t("animalDetailView.weights.idealPlaceholder")} value={ideaalKg} onChange={(e) => setIdeal(e.target.value)} />
          </FieldShell>
          <FieldShell label={`${t("animalDetailView.weights.max")} (kg)`} required>
            <input className="fui-field__input" inputMode="decimal" required aria-required="true"
              placeholder={t("animalDetailView.weights.maxPlaceholder")} value={maxKg} onChange={(e) => setMax(e.target.value)} />
          </FieldShell>
        </div>
        <FieldShell label={`${t("animalDetailView.weights.col.opmerking")} (${t("animalDetailView.weights.optional")})`}>
          <input className="fui-field__input" value={opmerking} onChange={(e) => setOpmerking(e.target.value)} />
        </FieldShell>
        <FieldShell label={t("animalDetailView.weights.changeReason")}>
          <input className="fui-field__input" placeholder={t("animalDetailView.weights.reasonPlaceholder")} value={reason} onChange={(e) => setReason(e.target.value)} />
        </FieldShell>
        {err && <p className="fawkes-error">{err}</p>}
      </div>
    </Modal>
  );
}

const WEIGHT_PREVIEW_LIMIT = 5;

export function WeightsTile({ animalId, chartHidden }: {
  animalId: string;
  /** Per-user "Grafiek tonen" preference (gewichten.chart, the Jmix chart-visible setting). */
  chartHidden?: boolean;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data: tile, isLoading, isError, refetch } = useTile<WeightTile>(animalId, "weights");
  const { mutate, isLoading: saving } = useCustomMutation();

  const [editor, setEditor] = useState<{ open: boolean; row: WeightMeting | null }>({ open: false, row: null });
  // Een taak "Wegen" uitvoeren: dit invoerscherm, en na opslaan is de taak af.
  const taak = useTaakVoorTegel(animalId, ["WEGING"], () => setEditor({ open: true, row: null }), editor.open);
  const [targetOpen, setTargetOpen] = useState(false);
  const [confirmRow, setConfirmRow] = useState<WeightMeting | null>(null);
  const [rangeKey, setRangeKey] = useState<RangeKey>("3m");
  const [notice, setNotice] = useState<WeightNotice>(null);
  const [showAllWeights, setShowAllWeights] = useState(false);
  // Single-select master list (Jmix parity): the grid selects a row and the Wijzigen/Verwijderen
  // buttons act on it — acc has no per-row icons.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const metingen = useMemo(() => tile?.metingen ?? [], [tile]);
  const rowsDesc = useMemo(
    () => [...metingen].sort((a, b) => (b.datum ?? "").localeCompare(a.datum ?? "")),
    [metingen],
  );
  // Jmix caps the inline grid at 5 rows and shows the remainder in an "Alles bekijken" dialog.
  const previewRows = rowsDesc.slice(0, WEIGHT_PREVIEW_LIMIT);
  const rangeByDate = useMemo(() => {
    const m = new Map<string, RangePoint>();
    for (const p of tile?.rangePoints ?? []) m.set(p.datum, p);
    return m;
  }, [tile]);

  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;

  const resolved = tile?.resolvedRange ?? null;
  const latest = rowsDesc.find((m) => m.datum != null && m.gewicht != null) ?? null;
  const st = rangeStatus(resolved);

  // Apply the chart's date-range filter (most-recent-N-days; mirrors applyChartDateRangeFilter).
  const cutoff = rangeKey === "all" ? null
    : localDateIso(new Date(Date.now() - (rangeKey === "3m" ? 90 : 30) * 86_400_000));
  const chartPoints = cutoff ? metingen.filter((m) => (isoDate(m.datum) ?? "") >= cutoff) : metingen;

  const run = (
    url: string,
    method: "post" | "put" | "delete",
    values: Record<string, unknown> | undefined,
    onOk?: () => void,
  ) => {
    setNotice(null);
    mutate(
      { url: `animals/${animalId}/${url}`, method, values: values ?? {} },
      {
        onSuccess: () => { refetch(); onOk?.(); setNotice("saved"); },
        onError: (err) => {
          const sc = (err as { statusCode?: number }).statusCode;
          setNotice(sc === 409 ? "conflict" : sc === 400 ? "invalid" : "error");
        },
      },
    );
  };

  const submitMeasurement = (v: { datum: string; gewicht: number; opmerking: string | null }) => {
    const row = editor.row;
    if (row) {
      run(`weights/${row.id}`, "put", { ...v, version: row.version }, () => setEditor({ open: false, row: null }));
    } else {
      run("weights", "post", v, () => { taak.klaar(); setEditor({ open: false, row: null }); });
    }
  };

  const submitTarget = (v: { minKg: number; ideaalKg: number | null; maxKg: number; opmerking: string | null; changeReason: string | null }) => {
    run("weight-target", "put", { ...v, version: tile?.streefgewicht?.version ?? null }, () => setTargetOpen(false));
  };

  const noticeText =
    notice === "saved" ? t("animalDetailView.weights.saved")
    : notice === "conflict" ? t("animalDetailView.weights.saveConflict")
    : notice === "invalid" ? t("animalDetailView.weights.saveInvalid")
    : notice === "error" ? t("animalDetailView.weights.saveError")
    : null;

  const selectedRow = selectedId != null ? rowsDesc.find((m) => String(m.id) === selectedId) ?? null : null;
  const verschillen = gewichtVerschillen(rowsDesc);
  const laatsteVerschil = latest ? verschillen.get(latest.id) : undefined;
  const band = resolved?.minKg != null && resolved?.maxKg != null
    ? `${num(resolved.minKg, locale)} – ${num(resolved.maxKg, locale)} kg` : null;
  // "Labrador Retriever — Volwassen" uit de regel van de streefband, als label onder de kop.
  const bron = resolved?.sourceLabel ? resolved.sourceLabel.replace(" — ", " • ") : null;
  const bandToon = st.key === "within" ? "binnen" : st.key === "below" ? "onder" : st.key === "above" ? "boven" : "geen";

  const MetingRegels = ({ rows, kiesbaar }: { rows: WeightMeting[]; kiesbaar: boolean }) => (
    <ul className="vg-metingen__lijst">
      {rows.map((m) => {
        const gekozen = kiesbaar && String(m.id) === selectedId;
        const inhoud = (
          <>
            <span className="vg-metingen__datum">{kaartDatum(m.datum)}</span>
            <strong className="vg-metingen__gewicht">{num(m.gewicht, locale, " kg")}</strong>
            <VerschilCel delta={verschillen.get(m.id)} locale={locale} />
            {m.opmerking && <span className="vg-metingen__weegschaal" title={m.opmerking}>{m.opmerking}</span>}
          </>
        );
        return (
          <li key={m.id} className={`vg-metingen__regel${gekozen ? " is-gekozen" : ""}`}>
            {kiesbaar ? (
              <button type="button" className="vg-metingen__knop" aria-pressed={gekozen}
                onClick={() => setSelectedId(gekozen ? null : String(m.id))}
                onDoubleClick={() => setEditor({ open: true, row: m })}>
                {inhoud}
              </button>
            ) : <div className="vg-metingen__knop">{inhoud}</div>}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="fawkes-weights vg">
      <div className="vg-top">
        {bron ? <span className="vg-bron">{bron}</span> : <span />}
        <select className="fui-field__input fui-select vg-periode" aria-label={t("animalDetailView.weights.periodLabel")}
          value={rangeKey} onChange={(e) => setRangeKey(e.target.value as RangeKey)}>
          {RANGE_KEYS.map((k) => <option key={k} value={k}>{t(`animalDetailView.weights.period.${k}`)}</option>)}
        </select>
      </div>

      <div className="vg-cijfers">
        <div className="vg-cijfer">
          <strong className="vg-cijfer__groot">{latest ? num(latest.gewicht, locale, " kg", 1) : "—"}</strong>
          <span>{t("animalDetailView.weights.current")}</span>
        </div>
        <div className="vg-cijfer vg-cijfer--datum">
          <strong>{latest ? kaartDatum(latest.datum) : "—"}</strong>
          <span>{t("animalDetailView.weights.latest")}</span>
        </div>
        {laatsteVerschil != null && (
          <div className={`vg-trend vg-trend--${Math.round(laatsteVerschil * 10) === 0 ? "gelijk" : laatsteVerschil > 0 ? "op" : "neer"}`}>
            <span className="vg-trend__pijl" aria-hidden="true">
              {laatsteVerschil >= 0 ? <ArrowUpIcon width={18} height={18} /> : <ArrowDownIcon width={18} height={18} />}
            </span>
            <span>
              <strong>{verschilTekst(laatsteVerschil, locale)}</strong>
              <span>{t("animalDetailView.weights.sincePrevious")}</span>
            </span>
          </div>
        )}
        <div className="vg-band">
          <span className={`vg-band__pil vg-band__pil--${bandToon}`}>
            <span className="vg-band__vink" aria-hidden="true">
              {bandToon === "binnen" ? <CheckIcon width={14} height={14} /> : bandToon === "geen" ? "?" : "!"}
            </span>
            {t(`animalDetailView.weights.band.${st.key}`)}
          </span>
          {band && <span className="vg-band__bereik">{t("animalDetailView.weights.bandRange")}: {band}</span>}
        </div>
      </div>

      {!chartHidden && (
        <div className="vg-grafiek">
          <span className="vg-grafiek__titel">{t("animalDetailView.weights.chartTitle")}</span>
          <div className="fawkes-weights__chart">
            <WeightChart points={chartPoints} resolved={resolved} rangeByDate={rangeByDate} locale={locale} t={t} />
          </div>
        </div>
      )}

      <div className="vg-onder">
        <div className="vg-metingen">
          <h4 className="vg-metingen__kop">{t("animalDetailView.weights.recent")}</h4>
          {previewRows.length === 0
            ? <p className="vg-metingen__leeg">{t("animalDetailView.weights.none")}</p>
            : <MetingRegels rows={previewRows} kiesbaar />}
        </div>
        {/* Knoppen zoals het ontwerp: Toevoegen bovenaan, Wijzigen/Verwijderen werken op de gekozen regel. */}
        <div className="vg-knoppen">
          <Button variant="primary" className="vg-knop vg-knop--hoofd" onClick={() => setEditor({ open: true, row: null })}>
            <PlusCircleSolidIcon width={20} height={20} /> {t("animalDetailView.weights.add")}
          </Button>
          <Button variant="secondary" className="vg-knop" disabled={!selectedRow}
            title={selectedRow ? undefined : t("animalDetailView.weights.pickRow")}
            onClick={() => selectedRow && setEditor({ open: true, row: selectedRow })}>
            <PencilIcon width={17} height={17} /> {t("animalDetailView.weights.edit")}
          </Button>
          <Button variant="secondary" className="vg-knop" disabled={!selectedRow}
            title={selectedRow ? undefined : t("animalDetailView.weights.pickRow")}
            onClick={() => selectedRow && setConfirmRow(selectedRow)}>
            <TrashIcon width={17} height={17} /> {t("animalDetailView.weights.delete")}
          </Button>
          <Button variant="secondary" className="vg-knop" onClick={() => setTargetOpen(true)}>
            <StavenGlyph /> {t("animalDetailView.weights.target")}
          </Button>
        </div>
        {/* Alleen als er meer metingen zijn dan er in beeld staan; anders opent hij hetzelfde lijstje
            dat er al is, en dat is een knop die niets toevoegt. */}
        {rowsDesc.length > previewRows.length && (
          <div className="vg-alles">
            <Button variant="secondary" onClick={() => setShowAllWeights(true)}>
              <OogGlyph /> {t("animalDetailView.weights.viewAll")}
            </Button>
          </div>
        )}
      </div>

      {noticeText && (
        <p className={notice === "saved" ? "fawkes-weights__notice" : "fawkes-error"} role="status">{noticeText}</p>
      )}

      <MeasurementDialog open={editor.open} row={editor.row} saving={saving}
        eerdereWeegschalen={[...new Set(metingen.map((m) => m.opmerking?.trim()).filter((x): x is string => !!x))]}
        onClose={() => setEditor({ open: false, row: null })} onSubmit={submitMeasurement} t={t} />
      <TargetDialog open={targetOpen} target={tile?.streefgewicht ?? null} saving={saving}
        onClose={() => setTargetOpen(false)} onSubmit={submitTarget}
        onRemove={() => run("weight-target", "delete", undefined, () => setTargetOpen(false))} t={t} />
      <ConfirmDialog
        open={confirmRow != null}
        title={t("animalDetailView.weights.deleteTitle")}
        confirmLabel={t("animalDetailView.weights.delete")}
        cancelLabel={t("common.cancel")}
        danger
        busy={saving}
        onConfirm={() => { const r = confirmRow; if (r) run(`weights/${r.id}`, "delete", undefined, () => { setConfirmRow(null); setSelectedId(null); }); }}
        onCancel={() => setConfirmRow(null)}
      >
        {t("animalDetailView.weights.deleteConfirm")}
      </ConfirmDialog>
      <Modal
        open={showAllWeights}
        title={t("animalDetailView.weights.restTitle")}
        icon={<WeegGlyph />}
        onClose={() => setShowAllWeights(false)}
        footer={<Button variant="primary" onClick={() => setShowAllWeights(false)}>{t("common.close")}</Button>}
      >
        {rowsDesc.length === 0
          ? <p className="vg-metingen__leeg">{t("animalDetailView.weights.none")}</p>
          : <div className="vg-metingen vg-metingen--los"><MetingRegels rows={rowsDesc} kiesbaar={false} /></div>}
      </Modal>
    </div>
  );
}

// ---- Voeding ---------------------------------------------------------------------------------------
// Editable port of the Jmix feeding fragment (AnimalFeedingTileFragment.{java,xml}): a bordered plan
// card (feed type/brand, amount/times, note) with a "Schema bewerken" dialog, over a feeding-log card
// (latest line + a selectable preview of the 3 most-recent feedings) with Toevoegen/Wijzigen/
// Verwijderen actions and a "Rest bekijken" overview dialog. Reads GET /api/animals/{id}/feeding and
// writes via PUT /feeding/plan, POST/PUT/DELETE /feeding/log (ROLE_ANIMAL_FEEDING_EDIT). Diet status is
// intentionally not shown/edited — the Jmix fragment never exposes it.
interface FeedingPlanData {
  id: number; version: number | null; feedType: string | null; brand: string | null;
  amountText: string | null; feedingTimes: string | null; dietStatus: string | null;
  note: string | null; activeFrom: string | null; activeTo: string | null;
}
interface FeedingLogData {
  id: number; version: number | null; fedAt: string | null; amountGrams: number | null;
  behavior: string | null; note: string | null;
  /** Hoeveel er van de portie gegeten is; leeg zolang dat niet bekend is. */
  eatenGrams?: number | null;
}
interface FeedingTileData {
  plan: FeedingPlanData | null; log: FeedingLogData[];
  /** Het huidige plan in onderdelen (brok, natvoer, vocht) en de plannen ervoor. */
  samenstelling?: Samenstelling | null; historie?: HistoriePlan[];
}

const FEEDING_PREVIEW_LIMIT = 3;
const BEHAVIOR_CODES = ["ALLES_GEGETEN", "BIJNA_OP", "GOED_GEGETEN", "SLECHT_GEGETEN", "ONBEKEND"] as const;
// Mirror the Jmix AnimalFeedingBehavior cssClass mapping → tone suffix for the badge.
const BEHAVIOR_TONE: Record<string, string> = {
  ALLES_GEGETEN: "great", BIJNA_OP: "almost", GOED_GEGETEN: "good", SLECHT_GEGETEN: "poor", ONBEKEND: "unknown",
};

interface LogForm { fedAt: string; amountGrams: string; eatenGrams: string; note: string }
type FeedingNotice =
  | "saved" | "conflict" | "error" | "timeRequired"
  | "fedAtRequired" | "amountInvalid" | "behaviorRequired" | "eatenTooMuch";

// Split the stored feeding-times text ("08:00 / 18:00", "08:00 · 18:00") into up to 3 HH:MM tokens —
// the inverse of the Jmix joinFeedingTimes/parseFeedingTimes.
function parseFeedingTimes(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(/[^0-9:]+/).map((s) => s.trim()).filter((s) => /^\d{1,2}:\d{2}$/.test(s)).slice(0, 3);
}
// An OffsetDateTime ISO string → the "yyyy-MM-ddTHH:mm" a <input type="datetime-local"> expects.
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : "";
}
function nowLocalInput(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function logToForm(l: FeedingLogData | null): LogForm {
  return {
    fedAt: toLocalInput(l?.fedAt ?? null) || nowLocalInput(),
    amountGrams: l?.amountGrams != null ? String(l.amountGrams) : "",
    eatenGrams: l?.eatenGrams != null ? String(l.eatenGrams) : "", note: l?.note ?? "",
  };
}

// ── Voeding: bouwstenen van het ontwerp 2026-09 ──────────────────────────────────────────────────

/**
 * De hoeveelheid per voerbeurt die een nieuwe regel al krijgt. Uit het schema: "3 × 75 g" → 75; alleen een
 * dagtotaal ("225 g per dag") → gedeeld door het aantal voertijden; één getal ("75 g") → dat getal. Staat
 * er niets bruikbaars in het schema, dan de hoeveelheid van de laatste registratie. Anders null.
 */
export function standaardHoeveelheid(
  schema: string | null,
  aantalVoertijden: number,
  log: { amountGrams: number | null }[],
): number | null {
  const tekst = (schema ?? "").replace(",", ".");
  const keer = tekst.match(/(\d+)\s*[x×*]\s*(\d+(?:\.\d+)?)\s*(?:g|gr|gram)?\b/i);
  if (keer) return Math.round(Number(keer[2]));
  const getal = tekst.match(/(\d+(?:\.\d+)?)\s*(?:g|gr|gram)\b/i);
  if (getal) {
    const g = Number(getal[1]);
    const perDag = /per\s*dag|p\/?d|daags/i.test(tekst);
    return Math.round(perDag && aantalVoertijden > 1 ? g / aantalVoertijden : g);
  }
  const vorige = log.find((l) => l.amountGrams != null && l.amountGrams > 0);
  return vorige?.amountGrams ?? null;
}


/** Een gewichtje (hoeveelheid). */
function GewichtGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="6" r="2.5" /><path d="M8.5 9h7l3 11h-13z" />
    </svg>
  );
}
/** Een vel met regels (opmerking). */
function FileTextGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4M9 12h6M9 16h6" />
    </svg>
  );
}

/** Het eerstvolgende tijdstip uit het schema, en of er vandaag een voorbije voertijd zonder registratie is. */
export function volgendeVoerbeurt(
  tijden: string[],
  log: { fedAt: string | null }[],
  nu: Date = new Date(),
): { tijd: string; minuten: number; morgen: boolean; achter: boolean } | null {
  if (tijden.length === 0) return null;
  const minutenVan = (hhmm: string) => {
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
  };
  const nuMin = nu.getHours() * 60 + nu.getMinutes();
  const gesorteerd = [...tijden].sort((a, b) => minutenVan(a) - minutenVan(b));
  const straks = gesorteerd.find((tijd) => minutenVan(tijd) > nuMin);
  const volgende = straks ?? gesorteerd[0];
  const morgen = !straks;
  const minuten = morgen ? 24 * 60 - nuMin + minutenVan(volgende) : minutenVan(volgende) - nuMin;
  // Achter op schema: de laatste voertijd die vandaag al voorbij is, heeft geen registratie vanaf een uur ervoor.
  const voorbij = gesorteerd.filter((tijd) => minutenVan(tijd) <= nuMin).pop();
  let achter = false;
  if (voorbij) {
    const vanaf = new Date(nu.getFullYear(), nu.getMonth(), nu.getDate(), 0, minutenVan(voorbij) - 60).getTime();
    achter = !log.some((l) => l.fedAt && new Date(l.fedAt).getTime() >= vanaf);
  }
  return { tijd: volgende, minuten, morgen, achter };
}

/** "Over 2 uur en 15 minuten" / "Morgen, over 13 uur". */
function wachtTekst(minuten: number, morgen: boolean, t: (k: string, o?: { args?: string[] }) => string): string {
  const uur = Math.floor(minuten / 60);
  const min = minuten % 60;
  const deel =
    uur > 0 && min > 0 ? t("animalDetailView.feeding.inHoursMinutes", { args: [String(uur), String(min)] })
    : uur > 0 ? t("animalDetailView.feeding.inHours", { args: [String(uur)] })
    : t("animalDetailView.feeding.inMinutes", { args: [String(Math.max(1, min))] });
  return morgen ? `${t("animalDetailView.feeding.tomorrow")}, ${deel.charAt(0).toLowerCase()}${deel.slice(1)}` : deel;
}

/** "Vandaag", "Gisteren" of de datum. */
function dagLabel(iso: string | null, t: (k: string) => string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const vandaag = new Date();
  const dag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const verschil = Math.round((dag(vandaag) - dag(d)) / 86_400_000);
  if (verschil === 0) return t("animalDetailView.feeding.today");
  if (verschil === 1) return t("animalDetailView.feeding.yesterday");
  return fmtFormalDate(iso);
}

/** Ontbijt / middagvoer / avondvoer, afgeleid van het tijdstip van voeren. */
function maaltijd(iso: string | null, t: (k: string) => string): string {
  if (!iso) return t("animalDetailView.feeding.meal.generic");
  const uur = new Date(iso).getHours();
  if (uur < 11) return t("animalDetailView.feeding.meal.breakfast");
  if (uur < 16) return t("animalDetailView.feeding.meal.lunch");
  return t("animalDetailView.feeding.meal.dinner");
}

function tijdVan(iso: string | null): string {
  if (!iso) return "--:--";
  const m = iso.match(/T(\d{2}:\d{2})/);
  return m ? m[1] : "--:--";
}

function VoedingVeld({ icoon, label, waarde, licht }: { icoon: ReactNode; label: string; waarde: string; licht?: boolean }) {
  return (
    <div className="vd-veld">
      <span className="vd-veld__icoon" aria-hidden="true">{icoon}</span>
      <span className="vd-veld__tekst">
        <span className="vd-veld__label">{label}</span>
        <strong className={`vd-veld__waarde${licht ? " is-licht" : ""}`}>{waarde}</strong>
      </span>
    </div>
  );
}

function VoedingRegel({ regel, voertype, gekozen, onKies, onWijzig, onVerwijder, gedrag, toon, t }: {
  regel: { id: number; fedAt: string | null; amountGrams: number | null; note: string | null };
  voertype: string | null; gekozen: boolean;
  onKies: () => void; onWijzig: () => void; onVerwijder: () => void;
  gedrag: string; toon: string; t: (k: string) => string;
}) {
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menu) return;
    const dicht = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setMenu(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setMenu(false); };
    document.addEventListener("mousedown", dicht);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", dicht); document.removeEventListener("keydown", esc); };
  }, [menu]);
  const onder = [voertype, regel.amountGrams != null ? `${regel.amountGrams} g` : null].filter(Boolean).join(" · ");
  return (
    <li className={`vd-regel${gekozen ? " is-gekozen" : ""}`} title={regel.note ?? undefined}>
      <button type="button" className="vd-regel__kies" aria-pressed={gekozen} onClick={onKies}>
        <span className="vd-regel__wanneer">
          <strong>{tijdVan(regel.fedAt)}</strong>
          <span>{dagLabel(regel.fedAt, t)}</span>
        </span>
        <span className="vd-regel__icoon" aria-hidden="true"><BowlGlyph /></span>
        <span className="vd-regel__wat">
          <strong>{maaltijd(regel.fedAt, t)}</strong>
          {onder && <span>{onder}</span>}
        </span>
        <span className={`vd-status vd-status--${toon}`}>
          <span className="vd-status__icoon" aria-hidden="true">{toon === "poor" ? "!" : toon === "unknown" ? "?" : "✓"}</span>
          {gedrag}
        </span>
      </button>
      <div className="vd-regel__menu" ref={ref}>
        <button type="button" className="vd-regel__puntjes" aria-haspopup="menu" aria-expanded={menu}
          aria-label={t("animalDetailView.feeding.rowActions")} onClick={() => setMenu((m) => !m)}>
          <svg width="4" height="16" viewBox="0 0 4 16" fill="currentColor" aria-hidden="true">
            <circle cx="2" cy="2" r="1.8" /><circle cx="2" cy="8" r="1.8" /><circle cx="2" cy="14" r="1.8" />
          </svg>
        </button>
        {menu && (
          <ul className="wa-kaart__lijst" role="menu">
            <li role="none"><button type="button" role="menuitem" onClick={() => { setMenu(false); onWijzig(); }}>
              {t("animalDetailView.feeding.edit")}</button></li>
            <li role="none"><button type="button" role="menuitem" className="is-gevaar" onClick={() => { setMenu(false); onVerwijder(); }}>
              {t("animalDetailView.feeding.delete")}</button></li>
          </ul>
        )}
      </div>
    </li>
  );
}

const lijn = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" } as const;
/** Een voerbak. */
function BowlGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" {...lijn}>
      <path d="M3 12h18l-1.6 6.2A2.4 2.4 0 0 1 17.1 20H6.9a2.4 2.4 0 0 1-2.3-1.8z" />
      <path d="M7 12c0-2.2 1.4-4 3-4s3 1.8 3 4M11 12c0-2.8 1.8-5 4-5s3 2 3 5" />
    </svg>
  );
}
/** Een maatbeker. */
function MaatGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" {...lijn}>
      <path d="M5 4h11l-1 16H6z" /><path d="M16 7h2.5a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5H15.5" />
      <path d="M8 9h3M8 13h3" />
    </svg>
  );
}
/** Een tekstballon. */
function PraatGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" {...lijn}>
      <path d="M4 5h16v11H9l-5 4z" /><path d="M8 9h8M8 12h5" />
    </svg>
  );
}
/** Een logboekvel. */
function LogGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" {...lijn}>
      <rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h4" />
    </svg>
  );
}
/** Een staafgrafiek. */
function GrafiekGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" {...lijn}>
      <path d="M4 20h16M7 20v-6M11 20V8M15 20v-9M19 20V5" />
    </svg>
  );
}

/** De kop van de voedingstegel: rond icoon, titel en ondertitel (ontwerp 2026-09). */
export function VoedingTegelKop({ titel, onder }: { titel: string; onder: string }) {
  return (
    <span className="vd-kop">
      <span className="vd-kop__icoon" aria-hidden="true"><BowlGlyph /></span>
      <span className="vd-kop__tekst">
        <span className="vd-kop__titel">{titel}</span>
        <span className="vd-kop__onder">{onder}</span>
      </span>
    </span>
  );
}

export function FeedingTile({ animalId, hiddenSections }: {
  animalId: string;
  /** Per-user hidden Voeding sections (`schema` / `log`, the Jmix SECTION_* keys). */
  hiddenSections?: Set<string>;
}) {
  const t = useTranslate();
  const showSchema = !hiddenSections?.has("schema");
  const showLog = !hiddenSections?.has("log");
  const { data, isLoading, isError, refetch } = useTile<FeedingTileData>(animalId, "feeding");
  const { mutate, isLoading: saving } = useCustomMutation();

  // Eén knop "Voedingsschema": het overzicht van alle schema's, met details en "Nieuw schema" erin
  // (Maarten, 2026-10-11). Een taak "Voeding" opent het venster meteen op een nieuw schema.
  const [schemaOpen, setSchemaOpen] = useState<false | "lijst" | "nieuw">(false);
  const taak = useTaakVoorTegel(animalId, ["VOEDING"], () => setSchemaOpen("nieuw"), schemaOpen !== false);
  const [logOpen, setLogOpen] = useState(false);
  const [logEditing, setLogEditing] = useState<FeedingLogData | null>(null);
  const [logForm, setLogForm] = useState<LogForm>(logToForm(null));
  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
  const [restOpen, setRestOpen] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [formError, setFormError] = useState<FeedingNotice | null>(null);

  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;

  const plan = data?.plan ?? null;
  const log = data?.log ?? [];
  const samenstelling = data?.samenstelling ?? null;
  const historie = data?.historie ?? [];
  const preview = log.slice(0, FEEDING_PREVIEW_LIMIT);
  const rest = log.slice(FEEDING_PREVIEW_LIMIT);

  // Wat er van de portie op is: "250 g gegeten (83%)". Nog niet ingevuld: dat zeggen, zodat je het bij de volgende
  // voerbeurt aanvult. Een oude regel zonder gegeten toont zijn eetgedrag.
  const gegetenTekst = (l: FeedingLogData): string => {
    if (l.eatenGrams != null && l.amountGrams) {
      return t("animalDetailView.feeding.eaten").replace("%s", String(l.eatenGrams))
        .replace("%p", String(Math.round((l.eatenGrams / l.amountGrams) * 100)));
    }
    return !l.behavior || l.behavior === "ONBEKEND" ? t("animalDetailView.feeding.eatenOpen") : behaviorLabel(l.behavior);
  };
  const behaviorLabel = (code: string | null) =>
    code && (BEHAVIOR_CODES as readonly string[]).includes(code)
      ? t(`animalDetailView.feeding.behavior.${code}`) : code ?? "—";
  // Strict Jmix-clone parity: Jmix orFallback / formatAmount use a plain ASCII hyphen "-" for empty
  // values and render grams as `<n>g` (no space, no thousands separator).
  const orDash = (v: string | null | undefined) => (v && v.trim() ? v : "-");
  const fmtGrams = (g: number | null | undefined) => (g == null ? "-" : `${g}g`);


  const errorText = (n: FeedingNotice | null) =>
    n === "conflict" ? t("animalDetailView.feeding.saveConflict")
    : n === "error" ? t("animalDetailView.feeding.saveError")
    : n === "timeRequired" ? t("animalDetailView.feeding.timeRequired")
    : n === "fedAtRequired" ? t("animalDetailView.feeding.fedAtRequired")
    : n === "amountInvalid" ? t("animalDetailView.feeding.amountInvalid")
    : n === "behaviorRequired" ? t("animalDetailView.feeding.behaviorRequired")
    : n === "eatenTooMuch" ? t("animalDetailView.feeding.eatenTooMuch")
    : null;

  const onErr = (err: unknown) =>
    setFormError((err as { statusCode?: number }).statusCode === 409 ? "conflict" : "error");
  const afterSave = (close: () => void) => {
    close(); setSelectedLogId(null); setFormError(null); setSavedFlash(true); refetch();
  };

  // De hoeveelheid staat al klaar (uit het schema, anders de vorige registratie): alleen aanpassen als het niet klopt.
  // Bij een plan in onderdelen is dat de hele portie per maaltijd: "10 + 10 + 10 is 30 gram" (Alma, Roze).
  const standaard = samenstelling && samenstelling.portie > 0 ? Math.round(samenstelling.portie)
    : standaardHoeveelheid(plan?.amountText ?? null, parseFeedingTimes(plan?.feedingTimes ?? null).length, log);
  const openNewLog = () => {
    setLogEditing(null);
    setLogForm({ ...logToForm(null), amountGrams: standaard != null ? String(standaard) : "" });
    setFormError(null); setSavedFlash(false); setLogOpen(true);
  };
  const openEditLog = () => {
    const sel = log.find((l) => l.id === selectedLogId);
    if (!sel) return;
    setLogEditing(sel); setLogForm(logToForm(sel)); setFormError(null); setSavedFlash(false); setLogOpen(true);
  };

  const saveLog = () => {
    // Per-field validation in the Jmix order (fedAt → amount → behavior), each with its own message.
    const grams = Number(logForm.amountGrams);
    if (!logForm.fedAt) { setFormError("fedAtRequired"); return; }
    if (!logForm.amountGrams || !Number.isFinite(grams) || grams <= 0) { setFormError("amountInvalid"); return; }
    // Hoeveel er gegeten is (Alma, 2026-10-09: "meten is weten"). Leeg mag: dat zie je vaak pas bij de volgende
    // voerbeurt. Het eetgedrag volgt daaruit op de server; bij een oude regel zonder gegeten blijft het staan.
    const gegeten = logForm.eatenGrams.trim() === "" ? null : Number(logForm.eatenGrams);
    if (gegeten != null && (!Number.isFinite(gegeten) || gegeten < 0 || gegeten > grams)) { setFormError("eatenTooMuch"); return; }
    const editing = logEditing;
    mutate(
      {
        url: editing ? `animals/${animalId}/feeding/log/${editing.id}` : `animals/${animalId}/feeding/log`,
        method: editing ? "put" : "post",
        values: {
          fedAt: logForm.fedAt, amountGrams: grams, eatenGrams: gegeten,
          behavior: gegeten == null ? (editing?.behavior ?? null) : null,
          note: logForm.note.trim() || null, version: editing?.version ?? null,
        },
        successNotification: false, errorNotification: false,
      },
      { onSuccess: () => afterSave(() => setLogOpen(false)), onError: onErr },
    );
  };

  const deleteSelected = () => {
    if (selectedLogId == null) return;
    mutate(
      {
        url: `animals/${animalId}/feeding/log/${selectedLogId}`, method: "delete", values: {},
        successNotification: false, errorNotification: false,
      },
      { onSuccess: () => afterSave(() => undefined), onError: onErr },
    );
  };

  const restColumns: Column<FeedingLogData>[] = [
    { header: t("animalDetailView.feeding.col.date"), cell: (l) => fmtFormalDateTime(l.fedAt) },
    { header: t("animalDetailView.feeding.col.amount"), cell: (l) => fmtGrams(l.amountGrams), align: "right" },
    { header: t("animalDetailView.feeding.col.eaten"), cell: (l) => gegetenTekst(l), align: "right" },
    { header: t("animalDetailView.feeding.col.behavior"), cell: (l) => behaviorLabel(l.behavior) },
    { header: t("animalDetailView.feeding.col.note"), cell: (l) => orDash(l.note) },
  ];
  // ── ontwerp 2026-09: schemablok met iconen, volgende voerbeurt, voedingslog met status en ⋮-menu ──
  const voertijden = parseFeedingTimes(plan?.feedingTimes ?? null);
  const volgende = volgendeVoerbeurt(voertijden, log);
  const openEditLogFor = (l: FeedingLogData) => {
    setSelectedLogId(l.id);
    setLogEditing(l); setLogForm(logToForm(l)); setFormError(null); setSavedFlash(false); setLogOpen(true);
  };
  const deleteLog = (id: number) => {
    mutate(
      {
        url: `animals/${animalId}/feeding/log/${id}`, method: "delete", values: {},
        successNotification: false, errorNotification: false,
      },
      { onSuccess: () => afterSave(() => undefined), onError: onErr },
    );
  };

  return (
    <div className="fawkes-feeding">
      <div className="fawkes-feeding__body vd">
      {showSchema && (
        <div className="vd-schema">
          <div className="vd-schema__rij">
            <VoedingVeld icoon={<BowlGlyph />} label={t("animalDetailView.feeding.field.feedType")} waarde={orDash(plan?.feedType)} />
            <VoedingVeld icoon={<TagIcon width={22} height={22} />} label={t("animalDetailView.feeding.field.brand")} waarde={orDash(plan?.brand)} />
          </div>
          <div className="vd-schema__rij">
            <VoedingVeld icoon={<MaatGlyph />} label={t("animalDetailView.feeding.field.amount")} waarde={orDash(plan?.amountText)} />
            <div className="vd-veld">
              <span className="vd-veld__icoon" aria-hidden="true"><ClockIcon width={22} height={22} /></span>
              <span className="vd-veld__tekst">
                <span className="vd-veld__label">{t("animalDetailView.feeding.field.times")}</span>
                {voertijden.length > 0 ? (
                  <span className="vd-tijden">
                    {voertijden.map((tijd) => <span key={tijd} className="vd-tijd">{tijd}</span>)}
                  </span>
                ) : (
                  <strong className="vd-veld__waarde">-</strong>
                )}
              </span>
            </div>
          </div>
          <div className="vd-schema__opmerking">
            <VoedingVeld icoon={<PraatGlyph />} label={t("animalDetailView.feeding.field.note")} waarde={orDash(plan?.note)} licht />
          </div>
        </div>
      )}

      {showSchema && <VoedingsplanOverzicht samenstelling={samenstelling} aantalEerdere={historie.length} onOpen={() => setSchemaOpen("lijst")} />}

      {showSchema && volgende && (
        <div className={`vd-volgende${volgende.achter ? " is-achter" : ""}`}>
          <span className="vd-volgende__klok" aria-hidden="true"><ClockIcon width={24} height={24} /></span>
          <span className="vd-volgende__tekst">
            <span className="vd-volgende__label">{t("animalDetailView.feeding.next")}</span>
            <span className="vd-volgende__tijd">
              <strong>{volgende.tijd}</strong>
              <span>{wachtTekst(volgende.minuten, volgende.morgen, t)}</span>
            </span>
          </span>
          <span className={`vd-volgende__status${volgende.achter ? " is-achter" : ""}`}>
            <span className="vd-volgende__stip" aria-hidden="true" />
            {t(volgende.achter ? "animalDetailView.feeding.behindSchedule" : "animalDetailView.feeding.onSchedule")}
          </span>
        </div>
      )}

      {showLog && (
        <div className="vd-log">
          <div className="vd-log__kop">
            <span className="vd-log__titel"><LogGlyph /> {t("animalDetailView.feeding.log")}</span>
            <span className="vd-log__aantal">
              {log.length === 1
                ? t("animalDetailView.feeding.countOne")
                : t("animalDetailView.feeding.countMany", { args: [String(log.length)] })}
            </span>
          </div>
          {preview.length === 0 ? (
            <p className="vd-log__leeg">{t("animalDetailView.feeding.noLog")}</p>
          ) : (
            <ul className="vd-log__lijst">
              {preview.map((l) => (
                <VoedingRegel
                  key={l.id}
                  regel={l}
                  voertype={plan?.feedType ?? null}
                  gekozen={selectedLogId === l.id}
                  onKies={() => { setSelectedLogId((cur) => (cur === l.id ? null : l.id)); setSavedFlash(false); }}
                  onWijzig={() => openEditLogFor(l)}
                  onVerwijder={() => deleteLog(l.id)}
                  gedrag={gegetenTekst(l)}
                  toon={BEHAVIOR_TONE[l.behavior ?? "ONBEKEND"] ?? "unknown"}
                  t={t}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      {savedFlash && <span className="fawkes-feeding__saved" role="status">{t("animalDetailView.feeding.saved")}</span>}

      <div className="vd-knoppen">
        {showLog && (
          <>
            <button type="button" className="vd-knop vd-knop--hoofd" onClick={openNewLog} disabled={saving}>
              <PlusCircleSolidIcon width={18} height={18} /> {t("animalDetailView.feeding.add")}
            </button>
            <button type="button" className="vd-knop" onClick={openEditLog} disabled={saving || selectedLogId == null}>
              <PencilIcon width={16} height={16} /> {t("animalDetailView.feeding.edit")}
            </button>
            <button type="button" className="vd-knop" onClick={deleteSelected} disabled={saving || selectedLogId == null}>
              <TrashIcon width={16} height={16} /> {t("animalDetailView.feeding.delete")}
            </button>
          </>
        )}
      </div>
      <div className="vd-knoppen vd-knoppen--2">
        {showSchema && (
          <button type="button" className="vd-knop" onClick={() => setSchemaOpen("lijst")} disabled={saving}>
            <CalendarIcon width={16} height={16} /> {t("voedingsschema.knop")}
          </button>
        )}
        {showLog && (
          <button type="button" className="vd-knop" onClick={() => setRestOpen(true)} disabled={rest.length === 0}>
            <GrafiekGlyph /> {t("animalDetailView.feeding.viewRest")}
          </button>
        )}
      </div>
      </div>

      <VoedingsschemaVenster
        animalId={animalId}
        open={schemaOpen !== false}
        meteenNieuw={schemaOpen === "nieuw"}
        onClose={() => setSchemaOpen(false)}
        onGewijzigd={() => { taak.klaar(); setSelectedLogId(null); setFormError(null); setSavedFlash(true); refetch(); }}
      />

      <Modal
        open={logOpen}
        title={t(logEditing ? "animalDetailView.feeding.logEditTitle" : "animalDetailView.feeding.logAddTitle")}
        subtitle={t(logEditing ? "animalDetailView.feeding.logEditSubtitle" : "animalDetailView.feeding.logAddSubtitle")}
        icon={<BowlGlyph />}
        onClose={() => setLogOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setLogOpen(false)}>{t("common.cancel")}</Button>
            <Button variant="primary" onClick={saveLog} disabled={saving}>
              <CheckIcon width={16} height={16} /> {t("common.save")}
            </Button>
          </>
        }
      >
        <div className="fawkes-feeding__form vd-form">
          <FieldShell label={t("animalDetailView.feeding.form.fedAt")} required>
            <span className="vd-invoer">
              <CalendarIcon width={18} height={18} aria-hidden="true" />
              <input type="datetime-local" className="fui-field__input" value={logForm.fedAt} onChange={(e) => setLogForm((f) => ({ ...f, fedAt: e.target.value }))} />
            </span>
            <span className="vd-hulp">{t("animalDetailView.feeding.form.fedAtHint")}</span>
          </FieldShell>
          <FieldShell label={t("animalDetailView.feeding.form.amountGrams")} required>
            <span className="vd-invoer">
              <GewichtGlyph />
              <input type="number" min={1} className="fui-field__input" value={logForm.amountGrams} onChange={(e) => setLogForm((f) => ({ ...f, amountGrams: e.target.value }))} />
            </span>
            <span className="vd-hulp">
              {standaard != null && !logEditing
                ? t("animalDetailView.feeding.form.amountPrefilled")
                : t("animalDetailView.feeding.form.amountHint")}
            </span>
          </FieldShell>
          <FieldShell label={t("animalDetailView.feeding.form.eaten")}>
            <span className="vd-invoer">
              <PawIcon width={18} height={18} aria-hidden="true" />
              <input type="number" min={0} className="fui-field__input" value={logForm.eatenGrams}
                aria-label={t("animalDetailView.feeding.form.eaten")}
                onChange={(e) => setLogForm((f) => ({ ...f, eatenGrams: e.target.value }))} />
            </span>
            <span className="vd-hulp">{t("animalDetailView.feeding.form.eatenHint")}</span>
          </FieldShell>
          <FieldShell label={t("animalDetailView.feeding.field.note")}>
            <span className="vd-invoer vd-invoer--vak">
              <FileTextGlyph />
              <textarea className="fui-field__input fawkes-feeding__textarea" rows={3}
                placeholder={t("animalDetailView.feeding.form.notePlaceholder")}
                value={logForm.note} onChange={(e) => setLogForm((f) => ({ ...f, note: e.target.value }))} />
            </span>
            <span className="vd-hulp">{t("animalDetailView.feeding.form.noteHint")}</span>
          </FieldShell>
          {formError && <span className="fawkes-feeding__form-error" role="alert">{errorText(formError)}</span>}
        </div>
      </Modal>

      <Modal open={restOpen} title={t("animalDetailView.feeding.restTitle")} onClose={() => setRestOpen(false)}>
        {/* Tile-scoped wide wrapper — the shared Modal caps at 460px, but the Jmix overview dialog is
            ~46rem wide so the date-time column reads on one line (see .fui-modal:has(.fawkes-feeding__rest)). */}
        <div className="fawkes-feeding__rest">
          <IndeelbareTabel tabelKey="animal-tiles" columns={restColumns} rows={rest} getRowKey={(l) => String(l.id)} />
        </div>
      </Modal>
    </div>
  );
}

// ---- Bescherming (vaccinations) --------------------------------------------------------------------
interface Vaccination {
  id: number; naam: string | null; merk: string | null; beginDatum: string | null;
  eindDatum: string | null; controle: boolean; dierenarts: string | null;
  ingevoerdDoor: string | null; inentingProductId: number | null; version: number | null;
}

interface VaccProduct {
  id: number;
  label: string;
  dagenGeldig: number | null;
  geldigheidsduurWaarde: number | null;
  geldigheidsduurEenheid: string | null;
}

// Functional port of the Jmix vaccination tile (AnimalVaccinationTileFragment): a filter row (type /
// status / search) over quick-filter type tags, the latest-3 record cards (the rest behind a "Rest
// bekijken" modal) and the two create buttons (Nieuwe enting / Nieuwe ontworm/ontvlooiing). Each card
// opens an add/edit/delete dialog. Type + status + title are derived the same way as the Java fragment;
// the cards are sorted by the same repeat-priority order. The enting flow picks a vaccination product
// (auto-filling "Volgende op" from its validity) and the parasite flow picks a treatment type. The
// AI-passport-import + file-upload sub-flow is a flagged follow-up (deferred).
const BINNENKORT_DAGEN = 30;
const PREVIEW_LIMIT = 3;

type TypeFilter = "all" | HealthType;
type StatusFilter = "all" | VaccStatus;
type DialogMode = "enting" | "parasiet" | "edit";
// The treatment-type options offered in the parasite/edit dialog's "Type" select.
type TreatmentType = "ontwormen" | "ontvlooien" | "combo";

type HealthType = "enting" | "ontwormen" | "ontvlooien" | "combo";
type VaccStatus = "actueel" | "binnenkort" | "verlopen" | "onbekend";

function vaccNormalize(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function isComboText(text: string): boolean {
  return (text.includes("ontworm") && (text.includes("ontvlooi") || text.includes("vlooi")))
    || text.includes("ontwormen/ontvlooien")
    || text.includes("ontworm/ontvlooi");
}

function determineHealthType(v: Vaccination): HealthType {
  const text = vaccNormalize(`${v.naam ?? ""} ${v.merk ?? ""}`);
  if (isComboText(text)) return "combo";
  if (text.includes("ontworm")) return "ontwormen";
  if (text.includes("ontvlooi") || text.includes("vlooi")) return "ontvlooien";
  return "enting";
}

function determineStatus(eindDatum: string | null): VaccStatus {
  if (!eindDatum) return "onbekend";
  const next = new Date(eindDatum);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  next.setHours(0, 0, 0, 0);
  if (next.getTime() < today.getTime()) return "verlopen";
  const soonCutoff = new Date(today);
  soonCutoff.setDate(soonCutoff.getDate() + BINNENKORT_DAGEN);
  if (next.getTime() <= soonCutoff.getTime()) return "binnenkort";
  return "actueel";
}

function isGenericTypeName(value: string | null, type: HealthType): boolean {
  const text = vaccNormalize(value);
  if (!text) return false;
  switch (type) {
    case "enting": return text === "enting" || text === "vaccinatie";
    case "ontwormen": return text.includes("ontworm");
    case "ontvlooien": return text.includes("ontvlooi") || text.includes("vlooi");
    case "combo": return isComboText(text);
  }
}

function vaccTitle(v: Vaccination, type: HealthType, typeLabel: string): string {
  const naam = v.naam?.trim() || null;
  const merk = v.merk?.trim() || null;
  if (naam && !isGenericTypeName(naam, type)) return `${typeLabel} - ${naam}`;
  if (merk && !isGenericTypeName(merk, type)) return `${typeLabel} - ${merk}`;
  return typeLabel;
}

/**
 * De naam zoals hij op de regel staat: het middel zelf, zonder het type ervoor.
 *
 * <p>{@link vaccTitle} zet "Enting - " ervoor, want daar staat het type nergens anders. Op deze regel
 * staat het type al als chip ernaast, dus dan is die aanhef dubbel — in de mockup staat er "Nobivac
 * Hond Puppy DP Plus" en verder niets.
 */
function beschermingNaam(v: Vaccination, typeLabel: string): string {
  const naam = v.naam?.trim() || null;
  const merk = v.merk?.trim() || null;
  const samen = [merk, naam].filter((d): d is string => !!d && !isGenericTypeName(d, "enting"));
  if (samen.length === 0) return typeLabel;
  // Staat het merk al vooraan in de naam, dan niet nog een keer ervoor plakken.
  if (samen.length === 2 && samen[1].toLowerCase().startsWith(samen[0].toLowerCase())) return samen[1];
  return samen.join(" ");
}

function enteredByOf(v: Vaccination): string {
  return v.ingevoerdDoor?.trim() || v.dierenarts?.trim() || "—";
}

/** Het plaatje per soort. Staat in public/bescherming; zie de mockup van 28-09-2026. */
const BESCHERMING_ICOON: Record<Exclude<HealthType, "combo">, string> = {
  enting: "/bescherming/spuit.png",
  ontwormen: "/bescherming/worm.png",
  ontvlooien: "/bescherming/vlooi.png",
};

/** Een combo telt als ontwormen: dat is het middel waar de meeste mensen hem op zoeken. */
function icoonVan(type: HealthType): string {
  return BESCHERMING_ICOON[type === "combo" ? "ontwormen" : type];
}

/**
 * "volgende over 6 weken" — de afstand tot een datum in de eenheid die er past.
 *
 * <p>Dagen tot twee weken, daarna weken tot een kwartaal, daarna maanden. Een hond die over drie
 * maanden moet, hoef je niet in 91 dagen te lezen.
 */
function afstandTot(datum: string | null, t: (k: string) => string): string | null {
  if (!datum) return null;
  const doel = new Date(datum);
  const nu = new Date();
  doel.setHours(0, 0, 0, 0);
  nu.setHours(0, 0, 0, 0);
  const dagen = Math.round((doel.getTime() - nu.getTime()) / 86400000);
  if (Number.isNaN(dagen) || dagen < 0) return null;
  if (dagen <= 14) return t("bescherming.eenheid.dag").replace("%d", String(dagen));
  if (dagen <= 90) return t("bescherming.eenheid.week").replace("%d", String(Math.round(dagen / 7)));
  return t("bescherming.eenheid.maand").replace("%d", String(Math.round(dagen / 30)));
}

/** Per soort: hoeveel er geregistreerd zijn en wanneer de eerstvolgende moet. */
function samenvatting(rows: Vaccination[], soort: Exclude<HealthType, "combo">) {
  const eigen = rows.filter((v) => matchesTypeFilter(soort, determineHealthType(v)));
  const volgende = eigen
    .map((v) => v.eindDatum)
    .filter((d): d is string => !!d && new Date(d).getTime() >= Date.now() - 86400000)
    .sort()[0] ?? null;
  return { aantal: eigen.length, volgende };
}

function VaccinationCard({ v, t, onClick }: {
  v: Vaccination; t: (k: string) => string; onClick: () => void;
}) {
  const type = determineHealthType(v);
  const status = determineStatus(v.eindDatum);
  const enteredBy = enteredByOf(v);
  const afstand = afstandTot(v.eindDatum, t);
  return (
    <button type="button" className={`bs-regel bs-regel--${type}`} onClick={onClick}>
      <span className="bs-regel__icoon" aria-hidden="true">
        <img src={icoonVan(type)} alt="" loading="lazy" />
      </span>
      <span className="bs-regel__datum">{fmtFormalDate(v.beginDatum)}</span>
      <span className="bs-regel__kern">
        <span className={`bs-chip bs-chip--${type}`}>{t(`animalDetailView.vaccination.type.${type}`)}</span>
        <strong className="bs-regel__naam">
          {beschermingNaam(v, t(`animalDetailView.vaccination.type.${type}`))}
        </strong>
        {enteredBy && <span className="bs-regel__bron">{enteredBy}</span>}
      </span>
      <span className={`bs-status bs-status--${status}`}>
        <span className="bs-status__kop">
          {status === "verlopen"
            ? t("animalDetailView.vaccination.status.verlopen")
            : afstand
              ? `${t("bescherming.volgendeOver")} ${afstand}`
              : t("bescherming.uitgevoerd")}
        </span>
        {v.eindDatum && <span className="bs-status__datum">{fmtFormalDate(v.eindDatum)}</span>}
      </span>
    </button>
  );
}

// --- filtering + sorting (mirrors the Jmix fragment) ---
function matchesTypeFilter(filter: TypeFilter, type: HealthType): boolean {
  if (filter === "all") return true;
  if (filter === type) return true;
  // a combo record matches both the ontwormen and ontvlooien quick filters
  return (filter === "ontwormen" || filter === "ontvlooien") && type === "combo";
}

function matchesSearch(v: Vaccination, search: string): boolean {
  const hay = [v.naam, v.merk, v.ingevoerdDoor, v.dierenarts]
    .map((s) => (s ?? "").toLowerCase()).join(" ");
  return hay.includes(search);
}

function matchesFilters(v: Vaccination, type: TypeFilter, status: StatusFilter, search: string): boolean {
  if (!matchesTypeFilter(type, determineHealthType(v))) return false;
  if (status !== "all" && determineStatus(v.eindDatum) !== status) return false;
  const q = search.trim().toLowerCase();
  return q === "" || matchesSearch(v, q);
}

const parseMs = (iso: string | null): number | null => (iso ? Date.parse(`${iso}T00:00:00Z`) : null);

function repeatKey(v: Vaccination): string {
  return `${v.inentingProductId ?? "null"}|${(v.naam ?? "-").trim().toLowerCase() || "-"}|${(v.merk ?? "-").trim().toLowerCase() || "-"}`;
}

// Jmix sortByRepeatPriority: older repeats of the same treatment sink below, then by next-date
// (eindDatum, nulls last), then by given-date (beginDatum, descending, nulls last).
function sortByRepeatPriority(items: Vaccination[]): Vaccination[] {
  if (items.length < 2) return items;
  const latestBegin = new Map<string, number>();
  for (const v of items) {
    const b = parseMs(v.beginDatum);
    if (b == null) continue;
    const cur = latestBegin.get(repeatKey(v));
    if (cur == null || b > cur) latestBegin.set(repeatKey(v), b);
  }
  const isOlderRepeat = (v: Vaccination): boolean => {
    const latest = latestBegin.get(repeatKey(v));
    const b = parseMs(v.beginDatum);
    return latest != null && b != null && b < latest;
  };
  return [...items].sort((a, b) => {
    const ar = isOlderRepeat(a) ? 1 : 0, br = isOlderRepeat(b) ? 1 : 0;
    if (ar !== br) return ar - br;
    const ae = parseMs(a.eindDatum), be = parseMs(b.eindDatum);
    if (ae !== be) { if (ae == null) return 1; if (be == null) return -1; return ae - be; }
    const ab = parseMs(a.beginDatum), bb = parseMs(b.beginDatum);
    if (ab === bb) return 0;
    if (ab == null) return 1; if (bb == null) return -1; return bb - ab;
  });
}

// --- date helpers (native yyyy-MM-dd, the LocalDate wire format) ---
function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// Config-leading "Volgende op" (Jmix applyEntingNextDate / plusGeldigheid): prefer the per-product
// geldigheidsduur (waarde + eenheid → years/months/weeks/days), then fall back to the legacy dagenGeldig,
// then "" — mirrors the server (InentingExpiryService) exactly, including the unknown-unit → years default.
function plusGeldigheidIso(iso: string, waarde: number, eenheid: string | null): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  const e = (eenheid ?? "").toLowerCase();
  if (e.includes("maand")) d.setUTCMonth(d.getUTCMonth() + waarde);
  else if (e.includes("week") || e.includes("weken")) d.setUTCDate(d.getUTCDate() + waarde * 7);
  else if (e.includes("dag")) d.setUTCDate(d.getUTCDate() + waarde);
  else d.setUTCFullYear(d.getUTCFullYear() + waarde); // jaar/jaren + unknown-unit default
  return d.toISOString().slice(0, 10);
}

function entingNextDateIso(p: VaccProduct | undefined, givenDate: string): string {
  if (!p || !givenDate) return "";
  if (p.geldigheidsduurWaarde != null) {
    return plusGeldigheidIso(givenDate, p.geldigheidsduurWaarde, p.geldigheidsduurEenheid);
  }
  if (p.dagenGeldig != null) return addDaysIso(givenDate, p.dagenGeldig);
  return "";
}

function treatmentNaam(type: TreatmentType): string {
  return type === "ontwormen" ? "Ontwormen" : type === "ontvlooien" ? "Ontvlooien" : "Ontwormen/ontvlooien";
}

// A labelled form field (label above the control), reusing the shared fui-field input styling.
function VaccField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="fawkes-vaccination-form__field">
      <span className="fawkes-vaccination-form__label">{label}</span>
      {children}
    </label>
  );
}

/**
 * Het dierblok bovenin de entingdialoog — foto, naam, ras en leeftijd, chipnummer, de laatste enting
 * en de vervaldatum (rood zodra hij verlopen is).
 *
 * <p>Stond er niet, en dat is meer dan een plaatje: wie een enting bijschrijft moet zien wélk dier hij
 * voor zich heeft en wanneer de vorige gegeven werd. Zonder die twee regels typt iemand een datum in
 * bij het verkeerde dier of zet hij een enting neer die er al staat.
 */
function VaccinationDialogHeader({ animalId, records, t }: {
  animalId: string; records: Vaccination[]; t: (k: string) => string;
}) {
  const { data } = useOne<AnimalBasis>({ resource: "animals", id: animalId });
  const animal = data?.data;
  const photoUrls = useAnimalPhotoUrls(animal ? [animal.id] : []);
  if (!animal) return null;

  const photo = photoUrls[String(animal.id)] ?? animal.fotoUrl ?? null;
  // De bron zet "(kruising)" achter het ras en laat het ras weg als er alleen kruising bekend is.
  const rasT = animal.ras
    ? animal.kruising
      ? `${animal.ras} (${t("animalDetailView.vaccination.dialog.header.kruising")})`
      : animal.ras
    : animal.kruising
      ? t("animalDetailView.vaccination.dialog.header.kruisingAlleen")
      : null;
  const onder = [rasT, animal.diersoort, dialoogLeeftijd(animal.geboortedatum)].filter(Boolean).join(" · ")
    || t("animalDetailView.vaccination.dialog.header.onbekend");

  // De laatste toediening en de eerstvolgende vervaldatum uit wat de tegel al opgehaald heeft.
  const laatste = records
    .filter((r) => r.beginDatum)
    .slice()
    .sort((a, b) => (a.beginDatum! < b.beginDatum! ? 1 : a.beginDatum! > b.beginDatum! ? -1 : 0))[0];
  const vervalt = records
    .filter((r) => r.eindDatum)
    .slice()
    .sort((a, b) => (a.eindDatum! < b.eindDatum! ? -1 : a.eindDatum! > b.eindDatum! ? 1 : 0))[0];
  // De bron rekent van vandaag naar de vervaldatum: negatief zodra hij voorbij is.
  const dagenTot = vervalt?.eindDatum ? -(dagenSindsIso(vervalt.eindDatum) ?? 0) : null;
  // Drie tinten, net als de bron: voorbij, binnen een maand, of nog ruim geldig.
  const vervalToon = dagenTot == null ? "leeg" : dagenTot < 0 ? "verlopen" : dagenTot <= 30 ? "bijna" : "geldig";

  return (
    <div className="fawkes-vaccination-header">
      <span className="fawkes-vaccination-header__photo">
        {/* De poot blijft eronder staan als het dier geen foto heeft. */}
        <span className="fawkes-vaccination-header__paw" aria-hidden="true">🐾</span>
        {photo ? <img src={photo} alt="" aria-hidden="true" /> : null}
      </span>
      <div className="fawkes-vaccination-header__body">
        <span className="fawkes-vaccination-header__naam">{animal.naam || "—"}</span>
        <span className="fawkes-vaccination-header__onder">{onder}</span>
        <div className="fawkes-vaccination-header__meta">
          <VaccinationHeaderRegel
            icon={<TagIcon width={15} height={15} aria-hidden="true" />}
            label={t("animalDetailView.vaccination.dialog.header.chip")}
            waarde={animal.chipPasnummer}
          />
          <VaccinationHeaderRegel
            icon={<CalendarIcon width={15} height={15} aria-hidden="true" />}
            label={t("animalDetailView.vaccination.dialog.header.last")}
            waarde={
              laatste?.beginDatum
                ? `${fmtFormalDate(laatste.beginDatum)}${laatste.naam ? ` (${laatste.naam})` : ""}`
                : null
            }
          />
          <VaccinationHeaderRegel
            icon={<ClockIcon width={15} height={15} aria-hidden="true" />}
            label={t("animalDetailView.vaccination.dialog.header.expires")}
            waarde={
              vervalt?.eindDatum
                ? `${fmtFormalDate(vervalt.eindDatum)} (${dagenTot} ${t("animalDetailView.vaccination.dialog.header.days")})`
                : null
            }
            toon={vervalToon}
          />
        </div>
      </div>
    </div>
  );
}

/** Eén regel in de kop: icoon, vet label, en de waarde — "—" als er niets bekend is. */
function VaccinationHeaderRegel({ icon, label, waarde, toon }: {
  icon: ReactNode; label: string; waarde: string | null;
  /** Kleurt de waarde; alleen de vervaldatum gebruikt dit. */
  toon?: "verlopen" | "bijna" | "geldig" | "leeg";
}) {
  return (
    <div className="fawkes-vaccination-header__regel">
      {icon}
      <span className="fawkes-vaccination-header__label">{label}:</span>
      <span
        className={`fawkes-vaccination-header__waarde${waarde ? "" : " fawkes-vaccination-header__waarde--leeg"}${
          toon && toon !== "leeg" ? ` fawkes-vaccination-header__waarde--${toon}` : ""
        }`}
      >
        {waarde ?? "—"}
      </span>
    </div>
  );
}

/** Bestandsgrootte zoals de bron hem toont: B onder een kilobyte, hele KB's, daarboven MB met één cijfer. */
function bestandsGrootte(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** De leeftijdnotatie van de dialoog: altijd jaren en maanden ("4j 0mnd"), zoals de bron. */
function dialoogLeeftijd(iso: string | null): string | null {
  if (!iso) return null;
  const geboorte = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(geboorte.getTime())) return null;
  const nu = new Date();
  let jaren = nu.getUTCFullYear() - geboorte.getUTCFullYear();
  let maanden = nu.getUTCMonth() - geboorte.getUTCMonth();
  if (nu.getUTCDate() < geboorte.getUTCDate()) maanden -= 1;
  if (maanden < 0) { maanden += 12; jaren -= 1; }
  if (jaren < 0) return null;
  return `${jaren}j ${maanden}mnd`;
}
/** Hoeveel dagen geleden deze datum was; negatief als hij nog moet komen. */
function dagenSindsIso(iso: string): number | null {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const nu = new Date();
  const vandaag = Date.UTC(nu.getFullYear(), nu.getMonth(), nu.getDate());
  return Math.round((vandaag - d.getTime()) / 86_400_000);
}

function VaccinationDialog({ animalId, mode, record, products, records, aiAvailable, onPassport, onClose, onSaved, t }: {
  animalId: string; mode: DialogMode; record: Vaccination | null; products: VaccProduct[];
  /** Wat de tegel al opgehaald heeft — voor "laatste enting" en de vervaldatum in de kop. */
  records: Vaccination[];
  /** Alleen met een ingestelde AI-provider heeft de uitleesknop zin. */
  aiAvailable: boolean;
  /** Neemt de gekozen bestanden mee naar het uitleesscherm. */
  onPassport: (files: File[]) => void;
  onClose: () => void; onSaved: () => void; t: (k: string) => string;
}) {
  const recordType = record ? determineHealthType(record) : null;
  const entingFlow = mode === "enting" || (mode === "edit" && recordType === "enting");

  const [treatmentType, setTreatmentType] = useState<TreatmentType>(
    recordType && recordType !== "enting" ? recordType : "ontwormen",
  );
  const [productId, setProductId] = useState<string>(record?.inentingProductId != null ? String(record.inentingProductId) : "");
  const [merk, setMerk] = useState<string>(record?.merk ?? "");
  const [givenDate, setGivenDate] = useState<string>(record?.beginDatum ?? todayIso());
  const [nextDate, setNextDate] = useState<string>(record?.eindDatum ?? "");
  // De paspoortpagina's blijven hier staan tot je op uitlezen drukt, zoals in de bron.
  const [paspoort, setPaspoort] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { mutate: create, isLoading: creating } = useCreate();
  const { mutate: update, isLoading: updating } = useUpdate();
  const { mutate: remove, isLoading: deleting } = useDelete();
  const busy = creating || updating || deleting;
  const resource = `animals/${animalId}/vaccinations`;

  // Enting "Volgende op" auto-fill = config-leading (Jmix applyEntingNextDate): per-product
  // geldigheidsduur, else the legacy dagenGeldig, else cleared. The field is read-only in the enting flow.
  useEffect(() => {
    if (!entingFlow) return;
    const p = products.find((x) => String(x.id) === productId);
    setNextDate(entingNextDateIso(p, givenDate));
  }, [entingFlow, productId, givenDate, products]);

  const title = mode === "enting" ? t("animalDetailView.vaccination.dialog.newEnting")
    : mode === "parasiet" ? t("animalDetailView.vaccination.dialog.newParasiet")
    : t("animalDetailView.vaccination.dialog.edit");

  function buildBody(): Record<string, unknown> | null {
    if (!givenDate) { setError(t("animalDetailView.vaccination.dialog.givenDateRequired")); return null; }
    let naam: string;
    let inentingProductId: number | null = null;
    if (entingFlow) {
      if (!productId) { setError(t("animalDetailView.vaccination.dialog.productRequired")); return null; }
      const p = products.find((x) => String(x.id) === productId);
      naam = (p?.label ?? "Enting").slice(0, 50);
      inentingProductId = Number(productId);
    } else {
      naam = treatmentNaam(treatmentType);
    }
    return { naam, merk: merk.trim() || null, beginDatum: givenDate, eindDatum: nextDate || null, inentingProductId };
  }

  function save() {
    const body = buildBody();
    if (!body) return;
    setError(null);
    const onError = (err: unknown) => {
      setError((err as { statusCode?: number }).statusCode === 409
        ? t("animalDetailView.vaccination.dialog.conflict")
        : t("animalDetailView.vaccination.dialog.saveError"));
    };
    if (mode === "edit" && record) {
      update({ resource, id: record.id, values: { ...body, version: record.version } }, { onSuccess: onSaved, onError });
    } else {
      create({ resource, values: body }, { onSuccess: onSaved, onError });
    }
  }

  function del() {
    if (!record) return;
    setError(null);
    remove({ resource, id: record.id }, {
      onSuccess: onSaved,
      onError: () => setError(t("animalDetailView.vaccination.dialog.deleteError")),
    });
  }

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      footer={
        <div className="fawkes-vaccination-form__actions">
          {mode === "edit" && (
            <Button variant="secondary" className="fawkes-vaccination-form__delete" onClick={del} disabled={busy}>
              {t("animalDetailView.vaccination.dialog.delete")}
            </Button>
          )}
          <span className="fawkes-vaccination-form__actions-spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={save} disabled={busy}>{t("common.save")}</Button>
        </div>
      }
    >
      <div className="fawkes-vaccination-form">
        <VaccinationDialogHeader animalId={animalId} records={records} t={t} />
        {entingFlow ? (
          <VaccField label={t("animalDetailView.vaccination.dialog.product")}>
            <select className="fui-field__input fui-select" value={productId} onChange={(e) => setProductId(e.target.value)}>
              <option value="">{t("animalDetailView.vaccination.dialog.productPlaceholder")}</option>
              {products.map((p) => <option key={p.id} value={String(p.id)}>{p.label}</option>)}
            </select>
          </VaccField>
        ) : (
          <VaccField label={t("animalDetailView.vaccination.dialog.type")}>
            <select className="fui-field__input fui-select" value={treatmentType} onChange={(e) => setTreatmentType(e.target.value as TreatmentType)}>
              <option value="ontwormen">{t("animalDetailView.vaccination.type.ontwormen")}</option>
              <option value="ontvlooien">{t("animalDetailView.vaccination.type.ontvlooien")}</option>
              {/* Jmix offers the "combo" type only in the new-parasite flow (setItems at fragment
                  line 488); on edit it is absent unless the record itself is a combo. */}
              {(mode === "parasiet" || recordType === "combo") && (
                <option value="combo">{t("animalDetailView.vaccination.type.combo")}</option>
              )}
            </select>
          </VaccField>
        )}

        <VaccField label={t("animalDetailView.vaccination.dialog.brand")}>
          <input className="fui-field__input" value={merk} onChange={(e) => setMerk(e.target.value)} maxLength={250} />
        </VaccField>

        <VaccField label={t("animalDetailView.vaccination.dialog.givenDate")}>
          <DateField className="fui-field__input" value={givenDate} onChange={(e) => setGivenDate(e.target.value)} />
        </VaccField>
        <VaccField label={t("animalDetailView.vaccination.dialog.nextDate")}>
          <DateField className="fui-field__input" value={nextDate} readOnly={entingFlow} disabled={entingFlow}
            onChange={(e) => setNextDate(e.target.value)} />
        </VaccField>

        {/* Paspoortfoto's erbij, en de AI die ze uitleest. Stond op een aparte knop bóven de tegel;
            op acc zit het in deze dialoog, en daar hoort het ook: je hebt het paspoort in je hand
            op het moment dat je de enting bijschrijft. De stroom eronder is dezelfde — de bestanden
            gaan mee naar het uitleesscherm, dat de regels laat nakijken voor ze worden opgeslagen. */}
        {entingFlow && mode !== "edit" && aiAvailable ? (
          <div className="fawkes-vaccination-form__ai">
            <label className="fawkes-vaccination-form__dropzone">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                multiple
                onChange={(e) => {
                  const gekozen = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  // Hier kies je in één keer alle bladzijden die je hebt, dus er valt daarna niets
                  // meer te verzamelen: meteen uitlezen. Een knop ertussen was een klik om niets.
                  if (gekozen.length) onPassport([...paspoort, ...gekozen]);
                }}
              />
              <span>{t("animalDetailView.vaccination.dialog.dropzone")}</span>
            </label>
            {/* Met de camera krijg je één bladzijde per keer, en een paspoort is zelden één bladzijde.
                Daarom leest hij hier niet meteen uit maar vraagt hij eerst of er nog een foto komt --
                anders zou elke bladzijde een eigen uitleesronde worden. Op een pc heeft deze knop geen
                zin naast de sleepzone; daar blijft hij verborgen. */}
            <label className="fawkes-vaccination-form__camera">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const gekozen = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  if (gekozen.length) setPaspoort((eerder) => [...eerder, ...gekozen]);
                }}
              />
              <span>
                {paspoort.length === 0
                  ? t("animalDetailView.vaccination.dialog.takePhoto")
                  : t("animalDetailView.vaccination.dialog.takeAnotherPhoto")}
              </span>
            </label>
            {paspoort.length > 0 ? (
              <div className="fawkes-vaccination-form__bestanden">
                <span className="fawkes-vaccination-form__bestanden-kop">
                  {t(
                    paspoort.length === 1
                      ? "animalDetailView.vaccination.dialog.uploadedFile"
                      : "animalDetailView.vaccination.dialog.uploadedFiles",
                  ).replace("%d", String(paspoort.length))}
                </span>
                {paspoort.map((f, i) => (
                  <div className="fawkes-vaccination-form__bestand" key={`${f.name}-${i}`}>
                    <span className="fawkes-vaccination-form__bestand-icoon" aria-hidden="true">
                      {f.type.startsWith("image/") ? "🖼" : "📄"}
                    </span>
                    <span className="fawkes-vaccination-form__bestand-naam" title={f.name}>{f.name}</span>
                    <span className="fawkes-vaccination-form__bestand-grootte">{bestandsGrootte(f.size)}</span>
                    <button
                      type="button"
                      className="fawkes-vaccination-form__bestand-weg"
                      title={t("common.delete")}
                      aria-label={t("common.delete")}
                      onClick={() => setPaspoort((eerder) => eerder.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            {paspoort.length > 0 ? (
              <div className="fawkes-vaccination-form__ai-acties">
                <p className="fawkes-vaccination-form__ai-vraag">
                  {t("animalDetailView.vaccination.dialog.nogEenFotoVraag")}
                </p>
                <Button variant="primary" onClick={() => onPassport(paspoort)} disabled={busy}>
                  {t("animalDetailView.vaccination.passport.scan")}
                </Button>
                <Button variant="secondary" onClick={() => setPaspoort([])} disabled={busy}>
                  {t("animalDetailView.vaccination.dialog.clearUploads")}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {error && <p className="fawkes-vaccination-form__error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

// ---- Bescherming: AI passport-import ---------------------------------------------------------------
// Ports the Jmix AnimalVaccinationTileFragment passport flow: upload passport photo(s) → "AI uitlezen"
// (VaccinationPassportAiService) → a review list (per-row checkbox + editable product/type/brand/dates)
// → "Uitvoeren" sends the chosen rows + the original files to POST .../vaccinations/passport-import in
// ONE multipart call. The server runs the fuzzy duplicate classification (story 24-3) — persisting only
// the NIEUW rows — and ALWAYS archives every uploaded file (images + PDFs) into Media & Documenten, even
// when 0 rows are imported (story 24-4). The old per-row POST loop + client-side exact dedup are gone.
interface PassportReviewRow {
  include: boolean;
  /** Geen enting maar een titerbepaling; die hoort niet als vaccinatie in het dossier. */
  titerbepaling: boolean;
  /** Of deze enting al bij het dier staat, zoals de server dat tijdens het uitlezen zag. */
  status: PassportSuggestion["status"];
  reden: string | null;
  productId: string; // select value (string id; "" = none chosen)
  type: string;
  brand: string;
  toedien: string; // yyyy-MM-dd
  vanaf: string;
  tot: string;
}

function VaccinationPassportDialog({ animalId, products, initialFiles, onClose, onDone, t }: {
  animalId: string; products: VaccProduct[];
  /** Bestanden die in de entingdialoog al gekozen zijn; die hoeft niemand nog een keer te zoeken. */
  initialFiles?: File[];
  onClose: () => void; onDone: (result: PassportImportResult) => void; t: Translate;
}) {
  const [step, setStep] = useState<"upload" | "review">("upload");
  const [files, setFiles] = useState<File[]>(initialFiles ?? []);
  const [rows, setRows] = useState<PassportReviewRow[]>([]);
  // Titerbepalingen komen los van de entingen terug: geen product en geen toediening, maar een meting
  // met per ziekte een uitslag. Ze worden ook langs een eigen weg vastgelegd.
  const [titers, setTiters] = useState<PassportTiterSuggestie[]>([]);
  const [titerAan, setTiterAan] = useState<boolean[]>([]);
  const { mutateAsync: bewaarTiter } = useCustomMutation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onPickFiles = (e: ChangeEvent<HTMLInputElement>) => {
    // Accept images (sent to the AI scan) AND PDFs (archived only) — the server silently skips
    // non-images for the scan but archives every uploaded file (mirrors Jmix).
    const picked = Array.from(e.target.files ?? []).filter((f) =>
      ["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(f.type),
    );
    e.target.value = "";
    if (picked.length) { setFiles((prev) => [...prev, ...picked]); setError(null); }
  };
  const removeFile = (i: number) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const toRow = (s: PassportSuggestion): PassportReviewRow => ({
    // Een titerbepaling staat standaard uit: het is geen enting, en deze import schrijft entingen weg.
    // Aanvinkbaar laten zou betekenen dat een meting als vaccinatie in het dossier belandt.
    // Een enting die al in het dossier staat ook: de server slaat die toch over, en zo zie je meteen
    // waarom er bij een tweede foto van hetzelfde boekje niets bijkomt.
    include: !s.titerbepaling && s.status !== "REEDS_AANWEZIG",
    titerbepaling: s.titerbepaling === true,
    status: s.status ?? null,
    reden: s.reden ?? null,
    productId: s.productId != null ? String(s.productId) : "",
    type: s.type ?? "",
    brand: s.brand ?? "",
    toedien: s.administrationDate ?? "",
    vanaf: s.validFrom ?? "",
    tot: s.validTo ?? "",
  });
  const patchRow = (i: number, patch: Partial<PassportReviewRow>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const runScan = async () => {
    if (files.length === 0) { setError(t("animalDetailView.vaccination.passport.uploadFirst")); return; }
    setBusy(true); setError(null);
    try {
      const res = await scanPassport(animalId, files);
      if (!res.available) { setError(t("animalDetailView.vaccination.passport.unavailable")); return; }
      const gevondenTiters = res.titerbepalingen ?? [];
      if (res.suggestions.length === 0 && gevondenTiters.length === 0) {
        setError(res.message || t("animalDetailView.vaccination.passport.none"));
        return;
      }
      setRows(res.suggestions.map(toRow));
      setTiters(gevondenTiters);
      // Een meting die al in het dossier staat laten we uit: de server legt hem toch niet twee keer
      // vast, en zo zie je meteen waarom er bij een tweede foto niets bijkomt.
      setTiterAan(gevondenTiters.map((tb) => tb.alAanwezig !== true));
      setStep("review");
    } catch {
      setError(t("animalDetailView.vaccination.passport.scanError"));
    } finally {
      setBusy(false);
    }
  };

  // Kom je hier met foto's die in de entingdialoog al gekozen zijn, dan valt er niets meer te kiezen:
  // dan leest hij meteen uit. Het tussenscherm vroeg alleen nog een keer om dezelfde bestanden.
  // Gaat het uitlezen mis, dan sta je alsnog op het keuzescherm en kun je het opnieuw proberen.
  const uitlezenGestart = useRef(false);
  useEffect(() => {
    if (uitlezenGestart.current || files.length === 0) return;
    uitlezenGestart.current = true;
    void runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runImport = async () => {
    setBusy(true); setError(null);
    // De aangevinkte regels. De server beslist met zijn dubbelcontrole wat er echt bij komt.
    // Zonder begindatum kan een enting niet bestaan, dus die regel valt af; zonder product kan het wel:
    // dan leggen we hem vast op naam en merk zoals ze in het paspoort staan. Dat is wat je wilt als
    // een vaccin niet in de productenlijst voorkomt -- uitgelezen is uitgelezen, en anders zou die
    // regel geruisloos verdwijnen.
    const importRows: PassportImportRow[] = [];
    const beginDates: string[] = [];
    for (const r of rows) {
      if (!r.include) continue;
      const begin = r.toedien || r.vanaf;
      if (!begin) continue;
      const pid = r.productId ? Number(r.productId) : null;
      const p = pid != null ? products.find((x) => x.id === pid) : undefined;
      const naam = (r.type.trim() || p?.label || r.brand.trim() || "Enting").slice(0, 50);
      const eind = r.tot || entingNextDateIso(p, begin) || null;
      importRows.push({ inentingProductId: pid, naam, merk: r.brand.trim() || null, beginDatum: begin, eindDatum: eind });
      beginDates.push(begin);
    }
    // Reference date for the archived files' display name ("Enting {date} — {filename}"): the earliest
    // confirmed begin date, else today (mirrors the Jmix givenDateField fallback to LocalDate.now()).
    const refDate = beginDates.length ? beginDates.slice().sort()[0] : todayIso();
    try {
      const result = await importPassport(animalId, files, importRows, refDate);
      // De aangevinkte titerbepalingen gaan langs hun eigen weg; ze horen niet bij de entingen.
      for (let i = 0; i < titers.length; i++) {
        if (!titerAan[i]) continue;
        await bewaarTiter({
          url: `animals/${animalId}/titerbepalingen`,
          method: "post",
          values: {
            datum: titers[i].datum,
            merk: titers[i].merk,
            notitie: null,
            uitslagen: titers[i].uitslagen,
          },
        });
      }
      onDone(result);
    } catch {
      setError(t("animalDetailView.vaccination.passport.scanError"));
    } finally {
      setBusy(false);
    }
  };

  const selectedCount = rows.filter((r) => r.include).length + titerAan.filter(Boolean).length;

  const footer = step === "upload" ? (
    <>
      <Button variant="secondary" onClick={onClose} disabled={busy}>{t("common.cancel")}</Button>
      <Button variant="primary" onClick={runScan} disabled={busy || files.length === 0}>
        {busy ? t("animalDetailView.vaccination.passport.scanning") : t("animalDetailView.vaccination.passport.scan")}
      </Button>
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose} disabled={busy}>{t("common.cancel")}</Button>
      <Button variant="primary" onClick={runImport} disabled={busy || selectedCount === 0}>
        {t("animalDetailView.vaccination.passport.execute")}
      </Button>
    </>
  );

  return (
    <Modal open title={t("animalDetailView.vaccination.passport.import")} onClose={onClose} footer={footer}>
      <div className="fawkes-passport">
        {step === "upload" && busy ? (
          /* Hij is al aan het uitlezen; de bestanden nog een keer tonen suggereert dat er nog iets
             gekozen moet worden. */
          <p className="fawkes-passport__helper" role="status">
            {t("animalDetailView.vaccination.passport.scanning")}
          </p>
        ) : step === "upload" ? (
          <>
            <p className="fawkes-passport__helper">{t("animalDetailView.vaccination.passport.uploadHelper")}</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="fawkes-passport__file-input"
              onChange={onPickFiles}
            />
            <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
              <UploadCloudIcon width={16} height={16} /> {t("animalDetailView.vaccination.passport.choose")}
            </Button>
            {files.length > 0 && (
              <ul className="fawkes-passport__files">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="fawkes-passport__file">
                    <span className="fawkes-passport__file-name">{f.name}</span>
                    <button type="button" className="fawkes-passport__file-remove"
                      aria-label={t("common.delete")} onClick={() => removeFile(i)}>×</button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <p className="fawkes-passport__summary">
              {t("animalDetailView.vaccination.passport.reviewSummary", { args: [String(rows.length)] })}
            </p>
            <div className="fawkes-passport__rows">
              {rows.map((r, i) => (
                <div key={i} className={`fawkes-passport__row${r.include ? "" : " fawkes-passport__row--off"}`}>
                  <label className="fawkes-passport__row-head">
                    {/* Een regel die al in het dossier staat kun je niet aanzetten: de server voegt hem
                        toch niet nog een keer toe, en een vinkje dat niets doet is erger dan geen vinkje. */}
                    <input type="checkbox" checked={r.include}
                      disabled={r.titerbepaling || r.status === "REEDS_AANWEZIG"}
                      onChange={(e) => patchRow(i, { include: e.target.checked })} />
                    <span className="fawkes-passport__row-title">
                      {r.type.trim() || r.brand.trim() || t("animalDetailView.vaccination.passport.lineFallback")}
                      {r.titerbepaling ? (
                        <em className="fawkes-passport__row-warn"> · {t("animalDetailView.vaccination.passport.titer")}</em>
                      ) : r.status === "REEDS_AANWEZIG" ? (
                        <em className="fawkes-passport__row-warn"> · {t("animalDetailView.vaccination.passport.alAanwezig")}</em>
                      ) : r.status === "MOGELIJK_DUPLICAAT" ? (
                        <em className="fawkes-passport__row-warn"> · {t("animalDetailView.vaccination.passport.mogelijkDubbel")}</em>
                      ) : (
                        !r.productId && <em className="fawkes-passport__row-warn"> · {t("animalDetailView.vaccination.passport.noProduct")}</em>
                      )}
                    </span>
                  </label>
                  {r.titerbepaling && (
                    <p className="fawkes-passport__helper">{t("animalDetailView.vaccination.passport.titerUitleg")}</p>
                  )}
                  {!r.titerbepaling && r.status === "REEDS_AANWEZIG" && (
                    <p className="fawkes-passport__helper">{t("animalDetailView.vaccination.passport.alAanwezigUitleg")}</p>
                  )}
                  {!r.titerbepaling && r.status === "MOGELIJK_DUPLICAAT" && (
                    <p className="fawkes-passport__helper">{t("animalDetailView.vaccination.passport.mogelijkDubbelUitleg")}</p>
                  )}
                  {!r.titerbepaling && !r.productId && (
                    <p className="fawkes-passport__helper">{t("animalDetailView.vaccination.passport.zonderProductUitleg")}</p>
                  )}
                  <div className="fawkes-passport__fields">
                    <label className="fawkes-passport__field">
                      <span>{t("animalDetailView.vaccination.passport.product")}</span>
                      <select className="fui-field__input fui-select" value={r.productId}
                        onChange={(e) => patchRow(i, { productId: e.target.value })}>
                        <option value="">{t("animalDetailView.vaccination.dialog.productPlaceholder")}</option>
                        {products.map((p) => <option key={p.id} value={String(p.id)}>{p.label}</option>)}
                      </select>
                    </label>
                    <label className="fawkes-passport__field">
                      <span>{t("animalDetailView.vaccination.passport.type")}</span>
                      <input className="fui-field__input" value={r.type} onChange={(e) => patchRow(i, { type: e.target.value })} />
                    </label>
                    <label className="fawkes-passport__field">
                      <span>{t("animalDetailView.vaccination.passport.brand")}</span>
                      <input className="fui-field__input" value={r.brand} onChange={(e) => patchRow(i, { brand: e.target.value })} />
                    </label>
                    <label className="fawkes-passport__field">
                      <span>{t("animalDetailView.vaccination.passport.toedien")}</span>
                      <DateField className="fui-field__input" value={r.toedien} onChange={(e) => patchRow(i, { toedien: e.target.value })} />
                    </label>
                    <label className="fawkes-passport__field">
                      <span>{t("animalDetailView.vaccination.passport.vanaf")}</span>
                      <DateField className="fui-field__input" value={r.vanaf} onChange={(e) => patchRow(i, { vanaf: e.target.value })} />
                    </label>
                    <label className="fawkes-passport__field">
                      <span>{t("animalDetailView.vaccination.passport.tot")}</span>
                      <DateField className="fui-field__input" value={r.tot} onChange={(e) => patchRow(i, { tot: e.target.value })} />
                    </label>
                  </div>
                </div>
              ))}
            </div>
            {titers.length > 0 && (
              <div className="fawkes-passport__titers">
                <h4 className="fawkes-passport__titers-kop">
                  {t("animalDetailView.vaccination.passport.titersKop")}
                </h4>
                <p className="fawkes-passport__helper">
                  {t("animalDetailView.vaccination.passport.titersUitleg")}
                </p>
                {titers.map((tb, i) => (
                  <div key={i} className={`fawkes-passport__row${titerAan[i] ? "" : " fawkes-passport__row--off"}`}>
                    <label className="fawkes-passport__row-head">
                      {/* Staat hij er al, dan kun je hem niet aanzetten: de server legt dezelfde meting
                          toch maar één keer vast, en een vinkje dat niets doet liegt. */}
                      <input type="checkbox" checked={titerAan[i] === true} disabled={tb.alAanwezig === true}
                        onChange={(e) => setTiterAan((prev) => prev.map((aan, j) => (j === i ? e.target.checked : aan)))} />
                      <span className="fawkes-passport__row-title">
                        {tb.merk?.trim() || t("animalDetailView.vaccination.passport.titer")}
                        {tb.datum && <em className="fawkes-passport__row-warn"> · {tb.datum}</em>}
                        {tb.alAanwezig === true && (
                          <em className="fawkes-passport__row-warn">
                            {" "}· {t("animalDetailView.vaccination.passport.alAanwezig")}
                          </em>
                        )}
                      </span>
                    </label>
                    {tb.alAanwezig === true && (
                      <p className="fawkes-passport__helper">
                        {t("animalDetailView.vaccination.passport.titerAlAanwezigUitleg")}
                      </p>
                    )}
                    <ul className="fawkes-passport__titer-uitslagen">
                      {tb.uitslagen.map((u, j) => (
                        <li key={j}>
                          <span>{u.ziekte}</span>
                          {u.score && <b> {u.score}</b>}
                          {u.voldoende === true && <em> · {t("animalDetailView.vaccination.passport.titerVoldoende")}</em>}
                          {u.voldoende === false && <em> · {t("animalDetailView.vaccination.passport.titerOnvoldoende")}</em>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {error && <p className="fawkes-passport__error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

/** Eén vastgelegde titerbepaling zoals de server hem teruggeeft. */
interface Titerbepaling {
  id: string;
  datum: string | null;
  merk: string | null;
  notitie: string | null;
  uitslagen: { ziekte: string; score: string | null; voldoende: boolean | null }[];
}

export function VaccinationsTile({ animalId, openInentingId }: { animalId: string; openInentingId?: number | null }) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useTile<Vaccination[]>(animalId, "vaccinations");
  const { data: productData } = useTile<VaccProduct[]>(animalId, "vaccinations/products");
  const products = productData ?? [];
  const rows = data ?? [];

  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [showRest, setShowRest] = useState(false);
  const [sortering, setSortering] = useState<"nieuwste" | "oudste">("nieuwste");
  const [dialog, setDialog] = useState<{ mode: DialogMode; record: Vaccination | null } | null>(null);
  // Een taak "Enting", "Ontwormen" of "Ontvlooien" uitvoeren: het invoerscherm, en na opslaan is de taak af.
  const taak = useTaakVoorTegel(animalId, ["ENTING", "ONTWORMING", "ONTVLOOIING"],
    (u) => setDialog({ mode: u.handeling === "ENTING" ? "enting" : "parasiet", record: null }), dialog !== null);
  const [passportOpen, setPassportOpen] = useState(false);
  // Bestanden die in de entingdialoog al gekozen zijn, zodat het uitleesscherm er meteen mee begint.
  const [passportFiles, setPassportFiles] = useState<File[]>([]);
  const [aiAvailable, setAiAvailable] = useState(false);
  const [importNotice, setImportNotice] = useState<string | null>(null);
  // Titerbepalingen staan naast de entingen: ze zeggen of enten nodig is, maar zijn zelf geen enting.
  const { data: titerData, refetch: refetchTiters } = useCustom<Titerbepaling[]>({
    url: `animals/${animalId}/titerbepalingen`, method: "get",
  });
  const titerbepalingen = useMemo(
    () => (Array.isArray(titerData?.data) ? titerData.data : []),
    [titerData],
  );

  // Gate the "Importeer uit paspoort" action on a configured provider (mirrors hasUsableProvider).
  useEffect(() => {
    let active = true;
    fetchPassportAiAvailability(animalId).then((v) => { if (active) setAiAvailable(v); });
    return () => { active = false; };
  }, [animalId]);

  // Deep-link from the Entingen-/Ontwormen-overzicht "Bewerken": once the records have loaded, open the
  // edit dialog on the requested inenting. The dialog derives its mode (enting vs parasiet) from the
  // record itself, so no mode needs to be carried. Re-runs only when the requested id changes; if the id
  // is not in the fetched list (stale link / hidden record) it opens nothing — the tile shows normally.
  const handledDeepLink = useRef<number | null>(null);
  useEffect(() => {
    if (openInentingId == null || handledDeepLink.current === openInentingId) return;
    const match = rows.find((v) => v.id === openInentingId);
    if (match) {
      handledDeepLink.current = openInentingId;
      setDialog({ mode: "edit", record: match });
    }
  }, [openInentingId, rows]);

  const filtered = useMemo(
    () => {
      const gevonden = rows.filter((v) => matchesFilters(v, typeFilter, statusFilter, search));
      // Sorteren op de datum waarop het gegeven is; bij gelijke datum blijft de oude volgorde staan,
      // die zet wat herhaald moet worden bovenaan.
      const opDatum = sortByRepeatPriority(gevonden).sort((a, b) => {
        const va = a.beginDatum ?? "";
        const vb = b.beginDatum ?? "";
        return sortering === "oudste" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
      return opDatum;
    },
    [rows, typeFilter, statusFilter, search, sortering],
  );
  const preview = filtered.slice(0, PREVIEW_LIMIT);
  const rest = filtered.slice(PREVIEW_LIMIT);

  if (isLoading || isError) {
    return <Status isLoading={isLoading} isError={isError} t={t} />;
  }

  const onSaved = () => { if (dialog?.mode !== "edit") taak.klaar(); setDialog(null); setShowRest(false); refetch(); };

  const typeOptions: TypeFilter[] = ["all", "enting", "ontwormen", "ontvlooien"];
  // De statuskeuzelijst is uit de mockup verdwenen; het belletje rechtsboven zet statusFilter nog wel.

  return (
    <div className="fawkes-vaccination">
      {/* Kop: waar de tegel over gaat, met rechts de knop naar wat aandacht vraagt. */}
      <div className="bs-kop">
        <span className="bs-kop__merk" aria-hidden="true"><ShieldIcon width={26} height={26} /></span>
        <span className="bs-kop__tekst">
          <strong>{t("animalDetailView.tile.bescherming")}</strong>
          <small>{t("bescherming.subtitel")}</small>
        </span>
        <button type="button" className={"bs-kop__bel" + (statusFilter === "verlopen" ? " is-aan" : "")}
          title={t("bescherming.aandacht")} aria-label={t("bescherming.aandacht")}
          aria-pressed={statusFilter === "verlopen"}
          onClick={() => setStatusFilter(statusFilter === "verlopen" ? "all" : "verlopen")}>
          <BelGlyph />
        </button>
      </div>

      {/* Drie samenvattingen; een klik filtert de lijst op die soort. */}
      <div className="bs-samenvatting">
        {(["enting", "ontwormen", "ontvlooien"] as const).map((soort) => {
          const sv = samenvatting(rows, soort);
          const afstand = afstandTot(sv.volgende, t);
          return (
            <button key={soort} type="button"
              className={"bs-kaart bs-kaart--" + soort + (typeFilter === soort ? " is-aan" : "")}
              aria-pressed={typeFilter === soort}
              onClick={() => setTypeFilter(typeFilter === soort ? "all" : soort)}>
              <span className="bs-kaart__icoon" aria-hidden="true">
                <img src={BESCHERMING_ICOON[soort]} alt="" loading="lazy" />
              </span>
              <span className="bs-kaart__tekst">
                <span className="bs-kaart__label">{t(`animalDetailView.vaccination.type.${soort}`)}</span>
                <strong className="bs-kaart__aantal">{sv.aantal}</strong>
                <span className="bs-kaart__volgende">
                  {afstand
                    ? <>{t("bescherming.volgendeOver")} <b>{afstand}</b></>
                    : t("bescherming.geenVolgende")}
                </span>
              </span>
              <ChevronRightIcon className="bs-kaart__pijl" width={18} height={18} aria-hidden="true" />
            </button>
          );
        })}
      </div>

      <div className="bs-filters">
        <span className="bs-zoek">
          <SearchIcon width={17} height={17} aria-hidden="true" />
          <input className="fui-field__input" value={search}
            placeholder={t("animalDetailView.vaccination.filter.searchPlaceholder")}
            aria-label={t("animalDetailView.vaccination.filter.searchPlaceholder")}
            onChange={(e) => setSearch(e.target.value)} />
        </span>
        <select className="fui-field__input fui-select" value={typeFilter}
          aria-label={t("animalDetailView.vaccination.filter.typeLabel")}
          onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}>
          {typeOptions.map((o) => (
            <option key={o} value={o}>
              {o === "all" ? t("animalDetailView.vaccination.filter.all") : t(`animalDetailView.vaccination.type.${o}`)}
            </option>
          ))}
        </select>
        <select className="fui-field__input fui-select" value={sortering}
          aria-label={t("bescherming.sorteren")}
          onChange={(e) => setSortering(e.target.value as "nieuwste" | "oudste")}>
          <option value="nieuwste">{t("bescherming.sorteer.nieuwste")}</option>
          <option value="oudste">{t("bescherming.sorteer.oudste")}</option>
        </select>
      </div>

      <div className="bs-lijst">
        {preview.length === 0 ? (
          <p className="fawkes-vaccination-empty">{t("animalDetailView.vaccination.empty")}</p>
        ) : (
          preview.map((v) => (
            <VaccinationCard key={v.id} v={v} t={t} onClick={() => setDialog({ mode: "edit", record: v })} />
          ))
        )}
      </div>

      {rest.length > 0 && (
        <Button variant="secondary" className="fawkes-vaccination-rest-btn" onClick={() => setShowRest(true)}>
          {t("animalDetailView.vaccination.viewRemaining")}
        </Button>
      )}

      {titerbepalingen.length > 0 && (
        <div className="bs-titers">
          <h4 className="bs-titers__kop">{t("animalDetailView.vaccination.titers.kop")}</h4>
          {titerbepalingen.map((tb) => (
            <div key={tb.id} className="bs-titers__regel">
              <span className="bs-titers__kop-regel">
                <b>{tb.merk?.trim() || t("animalDetailView.vaccination.titers.meting")}</b>
                {tb.datum && <span className="bs-titers__datum">{tb.datum}</span>}
              </span>
              <ul className="bs-titers__uitslagen">
                {tb.uitslagen.map((u, i) => (
                  <li key={i} className={
                    u.voldoende === true ? "is-voldoende" : u.voldoende === false ? "is-onvoldoende" : ""
                  }>
                    <span>{u.ziekte}</span>
                    {u.score && <b> {u.score}</b>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {importNotice && (
        <p className="fawkes-vaccination-import-notice" role="status">{importNotice}</p>
      )}

      {/* Drie knoppen zoals de mockup. Ontworming en ontvlooiing openen hetzelfde venster; dat is er
          één voor allebei, en daarin kies je het middel. */}
      <div className="bs-knoppen">
        <button type="button" className="bs-knop bs-knop--enting"
          onClick={() => setDialog({ mode: "enting", record: null })}>
          <PlusCircleSolidIcon width={18} height={18} /> {t("animalDetailView.vaccination.add.enting")}
        </button>
        <button type="button" className="bs-knop bs-knop--ontwormen"
          onClick={() => setDialog({ mode: "parasiet", record: null })}>
          <PlusCircleSolidIcon width={18} height={18} /> {t("bescherming.add.ontwormen")}
        </button>
        <button type="button" className="bs-knop bs-knop--ontvlooien"
          onClick={() => setDialog({ mode: "parasiet", record: null })}>
          <PlusCircleSolidIcon width={18} height={18} /> {t("bescherming.add.ontvlooien")}
        </button>
      </div>

      {showRest && (
        <Modal
          open
          title={t("animalDetailView.vaccination.remainingTitle")}
          onClose={() => setShowRest(false)}
          footer={<Button variant="primary" onClick={() => setShowRest(false)}>{t("common.close")}</Button>}
        >
          <div className="fawkes-vaccination-cards">
            {rest.map((v) => (
              <VaccinationCard key={v.id} v={v} t={t}
                onClick={() => { setShowRest(false); setDialog({ mode: "edit", record: v }); }} />
            ))}
          </div>
        </Modal>
      )}

      {dialog && (
        <VaccinationDialog
          key={`${dialog.mode}-${dialog.record?.id ?? "new"}`}
          animalId={animalId} mode={dialog.mode} record={dialog.record} products={products}
          records={rows} aiAvailable={aiAvailable}
          onPassport={(files) => { setDialog(null); setImportNotice(null); setPassportFiles(files); setPassportOpen(true); }}
          onClose={() => setDialog(null)} onSaved={onSaved} t={t}
        />
      )}

      {passportOpen && (
        <VaccinationPassportDialog
          animalId={animalId} products={products} initialFiles={passportFiles}
          onClose={() => { setPassportOpen(false); setPassportFiles([]); }}
          onDone={(result) => {
            setPassportOpen(false);
            setImportNotice(t("animalDetailView.vaccination.passport.resultDetailed", {
              args: [
                String(result.added),
                String(result.reedsAanwezig),
                String(result.mogelijkDuplicaat),
                String(result.archivedFiles),
              ],
            }));
            refetch();
            refetchTiters();
          }}
          t={t}
        />
      )}
    </div>
  );
}

// ---- Notities --------------------------------------------------------------------------------------
// Functional port of the Jmix notes tile (AnimalNotesTileFragment): a filter bar (date range · tag ·
// search) over a pinned-first, date-desc list of note cards (a preview of 3 + a "Rest bekijken"
// dialog), each card carrying pin/edit/delete hover actions, plus an add button. Create/edit open a
// dialog (title/date/note/tags/follow-up/author/pinned); pinning enforces the single-pinned-note rule
// server-side. Reads GET /api/animals/{id}/notes + the tag catalog .../notes/tag-options; writes
// POST/PUT/DELETE under .../notes (gated by ROLE_ANIMAL_NOTE_EDIT). Tag chips use the tag's colour.
interface NoteTag { id: number; label: string; color: string | null }
interface Note {
  id: number; date: string | null; title: string | null; note: string | null;
  author: string | null; pinned: boolean; followUpDate: string | null; tags: NoteTag[];
  version: number | null;
}
interface NoteTagOption { id: number; label: string; color: string | null }

const NOTE_PREVIEW_LIMIT = 3;
type NoteDateRange = "3m" | "1m" | "all";

interface NoteForm {
  title: string; date: string; note: string; author: string;
  followUpDate: string; pinned: boolean; tagIds: number[];
}

function emptyNoteForm(): NoteForm {
  return { title: "", date: "", note: "", author: "", followUpDate: "", pinned: false, tagIds: [] };
}

// yyyy-MM-dd from a LocalDate or LocalDateTime ISO string (the native date-input grain).
function toDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}
function todayInput(): string {
  return todayIso();
}
function daysAgoInput(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return localDateIso(d);
}

// Pinned first, then newest first — the Jmix sortNotes order (ISO strings sort lexically).
function sortNotes(notes: Note[]): Note[] {
  return [...notes].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return (b.date ?? "").localeCompare(a.date ?? "");
  });
}

function NoteCard({ note, t, onEdit, onTogglePin, onDelete }: {
  note: Note; t: (k: string) => string;
  onEdit: () => void; onTogglePin: () => void; onDelete: () => void;
}) {
  const pinLabel = t(note.pinned ? "animalDetailView.note.action.unpin" : "animalDetailView.note.action.pin");
  return (
    <article className={`fawkes-notes__card${note.pinned ? " fawkes-notes__card--pinned" : ""}`}>
      <header className="fawkes-notes__header">
        <span className="fawkes-notes__header-left">
          <span className="fawkes-notes__date">{fmtFormalDate(note.date)}</span>
          {note.title && <span className="fawkes-notes__title">{note.title}</span>}
          {note.pinned && <span className="fawkes-notes__pinned">{t("animalDetailView.note.pinned")}</span>}
        </span>
        <span className="fawkes-notes__actions">
          <button
            type="button"
            className={`fawkes-notes__action${note.pinned ? " fawkes-notes__action--pinned" : ""}`}
            title={pinLabel} aria-label={pinLabel} onClick={onTogglePin}
          >
            <PinIcon />
          </button>
          <button
            type="button" className="fawkes-notes__action"
            title={t("animalDetailView.note.action.edit")} aria-label={t("animalDetailView.note.action.edit")}
            onClick={onEdit}
          >
            <PencilIcon />
          </button>
          <button
            type="button" className="fawkes-notes__action fawkes-notes__action--delete"
            title={t("animalDetailView.note.action.delete")} aria-label={t("animalDetailView.note.action.delete")}
            onClick={onDelete}
          >
            <TrashIcon />
          </button>
        </span>
      </header>
      {note.note && <p className="fawkes-notes__body">{note.note}</p>}
      {note.tags.length > 0 && (
        <div className="fawkes-notes__tags">
          {note.tags.map((tag, i) => (
            <span
              key={`${tag.id}-${i}`} className="fawkes-notes__tag"
              style={tag.color ? { backgroundColor: tag.color } : undefined}
            >
              {tag.label}
            </span>
          ))}
        </div>
      )}
      <footer className="fawkes-notes__footer">
        <span className={`fawkes-notes__followup${note.followUpDate ? " fawkes-notes__followup--set" : ""}`}>
          {note.followUpDate && `${t("animalDetailView.note.followUp")}: ${fmtFormalDate(note.followUpDate)}`}
        </span>
        {note.author && (
          <span className="fawkes-notes__author">{t("animalDetailView.note.by")}: {note.author}</span>
        )}
      </footer>
    </article>
  );
}

// The create/edit dialog — title/date/note/tags/follow-up/author/pinned, mirroring the Jmix note
// dialog (all-but-follow-up required). Re-seeds whenever it (re)opens.
function NoteDialog({ open, mode, initial, catalog, currentUserName, saving, onClose, onSubmit, t }: {
  open: boolean; mode: "create" | "edit"; initial: Note | null; catalog: NoteTagOption[];
  currentUserName: string; saving: boolean; onClose: () => void;
  onSubmit: (form: NoteForm) => void; t: (k: string) => string;
}) {
  const [form, setForm] = useState<NoteForm>(emptyNoteForm());
  // Field-specific validation message key (Jmix shows one warning per missing field, in order).
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrorKey(null);
    if (mode === "edit" && initial) {
      setForm({
        title: initial.title ?? "", date: toDateInput(initial.date) || todayInput(),
        note: initial.note ?? "", author: initial.author ?? "",
        followUpDate: toDateInput(initial.followUpDate), pinned: initial.pinned,
        tagIds: initial.tags.map((tg) => tg.id),
      });
    } else {
      setForm({ ...emptyNoteForm(), date: todayInput(), author: currentUserName });
    }
  }, [open, mode, initial, currentUserName]);

  const setField = <K extends keyof NoteForm>(key: K, value: NoteForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrorKey(null);
  };
  const toggleTag = (id: number) => setForm((f) => ({
    ...f,
    tagIds: f.tagIds.includes(id) ? f.tagIds.filter((x) => x !== id) : [...f.tagIds, id],
  }));
  const submit = () => {
    // Validate in the Jmix dialog's order, surfacing the first missing field's message.
    if (!form.title.trim()) { setErrorKey("animalDetailView.note.validation.title"); return; }
    if (!form.date) { setErrorKey("animalDetailView.note.validation.date"); return; }
    if (!form.note.trim()) { setErrorKey("animalDetailView.note.validation.note"); return; }
    if (!form.author.trim()) { setErrorKey("animalDetailView.note.validation.author"); return; }
    onSubmit(form);
  };

  return (
    <Modal
      open={open}
      title={t(mode === "edit" ? "animalDetailView.note.dialog.editTitle" : "animalDetailView.note.dialog.createTitle")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={submit} disabled={saving}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-notes__form">
        {errorKey && (
          <span className="fawkes-notes__notice fawkes-notes__notice--required" role="alert">
            {t(errorKey)}
          </span>
        )}
        <label className="fawkes-notes__form-field">
          <span className="fawkes-notes__form-label">
            {t("animalDetailView.note.field.title")}
            <span className="fawkes-notes__required" aria-hidden="true">*</span>
          </span>
          <input className="fui-field__input" value={form.title} maxLength={255} onChange={(e) => setField("title", e.target.value)} />
        </label>
        <label className="fawkes-notes__form-field">
          <span className="fawkes-notes__form-label">
            {t("animalDetailView.note.field.date")}
            <span className="fawkes-notes__required" aria-hidden="true">*</span>
          </span>
          <DateField className="fui-field__input" value={form.date} onChange={(e) => setField("date", e.target.value)} />
        </label>
        <label className="fawkes-notes__form-field">
          <span className="fawkes-notes__form-label">
            {t("animalDetailView.note.field.note")}
            <span className="fawkes-notes__required" aria-hidden="true">*</span>
          </span>
          <textarea className="fui-field__input fawkes-notes__textarea" value={form.note} rows={4} onChange={(e) => setField("note", e.target.value)} />
        </label>
        {catalog.length > 0 && (
          <div className="fawkes-notes__form-field">
            <span className="fawkes-notes__form-label">{t("animalDetailView.note.field.tags")}</span>
            <div className="fawkes-notes__tag-picker">
              {catalog.map((tag) => (
                <label key={tag.id} className="fawkes-notes__tag-option">
                  <input type="checkbox" checked={form.tagIds.includes(tag.id)} onChange={() => toggleTag(tag.id)} />
                  {tag.label}
                </label>
              ))}
            </div>
          </div>
        )}
        <label className="fawkes-notes__form-field">
          <span className="fawkes-notes__form-label">{t("animalDetailView.note.field.followUp")}</span>
          <DateField className="fui-field__input" value={form.followUpDate} onChange={(e) => setField("followUpDate", e.target.value)} />
        </label>
        <label className="fawkes-notes__form-field">
          <span className="fawkes-notes__form-label">
            {t("animalDetailView.note.field.author")}
            <span className="fawkes-notes__required" aria-hidden="true">*</span>
          </span>
          <input className="fui-field__input" value={form.author} maxLength={100} onChange={(e) => setField("author", e.target.value)} />
        </label>
        <label className="fawkes-notes__pinned-toggle">
          <input type="checkbox" checked={form.pinned} onChange={(e) => setField("pinned", e.target.checked)} />
          {t("animalDetailView.note.field.pinned")}
        </label>
      </div>
    </Modal>
  );
}

export function NotesTile({ animalId }: { animalId: string }) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useCustom({ url: `animals/${animalId}/notes`, method: "get" });
  const rows = (data?.data as Note[] | undefined) ?? [];

  // The tag catalog (gated by ROLE_ANIMAL_NOTE_EDIT) — drives the dialog picker + the filter dropdown.
  const { data: tagData } = useCustom({ url: `animals/${animalId}/notes/tag-options`, method: "get" });
  const catalog = (tagData?.data as NoteTagOption[] | undefined) ?? [];

  const { data: identity } = useGetIdentity<{ id?: string; name?: string }>();
  const currentUserName = identity?.name ?? identity?.id ?? "";

  const { mutate: runMutation, isLoading: mutating } = useCustomMutation();

  const [dateRange, setDateRange] = useState<NoteDateRange>("3m");
  const [search, setSearch] = useState("");
  const [tagFilters, setTagFilters] = useState<NoteTagOption[]>([]);

  const [dialog, setDialog] = useState<{ mode: "create" | "edit"; note: Note | null } | null>(null);
  // Een taak "Notitie" uitvoeren: een nieuwe notitie, en na opslaan is de taak af.
  const taak = useTaakVoorTegel(animalId, ["NOTITIE"], () => setDialog({ mode: "create", note: null }), dialog !== null);
  const [deleteTarget, setDeleteTarget] = useState<Note | null>(null);
  const [showRest, setShowRest] = useState(false);
  const [notice, setNotice] = useState<"saved" | "deleted" | "error" | "conflict" | null>(null);

  const filtered = useMemo(() => {
    let out = sortNotes(rows);
    if (dateRange !== "all") {
      const cutoff = daysAgoInput(dateRange === "3m" ? 90 : 30);
      out = out.filter((n) => toDateInput(n.date) >= cutoff);
    }
    if (tagFilters.length > 0) {
      const ids = new Set(tagFilters.map((tg) => tg.id));
      out = out.filter((n) => n.tags.some((tg) => ids.has(tg.id)));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((n) =>
        (n.title ?? "").toLowerCase().includes(q) || (n.note ?? "").toLowerCase().includes(q));
    }
    return out;
  }, [rows, dateRange, tagFilters, search]);

  if (isLoading || isError) {
    return <Status isLoading={isLoading} isError={isError} t={t} />;
  }

  const preview = filtered.slice(0, NOTE_PREVIEW_LIMIT);
  const remaining = filtered.slice(NOTE_PREVIEW_LIMIT);

  const addTagFilter = (id: string) => {
    const tag = catalog.find((tg) => String(tg.id) === id);
    if (tag && !tagFilters.some((tf) => tf.id === tag.id)) setTagFilters((prev) => [...prev, tag]);
  };
  const removeTagFilter = (id: number) => setTagFilters((prev) => prev.filter((tf) => tf.id !== id));

  const afterWrite = (kind: "saved" | "deleted") => {
    setNotice(kind);
    refetch();
  };
  const onWriteError = (err: unknown) =>
    setNotice((err as { statusCode?: number }).statusCode === 409 ? "conflict" : "error");

  const submitNote = (form: NoteForm) => {
    const editing = dialog?.mode === "edit" ? dialog.note : null;
    const values = {
      title: form.title.trim(), date: form.date, note: form.note.trim(), author: form.author.trim(),
      followUpDate: form.followUpDate || null, pinned: form.pinned, tagIds: form.tagIds,
      version: editing?.version ?? undefined,
    };
    const url = editing ? `animals/${animalId}/notes/${editing.id}` : `animals/${animalId}/notes`;
    runMutation(
      { url, method: editing ? "put" : "post", values },
      { onSuccess: () => { if (!editing) taak.klaar(); setDialog(null); afterWrite("saved"); }, onError: onWriteError },
    );
  };

  const togglePin = (note: Note) => {
    runMutation(
      {
        url: `animals/${animalId}/notes/${note.id}/pinned`,
        method: "put",
        values: { pinned: !note.pinned, version: note.version ?? undefined },
      },
      { onSuccess: () => afterWrite("saved"), onError: onWriteError },
    );
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    runMutation(
      { url: `animals/${animalId}/notes/${deleteTarget.id}`, method: "delete", values: {} },
      { onSuccess: () => { setDeleteTarget(null); afterWrite("deleted"); }, onError: onWriteError },
    );
  };

  const noticeText =
    notice === "saved" ? t("animalDetailView.note.saved")
    : notice === "deleted" ? t("animalDetailView.note.deleted")
    : notice === "conflict" ? t("animalDetailView.note.saveConflict")
    : notice === "error" ? t("animalDetailView.note.saveError")
    : null;

  const renderCard = (n: Note) => (
    <NoteCard
      key={n.id} note={n} t={t}
      onEdit={() => { setNotice(null); setDialog({ mode: "edit", note: n }); }}
      onTogglePin={() => togglePin(n)}
      onDelete={() => { setNotice(null); setDeleteTarget(n); }}
    />
  );

  return (
    <div className="fawkes-notes">
      <div className="fawkes-notes__filter-bar">
        <select
          className="fui-field__input fui-select fawkes-notes__filter-date"
          value={dateRange} aria-label={t("animalDetailView.note.filter.dateRange")}
          onChange={(e) => setDateRange(e.target.value as NoteDateRange)}
        >
          <option value="3m">{t("animalDetailView.note.filter.range.3m")}</option>
          <option value="1m">{t("animalDetailView.note.filter.range.1m")}</option>
          <option value="all">{t("animalDetailView.note.filter.range.all")}</option>
        </select>
        {catalog.length > 0 && (
          <select
            className="fui-field__input fui-select fawkes-notes__filter-tag"
            value="" aria-label={t("animalDetailView.note.filter.tag")}
            onChange={(e) => { addTagFilter(e.target.value); e.currentTarget.value = ""; }}
          >
            <option value="">{t("animalDetailView.note.filter.tagPlaceholder")}</option>
            {catalog.map((tag) => <option key={tag.id} value={tag.id}>{tag.label}</option>)}
          </select>
        )}
        <input
          type="search" autoComplete="off" className="fui-field__input fawkes-notes__filter-search"
          value={search} placeholder={t("animalDetailView.note.filter.search")}
          aria-label={t("animalDetailView.note.filter.search")}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {tagFilters.length > 0 && (
        <div className="fawkes-notes__chips">
          {tagFilters.map((tag) => (
            <button
              type="button" key={tag.id} className="fawkes-notes__chip"
              style={tag.color ? { backgroundColor: tag.color } : undefined}
              onClick={() => removeTagFilter(tag.id)}
            >
              {tag.label}
            </button>
          ))}
          <button type="button" className="fawkes-notes__clear-filters" onClick={() => setTagFilters([])}>
            {t("animalDetailView.note.filter.clear")}
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="fawkes-notes__empty">{t("animalDetailView.note.empty")}</p>
      ) : (
        preview.map(renderCard)
      )}

      <button
        type="button" className="fawkes-notes__view-rest"
        disabled={remaining.length === 0} onClick={() => setShowRest(true)}
      >
        {t("animalDetailView.note.viewRest")}
      </button>

      <button
        type="button" className="fawkes-notes__add"
        onClick={() => { setNotice(null); setDialog({ mode: "create", note: null }); }}
      >
        <PlusCircleSolidIcon />
        {t("animalDetailView.note.add")}
      </button>

      {noticeText && (
        <span className={`fawkes-notes__notice fawkes-notes__notice--${notice}`} role="status">{noticeText}</span>
      )}

      <NoteDialog
        open={dialog !== null}
        mode={dialog?.mode ?? "create"}
        initial={dialog?.note ?? null}
        catalog={catalog}
        currentUserName={currentUserName}
        saving={mutating}
        onClose={() => setDialog(null)}
        onSubmit={submitNote}
        t={t}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title={t("animalDetailView.note.delete.title")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        busy={mutating}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      >
        {deleteTarget?.title
          ? t("animalDetailView.note.delete.messageNamed", { args: [deleteTarget.title] })
          : t("animalDetailView.note.delete.message")}
      </ConfirmDialog>

      <Modal
        open={showRest}
        title={t("animalDetailView.note.dialog.remainingTitle")}
        onClose={() => setShowRest(false)}
        footer={<Button variant="primary" onClick={() => setShowRest(false)}>{t("common.close")}</Button>}
      >
        <div className="fawkes-notes__popup-list">
          {remaining.length === 0
            ? <p className="fawkes-notes__empty">{t("animalDetailView.note.empty")}</p>
            : remaining.map(renderCard)}
        </div>
      </Modal>
    </div>
  );
}

// ---- Media -----------------------------------------------------------------------------------------
/** De periodes van het datumfilter, in de volgorde van acc. */
export const MEDIA_DATUM_FILTERS = ['6m', '12m', 'alles'] as const;
export type MediaDatumFilter = (typeof MEDIA_DATUM_FILTERS)[number];

/** Foto's, documenten, of allebei. */
// Video staat er sinds de kaarten los, en niet uit netheid: een filmpje viel onder "Documenten",
// terwijl het nu als video wordt getoond mét afspeelknop. Een filter dat het ergens anders
// onderbrengt dan waar het op lijkt, laat je zoeken.
export const MEDIA_SOORT_FILTERS = ['alles', 'foto', 'video', 'document'] as const;
export type MediaSoortFilter = (typeof MEDIA_SOORT_FILTERS)[number];

/**
 * Valt dit bestand onder de gekozen soort? "document" is met opzet het restant: alles wat geen foto
 * en geen video is. Zou het op een lijst van bekende documenttypen staan, dan verdween een bestand
 * met een onbekend type uit élk filter — en dan is het weg zonder dat iemand het merkt.
 */
export function valtOnderSoort(contentType: string | null, filter: MediaSoortFilter): boolean {
  if (filter === 'alles') return true;
  if (filter === 'foto') return isImage(contentType);
  if (filter === 'video') return isVideo(contentType);
  return !isImage(contentType) && !isVideo(contentType);
}

/**
 * Valt dit bestand binnen de gekozen periode?
 *
 * <p>Standaard staat het filter op een half jaar, net als op acc: een dier dat er jaren is heeft
 * tientallen foto's en je zoekt bijna altijd de recente. Een bestand zonder datum blijft staan —
 * dat is een gegevensgat en geen reden om het te verstoppen.
 */
export function binnenMediaPeriode(
  item: { uploadedAt?: string | null },
  filter: MediaDatumFilter,
  nu = new Date(),
): boolean {
  if (filter === 'alles' || !item.uploadedAt) return true;
  const maanden = filter === '6m' ? 6 : 12;
  const grens = new Date(nu);
  grens.setMonth(grens.getMonth() - maanden);
  return item.uploadedAt.slice(0, 10) >= grens.toISOString().slice(0, 10);
}

// The Media & documenten tile's FUNCTIONAL form. A close port of the Jmix `AnimalMediaTileFragment`
// (fragment/animal/animal-media-tile-fragment.{xml,java,css}): a uniform square-card grid of files —
// real image thumbnails (fetched authenticated, since the bearer can't ride a plain <img src>) or a
// doc glyph — each with a hover overlay (open / download / edit / delete) + coloured tag chips, and a
// dashed "add file" card opening the upload dialog. Backend: GET list + tag-options, POST upload,
// GET {id}/content, PUT {id} (metadata + optimistic-lock version), DELETE {id} (soft). The Jmix
// camera capture, image rotate, lightbox, copy-link and the date/type/tag filters are a flagged
// follow-up (Core CRUD scope, 2026-06-09).
export const tagChipStyle = (color: string | null): CSSProperties | undefined =>
  color ? { backgroundColor: color, color: "#fff" } : undefined;

export function isImage(contentType: string | null): boolean {
  return !!contentType && contentType.startsWith("image/");
}
// Jmix resolveFileIcon: pdf/text → FILE_TEXT glyph, everything else → the generic FILE glyph.

/**
 * Een video. Die mag al geüpload worden (de allow-list heeft "video/*"), maar viel tot nu toe in de
 * documenten-tak: een grijs bestandsicoontje met een bestandsnaam. Je zag dus niet wát erin zat en
 * je kon hem niet afspelen — terwijl een filmpje van een dier juist bedoeld is om te bekijken.
 */
export function isVideo(contentType: string | null): boolean {
  return !!contentType && contentType.startsWith("video/");
}

/** m:ss, zoals elke speler het toont. Alleen als de browser de duur echt weet. */
export function formatteerDuur(seconden: number | null): string | null {
  if (seconden == null || !Number.isFinite(seconden) || seconden <= 0) {
    return null;
  }
  const heel = Math.round(seconden);
  return `${Math.floor(heel / 60)}:${String(heel % 60).padStart(2, "0")}`;
}

/** Bestandsgrootte kort: 40 kB, 2,4 MB. Onder de kilobyte is het aantal bytes zelf duidelijker. */
export function formatteerGrootte(bytes: number | null, locale: string): string | null {
  if (bytes == null || bytes < 0) {
    return null;
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${Math.round(kb)} kB`;
  }
  return `${(kb / 1024).toLocaleString(locale, { maximumFractionDigits: 1 })} MB`;
}

/**
 * De korte soortaanduiding op het documentplaatje: PDF, DOCX, XLSX. Uit de bestandsnaam, want die
 * draagt de extensie die de gebruiker zelf herkent; het MIME-type is de terugval voor een bestand
 * zonder extensie. Nooit langer dan vier letters — het is een label, geen zin.
 */
export function bestandsSoort(fileName: string | null, contentType: string | null): string {
  const punt = (fileName ?? "").lastIndexOf(".");
  if (punt > 0 && punt < (fileName ?? "").length - 1) {
    return (fileName ?? "").slice(punt + 1).toUpperCase().slice(0, 4);
  }
  if (contentType === "application/pdf") return "PDF";
  if (contentType?.startsWith("text/")) return "TXT";
  const staart = contentType?.split("/")[1] ?? "";
  return staart ? staart.toUpperCase().slice(0, 4) : "?";
}

/**
 * De kleur van het documentplaatje, per soort. Dit is niet alleen opsmuk: in een raster met tien
 * bestanden vind je de factuur terug aan de rode vlek, lang voordat je de namen hebt gelezen.
 * Onbekende soorten blijven grijs — een kleur verzinnen zou juist verwarren.
 */
export function soortKleur(soort: string): string | undefined {
  if (soort === "PDF") return "#c0392b";
  if (soort.startsWith("DOC")) return "#2b579a";
  if (soort.startsWith("XLS") || soort === "CSV") return "#217346";
  if (soort.startsWith("PPT")) return "#c43e1c";
  if (soort === "TXT") return "#5a6672";
  return undefined;
}

// An authenticated image thumbnail: fetch the bytes as a blob → object URL (revoked on unmount). Docs
// and any failed/inflight image fall back to ImagePreview's camera placeholder or a file glyph.
/** Een pijl die uit een kader wijst — "Direct openen", zoals de bron. */

/** Vergrootglas met een plus — "Vergroten", zoals de bron. */

/** Een pijl die een kwartslag om een hoek draait — @fawkes/ui heeft er geen. */

/**
 * De duimnagel haalt de bytes zelf op, want het endpoint zit achter de login.
 *
 * <p>{@code item.version} hoort in de afhankelijkheden: draai je een foto, dan herschrijft de server de
 * bytes onder hetzelfde id. Zonder die versie draait dit effect nooit opnieuw en blijf je de oude
 * afbeelding zien — het draaien lukte wel, het scherm vroeg hem alleen nooit opnieuw op.
 */
function MediaThumb({ animalId, item }: { animalId: string; item: MediaItem }) {
  const [url, setUrl] = useState<string | null>(null);
  const image = isImage(item.contentType);
  useEffect(() => {
    if (!image) return;
    let revoked = false;
    let objectUrl: string | null = null;
    fetchMediaBlob(animalId, item.id)
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [animalId, item.id, item.version, image]);


  return <ImagePreview src={url} alt={item.fileName ?? ""} size="100%" className="fawkes-media-card__thumb" />;
}

/**
 * Een video in het raster: het eerste beeld als miniatuur, met een afspeelknop erover en de duur
 * eronder.
 *
 * <p>De duur komt uit het bestand zelf ({@code loadedmetadata}), niet uit de gegevens — die kennen
 * hem niet. Weet de browser hem niet, dan staat er niets; een verzonnen tijd is erger dan geen tijd.
 *
 * <p>Het beeld is een echt {@code <video>}-element en geen apart gemaakte poster. Dat scheelt een
 * hele keten (frames trekken op de server, opslaan, opruimen) en levert precies wat je wilt zien:
 * het begin van het filmpje. {@code preload="metadata"} haalt genoeg op voor het eerste beeld en de
 * duur, en niet de hele film.
 */
function MediaVideoThumb({ animalId, item }: { animalId: string; item: MediaItem }) {
  const [url, setUrl] = useState<string | null>(null);
  const [duur, setDuur] = useState<number | null>(null);
  useEffect(() => {
    let opgeruimd = false;
    let objectUrl: string | null = null;
    fetchMediaBlob(animalId, item.id)
      .then((blob) => {
        if (opgeruimd) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      opgeruimd = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [animalId, item.id, item.version]);

  const tekst = formatteerDuur(duur);
  return (
    <span className="fawkes-media-card__video">
      {url ? (
        <video
          className="fawkes-media-card__video-el"
          src={url}
          preload="metadata"
          muted
          playsInline
          // Zonder dit blijft het beeld zwart in browsers die pas bij het eerste frame tekenen.
          onLoadedMetadata={(e) => {
            const el = e.currentTarget;
            setDuur(Number.isFinite(el.duration) ? el.duration : null);
            try { el.currentTime = 0.1; } catch { /* sommige formaten laten dit niet toe */ }
          }}
        />
      ) : (
        <span className="fawkes-media-card__video-leeg" aria-hidden="true" />
      )}
      <span className="fawkes-media-card__play" aria-hidden="true">
        <svg width={22} height={22} viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5.5v13l11-6.5z" />
        </svg>
      </span>
      {tekst && <span className="fawkes-media-card__duur">{tekst}</span>}
    </span>
  );
}

/** Chain glyph for the copy-link action (@fawkes/ui has no link icon; matches the other inline glyphs). */

/**
 * Jmix parity (AnimalMediaTileFragment.openLightbox): clicking an image's view action opens it enlarged
 * in an in-app overlay (not a new browser tab). Fetches the authed bytes as a blob → object URL.
 */
function MediaLightbox({ animalId, item, onClose, t }: {
  animalId: string; item: MediaItem; onClose: () => void; t: (k: string) => string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    fetchMediaBlob(animalId, item.id, "inline")
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [animalId, item.id, item.version]);

  return (
    <Modal
      open
      title={item.fileName ?? ""}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>{t("common.close")}</Button>}
    >
      <div className="fawkes-media-lightbox">
        <ImagePreview src={url} alt={item.fileName ?? ""} size="100%" />
      </div>
    </Modal>
  );
}

type Notice = "uploaded" | "saved" | "deleted" | "uploadError" | "saveError" | "deleteError" | "rotateError" | "conflict";

function noticeKey(n: Notice): string {
  switch (n) {
    case "uploaded": return "animalDetailView.media.uploaded";
    case "saved": return "animalDetailView.media.saved";
    case "deleted": return "animalDetailView.media.deleted";
    case "uploadError": return "animalDetailView.media.uploadError";
    case "saveError": return "animalDetailView.media.saveError";
    case "deleteError": return "animalDetailView.media.deleteError";
    case "rotateError": return "animalDetailView.media.rotateError";
    case "conflict": return "animalDetailView.media.saveConflict";
  }
}

// De tags als gekleurde keuzeblokjes (ontwerp "Bestand toevoegen"): een vinkje, de naam in de tagkleur, en
// een lichte achtergrond in dezelfde tint. Gedeeld door het toevoegen en het bewerken.
function TagPicker({ catalog, selected, onToggle }: {
  catalog: MediaTag[]; selected: Set<number>; onToggle: (id: number) => void;
}) {
  if (catalog.length === 0) return null;
  return (
    <div className="wa-tags">
      {catalog.map((tag) => {
        const kleur = tag.color || "#5a7d6c";
        const aan = selected.has(tag.id);
        return (
          <label
            key={tag.id}
            className={`wa-tag${aan ? " is-aan" : ""}`}
            style={{
              color: kleur,
              background: `color-mix(in srgb, ${kleur} ${aan ? 16 : 9}%, #fff)`,
              borderColor: `color-mix(in srgb, ${kleur} ${aan ? 45 : 18}%, #fff)`,
            }}
          >
            <input type="checkbox" checked={aan} onChange={() => onToggle(tag.id)} />
            <span className="wa-tag__vak" aria-hidden="true" style={aan ? { background: kleur, borderColor: kleur } : undefined}>
              {aan && <CheckIcon width={12} height={12} />}
            </span>
            <span className="wa-tag__naam">{tag.label}</span>
          </label>
        );
      })}
    </div>
  );
}

// Mirrors the Jmix AnimalMediaTileFragment upload constraints: a 15 MB per-file cap and the accepted
// MIME allow-list (image / video / office / pdf / text). The allow-list is a client affordance — the
// backend re-validates the size and auto-classifies the category from the MIME type.
const MEDIA_MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024;
const MEDIA_BEELD_MIME = ["image/*", "video/*"];
const MEDIA_DOCUMENT_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
];
const MEDIA_ACCEPTED_MIME = [...MEDIA_BEELD_MIME, ...MEDIA_DOCUMENT_MIME];

// A file's MIME matches the allow-list when it satisfies a "type/*" wildcard or an exact entry. An
// unknown (empty) type passes — the picker's `accept` filter and the server have the final say.
function mediaTypeAccepted(type: string): boolean {
  if (!type) return true;
  return MEDIA_ACCEPTED_MIME.some((pattern) =>
    pattern.endsWith("/*") ? type.startsWith(pattern.slice(0, -1)) : pattern === type,
  );
}

/** Voorbeeld van één gekozen bestand: de foto zelf, de video met duur, of het documentplaatje. */
function GekozenBestand({ file, onRemove, t }: { file: File; onRemove: () => void; t: (k: string) => string }) {
  const foto = file.type.startsWith("image/");
  const video = file.type.startsWith("video/");
  const [url, setUrl] = useState<string | null>(null);
  const [duur, setDuur] = useState<number | null>(null);
  useEffect(() => {
    if (!foto && !video) return;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file, foto, video]);
  const soort = bestandsSoort(file.name, file.type);
  const kleur = soortKleur(soort) ?? "#6b7280";
  const duurTekst = formatteerDuur(duur);
  return (
    <div className="wa-gekozen">
      <div className={`wa-gekozen__beeld${foto || video ? "" : " is-doc"}`}>
        {foto && url && <img src={url} alt="" />}
        {video && url && (
          <>
            <video src={url} preload="metadata" muted playsInline
              onLoadedMetadata={(e) => setDuur(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : null)} />
            <span className="wa-gekozen__play" aria-hidden="true">
              <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l11-6.5z" /></svg>
            </span>
            {duurTekst && <span className="wa-gekozen__duur">{duurTekst}</span>}
          </>
        )}
        {!foto && !video && (
          <span className="wa-kaart__doc-rond" style={{ background: `color-mix(in srgb, ${kleur} 14%, #fff)` }}>
            <span className={`wa-kaart__doc-badge${soort.length > 3 ? " is-lang" : ""}`} style={{ background: kleur }}>{soort}</span>
          </span>
        )}
        <button type="button" className="wa-gekozen__weg" title={t("animalDetailView.media.removeFile")}
          aria-label={`${t("animalDetailView.media.removeFile")}: ${file.name}`} onClick={onRemove}>
          <XIcon width={13} height={13} />
        </button>
      </div>
      <strong className="wa-gekozen__naam" title={file.name}>{file.name}</strong>
      <span className="wa-gekozen__grootte">{formatteerGrootte(file.size, "nl-NL")}</span>
    </div>
  );
}

/** Schakelaar "Direct zichtbaar in pleeggezin", gedeeld door toevoegen en bewerken. */
function PleeggezinSchakelaar({ aan, onChange, t }: { aan: boolean; onChange: (aan: boolean) => void; t: (k: string) => string }) {
  return (
    <label className="wa-pleeggezin">
      <span className="fui-toggle">
        <input type="checkbox" role="switch" className="fui-toggle__input" checked={aan}
          aria-label={t("animalDetailView.media.fosterVisible")} onChange={(e) => onChange(e.target.checked)} />
        <span className="fui-toggle__track" aria-hidden="true"><span className="fui-toggle__thumb" /></span>
      </span>
      <span className="wa-pleeggezin__tekst">
        <strong>{t("animalDetailView.media.fosterVisible")}</strong>
        <span>{t("animalDetailView.media.fosterVisibleHint")}</span>
      </span>
    </label>
  );
}

export function UploadDialog({ open, onClose, catalog, onUpload, initialFiles, metPleeggezin = true, t }: {
  open: boolean; onClose: () => void; catalog: MediaTag[];
  /** Het schakelaartje "Direct zichtbaar in pleeggezin" — alleen zinvol bij een dier. */
  metPleeggezin?: boolean;
  onUpload: (files: File[], description: string, tagIds: number[], zichtbaarPleeggezin: boolean) => Promise<void>;
  /** Bestanden die al op het uploadvlak van de tegel zijn gevallen. */
  initialFiles?: File[];
  t: (k: string) => string;
}) {
  const [soort, setSoort] = useState<"beeld" | "document">("beeld");
  const [files, setFiles] = useState<File[]>([]);
  const [description, setDescription] = useState("");
  const [tagIds, setTagIds] = useState<Set<number>>(new Set());
  const [pleeggezin, setPleeggezin] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Append uploads, skipping a file already selected (same name + size) — mirrors the Jmix
  // putPendingUpload de-duplication so re-picking doesn't double the list.
  const addFiles = (incoming: File[]) => {
    const ok: File[] = [];
    const bad: string[] = [];
    for (const f of incoming) {
      if (f.size > MEDIA_MAX_FILE_SIZE_BYTES || !mediaTypeAccepted(f.type)) bad.push(f.name);
      else ok.push(f);
    }
    setRejected(bad);
    setFiles((prev) => {
      const next = [...prev];
      for (const f of ok) if (!next.some((e) => e.name === f.name && e.size === f.size)) next.push(f);
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;
    const start = initialFiles ?? [];
    setFiles([]);
    setDescription("");
    setTagIds(new Set());
    setPleeggezin(true);
    setBusy(false);
    setRejected([]);
    const alleenDocs = start.length > 0 && start.every((f) => !f.type.startsWith("image/") && !f.type.startsWith("video/"));
    setSoort(alleenDocs ? "document" : "beeld");
    if (start.length > 0) addFiles(start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (id: number) => setTagIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async () => {
    if (files.length === 0) return;
    setBusy(true);
    try {
      await onUpload(files, description, [...tagIds], pleeggezin);
    } finally {
      setBusy(false);
    }
  };

  const accept = (soort === "beeld" ? MEDIA_BEELD_MIME : MEDIA_DOCUMENT_MIME).join(",");

  return (
    <Modal
      open={open}
      size="wide"
      title={t("animalDetailView.media.uploadTitle")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={submit} disabled={busy || files.length === 0}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="wa-upload">
        <div className="wa-upload__soort" role="tablist" aria-label={t("animalDetailView.media.kindLabel")}>
          <button type="button" role="tab" aria-selected={soort === "beeld"}
            className={`wa-upload__soort-knop${soort === "beeld" ? " is-actief" : ""}`} onClick={() => setSoort("beeld")}>
            <ImageGlyph /> {t("animalDetailView.media.kindMedia")}
          </button>
          <button type="button" role="tab" aria-selected={soort === "document"}
            className={`wa-upload__soort-knop${soort === "document" ? " is-actief" : ""}`} onClick={() => setSoort("document")}>
            <FileIcon width={16} height={16} /> {t("animalDetailView.media.kindDocument")}
          </button>
        </div>

        <div
          className={`wa-upload__drop${dragOver ? " is-over" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(Array.from(e.dataTransfer.files ?? [])); }}
        >
          <UploadCloudIcon width={30} height={30} aria-hidden="true" />
          <strong>{t("animalDetailView.media.dropInDialog")}</strong>
          <span>{t("animalDetailView.media.maxPerFile")}</span>
          <input
            ref={inputRef} type="file" multiple accept={accept}
            className="fawkes-media-drop__input"
            aria-label={t("animalDetailView.media.chooseFiles")}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => { addFiles(Array.from(e.target.files ?? [])); e.target.value = ""; }}
          />
        </div>
        {rejected.length > 0 && (
          <p className="fawkes-media-drop__rejected">
            {t("animalDetailView.media.fileRejected")} {rejected.join(", ")}
          </p>
        )}

        {files.length > 0 && (
          <div className="wa-upload__gekozen">
            {files.map((f, i) => (
              <GekozenBestand key={`${f.name}-${f.size}-${i}`} file={f} t={t}
                onRemove={() => setFiles((prev) => prev.filter((_, j) => j !== i))} />
            ))}
          </div>
        )}

        <label className="wa-upload__veld">
          <span className="wa-upload__label">{t("animalDetailView.media.descriptionLabel")}</span>
          <textarea
            className="fui-field__input fawkes-media-textarea" rows={2}
            placeholder={t("animalDetailView.media.descriptionPlaceholder")}
            value={description} onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        {catalog.length > 0 && (
          <div className="wa-upload__veld">
            <span className="wa-upload__label">{t("animalDetailView.media.tagsLabel")}</span>
            <TagPicker catalog={catalog} selected={tagIds} onToggle={toggle} />
          </div>
        )}
        {metPleeggezin && <PleeggezinSchakelaar aan={pleeggezin} onChange={setPleeggezin} t={t} />}
      </div>
    </Modal>
  );
}

function MediaEditDialog({ open, item, onClose, catalog, onSave, t }: {
  open: boolean; item: MediaItem | null; onClose: () => void; catalog: MediaTag[];
  onSave: (description: string, tagIds: number[], zichtbaarPleeggezin: boolean) => Promise<void>; t: (k: string) => string;
}) {
  const [description, setDescription] = useState("");
  const [tagIds, setTagIds] = useState<Set<number>>(new Set());
  const [pleeggezin, setPleeggezin] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && item) {
      setDescription(item.description ?? "");
      setTagIds(new Set(item.tags.map((tg) => tg.id)));
      setPleeggezin(item.zichtbaarPleeggezin !== false);
      setBusy(false);
    }
  }, [open, item]);

  const toggle = (id: number) => setTagIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async () => {
    setBusy(true);
    try {
      await onSave(description, [...tagIds], pleeggezin);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open && !!item}
      title={t("animalDetailView.media.editTitle")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="wa-upload">
        <label className="wa-upload__veld">
          <span className="wa-upload__label">{t("animalDetailView.media.descriptionLabel")}</span>
          <textarea
            className="fui-field__input fawkes-media-textarea" rows={3}
            placeholder={t("animalDetailView.media.descriptionPlaceholder")}
            value={description} onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        {catalog.length > 0 && (
          <div className="wa-upload__veld">
            <span className="wa-upload__label">{t("animalDetailView.media.tagsLabel")}</span>
            <TagPicker catalog={catalog} selected={tagIds} onToggle={toggle} />
          </div>
        )}
        <PleeggezinSchakelaar aan={pleeggezin} onChange={setPleeggezin} t={t} />
      </div>
    </Modal>
  );
}

/** Groepen per maand, nieuwste eerst — "Deze maand", "Vorige maand", daarna "augustus 2026". */
export function perMaand(items: MediaItem[], t: (k: string) => string): { sleutel: string; titel: string; items: MediaItem[] }[] {
  const nu = new Date();
  const dezeMaand = `${nu.getFullYear()}-${nu.getMonth()}`;
  const vorige = new Date(nu.getFullYear(), nu.getMonth() - 1, 1);
  const vorigeMaand = `${vorige.getFullYear()}-${vorige.getMonth()}`;
  const gesorteerd = [...items].sort((a, b) => (b.uploadedAt ?? "").localeCompare(a.uploadedAt ?? ""));
  const groepen: { sleutel: string; titel: string; items: MediaItem[] }[] = [];
  for (const item of gesorteerd) {
    const d = item.uploadedAt ? new Date(item.uploadedAt) : null;
    const geldig = d && !Number.isNaN(d.getTime());
    const sleutel = geldig ? `${d!.getFullYear()}-${d!.getMonth()}` : "onbekend";
    let groep = groepen.find((g) => g.sleutel === sleutel);
    if (!groep) {
      const titel = !geldig
        ? t("animalDetailView.media.month.unknown")
        : sleutel === dezeMaand
          ? t("animalDetailView.media.month.current")
          : sleutel === vorigeMaand
            ? t("animalDetailView.media.month.previous")
            : d!.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });
      groep = { sleutel, titel: titel.charAt(0).toUpperCase() + titel.slice(1), items: [] };
      groepen.push(groep);
    }
    groep.items.push(item);
  }
  return groepen;
}

/** Datum zoals op de kaart: "12 jan 2025". */
export function kaartDatum(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }).replace(".", "");
}

/**
 * Eén bestand als kaart in het raster. Een foto of video vult de kaart (soorticoon linksboven, datum
 * linksonder, bij video afspeelknop en duur); een document is een lichte kaart met een gekleurd
 * bestandsplaatje in een rond vlak, de naam en de datum. Rechtsboven het "…"-menu met de acties.
 */
function MediaKaart({ animalId, item, t, onOpen, onRotate, onEdit, onDelete }: {
  animalId: string; item: MediaItem; t: (k: string) => string;
  onOpen: (item: MediaItem) => void; onRotate: (item: MediaItem) => void | Promise<void>;
  onEdit: (item: MediaItem) => void; onDelete: (item: MediaItem) => void;
}) {
  const foto = isImage(item.contentType);
  const video = isVideo(item.contentType);
  const beeld = foto || video;
  const soort = bestandsSoort(item.fileName, item.contentType);
  const kleur = soortKleur(soort) ?? "#6b7280";
  const [menuOpen, setMenuOpen] = useState(false);
  const [gekopieerd, setGekopieerd] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const dicht = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", dicht);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", dicht); document.removeEventListener("keydown", esc); };
  }, [menuOpen]);

  const kopieer = async () => {
    const link = `${globalThis.location?.origin ?? ""}/api/animals/${animalId}/media/${item.id}`;
    try {
      await navigator.clipboard.writeText(link);
      setGekopieerd(true);
      setTimeout(() => setGekopieerd(false), 1500);
    } catch { /* klembord niet beschikbaar */ }
  };
  const actie = (fn: () => void) => () => { setMenuOpen(false); fn(); };

  return (
    <div className={`wa-kaart${beeld ? " wa-kaart--beeld" : " wa-kaart--doc"}`} title={item.fileName ?? undefined}>
      <button type="button" className="wa-kaart__open" onClick={() => onOpen(item)}
        aria-label={`${t(beeld ? "animalDetailView.media.enlarge" : "animalDetailView.media.view")}: ${item.fileName ?? ""}`}>
        {foto && <MediaThumb animalId={animalId} item={item} />}
        {video && <MediaVideoThumb animalId={animalId} item={item} />}
        {!beeld && (
          <span className="wa-kaart__doc">
            <span className="wa-kaart__doc-rond" style={{ background: `color-mix(in srgb, ${kleur} 14%, #fff)` }}>
              <span className={`wa-kaart__doc-badge${soort.length > 3 ? " is-lang" : ""}`} style={{ background: kleur }}>{soort}</span>
            </span>
            <strong className="wa-kaart__doc-naam">{item.fileName ?? "—"}</strong>
            <span className="wa-kaart__doc-datum">{kaartDatum(item.uploadedAt)}</span>
          </span>
        )}
      </button>
      {beeld && (
        <>
          <span className="wa-kaart__soort" aria-hidden="true">
            {video ? <VideoGlyph /> : <ImageGlyph />}
          </span>
          <span className="wa-kaart__datum">{kaartDatum(item.uploadedAt)}</span>
        </>
      )}
      {item.tags.length > 0 && (
        <span className="wa-kaart__tags" aria-hidden="true">
          {item.tags.slice(0, 2).map((tag) => (
            <span className="fawkes-media-chip" key={tag.id} style={tagChipStyle(tag.color)}>{tag.label}</span>
          ))}
        </span>
      )}
      <div className="wa-kaart__menu" ref={menuRef}>
        <button type="button" className="wa-kaart__puntjes" aria-haspopup="menu" aria-expanded={menuOpen}
          aria-label={`${t("animalDetailView.media.actions")}: ${item.fileName ?? ""}`}
          onClick={() => setMenuOpen((o) => !o)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
          </svg>
        </button>
        {menuOpen && (
          <ul className="wa-kaart__lijst" role="menu">
            <li role="none"><button type="button" role="menuitem" onClick={actie(() => onOpen(item))}>
              {t(beeld ? "animalDetailView.media.enlarge" : "animalDetailView.media.view")}</button></li>
            {foto && (
              <li role="none"><button type="button" role="menuitem" onClick={actie(() => { void onRotate(item); })}>
                {t("animalDetailView.media.rotate")}</button></li>
            )}
            <li role="none"><button type="button" role="menuitem"
              onClick={actie(() => { void downloadMedia(animalId, item.id, item.fileName ?? "bestand"); })}>
              {t("animalDetailView.media.download")}</button></li>
            {!beeld && (
              <li role="none"><button type="button" role="menuitem" onClick={() => { void kopieer(); }}>
                {gekopieerd ? t("animalDetailView.media.copied") : t("animalDetailView.media.copyLink")}</button></li>
            )}
            <li role="none"><button type="button" role="menuitem" onClick={actie(() => onEdit(item))}>
              {t("animalDetailView.media.editTags")}</button></li>
            <li role="none"><button type="button" role="menuitem" className="is-gevaar" onClick={actie(() => onDelete(item))}>
              {t("animalDetailView.media.delete")}</button></li>
          </ul>
        )}
      </div>
    </div>
  );
}

export function ImageGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.8" /><path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

export function VideoGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="6" width="14" height="12" rx="2" /><path d="M22 8l-6 4 6 4z" />
    </svg>
  );
}

/** Twee rijen van drie: meer kaarten staan achter "Alle … bekijken". */
export const MEDIA_OP_TEGEL = 6;

export function MediaTile({ animalId }: { animalId: string }) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useCustom({ url: `animals/${animalId}/media`, method: "get" });
  const { data: tagData } = useCustom({ url: `animals/${animalId}/media/tag-options`, method: "get" });
  const rows = (data?.data as MediaItem[] | undefined) ?? [];
  const catalog = (tagData?.data as MediaTag[] | undefined) ?? [];

  const [uploadOpen, setUploadOpen] = useState(false);
  // Een taak "Document" uitvoeren: het uploadvenster, en na uploaden is de taak af.
  const taak = useTaakVoorTegel(animalId, ["DOCUMENT"], () => setUploadOpen(true), uploadOpen);
  // Bestanden die op het uploadvlak van de tegel vielen: het venster opent er meteen mee.
  const [gevallen, setGevallen] = useState<File[]>([]);
  const [editItem, setEditItem] = useState<MediaItem | null>(null);
  // Datum / soort / tag, zoals op acc. Client-side: de tegel heeft de hele lijst al binnen, dus
  // filteren hoeft de server niet nog een keer lastig te vallen — en het schakelt meteen.
  const [datumFilter, setDatumFilter] = useState<MediaDatumFilter>('6m');
  // Media (foto's + video's) of Documenten, zoals de twee tabbladen in WhatsApp. null = nog niet gekozen:
  // dan opent het tabblad waar iets in staat (media eerst).
  const [tabblad, setTabblad] = useState<"media" | "documenten" | null>(null);
  const [tagFilter, setTagFilter] = useState<string>('');
  // Jmix parity (openLightbox): the enlarged in-app image overlay.
  const [lightbox, setLightbox] = useState<MediaItem | null>(null);
  const [alleOpen, setAlleOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<MediaItem | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  if (isLoading || isError) {
    return <Status isLoading={isLoading} isError={isError} t={t} />;
  }

  const reload = () => { void refetch(); };

  const gefilterd = rows
    .filter((m) => binnenMediaPeriode(m, datumFilter))
    .filter((m) => !tagFilter || m.tags.some((tag) => String(tag.id) === tagFilter));
  const isBeeld = (m: MediaItem) => isImage(m.contentType) || isVideo(m.contentType);
  const media = gefilterd.filter(isBeeld);
  const documenten = gefilterd.filter((m) => !isBeeld(m));
  const actief = tabblad ?? (media.length > 0 || documenten.length === 0 ? "media" : "documenten");
  const lijst = actief === "media" ? media : documenten;
  const gesorteerd = [...lijst].sort((a, b) => (b.uploadedAt ?? "").localeCompare(a.uploadedAt ?? ""));

  // Eén kaart; vanuit het "alles"-venster sluit dat venster eerst, zodat de vergroting of het
  // bewerkvenster er niet achter valt.
  const kaart = (m: MediaItem, uitVenster = false) => {
    const via = (fn: (it: MediaItem) => void) => (it: MediaItem) => { if (uitVenster) setAlleOpen(false); fn(it); };
    return (
      <MediaKaart
        key={m.id} animalId={animalId} item={m} t={t}
        onOpen={via((it) => (isImage(it.contentType) || isVideo(it.contentType) ? setLightbox(it) : void openDocument(it)))}
        onRotate={draai}
        onEdit={via((it) => { setNotice(null); setEditItem(it); })}
        onDelete={via((it) => { setNotice(null); setDeleteItem(it); })}
      />
    );
  };

  const openDocument = async (item: MediaItem) => {
    try {
      const blob = await fetchMediaBlob(animalId, item.id, "inline");
      window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer");
    } catch { /* openen is best-effort, net als in de kaart */ }
  };
  const draai = async (it: MediaItem) => {
    setNotice(null);
    try {
      await rotateMedia(animalId, it.id);
      reload();
    } catch {
      setNotice("rotateError");
    }
  };

  const doUpload = async (files: File[], description: string, tagIds: number[], zichtbaarPleeggezin: boolean) => {
    try {
      await uploadMedia(animalId, files, description, tagIds, zichtbaarPleeggezin);
      taak.klaar();
      setUploadOpen(false);
      setNotice("uploaded");
      reload();
    } catch {
      setNotice("uploadError");
    }
  };

  const doEdit = async (description: string, tagIds: number[], zichtbaarPleeggezin: boolean) => {
    if (!editItem) return;
    try {
      await updateMedia(animalId, editItem.id, { description, tagIds, version: editItem.version, zichtbaarPleeggezin });
      setEditItem(null);
      setNotice("saved");
      reload();
    } catch (err) {
      const conflict = err instanceof MediaApiError && err.statusCode === 409;
      setNotice(conflict ? "conflict" : "saveError");
      if (conflict) { setEditItem(null); reload(); }
    }
  };

  const doDelete = async () => {
    if (!deleteItem) return;
    try {
      await deleteMedia(animalId, deleteItem.id);
      setDeleteItem(null);
      setNotice("deleted");
      reload();
    } catch {
      setNotice("deleteError");
    }
  };

  const isError_ = notice === "uploadError" || notice === "saveError" || notice === "deleteError"
    || notice === "rotateError" || notice === "conflict";

  return (
    <div className="fawkes-media">
      {notice && (
        <p className={isError_ ? "fawkes-media__notice fawkes-media__notice--error" : "fawkes-media__notice"}>
          {t(noticeKey(notice))}
        </p>
      )}
      {/* Kop zoals WhatsApp: twee tabbladen met aantallen, rechts een ronde + om iets toe te voegen. */}
      <div className="wa-media__kop">
        <div className="wa-media__tabs" role="tablist" aria-label={t("animalDetailView.media.tabs")}>
          <button
            type="button" role="tab" aria-selected={actief === "media"}
            className={`wa-media__tab${actief === "media" ? " is-actief" : ""}`}
            onClick={() => setTabblad("media")}
          >
            {t("animalDetailView.media.tab.media")} <span className="wa-media__aantal">{media.length}</span>
          </button>
          <button
            type="button" role="tab" aria-selected={actief === "documenten"}
            className={`wa-media__tab${actief === "documenten" ? " is-actief" : ""}`}
            onClick={() => setTabblad("documenten")}
          >
            {t("animalDetailView.media.tab.documents")} <span className="wa-media__aantal">{documenten.length}</span>
          </button>
        </div>
        <button
          type="button" className="wa-media__plus"
          aria-label={t("animalDetailView.media.addFile")} title={t("animalDetailView.media.addFile")}
          onClick={() => { setNotice(null); setUploadOpen(true); }}
        >
          <PlusIcon width={18} height={18} />
        </button>
      </div>
      <div className="wa-media__filters">
        <select className="wa-media__filter" value={datumFilter}
          aria-label={t("animalDetailView.media.filter.date")}
          onChange={(e) => setDatumFilter(e.target.value as MediaDatumFilter)}>
          {MEDIA_DATUM_FILTERS.map((k) => (
            <option key={k} value={k}>{t(`animalDetailView.media.filter.date.${k}`)}</option>
          ))}
        </select>
        {catalog.length > 0 && (
          <select className="wa-media__filter" value={tagFilter}
            aria-label={t("animalDetailView.media.filter.tag")}
            onChange={(e) => setTagFilter(e.target.value)}>
            <option value="">{t("animalDetailView.media.filter.tag.all")}</option>
            {catalog.map((tag) => (
              <option key={tag.id} value={String(tag.id)}>{tag.label}</option>
            ))}
          </select>
        )}
      </div>

      {gesorteerd.length > 0 && (
        <div className="wa-media__kaarten">
          {gesorteerd.slice(0, MEDIA_OP_TEGEL).map((m) => kaart(m))}
        </div>
      )}
      {/* De tegel blijft twee rijen hoog; de rest staat in een venster. */}
      {gesorteerd.length > MEDIA_OP_TEGEL && (
        <button type="button" className="wa-media__alles" onClick={() => setAlleOpen(true)}>
          {t("animalDetailView.media.showAll", { args: [String(gesorteerd.length)] })}
        </button>
      )}
      <Modal
        open={alleOpen}
        size="wide"
        title={`${t(actief === "media" ? "animalDetailView.media.tab.media" : "animalDetailView.media.tab.documents")} (${gesorteerd.length})`}
        onClose={() => setAlleOpen(false)}
        footer={<Button variant="secondary" onClick={() => setAlleOpen(false)}>{t("common.close")}</Button>}
      >
        <div className="wa-media__kaarten wa-media__kaarten--alles">
          {gesorteerd.map((m) => kaart(m, true))}
        </div>
      </Modal>
      {/* Het uploadvlak onderaan: klikken of een bestand erop laten vallen opent het uploadvenster. */}
      <button
        type="button"
        className="wa-media__drop"
        onClick={() => { setNotice(null); setGevallen([]); setUploadOpen(true); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); setNotice(null); setGevallen(Array.from(e.dataTransfer.files ?? [])); setUploadOpen(true); }}
      >
        <UploadCloudIcon width={30} height={30} aria-hidden="true" />
        <strong>{t("animalDetailView.media.dropTitle")}</strong>
        <span>{t("animalDetailView.media.dropHint")}</span>
      </button>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} catalog={catalog} onUpload={doUpload}
        initialFiles={gevallen} t={t} />
      <MediaEditDialog open={!!editItem} item={editItem} onClose={() => setEditItem(null)} catalog={catalog} onSave={doEdit} t={t} />
      {lightbox && (
        <MediaLightbox animalId={animalId} item={lightbox} onClose={() => setLightbox(null)} t={t} />
      )}
      <ConfirmDialog
        open={deleteItem != null}
        title={t("animalDetailView.media.deleteTitle")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={() => { void doDelete(); }}
        onCancel={() => setDeleteItem(null)}
      >
        {t("animalDetailView.media.deleteConfirm")}
        {deleteItem?.fileName ? ` (${deleteItem.fileName})` : ""}
      </ConfirmDialog>
    </div>
  );
}

// ---- Verblijf --------------------------------------------------------------------------------------
interface Verblijf {
  id: number; opnameDatum: string | null; vertrekDatum: string | null; actief: boolean;
  klantIn: string | null; klantUit: string | null; opnameSoort: string | null;
  vertrekSoort: string | null; gevondenGemeente: string | null; opmerkingIn: string | null; opmerkingUit: string | null;
  // ids + optimistic-lock version that the in-place IN/UIT edits round-trip
  klantInId: number | null; klantUitId: number | null; opnameSoortId: number | null;
  vertrekSoortId: number | null; gevondenGemeenteId: number | null;
  // read-only Jmix-parity display fields (V-code, resolved location, composite reasons)
  verblijfNummer: number | null; location: string | null; vestigingNaam: string | null;
  redenHerkomst: string | null; vertrekOpmerking: string | null;
  version: number | null;
  // ---- per-variant rendering (mirrors the Jmix AnimalStayTabFragment) ----
  // The discriminators the IN/UIT tiles branch on; null on legacy rows without a mapped option.
  shelterIntakeOption: string | null; shelterReleaseOption: string | null;
  foundLocation: FoundLocation | null;
  // Discrete reasons — `redenHerkomst`/`vertrekOpmerking` above collapse several of these into one
  // string for the flat tile, but the variant cards show them in separate places.
  gevondenOpmerking: string | null; redenAfstand: string | null; afstandOpmerking: string | null;
  /** Zwerver achtergelaten ("gedumpt") — getoond en wijzigbaar op de zwerftegel. */
  gedumpt?: boolean;
  retourReden: string | null; retourOpmerking: string | null;
  birthMotherAnimalId: number | null; birthMotherAnimalName: string | null;
  dierenAmbulanceRitNummer: string | null; dierenAmbulanceNaam: string | null; dierenAmbulanceInId: number | null;
  relatieIn: Relation | null; relatieUit: Relation | null;
  birthMother: BirthMother | null;
  // ---- UIT release detail (from the matched ShelterRelease; null when none matches) ----
  euthanasieReden: string | null; dierenartsNaam: string | null; euthanasieOpmerking: string | null;
  doodsoorzaak: string | null; externeDierenarts: string | null; overledenOpmerking: string | null;
  natureReleaseReden: string | null; natureReleaseLocatie: string | null; natureReleaseWoonplaats: string | null;
  natureReleaseLat: number | null; natureReleaseLong: number | null;
  /** The TNRC release location in its editable parts (null when the stay has no such row). */
  natureRelease: NatureReleaseEdit | null;
}
/** The `nature_release` row as "Vertrek bewerken" round-trips it. */
interface NatureReleaseEdit {
  atFoundLocation: boolean; straat: string | null; huisnummer: string | null;
  huisnummerToevoeging: string | null; postcode: string | null; woonplaats: string | null;
  gemeenteId: number | null; latitude: number | null; longitude: number | null;
  opmerking: string | null; dierenartsCheck: boolean | null;
}
/** The BIRTH variant's Moeder card. A null `id` means a name-only snapshot: no rows, no profile button. */
interface BirthMother {
  id: number | null; naam: string | null; diersoort: string | null; ras: string | null;
  chipPasnummer: string | null; geboortedatum: string | null;
}
interface FoundLocation {
  straat: string | null; huisnummer: string | null; huisnummerToevoeging: string | null;
  postcode: string | null; woonplaats: string | null; addressLine: string | null;
  latitude: number | null; longitude: number | null;
}
/** A klant as the relation card shows it (Vinder / Eigenaar / Adoptant): name + contact meta lines. */
interface Relation {
  id: number | null; naam: string | null; adres: string | null;
  telefoon: string | null; mobiel: string | null; email: string | null;
}
interface Foster {
  id: number;
  /** Which stay this placement belongs to — the tile only ever shows the current stay's own history. */
  asielVerblijfId: number | null;
  vanDatum: string | null; totDatum: string | null; klant: string | null;
  opmerkingen: string | null; categorieLabel: string | null; version: number | null;
  // Active placement only (else null): klant id + branch/species capacity badge.
  klantId?: number | null; maxCapaciteit?: number | null; huidigeBezetting?: number | null;
}
interface StayTile { verblijven: Verblijf[]; pleeggezin: Foster[] }

// Inclusive whole-day count, mirroring the Jmix computeDurationParts (ChronoUnit.DAYS.between + 1, so a
// same-day stay is 1 day). The open end defaults to today. Returns null when the end precedes the start.
function durationDays(from: string | null, to: string | null): number | null {
  if (!from) return null;
  const parse = (iso: string) => new Date(/^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(0, 10)}T00:00:00Z` : iso);
  const todayUtc = () => { const d = new Date(); return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())); };
  const start = parse(from);
  const end = to ? parse(to) : todayUtc();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const between = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  return between < 0 ? null : between + 1;
}

// Jmix singularises the day unit ("1 dag" vs "N dagen").
function dayUnit(t: (k: string) => string, n: number): string {
  return t(n === 1 ? "animalDetailView.stay.dayOne" : "animalDetailView.stay.days");
}

// Mirror of Jmix resolveIntakeIconKey/resolveDepartureIconKey (name-based; the option enum isn't on the
// wire). Drives the small glyph beside the Binnen/Uit side label.
function intakeIconKind(opnameSoort: string | null): StaySideIconKind {
  const n = (opnameSoort ?? "").toLowerCase();
  if (n.includes("zwerver") || n.includes("tnrc") || n.includes("wildekat") || n.includes("wilde kat")) return "paw";
  if (n.includes("retour")) return "home-move";
  return "login";
}
function departureIconKind(vertrekSoort: string | null): StaySideIconKind {
  const n = (vertrekSoort ?? "").toLowerCase();
  if (n.includes("overled") || n.includes("euthan")) return "heart-off";
  if (n.includes("retour") || n.includes("plaatsing") || n.includes("adoptie")) return "home-move";
  return "logout";
}

type StaySideIconKind = "login" | "logout" | "paw" | "home-move" | "heart-off";

// Small inline glyphs for the Binnen/Uit side labels (mirrors the Jmix iconHtml set). Token-stroked.
function StaySideIcon({ kind }: { kind: StaySideIconKind }) {
  const common = {
    viewBox: "0 0 24 24", width: 15, height: 15, fill: "none", stroke: "currentColor",
    strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true,
  };
  switch (kind) {
    case "paw":
      return (<svg {...common}><circle cx="6" cy="10" r="1.6" /><circle cx="10" cy="6.5" r="1.6" /><circle cx="14" cy="6.5" r="1.6" /><circle cx="18" cy="10" r="1.6" /><path d="M8 16.5c0-2 1.8-3.5 4-3.5s4 1.5 4 3.5-1.8 3-4 3-4-1-4-3Z" /></svg>);
    case "home-move":
      return (<svg {...common}><path d="M3 11l9-7 9 7" /><path d="M5 10v10h14V10" /><path d="M9 20v-6h6v6" /></svg>);
    case "heart-off":
      return (<svg {...common}><path d="M20.8 6.6a5 5 0 0 0-8.8-1.2A5 5 0 0 0 3.2 9c0 3.6 5.5 7.5 8.8 9.8" /><path d="M3 3l18 18" /></svg>);
    case "logout":
      return (<svg {...common}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>);
    default: // login
      return (<svg {...common}><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="M10 17l5-5-5-5" /><path d="M15 12H3" /></svg>);
  }
}

// Read-only mirror of the Jmix "Verblijven overzicht" stay card (animal-stay-tab-fragment.css
// `.animal-stay-tab__stay-card`): a timeline of cards, each with an IN side (left) and UIT side
// (right, right-aligned) split by a centred day-count, an "active" treatment for the current stay,
// and a "Nog niet uitgeboekt" empty state when the animal has not left yet.
// Join "date" and "location" (or "date" and "haler") with the Jmix " · " separator, dropping blanks
// and the "-"/"—" placeholders (mirrors combineWithDot).
function combineDot(a: string | null, b: string | null): string {
  const clean = (x: string | null) => { const v = (x ?? "").trim(); return v && v !== "-" && v !== "—" ? v : ""; };
  const f = clean(a), s = clean(b);
  return f && s ? `${f} · ${s}` : f || s;
}

type SideLine = { text: string; bold?: boolean };

function StaySide({ right, kind, label, lines }: {
  right?: boolean; kind: StaySideIconKind; label: string; lines: SideLine[];
}) {
  const visible = lines.filter((l) => l.text && l.text !== "-" && l.text !== "—");
  return (
    <div className={`fawkes-stay-card__side${right ? " fawkes-stay-card__side--right" : ""}`}>
      <span className="fawkes-stay-card__side-label">
        {right
          ? <>{label}<StaySideIcon kind={kind} /></>
          : <><StaySideIcon kind={kind} />{label}</>}
      </span>
      <div className="fawkes-stay-card__side-content">
        {visible.length === 0
          ? <div className="fawkes-stay-card__line">-</div>
          : visible.map((l, i) => (
              <div key={i} className={`fawkes-stay-card__line${l.bold ? " fawkes-stay-card__line--strong" : ""}`}>{l.text}</div>
            ))}
      </div>
    </div>
  );
}

function StayCard({ s, t, selected, onSelect }: {
  s: Verblijf; t: (k: string) => string; selected?: boolean; onSelect?: () => void;
}) {
  // Open means: not checked out. The stored `actief` column says TRUE on 177.946 of the 181.503 stays,
  // closed ones included, so reading it showed a stay from 2019 as "Huidig verblijf · Nog niet
  // uitgeboekt" beside the current one — while an animal is only ever in one place at a time.
  const active = !s.vertrekDatum;
  const days = durationDays(s.opnameDatum, s.vertrekDatum);
  const vcode = s.verblijfNummer != null ? `V${s.verblijfNummer}` : null;
  const dateLoc = combineDot(fmtFormalDate(s.opnameDatum), s.location);
  // Binnen lines: an active card leads with the (bold) intake type, then date·location, then brenger.
  const binnenLines: SideLine[] = [
    ...(active && s.opnameSoort ? [{ text: s.opnameSoort, bold: true }] : []),
    ...(dateLoc ? [{ text: dateLoc }] : []),
    ...(s.klantIn ? [{ text: s.klantIn }] : []),
  ];
  const uitLines: SideLine[] = [
    ...(s.vertrekSoort ? [{ text: s.vertrekSoort, bold: true }] : []),
    ...(() => { const dk = combineDot(fmtFormalDate(s.vertrekDatum), s.klantUit); return dk ? [{ text: dk }] : []; })(),
  ];
  return (
    <div className="fawkes-stays__item">
      {/* The dot follows the SELECTED card, not only the current stay: the green marks which stay the
          IN/UIT tiles are showing, and picking an older one must move that marker along. */}
      <span className={`fawkes-stays__dot${active || selected ? " fawkes-stays__dot--active" : ""}`} aria-hidden="true" />
      {/* Jmix parity: each card is clickable and drives the IN/UIT tiles (selectedOverviewVerblijf). */}
      <article
        className={`fawkes-stay-card${active ? " fawkes-stay-card--active" : ""}${selected ? " fawkes-stay-card--selected" : ""}${onSelect ? " fawkes-stay-card--clickable" : ""}`}
        onClick={onSelect}
        onKeyDown={onSelect ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } } : undefined}
        role={onSelect ? "button" : undefined}
        tabIndex={onSelect ? 0 : undefined}
        aria-pressed={onSelect ? !!selected : undefined}
      >
        <header className="fawkes-stay-card__header">
          <div className="fawkes-stay-card__header-left">
            {active && <span className="fawkes-stay-badge">{t("animalDetailView.stay.active")}</span>}
            <span className="fawkes-stay-card__title">
              {active ? t("animalDetailView.stay.currentStay") : (s.opnameSoort || t("animalDetailView.stay.title"))}
            </span>
          </div>
          {vcode && <span className="fawkes-stay-card__vcode">{vcode}</span>}
        </header>
        <div className="fawkes-stay-card__body">
          <StaySide kind={intakeIconKind(s.opnameSoort)} label={t("animalDetailView.stay.side.binnen")} lines={binnenLines} />
          {days != null && (
            <div className="fawkes-stay-card__duration">
              <span className="fawkes-stay-card__duration-value">{days}</span>
              <span className="fawkes-stay-card__duration-unit">{dayUnit(t, days)}</span>
            </div>
          )}
          {!active && (
            <StaySide right kind={departureIconKind(s.vertrekSoort)} label={t("animalDetailView.stay.side.uit")} lines={uitLines} />
          )}
        </div>
        {/* Jmix parity (AnimalStayTabFragment.buildActiveEmptyState): an active stay carries no UIT side,
            but does get a footer noting it has not been released yet. */}
        {active && (
          <footer className="fawkes-stay-card__empty-state">
            <LogoutIcon width={14} height={14} />
            <span>{t("animalDetailView.stay.notReleased")}</span>
          </footer>
        )}
      </article>
    </div>
  );
}

// Small inline glyphs for the foster row's manage buttons (hoisted function declarations so they're in
// scope above their use; the history tile keeps its own PencilGlyph further down).
function StayEditGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function StayTrashGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

// Mirror of the Jmix Pleeggezin tile timeline rows (PleeggezinDierVerblijfTile): each foster placement
// is a row with a "Pleeggezin — <family>" header (a "Huidig" badge while open) and a van → tot date
// line with the day count, plus any remarks. The migration adds the in-place manage affordances: a
// pencil (edit the period) and a trash (delete), matching the Jmix "Datums aanpassen" / delete actions.
function FosterRow({ f, t, locale, onEdit, onDelete, onOpenRelation }: {
  f: Foster; t: (k: string) => string; locale: string; onEdit: () => void; onDelete: () => void;
  onOpenRelation?: (klantId: number) => void;
}) {
  const current = !f.totDatum;
  const days = durationDays(f.vanDatum, f.totDatum);
  return (
    <article className={`fawkes-stay-foster__row${current ? " fawkes-stay-foster__row--current" : ""}`}>
      <header className="fawkes-stay-foster__head">
        <span className="fawkes-stay-foster__type">{t("animalDetailView.stay.tile.pleeggezin")}</span>
        <span className="fawkes-stay-foster__sep" aria-hidden="true">—</span>
        {onOpenRelation && f.klantId != null ? (
          <button type="button" className="fawkes-stay-foster__subject fawkes-relatie-link"
            onClick={() => onOpenRelation(f.klantId as number)} title={t("animalDetailView.stay.v2.openRelation")}>
            {f.klant ?? "—"}
          </button>
        ) : (
          <span className="fawkes-stay-foster__subject">{f.klant ?? "—"}</span>
        )}
        {current && <span className="fawkes-stay-badge">{t("animalDetailView.stay.current")}</span>}
        <span className="fawkes-stay-foster__row-actions">
          <button type="button" className="fawkes-stay-iconbtn" onClick={onEdit}
            aria-label={t("animalDetailView.stay.editFosterTitle")} title={t("animalDetailView.stay.editFosterTitle")}>
            <StayEditGlyph />
          </button>
          <button type="button" className="fawkes-stay-iconbtn fawkes-stay-iconbtn--danger" onClick={onDelete}
            aria-label={t("animalDetailView.stay.action.delete")} title={t("animalDetailView.stay.action.delete")}>
            <StayTrashGlyph />
          </button>
        </span>
      </header>
      <div className="fawkes-stay-foster__dates">
        <CalendarIcon width={14} height={14} aria-hidden="true" />
        <span className="fawkes-stay-foster__date">{formatDate(f.vanDatum, locale)}</span>
        <span className="fawkes-stay-foster__arrow" aria-hidden="true">→</span>
        <span className="fawkes-stay-foster__date">{f.totDatum ? formatDate(f.totDatum, locale) : t("animalDetailView.stay.now")}</span>
        {days != null && <span className="fawkes-stay-foster__days">{days} {dayUnit(t, days)}</span>}
      </div>
      {f.categorieLabel && (
        <div className="fawkes-stay-foster__categorie">
          <span className="fawkes-stay-foster__categorie-label">{t("animalDetailView.stay.foster.categorie")}:</span>
          <span className="fawkes-stay-foster__categorie-value">{f.categorieLabel}</span>
        </div>
      )}
      {f.opmerkingen && <p className="fawkes-stay-foster__note">{f.opmerkingen}</p>}
    </article>
  );
}

// The Jmix Verblijf tab is composed of FOUR tiles, so the React side mirrors that granularity:
// VerblijvenTile (the stay-card timeline), IntakeInTile / IntakeUitTile (the IN/UIT detail of the
// current stay) and PleeggezinTile (the foster timeline). All four read the same GET /api/animals/
// {id}/stays payload — react-query dedupes the shared key, so they stay independent building blocks
// (each rendered in its own Tile + selectable in the tile gallery) over a single network call.

// The current stay the IN/UIT tiles describe: the active one (mirrors the Jmix tiles), else the most
// recent (the api returns verblijven newest-first).
function currentStay(verblijven: Verblijf[]): Verblijf | null {
  if (verblijven.length === 0) return null;
  return verblijven.find((s) => !s.vertrekDatum) ?? verblijven[0];
}

// Jmix parity (AnimalStayTabFragment.selectedOverviewVerblijf): clicking a card in the Verblijven-
// overzicht makes the IN/UIT tiles render THAT stay instead of the current one. With nothing selected
// (or a stale id after a refetch) the tiles fall back to the current stay, exactly like Jmix.
function resolveStay(verblijven: Verblijf[], selectedId?: string | null): Verblijf | null {
  if (selectedId) {
    const picked = verblijven.find((s) => String(s.id) === String(selectedId));
    if (picked) return picked;
  }
  return currentStay(verblijven);
}

function useStays(animalId: string) {
  const { data, isLoading, isError, refetch } = useTile<StayTile>(animalId, "stays");
  return { verblijven: data?.verblijven ?? [], foster: data?.pleeggezin ?? [], isLoading, isError, refetch };
}

function TilePlaceholder({ text }: { text: string }) {
  return <p className="fawkes-tile-placeholder">{text}</p>;
}

const STAY_PREVIEW_LIMIT = 3;

// The spine-and-dot timeline, with active stays first, a "Historisch" divider, then historical ones
// (mirrors Jmix buildOverviewTimeline).
function StayTimeline({ list, t, selectedId, onSelect }: {
  list: Verblijf[]; t: (k: string) => string;
  selectedId?: string | null; onSelect?: (id: string) => void;
}) {
  const isActive = (s: Verblijf) => !s.vertrekDatum;
  const active = list.filter(isActive);
  const historical = list.filter((s) => !isActive(s));
  return (
    <div className="fawkes-stays__timeline">
      <span className="fawkes-stays__spine" aria-hidden="true" />
      {active.map((s) => <StayCard
          key={s.id} s={s} t={t}
          selected={selectedId != null && String(s.id) === String(selectedId)}
          onSelect={onSelect ? () => onSelect(String(s.id)) : undefined}
        />)}
      {active.length > 0 && historical.length > 0 && (
        <div className="fawkes-stays__divider"><hr /><span>{t("animalDetailView.stay.historical")}</span><hr /></div>
      )}
      {historical.map((s) => <StayCard
          key={s.id} s={s} t={t}
          selected={selectedId != null && String(s.id) === String(selectedId)}
          onSelect={onSelect ? () => onSelect(String(s.id)) : undefined}
        />)}
    </div>
  );
}

/**
 * Afdrukken bij het geselecteerde verblijf. Bijna alles op deze papieren hoort bij een verblijf en niet
 * bij het dier: wie het bracht, wanneer, waarom, en wie het meenam. Een dier dat drie keer is opgevangen
 * heeft dus drie verschillende verslagen — daarom kies je eerst een verblijf in de tijdlijn hierboven.
 */
function AfdrukKnop({ verblijf, t }: {
  verblijf: Verblijf | null;
  t: (k: string, o?: { args?: unknown[] }) => string;
}) {
  const [open, setOpen] = useState(false);
  const [keuzes, setKeuzes] = useState<AfdrukKeuze[] | null>(null);
  const [bezig, setBezig] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    if (!open || keuzes != null || verblijf == null) return;
    let levend = true;
    haalAfdrukKeuzes(String(verblijf.id))
      .then((lijst) => { if (levend) setKeuzes(lijst); })
      .catch(() => { if (levend) { setKeuzes([]); setFout(t("animalDetailView.stay.print.failed")); } });
    return () => { levend = false; };
  }, [open, keuzes, verblijf, t]);

  if (verblijf == null) return null;

  const druk = async (sleutel: string) => {
    setFout(null);
    setBezig(sleutel);
    try {
      await openAfdruk(String(verblijf.id), sleutel);
      setOpen(false);
    } catch {
      setFout(t("animalDetailView.stay.print.failed"));
    } finally {
      setBezig(null);
    }
  };

  return (
    <>
      <Button variant="secondary" className="fawkes-stays__print" onClick={() => setOpen(true)}>
        <FileTextIcon aria-hidden="true" />
        {t("animalDetailView.stay.print.button")}
      </Button>
      <Modal
        open={open}
        title={t("animalDetailView.stay.print.title")}
        onClose={() => setOpen(false)}
        footer={<Button variant="secondary" onClick={() => setOpen(false)}>{t("common.close")}</Button>}
      >
        <p className="fawkes-stays__print-intro">
          {t("animalDetailView.stay.print.intro", { args: [stayLabel(verblijf)] })}
        </p>
        {fout && <p className="fawkes-stays__print-error" role="alert">{fout}</p>}
        {keuzes == null && <p>{t("common.loading")}</p>}
        {keuzes != null && keuzes.length === 0 && !fout && (
          <p>{t("animalDetailView.stay.print.empty")}</p>
        )}
        <ul className="fawkes-stays__print-list">
          {(keuzes ?? []).map((k) => (
            <li key={k.sleutel}>
              <Button variant="secondary" disabled={bezig != null} onClick={() => druk(k.sleutel)}>
                {bezig === k.sleutel ? t("common.loading") : k.naam}
              </Button>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}

/** Het verblijf zoals het in de knoptekst staat: de periode, of de opnamedatum als het nog loopt. */
function stayLabel(v: Verblijf): string {
  const van = v.opnameDatum ?? "";
  return v.vertrekDatum ? `${van} – ${v.vertrekDatum}` : van;
}

// Verblijven overzicht — subtitle, a 3-stay preview + "Toon alle N" overflow dialog, active/historical split.
export function VerblijvenTile({ animalId, selectedStayId, onSelectStay }: {
  animalId: string;
  /** Jmix parity: the stay whose card is highlighted and that drives the IN/UIT tiles. */
  selectedStayId?: string | null;
  onSelectStay?: (id: string) => void;
}) {
  const t = useTranslate();
  const { verblijven, isLoading, isError } = useStays(animalId);
  const [showAll, setShowAll] = useState(false);
  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;
  if (verblijven.length === 0) return <TilePlaceholder text={t("animalDetailView.stay.empty.overzicht")} />;

  const count = verblijven.length;
  const years = verblijven.map((v) => v.opnameDatum?.slice(0, 4)).filter(Boolean).map(Number);
  const earliest = years.length ? Math.min(...years) : null;
  const countLabel = count === 1
    ? t("animalDetailView.stay.overview.countOne")
    : t("animalDetailView.stay.overview.countMany", { args: [count] });
  const subtitle = earliest != null
    ? `${countLabel} · ${t("animalDetailView.stay.overview.since", { args: [earliest] })}`
    : countLabel;

  return (
    <div className="fawkes-stays">
      <span className="fawkes-stays__subtitle">{subtitle}</span>
      <StayTimeline list={verblijven.slice(0, STAY_PREVIEW_LIMIT)} t={t} selectedId={selectedStayId} onSelect={onSelectStay} />
      {/* Alleen op het verblijftabblad, waar je een verblijf kunt kiezen. In de tegelgalerij is dit een
          voorbeeld zonder keuze; daar hoort geen werkende afdrukknop. */}
      {onSelectStay && <AfdrukKnop verblijf={resolveStay(verblijven, selectedStayId)} t={t} />}
      {count > STAY_PREVIEW_LIMIT && (
        <Button variant="secondary" className="fawkes-stays__viewall" onClick={() => setShowAll(true)}>
          {t("animalDetailView.stay.overview.viewAll", { args: [count] })}
        </Button>
      )}
      <Modal
        open={showAll}
        title={t("animalDetailView.stay.overview.allTitle")}
        onClose={() => setShowAll(false)}
        footer={<Button variant="primary" onClick={() => setShowAll(false)}>{t("common.close")}</Button>}
      >
        <StayTimeline list={verblijven} t={t} selectedId={selectedStayId} onSelect={onSelectStay} />
      </Modal>
    </div>
  );
}

// IN / UIT field grid: label/value pairs in a min-width:0 2-col grid so columns align and the
// opmerking spans the full width.
function StayFieldGrid({ children }: { children: ReactNode }) {
  return <div className="fawkes-stay-fields">{children}</div>;
}

// ── Editable stay/foster building blocks ───────────────────────────────────────────────────────────
// The IN/UIT tiles get a "Bewerken" dialog that maps 1:1 to the Jmix in-place quick-edits
// (persistBrengerEdit / persistHalerEditExtended) — field-level updates of the existing asiel_verblijf
// row. The full Inboeken/Uitboeken WIZARDS (which create stays, reserve numbers, sync death flags,
// drive exchanges) and the foster "Plaats" flow are a deliberately deferred, larger initiative.

type SaveNotice = "saved" | "error" | "conflict" | "invalid" | "required" | "ineligible" | null;

// A reference feed fetched only while its dialog is open (avoids loading e.g. all municipalities on
// every tile mount). Mirrors useRefList but gated by `enabled`.
function useOptions(url: string, enabled: boolean): OptionRow[] {
  const { data } = useCustom({ url, method: "get", queryOptions: { enabled } });
  return (data?.data as OptionRow[] | undefined) ?? [];
}

// Header action row pinned to the top-right of an editable tile.
function StayTileActions({ children }: { children: ReactNode }) {
  return <div className="fawkes-stay-tile__actions">{children}</div>;
}

function StayNotice({ notice, t }: { notice: SaveNotice; t: (k: string) => string }) {
  if (!notice || notice === "required" || notice === "invalid" || notice === "ineligible") return null;
  const key =
    notice === "saved" ? "animalDetailView.stay.notice.saved"
    : notice === "conflict" ? "animalDetailView.stay.notice.conflict"
    : "animalDetailView.stay.notice.error";
  return <p className={`fawkes-stay-tile__notice fawkes-stay-tile__notice--${notice}`} role="status">{t(key)}</p>;
}

// Map a Refine HttpError onto our notice vocabulary (409 → stale, 422 → business-rule).
function errorNotice(err: unknown): SaveNotice {
  const code = (err as { statusCode?: number }).statusCode;
  return code === 409 ? "conflict" : code === 422 ? "invalid" : "error";
}

// Same, for the foster-placement flow: a 422 here is the welkom-category guard (foster can't take this
// animal), so it maps to a placement-specific message rather than the date-period "invalid".
function placeErrorNotice(err: unknown): SaveNotice {
  const code = (err as { statusCode?: number }).statusCode;
  return code === 409 ? "conflict" : code === 422 ? "ineligible" : "error";
}

// ---- the shared building blocks of the IN/UIT edit dialogs -------------------------------------
// Both dialogs have the shape of their wizard: the type (read-only) plus the date on top, then one
// labelled block per subject — brenger, relatie, reden, plaats, opmerking. They live together here so
// the two sides cannot drift apart.

/** Whose name the intake's person block carries — Jmix names the card after the intake type. */
const INTAKE_RELATION_ROLE: Record<IntakeVariant, string> = {
  stray: "animalDetailView.stay.v2.vinder.role",
  wild: "animalDetailView.stay.v2.vinder.role",
  surrender: "animalDetailView.stay.v2.eigenaar.role",
  return: "animalDetailView.stay.v2.eigenaar.role",
  birth: "animalDetailView.stay.v2.brenger.role",
};

/** One labelled block: a title, an optional role chip ("Organisatie" / "Persoon") and its fields. */
function EditBlock({ title, chip, required, children }: {
  title: string; chip?: string | null; required?: boolean; children: ReactNode;
}) {
  return (
    <section className="fawkes-stay-edit__block">
      <div className="fawkes-stay-edit__block-head">
        <span className="fawkes-stay-edit__block-title">
          {title}
          {required && <span className="fawkes-stay-edit__req"> *</span>}
        </span>
        {chip && <span className="fawkes-stay-edit__chip">{chip}</span>}
      </div>
      {children}
    </section>
  );
}

/**
 * The dialog head: the stay's TYPE read-only beside its date. The type decides which steps/blocks
 * exist at all, so it is never a select here — "Wijzigen" re-runs the wizard on this stay.
 */
function StayTypeRow({ typeLabel, typeValue, dateLabel, dateValue, onDateChange, onTypeChange, t }: {
  typeLabel: string; typeValue: string | null; dateLabel: string; dateValue: string;
  onDateChange: (e: ChangeEvent<HTMLInputElement>) => void; onTypeChange?: () => void; t: (k: string) => string;
}) {
  return (
    <div className="fawkes-stay-edit__toprow">
      <div className="fui-field fawkes-stay-edit__type">
        <span className="fui-field__label">{typeLabel}</span>
        <div className="fawkes-stay-edit__type-row">
          <span className="fawkes-stay-edit__type-value">{typeValue || "—"}</span>
          {onTypeChange && (
            <Button variant="secondary" onClick={onTypeChange}>
              <RotateLeftIcon width={14} height={14} /> {t("animalDetailView.stay.typeChange")}
            </Button>
          )}
        </div>
      </div>
      <Field label={dateLabel} type="date" value={dateValue} required onChange={onDateChange} />
    </div>
  );
}

/** The address parts both location blocks edit (vindplaats bij binnenkomst, uitzetlocatie bij TNRC). */
interface AddressValue {
  straat: string; huisnummer: string; toevoeging: string; postcode: string; woonplaats: string;
}

/**
 * Address fields in postal order — loose in the two-column grid they wrapped into pairs that read as
 * nonsense ("Toevoeging | Straat"). Postcode/huisnummer/toevoeging narrow, straat/woonplaats full width.
 */
function AddressGrid({ value, onChange, disabled, t }: {
  value: AddressValue; onChange: (patch: Partial<AddressValue>) => void; disabled?: boolean;
  t: (k: string) => string;
}) {
  return (
    <div className="fawkes-stay-edit__addr">
      <Field label={t("inboeken.field.postcode")} value={value.postcode} disabled={disabled}
        onChange={(e) => onChange({ postcode: e.target.value })} />
      <Field label={t("inboeken.field.huisnummer")} value={value.huisnummer} disabled={disabled}
        onChange={(e) => onChange({ huisnummer: e.target.value })} />
      <Field label={t("inboeken.field.toevoeging")} value={value.toevoeging} disabled={disabled}
        onChange={(e) => onChange({ toevoeging: e.target.value })} />
      <div className="fawkes-stay-edit__addr-wide">
        <Field label={t("inboeken.field.straat")} value={value.straat} disabled={disabled}
          onChange={(e) => onChange({ straat: e.target.value })} />
      </div>
      <div className="fawkes-stay-edit__addr-wide">
        <Field label={t("inboeken.field.woonplaats")} value={value.woonplaats} disabled={disabled}
          onChange={(e) => onChange({ woonplaats: e.target.value })} />
      </div>
    </div>
  );
}

/**
 * The relation block (Vinder / Eigenaar / Adoptant / Haler): the chosen relation as the same card the
 * tile shows — name, address, contact — with a "Wijzigen" search toggle over
 * {@code /reference/klanten?q=} and a cross that clears it. Mirrors the Jmix RelatiePicker.
 */
function RelationField({ label, chip, required, relation, valueId, valueLabel, onChange, t }: {
  label: string; chip?: string; required?: boolean; relation: Relation | null;
  valueId: number | null; valueLabel: string | null;
  onChange: (id: number | null, label: string | null) => void; t: (k: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const results = useOptions(`animals/reference/klanten?q=${encodeURIComponent(q)}`, open);
  // The meta lines belong to the relation the STAY carries; after picking another one we only know its
  // name until the tile reloads, and printing the old address under a new name would be a lie.
  const meta = relation && relation.id === valueId ? relation : null;
  const contact = meta ? [meta.email, meta.mobiel || meta.telefoon].filter(Boolean).join(" · ") : "";
  return (
    <EditBlock title={label} chip={chip} required={required}>
      {valueId != null || valueLabel ? (
        <div className="fawkes-stay-edit__relcard">
          <div className="fawkes-stay-edit__relcard-text">
            <span className="fawkes-stay-edit__relcard-name">{valueLabel || t("animalDetailView.stay.klant.none")}</span>
            {meta?.adres && <span className="fawkes-stay-edit__relcard-line">{meta.adres}</span>}
            {contact && <span className="fawkes-stay-edit__relcard-line">{contact}</span>}
          </div>
          <div className="fawkes-stay-edit__relcard-actions">
            <Button variant="secondary" onClick={() => setOpen((o) => !o)}>
              <SearchIcon width={14} height={14} /> {t("animalDetailView.stay.klant.change")}
            </Button>
            <Button
              variant="secondary" aria-label={t("animalDetailView.stay.klant.clear")}
              title={t("animalDetailView.stay.klant.clear")} onClick={() => onChange(null, null)}
            >
              <XIcon width={14} height={14} />
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="secondary" onClick={() => setOpen(true)}>
          <SearchIcon width={14} height={14} /> {t("animalDetailView.stay.klant.search")}
        </Button>
      )}
      {open && (
        <div className="fawkes-stay-edit__klant-panel">
          <input
            className="fui-field__input" value={q} placeholder={t("animalDetailView.stay.klant.search")}
            onChange={(e) => setQ(e.target.value)} autoFocus
          />
          <ul className="fawkes-stay-edit__klant-results">
            {results.length === 0 ? (
              <li className="fawkes-stay-edit__klant-empty">{t("animalDetailView.stay.klant.empty")}</li>
            ) : (
              results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button" className="fawkes-stay-edit__klant-result"
                    onClick={() => { onChange(r.id, r.label); setOpen(false); setQ(""); }}
                  >
                    {r.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </EditBlock>
  );
}

// A modal with the standard Annuleren/Opslaan footer and an inline error/required notice.
function EditDialog({ open, title, onClose, onSave, saving, notice, t, wide, children }: {
  open: boolean; title: string; onClose: () => void; onSave: () => void; saving: boolean;
  notice: SaveNotice; t: (k: string) => string;
  /** The block dialogs (Binnenkomst/Vertrek bewerken) — a form-width dialog stacks every field. */
  wide?: boolean;
  children: ReactNode;
}) {
  const inlineKey =
    notice === "required" ? "animalDetailView.stay.notice.required"
    : notice === "invalid" ? "animalDetailView.stay.notice.invalidPeriod"
    : notice === "ineligible" ? "animalDetailView.stay.notice.ineligible"
    : notice === "conflict" ? "animalDetailView.stay.notice.conflict"
    : notice === "error" ? "animalDetailView.stay.notice.error"
    : null;
  return (
    <Modal
      open={open} title={title} onClose={onClose} size={wide ? "wide" : "default"}
      footer={
        <>
          {inlineKey && <span className="fawkes-stay-edit__error" role="alert">{t(inlineKey)}</span>}
          <Button variant="secondary" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={onSave} disabled={saving}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-stay-edit__form">{children}</div>
    </Modal>
  );
}

// IN edit — "Binnenkomst bewerken".
function StayIntakeModal({ animalId, stay, open, onClose, onSaved, onStartWizard, t }: {
  animalId: string; stay: Verblijf; open: boolean; onClose: () => void; onSaved: () => void;
  /** Escalation for the one field this dialog may not edit: re-run the wizard on this stay. */
  onStartWizard?: () => void;
  t: (k: string) => string;
}) {
  const [typeConfirm, setTypeConfirm] = useState(false);
  const [opnameDatum, setOpnameDatum] = useState("");
  const [opnameSoortId, setOpnameSoortId] = useState("");
  const [gemeenteId, setGemeenteId] = useState("");
  const [opmerkingIn, setOpmerkingIn] = useState("");
  const [klantInId, setKlantInId] = useState<number | null>(null);
  const [klantInLabel, setKlantInLabel] = useState<string | null>(null);
  const [notice, setNotice] = useState<SaveNotice>(null);
  // Vindplaats + dierenambulance are ordinary corrections (Jmix edits them right here); only the
  // intake TYPE escalates to the wizard, because it decides which steps exist at all.
  const [vind, setVind] = useState({
    straat: "", huisnummer: "", toevoeging: "", postcode: "", woonplaats: "", opmerking: "",
  });
  const [gedumpt, setGedumpt] = useState(false);
  const [ambulanceId, setAmbulanceId] = useState("");
  const [ritnummer, setRitnummer] = useState("");
  // Surrender: the reason + its remark, the same pair the wizard's "Reden afstand" step writes.
  const [redenAfstand, setRedenAfstand] = useState("");
  const [afstandOpmerking, setAfstandOpmerking] = useState("");
  // The pin. Correcting a found location without a map means typing coordinates blind — the wizard has
  // the map, so the edit dialog gets the same one: the address geocodes onto the pin, and a click on
  // the map writes the address back.
  const [geo, setGeo] = useState<{ lat: number | null; lng: number | null }>({ lat: null, lng: null });

  useEffect(() => {
    if (open) {
      setOpnameDatum(stay.opnameDatum ?? "");
      setOpnameSoortId(stay.opnameSoortId != null ? String(stay.opnameSoortId) : "");
      setGemeenteId(stay.gevondenGemeenteId != null ? String(stay.gevondenGemeenteId) : "");
      setOpmerkingIn(stay.opmerkingIn ?? "");
      setKlantInId(stay.klantInId);
      setKlantInLabel(stay.klantIn);
      setVind({
        straat: stay.foundLocation?.straat ?? "",
        huisnummer: stay.foundLocation?.huisnummer ?? "",
        toevoeging: stay.foundLocation?.huisnummerToevoeging ?? "",
        postcode: stay.foundLocation?.postcode ?? "",
        woonplaats: stay.foundLocation?.woonplaats ?? "",
        opmerking: stay.gevondenOpmerking ?? "",
      });
      setGedumpt(stay.gedumpt === true);
      setAmbulanceId(stay.dierenAmbulanceInId != null ? String(stay.dierenAmbulanceInId) : "");
      setRitnummer(stay.dierenAmbulanceRitNummer ?? "");
      setRedenAfstand(stay.redenAfstand ?? "");
      setAfstandOpmerking(stay.afstandOpmerking ?? "");
      setGeo({
        lat: stay.foundLocation?.latitude != null ? Number(stay.foundLocation.latitude) : null,
        lng: stay.foundLocation?.longitude != null ? Number(stay.foundLocation.longitude) : null,
      });
      setNotice(null);
    }
  }, [open, stay.id, stay.version]); // eslint-disable-line react-hooks/exhaustive-deps

  // The opnamesoort feed is gone with the editable select — the type is read-only here now.
  const gemeenten = useOptions("animals/reference/gemeenten", open);
  // Only a found-animal intake carries a vindplaats + a bringing ambulance (Jmix shows those cards
  // for the stray variant); for the other types the dialog stays the short form it was.
  const variant = intakeVariant(stay);
  const heeftVindplaats = variant === "stray" || variant === "wild";
  // The bringing organisation is asked on every intake (the Jmix "Brenger" card), not just the stray one.
  const ambulances = useOptions("intake/reference/dierenambulances", open);
  const gemeenteLabel = gemeenten.find((g) => String(g.id) === gemeenteId)?.label ?? stay.gevondenGemeente ?? "";
  // Same two-way geocoding the wizard's Vindplaats step uses: address → pin (debounced), map click →
  // address fields, with the Jmix gemeente cascade behind the reverse lookup.
  const { handlePick: handleMapPick } = useFoundLocationGeocoding({
    enabled: open && heeftVindplaats,
    address: {
      street: vind.straat,
      houseNumber: vind.huisnummer,
      houseNumberAddition: vind.toevoeging,
      postalCode: vind.postcode,
      city: vind.woonplaats,
      municipality: gemeenteLabel,
    },
    onResolvePin: (la, lo) => setGeo({ lat: la, lng: lo }),
    onReversePopulate: (addr) => {
      setVind((v) => ({
        ...v,
        straat: addr.street ?? v.straat,
        huisnummer: addr.houseNumber ?? v.huisnummer,
        toevoeging: addr.houseNumberAddition ?? v.toevoeging,
        postcode: addr.postalCode ?? v.postcode,
        woonplaats: addr.city ?? v.woonplaats,
      }));
      const gem = resolveGemeenteId(gemeenten, addr);
      if (gem != null) setGemeenteId(String(gem));
    },
  });
  const { mutate, isLoading: saving } = useCustomMutation();

  const save = () => {
    if (!opnameDatum) { setNotice("required"); return; }
    mutate(
      {
        url: `animals/${animalId}/stays/${stay.id}/intake`, method: "put",
        values: {
          opnameDatum, klantInId,
          asielOpnameSoortId: opnameSoortId ? Number(opnameSoortId) : null,
          gevondenGemeenteId: gemeenteId ? Number(gemeenteId) : null,
          opmerkingIn: opmerkingIn.trim() || null,
          // Only sent for the variants that show them; otherwise keep what the row already has.
          gevondenStraat: heeftVindplaats ? vind.straat.trim() || null : stay.foundLocation?.straat ?? null,
          gevondenHuisnummer: heeftVindplaats ? vind.huisnummer.trim() || null : stay.foundLocation?.huisnummer ?? null,
          gevondenHuisnummerToevoeging: heeftVindplaats
            ? vind.toevoeging.trim() || null
            : stay.foundLocation?.huisnummerToevoeging ?? null,
          gevondenPostcode: heeftVindplaats ? vind.postcode.trim() || null : stay.foundLocation?.postcode ?? null,
          gevondenWoonplaats: heeftVindplaats ? vind.woonplaats.trim() || null : stay.foundLocation?.woonplaats ?? null,
          gevondenOpmerking: heeftVindplaats ? vind.opmerking.trim() || null : stay.gevondenOpmerking ?? null,
          // Alleen bij een zwerver in beeld; null laat de kolom staan zoals hij is.
          gedumpt: heeftVindplaats ? gedumpt : null,
          gevondenLatitude: heeftVindplaats ? geo.lat : stay.foundLocation?.latitude ?? null,
          gevondenLongitude: heeftVindplaats ? geo.lng : stay.foundLocation?.longitude ?? null,
          dierenAmbulanceInId: ambulanceId ? Number(ambulanceId) : null,
          dierenAmbulanceRitNummer: ritnummer.trim() || null,
          redenAfstand: variant === "surrender" ? redenAfstand.trim() || null : stay.redenAfstand ?? null,
          afstandOpmerking:
            variant === "surrender" ? afstandOpmerking.trim() || null : stay.afstandOpmerking ?? null,
          version: stay.version,
        },
      },
      { onSuccess: onSaved, onError: (err) => setNotice(errorNotice(err)) },
    );
  };

  return (
    <EditDialog
      open={open} title={t("animalDetailView.stay.editIntakeTitle")} onClose={onClose} wide
      onSave={save} saving={saving} notice={notice} t={t}
    >
      {/* Read-only, like Jmix: the intake TYPE decides which wizard steps apply, so it cannot be
          swapped from a four-field dialog. "Wijzigen" re-opens the wizard on this stay. */}
      <StayTypeRow
        typeLabel={t("animalDetailView.stay.field.typeBinnenkomst")} typeValue={stay.opnameSoort}
        dateLabel={t("animalDetailView.stay.field.opname")} dateValue={opnameDatum}
        onDateChange={(e) => setOpnameDatum(e.target.value)}
        onTypeChange={onStartWizard ? () => setTypeConfirm(true) : undefined} t={t}
      />
      {/* Brenger = the organisation that delivered the animal (the ambulance + its ride number). */}
      <EditBlock
        title={t("animalDetailView.stay.v2.brenger.role")}
        chip={t("animalDetailView.stay.edit.chip.organisatie")}
      >
        <SelectField
          label={t("inboeken.field.dierenambulance")} value={ambulanceId}
          options={selectOptions(ambulances, ambulanceId, stay.dierenAmbulanceNaam, true)}
          onChange={(e) => setAmbulanceId(e.target.value)}
        />
        <Field
          label={t("inboeken.field.ritnummer")} value={ritnummer}
          onChange={(e) => setRitnummer(e.target.value)}
        />
      </EditBlock>
      {/* …and the PERSON behind the intake, whose role follows the intake type (Jmix names the card
          Vinder for a stray, Eigenaar for a surrender/retour). */}
      <RelationField
        label={t(INTAKE_RELATION_ROLE[variant])}
        chip={t("animalDetailView.stay.edit.chip.persoon")}
        required={variant === "surrender" || variant === "return"}
        relation={stay.relatieIn} valueId={klantInId} valueLabel={klantInLabel}
        onChange={(id, label) => { setKlantInId(id); setKlantInLabel(label); }} t={t}
      />
      {variant === "surrender" && (
        <EditBlock title={t("inboeken.field.redenAfstand")}>
          <SelectField
            label={t("animalDetailView.stay.edit.reden")} value={redenAfstand}
            // The reason is stored as its own text (the Jmix vocabulary), so value === label; an older
            // row with a reason outside the list keeps it rather than silently losing it.
            options={selectOptions(
              REDEN_AFSTAND_OPTIONS.map((r) => ({ id: r, label: r })), redenAfstand, redenAfstand, true,
            )}
            onChange={(e) => setRedenAfstand(e.target.value)}
          />
          <label className="fui-field fawkes-stay-edit__field--full">
            <span className="fui-field__label">{t("inboeken.field.opmerkingen")}</span>
            <textarea
              className="fui-field__input fawkes-stay-edit__textarea" rows={3} value={afstandOpmerking}
              onChange={(e) => setAfstandOpmerking(e.target.value)}
            />
          </label>
        </EditBlock>
      )}
      {heeftVindplaats && (
        <EditBlock title={t("inboeken.foundLocation.title")}>
          <AddressGrid value={vind} onChange={(patch) => setVind((v) => ({ ...v, ...patch }))} t={t} />
          <SelectField
            label={t("inboeken.field.gemeente")} value={gemeenteId}
            options={selectOptions(gemeenten, gemeenteId, stay.gevondenGemeente, true)}
            onChange={(e) => setGemeenteId(e.target.value)}
          />
          <div className="fui-field fawkes-stay-edit__field--full">
            <ToggleField
              label={t("inboeken.field.gedumpt")}
              checked={gedumpt}
              onChange={(e) => setGedumpt(e.target.checked)}
            />
          </div>
          <label className="fui-field fawkes-stay-edit__field--full">
            <span className="fui-field__label">{t("animalDetailView.stay.field.gevondenOpmerking")}</span>
            <textarea
              className="fui-field__input fawkes-stay-edit__textarea" rows={2} value={vind.opmerking}
              onChange={(e) => setVind((v) => ({ ...v, opmerking: e.target.value }))}
            />
          </label>
          <div className="fui-field fawkes-stay-edit__field--full">
            <span className="fawkes-inboeken__helper">{t("inboeken.map.info")}</span>
            <div className="fawkes-stay-edit__map">
              <FoundLocationMap lat={geo.lat} lng={geo.lng} onPick={handleMapPick} />
            </div>
          </div>
        </EditBlock>
      )}
      <EditBlock title={t("animalDetailView.stay.field.opmerking")}>
        <label className="fui-field fawkes-stay-edit__field--full">
          <textarea
            className="fui-field__input fawkes-stay-edit__textarea" rows={3} value={opmerkingIn}
            aria-label={t("animalDetailView.stay.field.opmerking")}
            onChange={(e) => setOpmerkingIn(e.target.value)}
          />
        </label>
      </EditBlock>
      {/* Jmix guards the escalation with a confirm: the wizard OVERWRITES this stay, and whatever is
          typed in this dialog is dropped on the way. */}
      <Modal
        open={typeConfirm}
        title={t("animalDetailView.stay.typeChange.title")}
        onClose={() => setTypeConfirm(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTypeConfirm(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => { setTypeConfirm(false); onStartWizard?.(); }}>
              {t("animalDetailView.stay.typeChange.confirm")}
            </Button>
          </>
        }
      >
        <p>{t("animalDetailView.stay.typeChange.body")}</p>
      </Modal>
    </EditDialog>
  );
}

/** Whose name the release's person block carries (null = this flow has no counterparty at all). */
const RELEASE_RELATION_ROLE: Record<ReleaseVariant, string | null> = {
  adoption: "animalDetailView.stay.v2.adoptant.role",
  returnOwner: "animalDetailView.stay.v2.eigenaar.role",
  deceased: null,
  euthanasia: null,
  natureRelease: null,
};

// UIT edit — "Vertrek bewerken" (also the "Vertrek registreren" entry point while the stay is open).
// Built like "Binnenkomst bewerken": the departure TYPE read-only beside the date (changing it re-runs
// the uitboek-wizard, because it decides which blocks exist), then the blocks this flow actually has.
function StayReleaseModal({ animalId, stay, open, onClose, onSaved, onStartWizard, t }: {
  animalId: string; stay: Verblijf; open: boolean; onClose: () => void; onSaved: () => void;
  /** Escalation for the one field this dialog may not edit: re-run the release wizard on this stay. */
  onStartWizard?: () => void;
  t: (k: string) => string;
}) {
  const [typeConfirm, setTypeConfirm] = useState(false);
  const [vertrekDatum, setVertrekDatum] = useState("");
  const [opmerkingUit, setOpmerkingUit] = useState("");
  const [klantUitId, setKlantUitId] = useState<number | null>(null);
  const [klantUitLabel, setKlantUitLabel] = useState<string | null>(null);
  const [notice, setNotice] = useState<SaveNotice>(null);
  // TNRC: the release location, the same block the wizard's Uitzetten step shows.
  const [natuurPlek, setNatuurPlek] = useState<"found" | "other">("other");
  const [natuur, setNatuur] = useState<AddressValue>({
    straat: "", huisnummer: "", toevoeging: "", postcode: "", woonplaats: "",
  });
  const [natuurGemeenteId, setNatuurGemeenteId] = useState("");
  const [natuurReden, setNatuurReden] = useState("");
  const [natuurVetCheck, setNatuurVetCheck] = useState(false);
  const [natuurGeo, setNatuurGeo] = useState<{ lat: number | null; lng: number | null }>({ lat: null, lng: null });

  const variant = releaseVariant(stay);
  const isNatuur = variant === "natureRelease";
  const relatieRol = variant ? RELEASE_RELATION_ROLE[variant] : "animalDetailView.stay.field.haler";
  const vindplaats = stay.foundLocation;
  // "Op de vangplek" needs an intake location to point at; without one the card is dead and the flow
  // stands on "Andere locatie" (same rule as the wizard).
  const heeftVindplaats = !!(vindplaats && (vindplaats.straat || vindplaats.woonplaats
    || vindplaats.postcode || vindplaats.latitude != null));
  const opVindplaats = natuurPlek === "found" && heeftVindplaats;

  useEffect(() => {
    if (!open) return;
    setVertrekDatum(stay.vertrekDatum ?? "");
    setOpmerkingUit(stay.opmerkingUit ?? "");
    setKlantUitId(stay.klantUitId);
    setKlantUitLabel(stay.klantUit);
    setNotice(null);
    const nr = stay.natureRelease;
    setNatuurReden(nr?.opmerking ?? "");
    setNatuurVetCheck(nr?.dierenartsCheck === true);
    // "Op de vangplek" IS the intake's found location, so the address shown under it comes from the
    // vindplaats — never from the stored release row. A row saved before the address was corrected
    // (or by an older client) would otherwise print a street the card contradicts one line above.
    const opVangplek = nr?.atFoundLocation === true && heeftVindplaats;
    setNatuurPlek(opVangplek ? "found" : "other");
    if (opVangplek) {
      setNatuur({
        straat: vindplaats?.straat ?? "", huisnummer: vindplaats?.huisnummer ?? "",
        toevoeging: vindplaats?.huisnummerToevoeging ?? "", postcode: vindplaats?.postcode ?? "",
        woonplaats: vindplaats?.woonplaats ?? "",
      });
      setNatuurGemeenteId(stay.gevondenGemeenteId != null ? String(stay.gevondenGemeenteId) : "");
      setNatuurGeo({
        lat: vindplaats?.latitude != null ? Number(vindplaats.latitude) : null,
        lng: vindplaats?.longitude != null ? Number(vindplaats.longitude) : null,
      });
      return;
    }
    setNatuur({
      straat: nr?.straat ?? "", huisnummer: nr?.huisnummer ?? "", toevoeging: nr?.huisnummerToevoeging ?? "",
      postcode: nr?.postcode ?? "", woonplaats: nr?.woonplaats ?? "",
    });
    setNatuurGemeenteId(nr?.gemeenteId != null ? String(nr.gemeenteId) : "");
    setNatuurGeo({
      lat: nr?.latitude != null ? Number(nr.latitude) : null,
      lng: nr?.longitude != null ? Number(nr.longitude) : null,
    });
  }, [open, stay.id, stay.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const gemeenten = useOptions("animals/reference/gemeenten", open && isNatuur);
  const gemeenteLabel = gemeenten.find((g) => String(g.id) === natuurGemeenteId)?.label ?? "";
  // Same two-way geocoding as the wizard + the intake dialog: address → pin, map click → address.
  const { handlePick: handleNatuurPick } = useFoundLocationGeocoding({
    enabled: open && isNatuur,
    address: {
      street: natuur.straat, houseNumber: natuur.huisnummer, houseNumberAddition: natuur.toevoeging,
      postalCode: natuur.postcode, city: natuur.woonplaats, municipality: gemeenteLabel,
    },
    onResolvePin: (la, lo) => setNatuurGeo({ lat: la, lng: lo }),
    onReversePopulate: (addr) => {
      setNatuur((v) => ({
        ...v,
        straat: addr.street ?? v.straat,
        huisnummer: addr.houseNumber ?? v.huisnummer,
        toevoeging: addr.houseNumberAddition ?? v.toevoeging,
        postcode: addr.postalCode ?? v.postcode,
        woonplaats: addr.city ?? v.woonplaats,
      }));
      const gem = resolveGemeenteId(gemeenten, addr);
      if (gem != null) setNatuurGemeenteId(String(gem));
    },
  });

  /** Copy the intake's found location into the release location — "terug naar waar hij vandaan kwam". */
  const vulUitVindplaats = () => {
    if (!vindplaats) return;
    setNatuur({
      straat: vindplaats.straat ?? "", huisnummer: vindplaats.huisnummer ?? "",
      toevoeging: vindplaats.huisnummerToevoeging ?? "", postcode: vindplaats.postcode ?? "",
      woonplaats: vindplaats.woonplaats ?? "",
    });
    if (stay.gevondenGemeenteId != null) setNatuurGemeenteId(String(stay.gevondenGemeenteId));
    if (vindplaats.latitude != null && vindplaats.longitude != null) {
      setNatuurGeo({ lat: Number(vindplaats.latitude), lng: Number(vindplaats.longitude) });
    }
  };

  const { mutate, isLoading: saving } = useCustomMutation();

  const save = () => {
    if (!vertrekDatum || stay.vertrekSoortId == null) { setNotice("required"); return; }
    mutate(
      {
        url: `animals/${animalId}/stays/${stay.id}/release`, method: "put",
        values: {
          vertrekDatum, klantUitId, asielVertrekSoortId: stay.vertrekSoortId,
          opmerkingUit: opmerkingUit.trim() || null,
          // Only TNRC owns a release location; other flows leave the stored row untouched.
          natureRelease: isNatuur
            ? {
              atFoundLocation: opVindplaats,
              straat: natuur.straat.trim() || null,
              huisnummer: natuur.huisnummer.trim() || null,
              huisnummerToevoeging: natuur.toevoeging.trim() || null,
              postcode: natuur.postcode.trim() || null,
              woonplaats: natuur.woonplaats.trim() || null,
              gemeenteId: natuurGemeenteId ? Number(natuurGemeenteId) : null,
              latitude: natuurGeo.lat, longitude: natuurGeo.lng,
              opmerking: natuurReden.trim() || null,
              dierenartsCheck: natuurVetCheck,
            }
            : null,
          version: stay.version,
        },
      },
      { onSuccess: onSaved, onError: (err) => setNotice(errorNotice(err)) },
    );
  };

  return (
    <EditDialog
      open={open} title={t("animalDetailView.stay.editReleaseTitle")} onClose={onClose} wide
      onSave={save} saving={saving} notice={notice} t={t}
    >
      <StayTypeRow
        typeLabel={t("animalDetailView.stay.field.typeVertrek")} typeValue={stay.vertrekSoort}
        dateLabel={t("animalDetailView.stay.field.vertrek")} dateValue={vertrekDatum}
        onDateChange={(e) => setVertrekDatum(e.target.value)}
        onTypeChange={onStartWizard ? () => setTypeConfirm(true) : undefined} t={t}
      />
      {relatieRol && (
        <RelationField
          label={t(relatieRol)} chip={t("animalDetailView.stay.edit.chip.persoon")}
          relation={stay.relatieUit} valueId={klantUitId} valueLabel={klantUitLabel}
          onChange={(id, label) => { setKlantUitId(id); setKlantUitLabel(label); }} t={t}
        />
      )}
      {isNatuur && (
        <EditBlock title={t("uitboeken.nature.place.label")}>
          {/* The counted answer leads the block: back to the catch site, or somewhere else. */}
          <div className="fui-field fawkes-stay-edit__field--full">
            <div className="fawkes-inboeken__choice fawkes-inboeken__choice--place">
              <button
                type="button" disabled={!heeftVindplaats}
                className={opVindplaats ? "is-selected" : ""} aria-pressed={opVindplaats}
                onClick={() => { if (!heeftVindplaats) return; setNatuurPlek("found"); vulUitVindplaats(); }}
              >
                {opVindplaats && (
                  <span className="fawkes-inboeken__choice-icon" aria-hidden="true"><PinIcon width={14} height={14} /></span>
                )}
                <span className="fawkes-inboeken__choice-text">
                  <span className="fawkes-inboeken__choice-title">{t("uitboeken.nature.place.found")}</span>
                  <span className="fawkes-inboeken__muted">
                    {heeftVindplaats
                      ? (vindplaats?.addressLine
                        || [vindplaats?.postcode, vindplaats?.woonplaats].filter(Boolean).join(" ") || "—")
                      : t("uitboeken.nature.place.foundUnknown")}
                  </span>
                </span>
              </button>
              <button
                type="button" className={natuurPlek === "other" ? "is-selected" : ""}
                aria-pressed={natuurPlek === "other"} onClick={() => setNatuurPlek("other")}
              >
                {natuurPlek === "other" && (
                  <span className="fawkes-inboeken__choice-icon" aria-hidden="true"><PinIcon width={14} height={14} /></span>
                )}
                <span className="fawkes-inboeken__choice-text">
                  <span className="fawkes-inboeken__choice-title">{t("uitboeken.nature.place.other")}</span>
                  <span className="fawkes-inboeken__muted">{t("uitboeken.nature.place.otherInfo")}</span>
                </span>
              </button>
            </div>
          </div>
          {/* Locked to the intake's spot: "op de vangplek" IS that address, so it is shown, not edited. */}
          <AddressGrid
            value={natuur} disabled={opVindplaats}
            onChange={(patch) => setNatuur((v) => ({ ...v, ...patch }))} t={t}
          />
          <SelectField
            label={t("inboeken.field.gemeente")} value={natuurGemeenteId} disabled={opVindplaats}
            options={selectOptions(gemeenten, natuurGemeenteId, null, true)}
            onChange={(e) => setNatuurGemeenteId(e.target.value)}
          />
          <Field
            label={t("animalDetailView.stay.v2.release.redenUitzetten")} value={natuurReden}
            onChange={(e) => setNatuurReden(e.target.value)}
          />
          <label className="fawkes-inboeken__terms-agree fawkes-stay-edit__field--full">
            <input type="checkbox" checked={natuurVetCheck} onChange={(e) => setNatuurVetCheck(e.target.checked)} />
            <span>{t("uitboeken.nature.vetCheck")}</span>
          </label>
          <div className="fui-field fawkes-stay-edit__field--full">
            <span className="fawkes-inboeken__helper">
              {t(opVindplaats ? "uitboeken.nature.mapLocked" : "uitboeken.nature.mapInfo")}
            </span>
            <div className="fawkes-stay-edit__map">
              <FoundLocationMap
                lat={natuurGeo.lat} lng={natuurGeo.lng}
                // A click moves the pin — and since that is no longer the found location, the answer
                // flips to "Andere locatie" rather than leaving the card claiming something untrue.
                onPick={opVindplaats ? undefined : (la, lo) => { setNatuurPlek("other"); handleNatuurPick(la, lo); }}
              />
            </div>
          </div>
        </EditBlock>
      )}
      <EditBlock title={t("animalDetailView.stay.field.opmerking")}>
        <label className="fui-field fawkes-stay-edit__field--full">
          <textarea
            className="fui-field__input fawkes-stay-edit__textarea" rows={3} value={opmerkingUit}
            aria-label={t("animalDetailView.stay.field.opmerking")}
            onChange={(e) => setOpmerkingUit(e.target.value)}
          />
        </label>
      </EditBlock>
      {/* Same guard as the intake side: the wizard OVERWRITES this departure. */}
      <Modal
        open={typeConfirm}
        title={t("animalDetailView.stay.releaseTypeChange.title")}
        onClose={() => setTypeConfirm(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTypeConfirm(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => { setTypeConfirm(false); onStartWizard?.(); }}>
              {t("animalDetailView.stay.typeChange.confirm")}
            </Button>
          </>
        }
      >
        <p>{t("animalDetailView.stay.releaseTypeChange.body")}</p>
      </Modal>
    </EditDialog>
  );
}

// Foster period edit — "Datums aanpassen" (only van/tot, like the Jmix dialog; the family + remarks are
// not editable here). The server enforces the stay-bounds + overlap rules (422); the client guards the
// cheap tot >= van case up front.
function FosterDatesModal({ animalId, foster, open, onClose, onSaved, t }: {
  animalId: string; foster: Foster; open: boolean; onClose: () => void; onSaved: () => void; t: (k: string) => string;
}) {
  const [vanDatum, setVanDatum] = useState("");
  const [totDatum, setTotDatum] = useState("");
  const [notice, setNotice] = useState<SaveNotice>(null);

  useEffect(() => {
    if (open) {
      setVanDatum(foster.vanDatum ? foster.vanDatum.slice(0, 10) : "");
      setTotDatum(foster.totDatum ? foster.totDatum.slice(0, 10) : "");
      setNotice(null);
    }
  }, [open, foster.id, foster.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const { mutate, isLoading: saving } = useCustomMutation();

  const save = () => {
    if (!vanDatum) { setNotice("required"); return; }
    if (totDatum && totDatum < vanDatum) { setNotice("invalid"); return; }
    mutate(
      {
        url: `animals/${animalId}/foster/${foster.id}`, method: "put",
        values: { vanDatum, totDatum: totDatum || null, version: foster.version },
      },
      { onSuccess: onSaved, onError: (err) => setNotice(errorNotice(err)) },
    );
  };

  return (
    <EditDialog
      open={open} title={t("animalDetailView.stay.editFosterTitle")} onClose={onClose}
      onSave={save} saving={saving} notice={notice} t={t}
    >
      <Field
        label={t("animalDetailView.stay.field.van")} type="date" value={vanDatum} required
        onChange={(e) => setVanDatum(e.target.value)}
      />
      <Field
        label={t("animalDetailView.stay.field.tot")} type="date" value={totDatum}
        onChange={(e) => setTotDatum(e.target.value)}
      />
    </EditDialog>
  );
}

// One foster offered by the "Plaats" picker — the shape of /api/animals/{id}/foster/eligible (a port of
// the Jmix EligiblePleeggezin row): name + city, a capacity badge (huidigeBezetting / maxCapaciteit),
// the welkom-category chips, and a `geschikt` flag (capacity configured AND ≥1 category) driving the
// "Geschikt" vs "Niet geschikt" split. Only suitable rows are selectable.
type EligibleFoster = {
  klantId: number;
  naam: string | null;
  city: string | null;
  maxCapaciteit: number | null;
  huidigeBezetting: number;
  welkomCategorieen: { id: number; naam: string | null }[];
  geschikt: boolean;
};

// Foster placement — "Plaats bij pleeggezin". A full port of the Jmix placeDierBijPleeggezin dialog: the
// operator searches/filters the fosters (with a category filter), the list is split into "Geschikte
// pleeggezinnen" (selectable) and "Niet geschikt" (shown for context, not selectable), and each row
// shows the foster's capacity badge + welkom-category chips + a filter-aware match indicator. The server
// resolves the open stay, start date and bookkeeping and re-enforces the welkom-category guard (a 422
// race surfaces as the "ineligible" notice). Inline foster creation is intentionally out of scope (the
// Jmix dialog has none — a foster family is a relatie-module entity with adres + contact).
function FosterPlaceModal({ animalId, open, onClose, onSaved, t }: {
  animalId: string; open: boolean; onClose: () => void; onSaved: () => void;
  t: (k: string, opts?: { args?: (string | number)[] }) => string;
}) {
  const [klantId, setKlantId] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const [catId, setCatId] = useState<number | null>(null);
  const [notice, setNotice] = useState<SaveNotice>(null);

  useEffect(() => {
    if (open) { setKlantId(null); setQ(""); setCatId(null); setNotice(null); }
  }, [open]);

  const { data } = useCustom({
    url: `animals/${animalId}/foster/eligible`, method: "get", queryOptions: { enabled: open },
  });
  const fosters = (data?.data as EligibleFoster[] | undefined) ?? [];
  const { data: catData } = useCustom({
    url: `animals/${animalId}/foster/categories`, method: "get", queryOptions: { enabled: open },
  });
  const categories = (catData?.data as { id: number; naam: string | null }[] | undefined) ?? [];

  const needle = q.trim().toLowerCase();
  const visible = fosters.filter((f) => !needle || (f.naam ?? "").toLowerCase().includes(needle));
  const suitable = visible.filter((f) => f.geschikt);
  const unsuitable = visible.filter((f) => !f.geschikt);

  const { mutate, isLoading: saving } = useCustomMutation();
  const save = () => {
    if (klantId == null) { setNotice("required"); return; }
    mutate(
      { url: `animals/${animalId}/foster`, method: "post", values: { klantId } },
      { onSuccess: onSaved, onError: (err) => setNotice(placeErrorNotice(err)) },
    );
  };

  return (
    <EditDialog
      open={open} title={t("animalDetailView.stay.placeTitle")} onClose={onClose}
      onSave={save} saving={saving} notice={notice} t={t}
    >
      <div className="fawkes-stay-place fawkes-stay-edit__field--full">
        <p className="fawkes-stay-place__subtitle">{t("animalDetailView.stay.place.subtitle")}</p>
        <div className="fawkes-stay-place__filters">
          <input
            className="fui-field__input fawkes-stay-place__search" value={q}
            placeholder={t("animalDetailView.stay.place.search")}
            onChange={(e) => setQ(e.target.value)} autoFocus
          />
          {categories.length > 0 && (
            <select
              className="fui-select fawkes-stay-place__cat-filter"
              aria-label={t("animalDetailView.stay.foster.categorie")}
              value={catId ?? ""}
              onChange={(e) => setCatId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">{t("animalDetailView.stay.place.filterAll")}</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.naam ?? "—"}</option>)}
            </select>
          )}
        </div>
        {suitable.length === 0 && unsuitable.length === 0 ? (
          <p className="fawkes-stay-edit__klant-empty">{t("animalDetailView.stay.place.empty")}</p>
        ) : (
          <div className="fawkes-stay-place__list">
            {suitable.length > 0 && (
              <>
                <h5 className="fawkes-stay-place__section fawkes-stay-place__section--ok">
                  {t("animalDetailView.stay.place.suitable", { args: [suitable.length] })}
                </h5>
                {suitable.map((f) => (
                  <FosterPickRow
                    key={f.klantId} f={f} selected={klantId === f.klantId} filterCatId={catId}
                    onSelect={() => { setKlantId(klantId === f.klantId ? null : f.klantId); setNotice(null); }}
                    t={t}
                  />
                ))}
              </>
            )}
            {unsuitable.length > 0 && (
              <>
                <h5 className="fawkes-stay-place__section fawkes-stay-place__section--bad">
                  {t("animalDetailView.stay.place.unsuitable", { args: [unsuitable.length] })}
                </h5>
                {unsuitable.map((f) => (
                  <FosterPickRow key={f.klantId} f={f} selected={false} filterCatId={catId} onSelect={null} t={t} />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </EditDialog>
  );
}

// One row in the "Plaats" picker. Suitable rows are a selectable <button> with a check box; unsuitable
// rows are a static <div> with a "Niet geschikt" badge. Mirrors the Jmix buildPleeggezinRichRow: capacity
// badge (bezet/max + progress bar, red when full or unset), welkom-category pills, and a filter-aware
// "Geschikt voor" match indicator.
function FosterPickRow({ f, selected, filterCatId, onSelect, t }: {
  f: EligibleFoster; selected: boolean; filterCatId: number | null;
  onSelect: (() => void) | null; t: (k: string, opts?: { args?: (string | number)[] }) => string;
}) {
  const selectable = onSelect != null;
  const max = f.maxCapaciteit;
  const hasCap = max != null && max > 0;
  const full = hasCap && f.huidigeBezetting >= (max as number);
  const pct = hasCap ? Math.min(100, (f.huidigeBezetting * 100) / (max as number)) : 0;

  // Filter-aware match indicator (Jmix part 4). For an unsuitable row: "no categories set".
  let match: { tone: "ok" | "warn"; label: string; sub: string };
  if (!f.geschikt) {
    match = { tone: "warn", label: t("animalDetailView.stay.place.noCategories"), sub: "" };
  } else if (filterCatId != null && !f.welkomCategorieen.some((c) => c.id === filterCatId)) {
    const first = f.welkomCategorieen[0]?.naam ?? "—";
    match = {
      tone: "warn",
      label: t("animalDetailView.stay.place.onlyCat", { args: [first] }),
      sub: t("animalDetailView.stay.place.notIdeal"),
    };
  } else {
    match = {
      tone: "ok",
      label: t("animalDetailView.stay.place.suitableShort"),
      sub: t("animalDetailView.stay.place.fullMatch"),
    };
  }

  const body = (
    <>
      <div className="fawkes-stay-place__row-head">
        <span className="fawkes-stay-place__row-name">{f.naam || `#${f.klantId}`}</span>
        {f.welkomCategorieen.slice(0, 3).map((c) => (
          <span key={c.id} className="fawkes-stay-place__pill">{c.naam ?? "—"}</span>
        ))}
      </div>
      {f.city && <div className="fawkes-stay-place__row-city">{f.city}</div>}
      <div className="fawkes-stay-place__row-meta">
        <div className="fawkes-stay-place__cap">
          <span className="fawkes-stay-place__meta-label">{t("animalDetailView.stay.place.capacity")}</span>
          {hasCap ? (
            <>
              <span className={`fawkes-stay-place__cap-value${full ? " fawkes-stay-place__cap-value--full" : ""}`}>
                <span aria-hidden="true">🐾</span> {f.huidigeBezetting} / {max}
              </span>
              <div className="fawkes-stay-place__bar">
                <div
                  className={`fawkes-stay-place__bar-fill${full ? " fawkes-stay-place__bar-fill--full" : ""}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="fawkes-stay-place__cap-hint">
                {t("animalDetailView.stay.place.capacityHint", { args: [f.huidigeBezetting, max as number] })}
              </span>
            </>
          ) : (
            <>
              <span className="fawkes-stay-place__cap-value fawkes-stay-place__cap-value--unset">
                {t("animalDetailView.stay.place.capacityUnset")}
              </span>
              <span className="fawkes-stay-place__cap-hint">{t("animalDetailView.stay.place.capacityUnsetHint")}</span>
            </>
          )}
        </div>
        <div className="fawkes-stay-place__match">
          <span className="fawkes-stay-place__meta-label">{t("animalDetailView.stay.place.suitableFor")}</span>
          <span className={`fawkes-stay-place__match-main fawkes-stay-place__match-main--${match.tone}`}>{match.label}</span>
          {match.sub && <span className="fawkes-stay-place__match-sub">{match.sub}</span>}
        </div>
      </div>
    </>
  );

  if (!selectable) {
    return (
      <div className="fawkes-stay-place__row fawkes-stay-place__row--bad">
        {body}
        <span className="fawkes-stay-place__badge-bad">{t("animalDetailView.stay.place.notSuited")}</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      className={`fawkes-stay-place__row fawkes-stay-place__row--ok${selected ? " fawkes-stay-place__row--selected" : ""}`}
      aria-pressed={selected} onClick={onSelect}
    >
      {body}
      <span className={`fawkes-stay-place__check${selected ? " fawkes-stay-place__check--on" : ""}`} aria-hidden="true">
        {selected ? "✓" : ""}
      </span>
    </button>
  );
}

// A simple confirm dialog (delete / retour).
// IN — the intake side of the current stay (brought-by relation + intake meta), with an edit dialog.
// ---- Stray intake tile (Jmix renderStrayBrengerOverviewV2) -----------------------------------------
// Meta-line glyphs — the same SVG paths the Jmix fragment inlines (SVG_RIT/SVG_HOME/SVG_PHONE/SVG_MAIL).
function RitGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M5 9h14M5 15h14M10 3l-2 18M16 3l-2 18" />
    </svg>
  );
}
function AddressGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9" />
    </svg>
  );
}
function PhoneGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
    </svg>
  );
}
function MailGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}
function NoteGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-7l-4 4v-4H6a2 2 0 0 1-2-2V7z" />
    </svg>
  );
}
// The Moeder card's row icons (Jmix iconHtml("paw"/"shield-check"/"calendar")).
function PawGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <ellipse cx="7" cy="8" rx="2" ry="2.6" />
      <ellipse cx="12" cy="6" rx="2" ry="2.8" />
      <ellipse cx="17" cy="8" rx="2" ry="2.6" />
      <path d="M12 12c-3 0-5 2.2-5 4.4 0 1.7 1.3 2.6 2.8 2.6h4.4c1.5 0 2.8-.9 2.8-2.6C17 14.2 15 12 12 12z" />
    </svg>
  );
}
function ShieldGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 3l7 3v6c0 4.4-3 8-7 9-4-1-7-4.6-7-9V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
function StayCalendarGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

// Jmix gates this on `ShelterIntakeOption.STRAY` exactly — NOT on the soort name. A "Wilde kat" soort
// renders here only when its option is STRAY; otherwise it falls to the legacy flat variant below.
const STRAY_INTAKE_OPTION = "STRAY";

/** Jmix resolveVcodeLabel: plain "V" + verblijfNummer, no padding. Null → no dossier span at all. */
function vcodeLabel(stay: Verblijf): string | null {
  return stay.verblijfNummer == null ? null : `V${stay.verblijfNummer}`;
}

/** Jmix joinAddressParts: space-joined, blanks dropped. */
function joinParts(sep: string, ...parts: (string | null | undefined)[]): string | null {
  const joined = parts.filter((p) => p != null && p.trim() !== "").join(sep);
  return joined === "" ? null : joined;
}

/**
 * Where to put the map pin, mirroring Jmix buildVindplaatsCardV2: geocode the address when there is one,
 * else fall back to the stored coordinates.
 *
 * Jmix geocodes on *every* render because the stored coordinates can be stale. We keep the same
 * preference but run it in an effect keyed on the address, so it costs one call per address rather than
 * one per render — and it goes through our own `/api/geocode` proxy, not the browser→Nominatim direct.
 */
function useVindplaatsCoords(loc: FoundLocation | null, gemeente?: string | null): { lat: number; lng: number } | null {
  const stored = loc && loc.latitude != null && loc.longitude != null
    ? { lat: Number(loc.latitude), lng: Number(loc.longitude) }
    : null;
  const [geocoded, setGeocoded] = useState<{ lat: number; lng: number } | null>(null);

  // The gemeente belongs in the query. Dropping it meant a stay that recorded only "Gemeente
  // Enschede" — everything the operator filled in for a TNRC/zwerver without a street — geocoded to
  // nothing, and the card showed an empty grey rectangle where the map belongs.
  const query = buildAddressQuery({
    street: loc?.straat ?? "",
    houseNumber: loc?.huisnummer ?? "",
    houseNumberAddition: loc?.huisnummerToevoeging ?? "",
    postalCode: loc?.postcode ?? "",
    city: loc?.woonplaats ?? "",
    municipality: gemeente ?? "",
  });

  useEffect(() => {
    let cancelled = false;
    setGeocoded(null);
    if (!query) return;
    forwardGeocode(query)
      .then((hit) => {
        if (!cancelled && hit) setGeocoded({ lat: hit.lat, lng: hit.lon });
      })
      .catch(() => {
        /* a failed geocode just leaves the stored coordinates (or the empty state) in place */
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  return geocoded ?? stored;
}

function StrayTileHead({ stay, animalName, onEdit }: { stay: Verblijf; animalName: string | null; onEdit?: () => void }) {
  const t = useTranslate();
  const vcode = vcodeLabel(stay);
  // Jmix emits two spans and only emphasises the second: "{naam} is binnengebracht als " + <accent>soort</accent>.
  // "zwerver" is the fallback for a missing soort, not a constant.
  // An all-caps name is an abbreviation (TNRC) — lowercasing it turned the heading into "binnengebracht
  // als tnrc". Only ordinary words get the Jmix lowercase treatment.
  const soortNaam = stay.opnameSoort ?? t("animalDetailView.stay.v2.strayFallback");
  const accent = soortNaam === soortNaam.toUpperCase() ? soortNaam : soortNaam.toLowerCase();
  return (
    <div className="tile-head">
      <img className="fawkes-stay-v2__corner-icon" src="/intake/icon_in.png" alt="" />
      <div className="head-text">
        <div className="head-eyebrow">{t("animalDetailView.stay.v2.eyebrow")}</div>
        <h2 className="head-title">
          <span>{t("animalDetailView.stay.v2.broughtAsPrefix", { args: [animalName?.trim() || "-"] })}</span>
          <span className="accent">{accent}</span>
        </h2>
        <div className="head-meta">
          <span className="tabular">{fmtFormalDate(stay.opnameDatum)}</span>
          {vcode && (
            <>
              <span aria-hidden="true"> · </span>
              <span>{t("animalDetailView.stay.v2.dossier", { args: [vcode] })}</span>
            </>
          )}
        </div>
      </div>
      {onEdit && (
        <button type="button" className="fawkes-stay-v2__edit" title={t("animalDetailView.stay.action.edit")} onClick={onEdit}>
          <StayEditGlyph />
        </button>
      )}
    </div>
  );
}

function MetaLine({ icon, text, tabular }: { icon: ReactNode; text: string; tabular?: boolean }) {
  return (
    <div className="meta-line">
      {icon}
      <span className={tabular ? "tabular" : undefined}>{text}</span>
    </div>
  );
}

function BrengerCard({ stay }: { stay: Verblijf }) {
  const t = useTranslate();
  const naam = stay.dierenAmbulanceNaam?.trim() || null;
  const rit = stay.dierenAmbulanceRitNummer?.trim() || null;
  return (
    <div className="r-card">
      <div className="avatar">
        <img src="/intake/icon_brenger.png" alt="" />
      </div>
      <div className="r-body">
        <div className="r-head">
          <span className="role">{t("animalDetailView.stay.v2.brenger.role")}</span>
          <span className="type-tag">{t("animalDetailView.stay.v2.organisation")}</span>
          {naam && <span className="complete-badge">{t("animalDetailView.stay.v2.complete")}</span>}
        </div>
        <div className="r-name">{naam ?? t("animalDetailView.stay.v2.unknown")}</div>
        {rit ? (
          <div className="meta-lines">
            <MetaLine icon={<RitGlyph />} text={t("animalDetailView.stay.v2.brenger.rit", { args: [rit] })} tabular />
          </div>
        ) : (
          <div className="meta-empty">{t("animalDetailView.stay.v2.brenger.noRit")}</div>
        )}
      </div>
    </div>
  );
}

/**
 * Jmix buildRelationCardV2 / buildEmptyVinderCardV2 — a klant card (Vinder on the stray tile, Eigenaar
 * on the surrender/return ones) and its empty state.
 */
/**
 * The declaration inset on the relation card (Jmix `buildVerklaringInset`): shield icon, the declaration
 * title, "Ondertekend op {opnamedatum}" and an "Openen" action.
 *
 * Jmix opens the stored PDF from its own `COMM_INTAKE_DOCUMENT` + Jmix FileStorage. The api archives the
 * very same rendered declaration as an animal document instead (`IntakeDocumentArchiver` →
 * `MediaCommandService.upload`, file name `<templatekey>-<dd-MM-yyyy>.pdf`), so this resolves the newest
 * matching file from the animal's media and opens its bytes in a new tab. Declarations produced by the
 * legacy Jmix wizard live only in Jmix's own store and are therefore not found here — that surfaces as
 * the same "no stored declaration" notice Jmix shows when a stay has none.
 */
function VerklaringInset({ animalId, title, templateKey, signedDate }: {
  animalId: string;
  title: string;
  templateKey: string;
  signedDate: string | null;
}) {
  const t = useTranslate();
  const [notice, setNotice] = useState<string | null>(null);
  const { data } = useCustom<{ id: number; fileName: string | null; uploadedAt: string | null }[]>({
    url: `animals/${animalId}/media`,
    method: "get",
  });
  const doc = useMemo(() => {
    const rows = Array.isArray(data?.data) ? data.data : [];
    const prefix = `${templateKey.toLowerCase()}-`;
    return (
      rows
        .filter((r) => (r.fileName ?? "").toLowerCase().startsWith(prefix))
        // Newest archived copy wins (the archiver stamps the date into the file name).
        .sort((a, b) => (b.uploadedAt ?? "").localeCompare(a.uploadedAt ?? ""))[0] ?? null
    );
  }, [data, templateKey]);

  const open = async () => {
    if (!doc) {
      setNotice(t("animalDetailView.stay.v2.verklaring.notFound").replace("%s", title.toLowerCase()));
      return;
    }
    setNotice(null);
    try {
      const blob = await fetchMediaBlob(animalId, doc.id, "inline");
      window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer");
    } catch {
      setNotice(t("animalDetailView.stay.v2.verklaring.openFailed"));
    }
  };

  return (
    <div className="verklaring-inset">
      <span className="verklaring-inset__icon" aria-hidden="true">
        <ShieldGlyph />
      </span>
      <div className="verklaring-inset__body">
        <div className="verklaring-inset__title">{title}</div>
        <div className="verklaring-inset__meta">
          {/* Jmix DATE_FORMATTER is dd-MM-yyyy (not a locale-formatted date) — fmtFormalDate matches it. */}
          {t("animalDetailView.stay.v2.verklaring.signedOn")} {fmtFormalDate(signedDate)}
        </div>
        {notice && <div className="verklaring-inset__notice">{notice}</div>}
      </div>
      <button type="button" className="verklaring-inset__open" onClick={open}>
        {t("animalDetailView.stay.v2.verklaring.open")} ↗
      </button>
    </div>
  );
}

function RelationCard({ relation, role, verklaring, onOpenRelation }: {
  relation: Relation | null;
  /** The role label — "Vinder" for stray, "Eigenaar" for the owner flows. */
  role: string;
  /** Optional declaration inset rendered below the contact lines (Jmix `buildVerklaringInset`). */
  verklaring?: ReactNode;
  onOpenRelation?: (klantId: number) => void;
}) {
  const t = useTranslate();
  if (!relation) {
    return (
      <div className="r-card">
        <div className="avatar">
          <img src="/intake/icon_vinder_leeg.png" alt="" />
        </div>
        <div className="r-body">
          <div className="r-head">
            <span className="role">{role}</span>
            <span className="type-tag">{t("animalDetailView.stay.v2.person")}</span>
          </div>
          <div className="r-name r-name--empty">{t("animalDetailView.stay.v2.vinder.none")}</div>
          <div className="meta-empty">{t("animalDetailView.stay.v2.vinder.noneHint")}</div>
        </div>
      </div>
    );
  }
  const naam = relation.naam?.trim() || "-";
  return (
    <div className="r-card">
      <div className="avatar">
        <img src="/intake/icon_vinder.png" alt="" />
      </div>
      <div className="r-body">
        <div className="r-head">
          <span className="role">{role}</span>
          <span className="type-tag">{t("animalDetailView.stay.v2.person")}</span>
          <span className="complete-badge">{t("animalDetailView.stay.v2.complete")}</span>
        </div>
        <div className="r-name">{naam}</div>
        <div className="meta-lines">
          {relation.adres && <MetaLine icon={<AddressGlyph />} text={relation.adres} />}
          {relation.telefoon && <MetaLine icon={<PhoneGlyph />} text={relation.telefoon} tabular />}
          {relation.mobiel && <MetaLine icon={<PhoneGlyph />} text={relation.mobiel} tabular />}
          {relation.email && <MetaLine icon={<MailGlyph />} text={relation.email} />}
        </div>
        {verklaring}
        {onOpenRelation && relation.id != null && (
          <div className="fawkes-stay-v2__action-row">
            <Button variant="secondary" onClick={() => onOpenRelation(relation.id as number)}>
              {t("animalDetailView.stay.v2.openRelation")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** The four-corner "enlarge" arrows for the map's corner button (@fawkes/ui has no such icon). */
function ExpandGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
  );
}

function VindplaatsCard({ stay }: { stay: Verblijf }) {
  const t = useTranslate();
  const loc = stay.foundLocation;
  const coords = useVindplaatsCoords(loc, stay.gevondenGemeente);
  // Jmix put an enlarge control in the map's top-right corner (openFullMapDialog): the card map is a
  // thumbnail, and you open the real thing to actually read the streets around the pin.
  const [fullMap, setFullMap] = useState(false);

  const straat = loc ? joinParts(" ", loc.straat, loc.huisnummer, loc.huisnummerToevoeging) : null;
  const woonplaats = loc?.woonplaats?.trim() || null;
  const postcode = loc?.postcode?.trim() || null;
  const gemeente = stay.gevondenGemeente?.trim() || null;

  const naam = straat
    ? joinParts(", ", straat, woonplaats)
    : woonplaats ?? t("animalDetailView.stay.v2.vindplaats.unknown");

  return (
    <div className="vp-card">
      {coords ? (
        <div className="vp-map">
          <FoundLocationMap lat={coords.lat} lng={coords.lng} interactive={false} />
          <button
            type="button"
            className="vp-map__expand"
            title={t("animalDetailView.stay.v2.map.expand")}
            aria-label={t("animalDetailView.stay.v2.map.expand")}
            onClick={() => setFullMap(true)}
          >
            <ExpandGlyph />
          </button>
        </div>
      ) : (
        <div className="vp-map vp-map--empty" aria-hidden="true" />
      )}
      {coords && (
        <Modal
          open={fullMap}
          title={naam ?? t("animalDetailView.stay.v2.vindplaats.eyebrow")}
          onClose={() => setFullMap(false)}
          footer={<Button variant="secondary" onClick={() => setFullMap(false)}>{t("common.close")}</Button>}
        >
          {/* Interactive here: zoom/pan is the whole point of enlarging it. */}
          <div className="fawkes-map-dialog">
            <FoundLocationMap lat={coords.lat} lng={coords.lng} />
          </div>
        </Modal>
      )}
      <div className="vp-overlay">
        <div className="bg-pill">
          <div className="vp-eyebrow">{t("animalDetailView.stay.v2.vindplaats.eyebrow")}</div>
          <div className="vp-name">{naam}</div>
          <div className="vp-meta">
            {postcode && <span className="tabular">{postcode}</span>}
            {gemeente && <span>{postcode ? ` · ${t("animalDetailView.stay.v2.vindplaats.gemeente", { args: [gemeente] })}` : t("animalDetailView.stay.v2.vindplaats.gemeente", { args: [gemeente] })}</span>}
          </div>
          <div className={`vp-dumped${stay.gedumpt ? " vp-dumped--ja" : ""}`}>
            {t("inboeken.field.gedumpt")}: <strong>{stay.gedumpt ? t("inboeken.gedumpt.ja") : t("inboeken.gedumpt.nee")}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Legacy IN variants: SURRENDER / RETURN / BIRTH (Jmix renderStrayBrengerOverviewFromVerblijf,
// the fall-through below the STRAY early-return). Note this path has its OWN header vocabulary — a
// static "INTAKE" title and a per-variant summary sentence, and no dossiernummer.
const REDEN_AFSTAND_OPTIONS = ["Particulier", "Sociaal", "Preventie", "LID-afstand", "Rijksafstand"];
const REDEN_RETOUR_OPTIONS = [
  "Gedragsproblemen", "Niet passend in gezin", "Allergie", "Verhuizing",
  "Overleden eigenaar", "Financieel", "Anders",
];

type IntakeVariant = "stray" | "surrender" | "return" | "birth" | "wild";

/**
 * Jmix's variant dispatch, verbatim: the option enum OR an exact/substring match on the lower-cased
 * soort name. The if/else order matters — birth beats return beats surrender.
 */
function intakeVariant(stay: Verblijf): IntakeVariant {
  const option = stay.shelterIntakeOption;
  // valueOrDash: Jmix turns a blank soort name into "-", never null.
  const n = (stay.opnameSoort?.trim() || "-").toLowerCase();
  const isSurrender = option === "SURRENDER" || n === "afstand" || n === "afstandsdier";
  const isReturn = option === "RETURN" || n === "retour na plaatsing";
  const isBirth = n.includes("geboort");
  const isOwnerFlow = isSurrender || isReturn;
  if (isBirth) return "birth";
  if (isReturn) return "return";
  if (isSurrender) return "surrender";
  // "TNRC" is the current name of the wilde-kat type; the old names keep older rows rendering right.
  if (!isOwnerFlow && (n.includes("tnrc") || n.includes("wilde kat") || n.includes("wildekat"))) return "wild";
  return "stray";
}

/**
 * Jmix buildRedenCard — an editable card: the select saves on change, the text area on blur.
 *
 * Jmix saves the whole managed entity with no version check; the SPA holds a detached copy, so we PUT a
 * narrow request carrying the optimistic-lock version and re-seed from the refreshed tile. A stored
 * reden that isn't one of the options renders as no selection, exactly like the Jmix select.
 */
function RedenCard({ animalId, stay, title, options, reden, opmerking, endpoint, onSaved }: {
  animalId: string;
  stay: Verblijf;
  title: string;
  options: string[];
  reden: string | null;
  opmerking: string | null;
  endpoint: "reden-afstand" | "reden-retour";
  onSaved: () => void;
}) {
  const t = useTranslate();
  const { mutate } = useCustomMutation();
  const [draft, setDraft] = useState(opmerking ?? "");
  const [failed, setFailed] = useState(false);
  // Re-seed when the tile refreshes (a save returns the whole tile).
  useEffect(() => setDraft(opmerking ?? ""), [opmerking]);

  const selected = options.find((o) => o.toLowerCase() === (reden ?? "").toLowerCase()) ?? "";

  const save = (nextReden: string, nextOpmerking: string) => {
    setFailed(false);
    mutate(
      {
        url: `animals/${animalId}/stays/${stay.id}/${endpoint}`,
        method: "put",
        values: { reden: nextReden || null, opmerking: nextOpmerking || null, version: stay.version },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: onSaved, onError: () => setFailed(true) },
    );
  };

  return (
    <div className="notes-card">
      <div className="notes-row">
        <span className="notes-label">{title}</span>
        <select
          className="fui-field__input notes-select"
          value={selected}
          onChange={(e) => save(e.target.value, draft)}
        >
          <option value="">{t("animalDetailView.stay.v2.reden.choose")}</option>
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
      <div className="notes-row">
        <span className="notes-label">{t("animalDetailView.stay.v2.note.label")}</span>
        <textarea
          className="fui-field__input notes-textarea"
          placeholder={t("animalDetailView.stay.v2.note.empty")}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { if ((opmerking ?? "") !== draft) save(selected, draft); }}
        />
      </div>
      {failed && (
        <span className="fawkes-error" role="alert">
          {t("animalDetailView.stay.v2.reden.saveFailed", { args: [title.toLowerCase()] })}
        </span>
      )}
    </div>
  );
}

/** Jmix buildMoederCardFromVerblijf — the BIRTH variant's only card. */
function MoederCard({ mother, onOpenAnimal }: { mother: BirthMother | null; onOpenAnimal?: (id: string) => void }) {
  const t = useTranslate();
  if (!mother) {
    return (
      <div className="r-card r-card--empty">
        <div className="avatar"><PawGlyph /></div>
        <div className="r-body">
          <div className="r-head">
            <span className="role">{t("animalDetailView.stay.v2.moeder.role")}</span>
            <span className="type-tag">{t("animalDetailView.stay.v2.moeder.type")}</span>
          </div>
          <div className="r-name r-name--empty">{t("animalDetailView.stay.v2.moeder.unknown")}</div>
          <div className="meta-empty">{t("animalDetailView.stay.v2.moeder.unknownHint")}</div>
        </div>
      </div>
    );
  }
  const soortRas = [mother.diersoort, mother.ras].filter((v) => v && v.trim() !== "").join(" ");
  return (
    <div className="r-card">
      <div className="avatar"><PawGlyph /></div>
      <div className="r-body">
        <div className="r-head">
          <span className="role">{t("animalDetailView.stay.v2.moeder.role")}</span>
          <span className="type-tag">{t("animalDetailView.stay.v2.moeder.type")}</span>
        </div>
        <div className="r-name">{mother.naam?.trim() || "-"}</div>
        {/* Jmix only shows the detail rows for a linked animal — a name-only snapshot gets none. */}
        <div className="meta-lines">
          {soortRas && <MetaLine icon={<PawGlyph />} text={soortRas} />}
          {mother.chipPasnummer && (
            <MetaLine icon={<ShieldGlyph />} text={t("animalDetailView.stay.v2.moeder.chip", { args: [mother.chipPasnummer] })} tabular />
          )}
          {mother.geboortedatum && (
            <MetaLine icon={<StayCalendarGlyph />} text={t("animalDetailView.stay.v2.moeder.born", { args: [fmtFormalDate(mother.geboortedatum)] })} />
          )}
        </div>
        {onOpenAnimal && mother.id != null && (
          <div className="fawkes-stay-v2__action-row">
            <Button variant="secondary" onClick={() => onOpenAnimal(String(mother.id))}>
              {t("animalDetailView.stay.v2.moeder.toProfile")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Jmix buildIntakeTileHeader + buildIntakeTileSummary — the legacy header. Note it always renders
 * icon_in.png: Jmix computes a per-variant icon key but buildIntakeTileHeader ignores it, so the icon
 * never reaches the DOM. The variant only survives as a CSS class.
 */
function LegacyIntakeHead({ stay, variant, animalName, onEdit }: {
  stay: Verblijf; variant: IntakeVariant; animalName: string | null; onEdit?: () => void;
}) {
  const t = useTranslate();
  const naam = animalName?.trim() || "-";
  const soort = (stay.opnameSoort?.trim() || "-").toLowerCase();
  const summary =
    variant === "birth" ? (
      <>
        <span>{t("animalDetailView.stay.v2.summary.bornPrefix", { args: [naam] })}</span>
        <span className="summary-em">{t("animalDetailView.stay.v2.summary.bornEm")}</span>
        <span>{t("animalDetailView.stay.v2.summary.bornSuffix")}</span>
      </>
    ) : variant === "surrender" || variant === "return" ? (
      <>
        <span>{t("animalDetailView.stay.v2.summary.opnamePrefix")}</span>
        <span className="summary-em">{soort}</span>
      </>
    ) : (
      <>
        <span>{t("animalDetailView.stay.v2.broughtAsPrefix", { args: [naam] })}</span>
        <span className="summary-em">{soort}</span>
      </>
    );
  return (
    <>
      <div className="intake-tile-header">
        <img className="fawkes-stay-v2__corner-icon" src="/intake/icon_in.png" alt="" />
        <span className="intake-tile-title">{t("animalDetailView.stay.v2.legacyTitle")}</span>
        {onEdit && (
          <button type="button" className="fawkes-stay-v2__edit" title={t("animalDetailView.stay.action.edit")} onClick={onEdit}>
            <StayEditGlyph />
          </button>
        )}
      </div>
      <div className="intake-tile-summary">{summary}</div>
      <span className="intake-tile-date-pill">
        <StayCalendarGlyph />
        <span>{fmtFormalDate(stay.opnameDatum)}</span>
      </span>
    </>
  );
}

/** Jmix buildNoteCardV2 — always rendered, with a placeholder when there is no remark. */
function NoteCardV2({ text }: { text: string | null }) {
  const t = useTranslate();
  return (
    <div className="note-card">
      <NoteGlyph />
      <div>
        <div className="note-label">{t("animalDetailView.stay.v2.note.label")}</div>
        <div className="note-text">{text?.trim() || t("animalDetailView.stay.v2.note.empty")}</div>
      </div>
    </div>
  );
}

// The invoice attached to a stay (GET /api/animals/{id}/stays/{stayId}/factuur?bron=…). A faithful port
// of the Jmix ShelterWizardFactuurService.FactuurWeergave. `factuurNummer` is "Concept" when unnumbered.
interface FactuurCardData {
  factuurId: number;
  factuurNummer: string;
  factuurDatum: string | null;
  regels: { omschrijving: string | null; aantal: number | null; prijs: number | null; totaal: number | null }[];
  totaal: number;
  betaald: number;
  openstaand: number;
}

/**
 * The factuur card on the Verblijf tile (Jmix `buildFactuurCardFromVerblijf` → `buildRealFactuurCard` /
 * `buildGeenFactuurCard`). Fetches the invoice tied to the stay for the given source; shows the real
 * header + every line + totals, or the empty state when no (concept) invoice exists yet. A 204 from the
 * endpoint (no invoice) surfaces as `data` being empty. "Open factuur" deep-links to the invoice detail
 * when a handler is wired (same optionality as the relation card's "Relatie openen").
 */
function FactuurCard({ animalId, stayId, bron, onOpenFactuur }: {
  animalId: string;
  stayId: number;
  bron: string;
  onOpenFactuur?: (factuurId: number) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading } = useCustom<FactuurCardData>({
    url: `animals/${animalId}/stays/${stayId}/factuur?bron=${encodeURIComponent(bron)}`,
    method: "get",
  });
  const fw = data?.data;

  const title = t("animalDetailView.stay.v2.factuur.title");
  if (isLoading) {
    return null;
  }
  if (!fw || fw.factuurId == null) {
    return (
      <div className="fawkes-factuur-card">
        <div className="fawkes-factuur-card__header">
          <span className="fawkes-factuur-card__title">{title}</span>
        </div>
        <div className="fawkes-factuur-card__empty">{t("animalDetailView.stay.v2.factuur.empty")}</div>
      </div>
    );
  }
  return (
    <div className="fawkes-factuur-card">
      <div className="fawkes-factuur-card__header">
        <span className="fawkes-factuur-card__title">{title}</span>
        {onOpenFactuur && (
          <button
            type="button"
            className="fawkes-factuur-card__open"
            onClick={() => onOpenFactuur(fw.factuurId)}
          >
            {t("animalDetailView.stay.v2.factuur.open")} ↗
          </button>
        )}
      </div>
      <div className="fawkes-factuur-card__meta">
        <span>
          <b>{t("animalDetailView.stay.v2.factuur.nummer")}:</b> {fw.factuurNummer}
        </span>
        <span>
          {/* Jmix formats invoice dates with its dd-MM-yyyy DATE_FORMATTER, not a locale date. */}
          <b>{t("animalDetailView.stay.v2.factuur.datum")}:</b> {fmtFormalDate(fw.factuurDatum)}
        </span>
      </div>
      <div className="fawkes-factuur-card__table">
        <div className="fawkes-factuur-card__row fawkes-factuur-card__row--head">
          <span>{t("animalDetailView.stay.v2.factuur.col.omschrijving")}</span>
          <span>{t("animalDetailView.stay.v2.factuur.col.aantal")}</span>
          <span>{t("animalDetailView.stay.v2.factuur.col.prijs")}</span>
          <span>{t("animalDetailView.stay.v2.factuur.col.totaal")}</span>
        </div>
        {fw.regels.map((r, i) => (
          <div className="fawkes-factuur-card__row" key={`${r.omschrijving ?? ""}-${i}`}>
            <span>{r.omschrijving || "—"}</span>
            <span>{r.aantal == null ? "1" : num(r.aantal, locale)}</span>
            <span>{formatEuro(r.prijs, locale)}</span>
            <span>{formatEuro(r.totaal, locale)}</span>
          </div>
        ))}
      </div>
      <div className="fawkes-factuur-card__subtotals">
        <span><b>{t("animalDetailView.stay.v2.factuur.totaal")}:</b> {formatEuro(fw.totaal, locale)}</span>
        <span><b>{t("animalDetailView.stay.v2.factuur.betaald")}:</b> {formatEuro(fw.betaald, locale)}</span>
      </div>
      <div className="fawkes-factuur-card__openstaand">
        <span className="fawkes-factuur-card__openstaand-label">{t("animalDetailView.stay.v2.factuur.openstaand")}:</span>
        <span className="fawkes-factuur-card__openstaand-value">{formatEuro(fw.openstaand, locale)}</span>
      </div>
    </div>
  );
}

/**
 * The SURRENDER / RETURN / BIRTH variants (Jmix's legacy fall-through path).
 *
 * SURRENDER now renders the real factuur card (Jmix `buildFactuurCardFromVerblijf`, source "intake").
 * Still not ported: the "Afstandsverklaring" inset on the relation card (needs the verklaring documents,
 * phase 7). RETURN's Jmix `buildCreditFactuurCardFromVerblijf` renders hard-coded demo data, not a real
 * source, so it is deliberately left out rather than reproduced as fake figures.
 */
function LegacyIntakeTile({ animalId, stay, variant, onEdit, onOpenRelation, onOpenAnimal, onOpenFactuur, onSaved }: {
  animalId: string;
  stay: Verblijf;
  variant: IntakeVariant;
  onEdit?: () => void;
  onOpenRelation?: (klantId: number) => void;
  onOpenAnimal?: (id: string) => void;
  onOpenFactuur?: (factuurId: number) => void;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const { data: animal } = useOne<AnimalBasis>({ resource: "animals", id: animalId });
  return (
    <div className={`fawkes-stay-v2 fawkes-stay-v2--${variant}`}>
      <div className="tile">
        <LegacyIntakeHead stay={stay} variant={variant} animalName={animal?.data?.naam ?? null} onEdit={onEdit} />
        {variant === "birth" ? (
          <MoederCard mother={stay.birthMother} onOpenAnimal={onOpenAnimal} />
        ) : (
          <>
            {/* Jmix isOwnerFlow: both surrender and return show the Afstandsverklaring inset, signed on
                the intake date. */}
            <RelationCard
              relation={stay.relatieIn}
              role={t("animalDetailView.stay.v2.eigenaar.role")}
              onOpenRelation={onOpenRelation}
              verklaring={
                <VerklaringInset
                  animalId={animalId}
                  title={t("animalDetailView.stay.v2.verklaring.afstand")}
                  templateKey="AFSTANDSVERKLARING"
                  signedDate={stay.opnameDatum}
                />
              }
            />
            {variant === "return" ? (
              <RedenCard
                animalId={animalId} stay={stay} endpoint="reden-retour"
                title={t("animalDetailView.stay.v2.reden.retour")} options={REDEN_RETOUR_OPTIONS}
                reden={stay.retourReden} opmerking={stay.retourOpmerking} onSaved={onSaved}
              />
            ) : (
              <RedenCard
                animalId={animalId} stay={stay} endpoint="reden-afstand"
                title={t("animalDetailView.stay.v2.reden.afstand")} options={REDEN_AFSTAND_OPTIONS}
                reden={stay.redenAfstand} opmerking={stay.afstandOpmerking} onSaved={onSaved}
              />
            )}
            {/* De factuur bij deze opname (Jmix buildFactuurCardFromVerblijf, "intake"). Ook bij een
                retour na plaatsing: een restitutie levert daar een creditfactuur op, en die stond
                nergens in beeld — het leek daardoor alsof hij niet was aangemaakt. */}
            {(variant === "surrender" || variant === "return") && (
              <FactuurCard animalId={animalId} stayId={stay.id} bron="intake" onOpenFactuur={onOpenFactuur} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StrayIntakeTile({ animalId, stay, onEdit, onOpenRelation }: {
  animalId: string;
  stay: Verblijf;
  onEdit?: () => void;
  onOpenRelation?: (klantId: number) => void;
}) {
  const t = useTranslate();
  // The H2 reads "{naam} is binnengebracht als …". Fetched here rather than in IntakeInTile so only the
  // stray variant pays for it — react-query serves it from cache when the Basis tile is also mounted.
  const { data: animal } = useOne<AnimalBasis>({ resource: "animals", id: animalId });
  return (
    <div className="fawkes-stay-v2">
      <div className="tile">
        <StrayTileHead stay={stay} animalName={animal?.data?.naam ?? null} onEdit={onEdit} />
        <div className="cards">
          <BrengerCard stay={stay} />
          {/* A stray's finder signs a vindersverklaring — same inset the owner flows show for the
              afstandsverklaring, so the document is reachable from the tile. */}
          <RelationCard
            relation={stay.relatieIn}
            role={t("animalDetailView.stay.v2.vinder.role")}
            onOpenRelation={onOpenRelation}
            verklaring={
              <VerklaringInset
                animalId={animalId}
                title={t("animalDetailView.stay.v2.verklaring.vinder")}
                templateKey="VINDERSVERKLARING"
                signedDate={stay.opnameDatum}
              />
            }
          />
          <VindplaatsCard stay={stay} />
          <NoteCardV2 text={stay.gevondenOpmerking} />
        </div>
      </div>
    </div>
  );
}

export function IntakeInTile({ animalId, selectedStayId, onStartInboeken, onEditInboeken, onOpenRelation, onOpenAnimal, onOpenFactuur }: { animalId: string; selectedStayId?: string | null; onStartInboeken?: (id: string) => void; onEditInboeken?: (verblijfId: string) => void; onOpenRelation?: (klantId: number) => void; onOpenAnimal?: (id: string) => void; onOpenFactuur?: (factuurId: number) => void }) {
  const t = useTranslate();
  const { verblijven, isLoading, isError, refetch } = useStays(animalId);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;
  const stay = resolveStay(verblijven, selectedStayId);
  if (!stay) {
    return (
      <div className="fawkes-stay-tile">
        <TilePlaceholder text={t("animalDetailView.stay.empty.in")} />
        {onStartInboeken && (
          <StayTileActions>
            <Button onClick={() => onStartInboeken(animalId)}>{t("inboeken.start")}</Button>
          </StayTileActions>
        )}
      </div>
    );
  }
  // Jmix splits this in two, and so do we: the pencil ("Gegevens") opens the in-place
  // "Binnenkomst bewerken" dialog for the fields that are just data — datum, brenger, vinder,
  // vindplaats, opmerkingen. The TYPE of intake is not one of those: changing it changes which steps
  // apply, so that field sits read-only in the dialog behind a "Wijzigen" button that re-opens the
  // wizard on this stay (after a confirm).
  const openBewerken = () => { setNotice(null); setEditing(true); };

  const editModal = (
    <StayIntakeModal
      animalId={animalId} stay={stay} open={editing} onClose={() => setEditing(false)}
      onStartWizard={onEditInboeken ? () => { setEditing(false); onEditInboeken(String(stay.id)); } : undefined}
      onSaved={() => { setEditing(false); setNotice("saved"); refetch(); }} t={t}
    />
  );

  // The V2 stray tile also serves the wilde-kat/TNRC intake. That row carries no `shelter_intake_option`
  // in the database; the api now sends the RESOLVED option (ShelterIntakeOptions maps tnrc/wilde kat to
  // STRAY), so the first test normally covers it. The `|| "wild"` stays on purpose: it is not provably
  // redundant — a wild-named row with a stored BIRTH resolves to BIRTH in the api but "wild" here — and
  // without it such a row would drop into the flat fallback grid instead of the tile built for it.
  if (stay.shelterIntakeOption === STRAY_INTAKE_OPTION || intakeVariant(stay) === "wild") {
    return (
      <div className="fawkes-stay-tile">
        <StrayIntakeTile
          animalId={animalId}
          stay={stay}
          onEdit={openBewerken}
          onOpenRelation={onOpenRelation}
        />
        <StayNotice notice={notice} t={t} />
        {editModal}
      </div>
    );
  }

  const variant = intakeVariant(stay);
  if (variant === "surrender" || variant === "return" || variant === "birth") {
    return (
      <div className="fawkes-stay-tile">
        <LegacyIntakeTile
          animalId={animalId}
          stay={stay}
          variant={variant}
          onEdit={openBewerken}
          onOpenRelation={onOpenRelation}
          onOpenAnimal={onOpenAnimal}
          onOpenFactuur={onOpenFactuur}
          onSaved={() => { setNotice("saved"); refetch(); }}
        />
        <StayNotice notice={notice} t={t} />
        {editModal}
      </div>
    );
  }

  return (
    <div className="fawkes-stay-tile">
      <StayTileActions>
        <Button variant="secondary" onClick={openBewerken}>
          {t("animalDetailView.stay.action.edit")}
        </Button>
      </StayTileActions>
      <StayFieldGrid>
        <ReadField label={t("animalDetailView.stay.field.typeBinnenkomst")} value={stay.opnameSoort} />
        <ReadField label={t("animalDetailView.stay.field.opname")} value={fmtFormalDate(stay.opnameDatum)} />
        <ReadField label={t("animalDetailView.stay.field.naamBrenger")} value={stay.klantIn} />
        <ReadField label={t("animalDetailView.stay.field.locatie")} value={stay.location} />
        {stay.redenHerkomst && (
          <ReadField label={t("animalDetailView.stay.field.redenHerkomst")} value={stay.redenHerkomst} span2 />
        )}
      </StayFieldGrid>
      <StayNotice notice={notice} t={t} />
      {editModal}
    </div>
  );
}

// UIT — the release side of the current stay; the Uitboeken wizard ("Uitboeken") for the full release
// flow while the stay is open, plus the in-place "Vertrek registreren"/"Bewerken" quick-edit.
// ---- UIT (release) variants -------------------------------------------------------------------------
// Jmix `resolveReleaseFlow` dispatches purely on the shelter_release_option enum, and so does this switch. The
// api now sends the RESOLVED option (ShelterReleaseOptions: the configured value, else a display-name fallback
// for a blank column), so a never-configured "Geplaatst" still arrives here as ADOPTION — the same flow the
// release wizard offered and the release write acted on. Only the two counterparty flows are ported so far;
// every other flow keeps the flat grid.
type ReleaseVariant = "adoption" | "returnOwner" | "deceased" | "euthanasia" | "natureRelease";

function releaseVariant(stay: Verblijf): ReleaseVariant | null {
  switch (stay.shelterReleaseOption) {
    case "ADOPTION": return "adoption";
    case "RETURN_TO_OWNER": return "returnOwner";
    case "DECEASED": return "deceased";
    case "EUTHANASIA": return "euthanasia";
    case "NATURE_RELEASE": return "natureRelease";
    default: return null; // EXCHANGE + unmapped rows keep the flat grid (Jmix renders plain rows too).
  }
}

/** Jmix `buildReleaseTileSummary(withDuration)`: only DECEASED drops the "na {duur}" clause. */
const RELEASE_WITH_DURATION: Record<ReleaseVariant, boolean> = {
  adoption: true, returnOwner: true, euthanasia: true, natureRelease: true, deceased: false,
};
const RELEASE_VARIANT_CLASS: Record<ReleaseVariant, string> = {
  adoption: "adoption", returnOwner: "return-owner", deceased: "deceased",
  euthanasia: "euthanasia", natureRelease: "nature-release",
};

function InfoGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}
function StethoscopeGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3v6a5 5 0 0 0 10 0V3M4 3h3M15 3h3" />
      <path d="M11 14v2a5 5 0 0 0 10 0v-2" />
      <circle cx="21" cy="11" r="2" />
    </svg>
  );
}

/** Jmix `buildReasonRow`: icon + label + value (+ optional meta line). A blank value renders "-". */
function ReasonRow({ icon, label, value, meta }: {
  icon: ReactNode; label: string; value: string | null; meta?: string | null;
}) {
  return (
    <div className="reason-row">
      <span className="reason-icon" aria-hidden="true">{icon}</span>
      <div className="reason-body">
        <div className="reason-label">{label}</div>
        <div className="reason-value">{value?.trim() ? value : "-"}</div>
        {meta?.trim() ? <div className="reason-value-meta">{meta}</div> : null}
      </div>
    </div>
  );
}

/** Jmix `buildReadonlyNotesRow` pair inside `buildUitzetRedenCard`. */
function ReadonlyNotesRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="notes-row">
      <span className="notes-label">{label}</span>
      <div className="notes-readonly">{value}</div>
    </div>
  );
}

/**
 * Jmix renderRichHalerDeceasedFull external-vet meta: the raw `external_vet` flag ("Ja"/"Nee") becomes a
 * sub-line under the cause of death. Null/blank → no meta line (Jmix passes null).
 */
function externeVetMeta(externeVet: string | null, t: (k: string) => string): string | null {
  const v = externeVet?.trim();
  if (!v) return null;
  return /^ja$/i.test(v)
    ? t("animalDetailView.stay.v2.release.externeVetBetrokken")
    : t("animalDetailView.stay.v2.release.geenExterneVet");
}

/**
 * The NATURE_RELEASE release-location card — Jmix buildNatureReleaseLocationCard. Reuses the exact OSM
 * component the stray Vindplaats card uses ({@link FoundLocationMap}). Only rendered when the release
 * carries coordinates; the address/city sit in the overlay pill.
 */
function NatureReleaseLocationCard({ stay }: { stay: Verblijf }) {
  const t = useTranslate();
  // Same enlarge control as the Vindplaats card: the tile map is a thumbnail, you open the real one to
  // read the streets around the pin.
  const [fullMap, setFullMap] = useState(false);
  if (stay.natureReleaseLat == null || stay.natureReleaseLong == null) return null;
  const locatie = stay.natureReleaseLocatie?.trim() || null;
  const woonplaats = stay.natureReleaseWoonplaats?.trim() || null;
  const naam = locatie ?? woonplaats ?? t("animalDetailView.stay.v2.uitzetlocatie.unknown");
  return (
    <div className="vp-card">
      <div className="vp-map">
        <FoundLocationMap lat={Number(stay.natureReleaseLat)} lng={Number(stay.natureReleaseLong)} interactive={false} />
        <button
          type="button" className="vp-map__expand"
          title={t("animalDetailView.stay.v2.map.expand")} aria-label={t("animalDetailView.stay.v2.map.expand")}
          onClick={() => setFullMap(true)}
        >
          <ExpandGlyph />
        </button>
      </div>
      <Modal
        open={fullMap} title={naam} onClose={() => setFullMap(false)}
        footer={<Button variant="secondary" onClick={() => setFullMap(false)}>{t("common.close")}</Button>}
      >
        <div className="fawkes-map-dialog">
          <FoundLocationMap lat={Number(stay.natureReleaseLat)} lng={Number(stay.natureReleaseLong)} />
        </div>
      </Modal>
      <div className="vp-overlay">
        <div className="bg-pill">
          <div className="vp-eyebrow">{t("animalDetailView.stay.v2.uitzetlocatie.eyebrow")}</div>
          <div className="vp-name">{naam}</div>
          {locatie && woonplaats && (
            <div className="vp-meta"><span>{woonplaats}</span></div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Jmix `computeVerblijfsduurDays`: inclusive day count — both the intake and the departure day count. */
function verblijfsduurDagen(stay: Verblijf): number | null {
  if (!stay.opnameDatum || !stay.vertrekDatum) return null;
  const from = Date.parse(`${stay.opnameDatum}T00:00:00Z`);
  const to = Date.parse(`${stay.vertrekDatum}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return null;
  return Math.round((to - from) / 86_400_000) + 1;
}

/** Jmix `buildReleaseTileHeader` + `buildReleaseTileSummary`: "VERTREK", "{naam} is na {duur} {werkwoord}". */
function ReleaseHead({ stay, variant, animalName, onEdit }: {
  stay: Verblijf; variant: ReleaseVariant; animalName: string | null; onEdit?: () => void;
}) {
  const t = useTranslate();
  const naam = animalName?.trim() || "-";
  const dagen = verblijfsduurDagen(stay);
  const duurTekst =
    dagen == null
      ? "-"
      : dagen === 1
        ? t("animalDetailView.stay.v2.duration.day")
        : t("animalDetailView.stay.v2.duration.days").replace("%d", String(dagen));
  const withDuration = RELEASE_WITH_DURATION[variant];
  const vcode = vcodeLabel(stay);
  // Built exactly like the intake head (StrayTileHead): boxed glyph, eyebrow, title, meta line, pencil.
  // The two tiles sit side by side, so anything else reads as two different screens.
  return (
    <div className="tile-head">
      <span className="fawkes-stay-v2__corner-icon fawkes-stay-v2__corner-icon--glyph" aria-hidden="true">
        <StaySideIcon kind={departureIconKind(stay.vertrekSoort)} />
      </span>
      <div className="head-text">
        <div className="head-eyebrow">{t("animalDetailView.stay.v2.releaseTitle")}</div>
        <h2 className="head-title">
          {withDuration ? (
            <>
              <span>{t("animalDetailView.stay.v2.summary.releasePrefix", { args: [naam] })}</span>
              <span className="head-title__duration">{duurTekst}</span>{" "}
            </>
          ) : (
            <span>{t("animalDetailView.stay.v2.summary.releasePrefixNoDuration", { args: [naam] })}</span>
          )}
          <span className="accent">{t(`animalDetailView.stay.v2.release.${variant}`)}</span>
        </h2>
        <div className="head-meta">
          <span className="tabular">{fmtFormalDate(stay.vertrekDatum)}</span>
          {vcode && (
            <>
              <span aria-hidden="true"> · </span>
              <span>{t("animalDetailView.stay.v2.dossier", { args: [vcode] })}</span>
            </>
          )}
        </div>
      </div>
      {onEdit && (
        <button type="button" className="fawkes-stay-v2__edit" title={t("animalDetailView.stay.action.edit")} onClick={onEdit}>
          <StayEditGlyph />
        </button>
      )}
    </div>
  );
}

/**
 * The ADOPTION / RETURN_TO_OWNER release tile (Jmix `renderRichHalerAdoption` /
 * `renderRichHalerReturnToOwner`, via their `…FromVerblijf` fallbacks — the SPA has no ShelterRelease read
 * model, and Jmix's fallback reads exactly the vertrekdatum + klantUit we do have). Header + counterparty
 * card with its declaration inset + the release invoice.
 */
function ReleaseVariantTile({ animalId, stay, variant, onEdit, onOpenRelation, onOpenFactuur }: {
  animalId: string;
  stay: Verblijf;
  variant: ReleaseVariant;
  onEdit?: () => void;
  onOpenRelation?: (klantId: number) => void;
  onOpenFactuur?: (factuurId: number) => void;
}) {
  const t = useTranslate();
  const { data: animal } = useOne<AnimalBasis>({ resource: "animals", id: animalId });
  const animalName = animal?.data?.naam ?? null;
  const isAdoption = variant === "adoption";
  const isCounterparty = variant === "adoption" || variant === "returnOwner";
  const verklaringTitle = t(
    isAdoption ? "animalDetailView.stay.v2.verklaring.plaatsing" : "animalDetailView.stay.v2.verklaring.retourEigenaar",
  );
  return (
    <div className={`fawkes-stay-v2 fawkes-stay-v2--${RELEASE_VARIANT_CLASS[variant]}`}>
      <div className="tile">
        <ReleaseHead stay={stay} variant={variant} animalName={animalName} onEdit={onEdit} />

        {isCounterparty && (
          <>
            <RelationCard
              relation={stay.relatieUit}
              role={t(isAdoption ? "animalDetailView.stay.v2.adoptant.role" : "animalDetailView.stay.v2.eigenaar.role")}
              onOpenRelation={onOpenRelation}
              verklaring={
                <VerklaringInset
                  animalId={animalId}
                  title={verklaringTitle}
                  templateKey={isAdoption ? "PLAATSINGSOVEREENKOMST" : "RETOUR_EIGENAAR"}
                  signedDate={stay.vertrekDatum}
                />
              }
            />
            {/* Jmix buildPlaatsingFactuurCard → laadFactuurVoorVerblijf(verblijfId, "release"). */}
            <FactuurCard animalId={animalId} stayId={stay.id} bron="release" onOpenFactuur={onOpenFactuur} />
          </>
        )}

        {/* Jmix renderRichHalerDeceasedFull: Tijdstip + Doodsoorzaak (with an external-vet meta line),
            then an optional remark card — all read from the matched ShelterRelease. */}
        {variant === "deceased" && (
          <>
            <div className="reason-card">
              <ReasonRow icon={<ClockGlyph />} label={t("animalDetailView.stay.v2.release.tijdstip")} value={fmtFormalDate(stay.vertrekDatum)} />
              <ReasonRow
                icon={<InfoGlyph />}
                label={t("animalDetailView.stay.v2.release.doodsoorzaak")}
                value={stay.doodsoorzaak}
                meta={externeVetMeta(stay.externeDierenarts, t)}
              />
            </div>
            {stay.overledenOpmerking && (
              <div className="notes-card notes-card--readonly">
                <ReadonlyNotesRow label={t("animalDetailView.stay.v2.release.opmerking")} value={stay.overledenOpmerking} />
              </div>
            )}
          </>
        )}

        {/* Jmix renderRichHalerEuthanasiaFull: Medische indicatie + "Besluit genomen door: Dierenarts"
            (the vet name as its meta line), then an optional remark card — from the ShelterRelease. */}
        {variant === "euthanasia" && (
          <>
            <div className="reason-card">
              <ReasonRow icon={<InfoGlyph />} label={t("animalDetailView.stay.v2.release.indicatie")} value={stay.euthanasieReden} />
              <ReasonRow
                icon={<StethoscopeGlyph />}
                label={t("animalDetailView.stay.v2.release.besluitDoor")}
                value={t("animalDetailView.stay.v2.release.dierenarts")}
                meta={stay.dierenartsNaam}
              />
            </div>
            {stay.euthanasieOpmerking && (
              <div className="notes-card notes-card--readonly">
                <ReadonlyNotesRow label={t("animalDetailView.stay.v2.release.opmerking")} value={stay.euthanasieOpmerking} />
              </div>
            )}
          </>
        )}

        {/* Jmix renderRichHalerOverviewForNatureRelease: the "Reden uitzetten" note (natureReleaseRemark)
            + the release-location card with an OSM map when coordinates are present. The Jmix "Opmerking"
            row has no backing column, so it stays a neutral placeholder. */}
        {variant === "natureRelease" && (
          <>
            <div className="notes-card notes-card--readonly">
              {/* The counted answer, in words: back to the catch site or somewhere else. It is what the
                  overviews tally, so the tile has to show which of the two this release was. */}
              {stay.natureRelease && (
                <ReadonlyNotesRow
                  label={t("uitboeken.nature.place.label")}
                  value={t(stay.natureRelease.atFoundLocation
                    ? "uitboeken.nature.place.found"
                    : "uitboeken.nature.place.other")}
                />
              )}
              <ReadonlyNotesRow label={t("animalDetailView.stay.v2.release.redenUitzetten")} value={stay.natureReleaseReden ?? "-"} />
            </div>
            <NatureReleaseLocationCard stay={stay} />
            {/* One remark, under the map — exactly where the intake side puts its own. */}
            <NoteCardV2 text={stay.opmerkingUit} />
          </>
        )}
      </div>
    </div>
  );
}

export function IntakeUitTile({ animalId, selectedStayId, onStartUitboeken, onOpenRelation, onOpenFactuur }: { animalId: string; selectedStayId?: string | null; onStartUitboeken?: (id: string) => void; onOpenRelation?: (klantId: number) => void; onOpenFactuur?: (factuurId: number) => void }) {
  const t = useTranslate();
  const { verblijven, isLoading, isError, refetch } = useStays(animalId);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;
  const stay = resolveStay(verblijven, selectedStayId);
  if (!stay) return <TilePlaceholder text={t("animalDetailView.stay.empty.uit")} />;
  const released = !!stay.vertrekDatum;
  return (
    <div className="fawkes-stay-tile">
      {/* acc's UIT card header: the vertrek glyph on the left, "UIT" centred, and the action on the
          right — one row, not a loose button bar under the title. One way out while the stay is open:
          the Uitboeken wizard. The old "Vertrek registreren" button opened a four-field dialog that
          closed the stay behind the wizard's back — two buttons for one act, and the short one skipped
          everything the flow needs (kosten, voorwaarden, chip afmelden). Once the animal HAS left, the
          dialog returns as the plain correction it is. */}
      {/* Once released, the flow tile carries its OWN header (eyebrow + sentence + pencil), exactly
          like the INTAKE tile beside it — a "UIT" bar above that is a second header for one tile.
          While the stay is open there is no flow tile, so the bar is what holds the Uitboeken button;
          the flat fallback has no pencil, so it keeps its Wijzigen there too. */}
      {(!released || !releaseVariant(stay)) && (
        <div className="fawkes-stay-uit__head">
          <span className="fawkes-stay-uit__glyph" aria-hidden="true">
            <LogoutIcon width={18} height={18} />
          </span>
          <h2 className="fawkes-stay-uit__title">{t("animalDetailView.stay.tile.uit")}</h2>
          <div className="fawkes-stay-uit__action">
            {!released && onStartUitboeken && (
              <Button variant="secondary" onClick={() => onStartUitboeken(animalId)}>
                <LogoutIcon width={16} height={16} /> {t("uitboeken.start")}
              </Button>
            )}
            {released && (
              <Button variant="secondary" onClick={() => { setNotice(null); setEditing(true); }}>
                {t("animalDetailView.stay.action.edit")}
              </Button>
            )}
          </div>
        </div>
      )}
      {released && releaseVariant(stay) ? (
        // Jmix renders a rich per-flow tile for the counterparty flows; everything else keeps the flat grid.
        <ReleaseVariantTile
          animalId={animalId}
          stay={stay}
          variant={releaseVariant(stay) as ReleaseVariant}
          onEdit={() => { setNotice(null); setEditing(true); }}
          onOpenRelation={onOpenRelation}
          onOpenFactuur={onOpenFactuur}
        />
      ) : released ? (
        <StayFieldGrid>
          <ReadField label={t("animalDetailView.stay.field.typeVertrek")} value={stay.vertrekSoort} />
          <ReadField label={t("animalDetailView.stay.field.vertrek")} value={fmtFormalDate(stay.vertrekDatum)} />
          <ReadField label={t("animalDetailView.stay.field.naamHaler")} value={stay.klantUit} />
          {stay.vertrekOpmerking && (
            <ReadField label={t("animalDetailView.stay.field.opmerking")} value={stay.vertrekOpmerking} span2 />
          )}
        </StayFieldGrid>
      ) : (
        <TilePlaceholder text={t("animalDetailView.stay.empty.uitForStay")} />
      )}
      <StayNotice notice={notice} t={t} />
      <StayReleaseModal
        animalId={animalId} stay={stay} open={editing} onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); setNotice("saved"); refetch(); }}
        // Changing the departure TYPE means re-running the uitboek-wizard on this stay, like the
        // intake dialog escalates to the inboek-wizard.
        onStartWizard={onStartUitboeken ? () => { setEditing(false); onStartUitboeken(animalId); } : undefined}
        t={t}
      />
    </div>
  );
}

// One row in the "Historie binnen dit verblijf" timeline — either an asiel period (opnamedatum → first
// foster / vertrek / today) or a pleeggezin period. Port of the Jmix TimelineEntry.
interface FosterTimelineEntry {
  kind: "asiel" | "pleeggezin";
  subject: string | null;
  from: string | null;
  /** null = open-ended ("heden"). */
  to: string | null;
  days: number | null;
  current: boolean;
  foster: Foster | null;
}

/**
 * Port of the Jmix `PleeggezinDierVerblijfTile.buildTimeline`: interleave the stay's asiel periods with
 * its pleeggezin periods (newest-first), so the history is never empty for an animal that has a stay —
 * an animal that has only been in the shelter still shows one "Asiel" period.
 */
function buildFosterTimeline(stay: Verblijf, foster: Foster[]): FosterTimelineEntry[] {
  const entries: FosterTimelineEntry[] = [];
  const opname = stay.opnameDatum ? stay.opnameDatum.slice(0, 10) : null;
  if (!opname) return entries;
  const vertrek = stay.vertrekDatum ? stay.vertrekDatum.slice(0, 10) : null;
  const today = new Date().toISOString().slice(0, 10);
  const asc = [...foster].filter((f) => f.vanDatum).sort((a, b) => (a.vanDatum! < b.vanDatum! ? -1 : 1));

  let cursor: string | null = opname;
  let openEnded = false;
  for (const pg of asc) {
    const pgStart = pg.vanDatum!.slice(0, 10);
    if (cursor && pgStart > cursor) {
      entries.push({ kind: "asiel", subject: stay.vestigingNaam ?? stay.location, from: cursor, to: pgStart, days: durationDays(cursor, pgStart), current: false, foster: null });
    }
    const pgEnd = pg.totDatum ? pg.totDatum.slice(0, 10) : null;
    entries.push({ kind: "pleeggezin", subject: pg.klant, from: pgStart, to: pgEnd, days: durationDays(pgStart, pgEnd), current: pgEnd == null, foster: pg });
    if (pgEnd) cursor = pgEnd;
    else { openEnded = true; cursor = null; break; }
  }
  if (!openEnded && cursor) {
    const end = vertrek ?? today;
    if (cursor <= end) {
      entries.push({ kind: "asiel", subject: stay.vestigingNaam ?? stay.location, from: cursor, to: vertrek, days: durationDays(cursor, end), current: vertrek == null, foster: null });
    }
  }
  entries.reverse();
  return entries;
}

/** An "Asiel" period row in the foster timeline (no edit/delete — only pleeggezin rows are editable). */
function AsielTimelineRow({ e, t, locale }: { e: FosterTimelineEntry; t: (k: string) => string; locale: string }) {
  return (
    <article className={`fawkes-stay-foster__row fawkes-stay-foster__row--asiel${e.current ? " fawkes-stay-foster__row--current" : ""}`}>
      <header className="fawkes-stay-foster__head">
        <span className="fawkes-stay-foster__type">{t("animalDetailView.stay.foster.asiel")}</span>
        <span className="fawkes-stay-foster__sep" aria-hidden="true">—</span>
        <span className="fawkes-stay-foster__subject">{e.subject ?? "—"}</span>
        {e.current && <span className="fawkes-stay-badge">{t("animalDetailView.stay.current")}</span>}
      </header>
      <div className="fawkes-stay-foster__dates">
        <CalendarIcon width={14} height={14} aria-hidden="true" />
        <span className="fawkes-stay-foster__date">{formatDate(e.from, locale)}</span>
        <span className="fawkes-stay-foster__arrow" aria-hidden="true">→</span>
        <span className="fawkes-stay-foster__date">{e.to ? formatDate(e.to, locale) : t("animalDetailView.stay.now")}</span>
        {e.days != null && <span className="fawkes-stay-foster__days">{e.days} {dayUnit(t, e.days)}</span>}
      </div>
    </article>
  );
}

// Pleeggezin — Jmix `PleeggezinDierVerblijfTile`: a status card (badge + "Op dit moment in asiel" /
// "Huidig verblijf in pleeggezin" + V-id + Categorie select), the "Historie binnen dit verblijf" timeline
// (asiel + pleeggezin periods), a "Retour naar asiel" action that closes the open placement, and "Plaats
// bij pleeggezin" to start a new placement. "Plaats" needs an open stay (the animal must be in the
// shelter); the button hides otherwise.
export function PleeggezinTile({ animalId, selectedStayId, onOpenRelation }: {
  animalId: string; selectedStayId?: string | null;
  /** Doorklikken naar het pleeggezin (de relatie); viel weg in de nieuwe kaart (Maarten, 2026-10-09). */
  onOpenRelation?: (klantId: number) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { foster, verblijven, isLoading, isError, refetch } = useStays(animalId);
  const [editTarget, setEditTarget] = useState<Foster | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Foster | null>(null);
  const [retourOpen, setRetourOpen] = useState(false);
  const [placeOpen, setPlaceOpen] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate: mutateEnd, isLoading: ending } = useCustomMutation();
  const { mutate: mutateDelete, isLoading: deleting } = useCustomMutation();
  // Categorie select options (Jmix buildVerblijfCategorieRow) — the active intake categories for the
  // animal's branch + species. Shown even without an active pleeggezin, like Jmix.
  const { data: catData } = useCustom<{ id: number; naam: string | null }[]>({
    url: `animals/${animalId}/foster/categories`,
    method: "get",
  });
  const categories = catData?.data ?? [];
  // Currently-persisted categorie for the open stay (Jmix findCategorieForAsielVerblijf) — seeds the
  // selector; changing it upserts via PUT /foster/categorie (Jmix setCategorieForAsielVerblijf).
  const { data: curCatData, refetch: refetchCat } = useCustom<{ id?: number; naam: string | null }>({
    url: `animals/${animalId}/foster/categorie`,
    method: "get",
  });
  const currentCatId = curCatData?.data?.id ?? null;
  const { mutate: mutateCat, isLoading: savingCat } = useCustomMutation();

  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;

  const hasOpenStay = verblijven.some((v) => !v.vertrekDatum);
  // Every tile on the Verblijf tab shows the ONE stay highlighted in the overview.
  const stay = resolveStay(verblijven, selectedStayId);
  // "Historie binnen dit verblijf" means exactly that. The feed carries every placement the animal ever
  // had; showing them all put a 2019 pleeggezin (and the asiel period it implied) under a stay booked in
  // today.
  const stayFoster = stay ? foster.filter((f) => f.asielVerblijfId === stay.id) : [];
  const hasOpen = stayFoster.some((f) => !f.totDatum);
  const activeFoster = stayFoster.find((f) => !f.totDatum) ?? null;
  const vcode = stay?.verblijfNummer != null ? `V${stay.verblijfNummer}` : null;
  const timeline = stay ? buildFosterTimeline(stay, stayFoster) : [];
  const done = (n: SaveNotice) => { setNotice(n); refetch(); };

  const saveCategorie = (value: string) => {
    setNotice(null);
    mutateCat(
      {
        url: `animals/${animalId}/foster/categorie`,
        method: "put",
        values: { opvangcategorieId: value === "" ? null : Number(value) },
      },
      {
        onSuccess: () => { refetchCat(); done("saved"); },
        onError: (err) => { refetchCat(); done(errorNotice(err)); },
      },
    );
  };

  const confirmRetour = () =>
    mutateEnd(
      { url: `animals/${animalId}/foster/end`, method: "post", values: {} },
      { onSuccess: () => { setRetourOpen(false); done("saved"); }, onError: (err) => { setRetourOpen(false); done(errorNotice(err)); } },
    );

  const confirmDelete = () => {
    if (!deleteTarget) return;
    mutateDelete(
      { url: `animals/${animalId}/foster/${deleteTarget.id}`, method: "delete", values: {} },
      { onSuccess: () => { setDeleteTarget(null); done("saved"); }, onError: (err) => { setDeleteTarget(null); done(errorNotice(err)); } },
    );
  };

  return (
    <div className="fawkes-stay-tile fawkes-pg-tile">
      {/* Status card (Jmix buildStatusCard): badge + current-location heading + V-id, then the Categorie
          select — always visible, even without an active pleeggezin. */}
      {stay && (
        <div className="fawkes-pg-status">
          <div className="fawkes-pg-status__top">
            <span className={activeFoster ? "fawkes-pg-badge fawkes-pg-badge--active" : "fawkes-pg-badge"}>
              {activeFoster ? t("animalDetailView.stay.foster.actief") : t("animalDetailView.stay.foster.geenPleeggezin")}
            </span>
            <span className="fawkes-pg-status__heading">
              {activeFoster
                ? t("animalDetailView.stay.foster.huidigInPleeggezin")
                : t("animalDetailView.stay.foster.opDitMomentInAsiel")}
            </span>
            {vcode && <span className="fawkes-pg-vid">{vcode}</span>}
          </div>
          <label className="fawkes-pg-categorie">
            <span className="fawkes-pg-categorie__label">{t("animalDetailView.stay.foster.categorie")}</span>
            <select
              className="fawkes-pg-categorie__select"
              value={currentCatId == null ? "" : String(currentCatId)}
              disabled={categories.length === 0 || savingCat}
              onChange={(e) => saveCategorie(e.target.value)}
            >
              <option value="">
                {categories.length
                  ? t("animalDetailView.stay.foster.kiesCategorie")
                  : t("animalDetailView.stay.foster.geenCategorieen")}
              </option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.naam ?? "—"}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Active-foster card (Jmix buildStatusCard active branch): klant avatar + name + branch + since +
          days-in-care. The "Capaciteit gebruikt N/M" pill still needs the api capacity feed. */}
      {activeFoster && (
        <div className="fawkes-pg-foster-card">
          <span className="fawkes-pg-foster-card__avatar" aria-hidden="true">
            <UserIcon width={20} height={20} />
          </span>
          <div className="fawkes-pg-foster-card__body">
            {onOpenRelation && activeFoster.klantId != null ? (
              <button type="button" className="fawkes-pg-foster-card__name fawkes-relatie-link"
                onClick={() => onOpenRelation(activeFoster.klantId as number)} title={t("animalDetailView.stay.v2.openRelation")}>
                {activeFoster.klant ?? "—"}
              </button>
            ) : (
              <span className="fawkes-pg-foster-card__name">{activeFoster.klant ?? "—"}</span>
            )}
            {stay?.vestigingNaam && (
              <span className="fawkes-pg-foster-card__meta">{stay.vestigingNaam}</span>
            )}
            {activeFoster.vanDatum && (
              <span className="fawkes-pg-foster-card__meta">
                {t("animalDetailView.stay.foster.sinds")} {fmtFormalDate(activeFoster.vanDatum)}
              </span>
            )}
            {(() => {
              const d = durationDays(activeFoster.vanDatum, null);
              return d != null ? (
                <span className="fawkes-pg-foster-card__meta">
                  {d} {dayUnit(t, d)} {t("animalDetailView.stay.foster.aanwezig")}
                </span>
              ) : null;
            })()}
          </div>
          {activeFoster.maxCapaciteit != null && (
            <div className="fawkes-pg-foster-card__capacity">
              <span className="fawkes-pg-foster-card__capacity-label">
                {t("animalDetailView.stay.foster.capaciteit")}
              </span>
              <span className="fawkes-pg-foster-card__capacity-value">
                <UserIcon width={15} height={15} aria-hidden="true" />
                {(activeFoster.huidigeBezetting ?? 0)}/{activeFoster.maxCapaciteit}
              </span>
            </div>
          )}
        </div>
      )}

      {hasOpen && (
        <StayTileActions>
          <Button variant="secondary" onClick={() => { setNotice(null); setRetourOpen(true); }}>
            {t("animalDetailView.stay.action.retour")}
          </Button>
        </StayTileActions>
      )}

      {timeline.length > 0 ? (
        <div className="fawkes-stay-foster">
          <h4 className="fawkes-stay-foster__title">{t("animalDetailView.stay.foster.historyTitle")}</h4>
          {timeline.map((e, i) =>
            e.kind === "pleeggezin" && e.foster ? (
              <FosterRow
                key={`pg-${e.foster.id}`} f={e.foster} t={t} locale={locale} onOpenRelation={onOpenRelation}
                onEdit={() => { setNotice(null); setEditTarget(e.foster); }}
                onDelete={() => { setNotice(null); setDeleteTarget(e.foster); }}
              />
            ) : (
              <AsielTimelineRow key={`asiel-${i}`} e={e} t={t} locale={locale} />
            ),
          )}
        </div>
      ) : (
        <TilePlaceholder text={t("animalDetailView.stay.empty.pleeggezin")} />
      )}

      {hasOpenStay && (
        <StayTileActions>
          <Button variant="primary" onClick={() => { setNotice(null); setPlaceOpen(true); }}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("animalDetailView.stay.action.place")}
          </Button>
        </StayTileActions>
      )}
      <StayNotice notice={notice} t={t} />

      {editTarget && (
        <FosterDatesModal
          animalId={animalId} foster={editTarget} open={!!editTarget} onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); done("saved"); }} t={t}
        />
      )}
      <FosterPlaceModal
        animalId={animalId} open={placeOpen} onClose={() => setPlaceOpen(false)}
        onSaved={() => { setPlaceOpen(false); done("saved"); }} t={t}
      />
      <ConfirmDialog
        cancelLabel={t("common.cancel")}
        open={!!deleteTarget} title={t("animalDetailView.stay.confirmDeleteTitle")}
        confirmLabel={t("animalDetailView.stay.action.delete")}
        danger busy={deleting} onConfirm={confirmDelete} onCancel={() => setDeleteTarget(null)}
      >
        {t("animalDetailView.stay.confirmDeleteBody")}
      </ConfirmDialog>
      <ConfirmDialog
        cancelLabel={t("common.cancel")}
        open={retourOpen} title={t("animalDetailView.stay.confirmRetourTitle")}
        confirmLabel={t("animalDetailView.stay.action.retour")}
        busy={ending} onConfirm={confirmRetour} onCancel={() => setRetourOpen(false)}
      >
        {t("animalDetailView.stay.confirmRetourBody")}
      </ConfirmDialog>
    </div>
  );
}

// ---- Geschiedenis ----------------------------------------------------------------------------------
// Read-only mirror of the Jmix animal Geschiedenis tab (fragment/relatie/RelatieGeschiedenisPanel,
// fed by AuditService.findByDier): a search box over a day-grouped timeline of audit events. Each
// event is a coloured icon-circle + a humanised title, an optional formatted payload summary (clamped
// to two lines with a "Toon meer" expander past ~110 chars) and a "time · user" meta line. The title-,
// payload- and icon-derivation below port that panel's logic verbatim; USERANIMALPREFERENCE_* rows are
// UI-state (not domain changes) and are hidden exactly as the Jmix filterHidden does.
interface Event {
  id: number; occurredAt: string | null; entityType: string | null; eventType: string | null; payload: string | null; userId: string | null;
}

const PAYLOAD_CLAMP_THRESHOLD = 110;

function isHiddenEvent(eventType: string | null): boolean {
  return !!eventType && eventType.startsWith("USERANIMALPREFERENCE_");
}

// Jmix iconCircleVariant — the colour family that tints the icon circle per event category (the
// glyph itself comes from glyphFor below).
function iconCircleVariant(e: Event): string {
  const code = e.eventType ?? "";
  const type = e.entityType ?? "";
  if (code.endsWith("_STOPPED") || code.endsWith("_DELETED")) return "danger";
  if (code.endsWith("_PAUSED")) return "warning";
  if (code.startsWith("DONATION_")) return "donation";
  if (code.startsWith("RECURRING_")) return "recurring";
  if (code.startsWith("PROFILE_")) return "profile";
  if (code.startsWith("PLEEGGEZIN_")) return "pleeggezin";
  if (code.startsWith("KLANT_")) return "algemeen";
  if (type.startsWith("Pleeggezin")) return "pleeggezin";
  if (type === "Klant") return "algemeen";
  return "neutral";
}

// eventLabel — Jmix switch for the codes with a bespoke phrasing, else humanize(<ENTITY>_<TYPE>).
// Special cases + the entity/verb maps are i18n'd (nl = the Jmix literal, en = its translation).
const EVENT_TITLE_CODES = new Set([
  "KLANT_CREATED", "KLANT_UPDATED", "KLANT_DELETED",
  "PLEEGGEZIN_VERBLIJF_PLAATSING", "PLEEGGEZIN_VERBLIJF_EINDE",
  "PLEEGGEZIN_VERBLIJF_UPDATED", "PLEEGGEZIN_VERBLIJF_DELETED",
]);
const ENTITY_CODE_KEYS = new Set([
  "DIER", "ASIELVERBLIJF", "ASIELVERBLIJFNOTITIE", "ASIELVERBLIJFSTATUSPERIODE", "ASIELVERBLIJFOPVANGCATEGORIE",
  "MEETMOMENTHOND", "MEETMOMENTKAT", "TNRVANGACTIEDIER", "DOSSIERREGEL", "USERANIMALPREFERENCE",
  "EXCHANGE", "KLANT", "FACTUUR", "VESTIGING",
]);
const VERB_KEYS = new Set(["CREATED", "UPDATED", "DELETED", "SENT", "ACCEPTED", "CANCELLED", "COMPLETED", "REJECTED"]);

function entityCodeLabel(t: (k: string) => string, upper: string): string {
  if (ENTITY_CODE_KEYS.has(upper)) return t(`animalDetailView.history.ent.${upper}`);
  const l = upper.toLowerCase();
  return l.charAt(0).toUpperCase() + l.slice(1);
}

function verbLabel(t: (k: string) => string, type: string): string {
  return VERB_KEYS.has(type) ? t(`animalDetailView.history.verb.${type}`) : type.toLowerCase();
}

// Fallback for generic <ENTITY>_<TYPE> codes: "ASIELVERBLIJF_CREATED" → "Asielverblijf aangemaakt".
function humanizeCode(t: (k: string) => string, code: string): string {
  const idx = code.lastIndexOf("_");
  if (idx > 0) return `${entityCodeLabel(t, code.slice(0, idx))} ${verbLabel(t, code.slice(idx + 1))}`;
  const lower = code.replace(/_/g, " ").toLowerCase();
  return lower ? lower.charAt(0).toUpperCase() + lower.slice(1) : code;
}

function eventTitle(t: (k: string) => string, code: string | null): string {
  if (!code) return t("animalDetailView.history.changed");
  return EVENT_TITLE_CODES.has(code) ? t(`animalDetailView.history.evt.${code}`) : humanizeCode(t, code);
}

// ── Payload formatter (port of RelatieGeschiedenisPanel) ──────────────────────────────────────────
// Jmix-system + back-reference fields we never surface.
const HIDDEN_ATTRS = new Set(["dier", "klant", "version", "uuid", "createdAt", "createdBy", "updatedAt", "updatedBy"]);

// "asielBrengerSoort" → "Asielbrenger soort".
function humanizeCamelCase(camel: string): string {
  if (!camel) return camel;
  let out = "";
  for (let i = 0; i < camel.length; i++) {
    const c = camel[i];
    if (i > 0 && c >= "A" && c <= "Z") out += ` ${c.toLowerCase()}`;
    else if (i === 0) out += c.toUpperCase();
    else out += c;
  }
  return out;
}

const ID_REF_PATTERN = /Id\{[\w.]+\.([A-Z]\w+),\s*([^}]+)\}/g;
const FQN_PREFIX_PATTERN = /(?:[a-z]\w*\.){2,}([A-Z]\w+)/g;

// Replace Java FQN jargon (Id{…} wrappers + bare FQN toString) with the simple class name.
function stripFqn(value: string): string {
  if (!value) return value;
  return value.replace(ID_REF_PATTERN, "$1#$2").replace(FQN_PREFIX_PATTERN, "$1");
}

// Generic audit-listener payloads: "attr: 'value'; attr: 'old' → 'new'". Humanise the camelCase keys,
// strip technical fields, join with " · "; null when nothing readable remains.
function formatChangeListPayload(raw: string): string | null {
  const parts: string[] = [];
  for (const entry of raw.split(";")) {
    const trimmed = entry.trim();
    if (!trimmed) continue;
    const idx = trimmed.indexOf(":");
    if (idx <= 0) { parts.push(stripFqn(trimmed)); continue; }
    const key = trimmed.slice(0, idx).trim();
    const value = trimmed.slice(idx + 1).trim();
    if (HIDDEN_ATTRS.has(key)) continue;
    parts.push(`${humanizeCamelCase(key)}: ${stripFqn(value)}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

// Hand-written "key=value, key=value" payloads (pleeggezin placement etc.). The donation-specific
// label vocab lives in the relatie donateur tab; for the animal context we surface the date/range,
// capacity, category and free-text keys that actually occur here.
function formatKeyValuePayload(raw: string): string {
  const kv: Record<string, string> = {};
  for (const pair of raw.split(",")) {
    const idx = pair.indexOf("=");
    if (idx > 0) {
      const k = pair.slice(0, idx).trim();
      const v = pair.slice(idx + 1).trim();
      if (v && v.toLowerCase() !== "null") kv[k] = v;
    }
  }
  const parts: string[] = [];
  if (kv.datum) parts.push(`op ${kv.datum}`);
  if (kv.max) parts.push(`capaciteit: ${kv.max}`);
  if (kv.diersoort) parts.push(kv.diersoort);
  if (kv.categorie) parts.push(kv.categorie);
  if (kv.velden) parts.push(`velden: ${kv.velden}`);
  if (kv.titel) parts.push(kv.titel);
  if (kv.van && kv.tot) parts.push(`${kv.van} — ${kv.tot}`);
  else if (kv.van) parts.push(`vanaf ${kv.van}`);
  return parts.length ? parts.join(" · ") : raw;
}

function formatPayload(raw: string | null): string | null {
  if (!raw || !raw.trim()) return null;
  if (raw.includes("=")) return formatKeyValuePayload(raw);
  if (raw.includes(": ")) return formatChangeListPayload(raw);
  return raw;
}

// ── Day / time helpers ────────────────────────────────────────────────────────────────────────────
function dayKey(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayHeader(t: (k: string) => string, locale: string, key: string): string {
  if (!key) return t("animalDetailView.history.unknownDate");
  const now = new Date();
  const todayKey = dayKey(now.toISOString());
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  if (key === todayKey) return t("animalDetailView.history.today");
  if (key === dayKey(yest.toISOString())) return t("animalDetailView.history.yesterday");
  return new Intl.DateTimeFormat(locale, { dateStyle: "long", timeZone: "UTC" }).format(new Date(`${key}T00:00:00Z`));
}

function timeOf(iso: string | null, locale: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { timeStyle: "short" }).format(d);
}

// ── Event glyphs ────────────────────────────────────────────────────────────────────────────────
// Port of the Jmix `iconFor` switch: the line glyph drawn inside the (colour-tinted) icon circle,
// chosen per event category. Local tile-scoped SVGs (sized via `.fawkes-history__glyph`) so every
// glyph shares one stroke weight and the icon circle stays visually uniform. For real animal data
// every event resolves to the pencil (the generic RelatieAuditListener only emits `<ENTITY>_<TYPE>`
// codes), but the foster/donation/exchange glyphs render faithfully for the cross-category rows the
// shared panel and the dev gallery can show.
const glyph = (children: ReactNode) => (
  <svg className="fawkes-history__glyph" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const PencilGlyph = () => glyph(<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>);
const BanGlyph = () => glyph(<><circle cx="12" cy="12" r="9" /><line x1="5.6" y1="5.6" x2="18.4" y2="18.4" /></>);
const PauseGlyph = () => glyph(<><line x1="9" y1="5" x2="9" y2="19" /><line x1="15" y1="5" x2="15" y2="19" /></>);
const EuroGlyph = () => glyph(<><path d="M17 6.5a6.5 6.5 0 1 0 0 11" /><line x1="4" y1="10" x2="13" y2="10" /><line x1="4" y1="14" x2="12" y2="14" /></>);
const RefreshGlyph = () => glyph(<><polyline points="21 4 21 9 16 9" /><path d="M20 9A8 8 0 1 0 21 14" /></>);
const HeartGlyph = () => glyph(<path d="M12 20.5C5 16 3 11.5 3 8.5A4.3 4.3 0 0 1 12 7a4.3 4.3 0 0 1 9 1.5c0 3-2 7.5-9 12Z" />);
const UserGlyph = () => glyph(<><circle cx="12" cy="8" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" /></>);
const HomeGlyph = () => glyph(<><path d="M4 11l8-7 8 7" /><path d="M6 10v9h12v-9" /></>);
const CheckGlyph = () => glyph(<polyline points="4 12 10 18 20 6" />);
const CalendarGlyph = () => glyph(<><rect x="4" y="5" width="16" height="16" rx="2" /><line x1="4" y1="9" x2="20" y2="9" /><line x1="9" y1="3" x2="9" y2="7" /><line x1="15" y1="3" x2="15" y2="7" /></>);
const BullseyeGlyph = () => glyph(<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.4" /></>);
const UserCardGlyph = () => glyph(<><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="8" cy="11" r="2" /><path d="M5 16.2a3 3 0 0 1 6 0" /><line x1="14" y1="10" x2="18" y2="10" /><line x1="14" y1="14" x2="18" y2="14" /></>);

// Mirrors RelatieGeschiedenisPanel.iconFor: STOPPED/PAUSED suffix first, then the code prefix, then
// the entity-type fallback, else the generic pencil.
function glyphFor(e: Event): ReactNode {
  const code = e.eventType ?? "";
  const type = e.entityType ?? "";
  if (code.endsWith("_STOPPED")) return <BanGlyph />;
  if (code.endsWith("_PAUSED")) return <PauseGlyph />;
  if (code.startsWith("DONATION_")) return <EuroGlyph />;
  if (code.startsWith("RECURRING_")) return <RefreshGlyph />;
  if (code.startsWith("PROFILE_")) return <HeartGlyph />;
  if (code.startsWith("PLEEGGEZIN_PROFIEL_")) return <UserGlyph />;
  if (code.startsWith("PLEEGGEZIN_CAPACITEIT_")) return <HomeGlyph />;
  if (code.startsWith("PLEEGGEZIN_WELKOM_")) return <CheckGlyph />;
  if (code.startsWith("PLEEGGEZIN_AFWEZIGHEID_")) return <CalendarGlyph />;
  if (code.startsWith("PLEEGGEZIN_VERBLIJF_")) return <BullseyeGlyph />;
  if (code.startsWith("KLANT_")) return <UserCardGlyph />;
  if (type.startsWith("Pleeggezin")) return <HomeGlyph />;
  if (type === "Klant") return <UserCardGlyph />;
  return <PencilGlyph />;
}

// Small standalone clock used by the unfiltered empty state (the shared icon set has no clock).
const ClockGlyph = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 16 14" />
  </svg>
);

function HistoryRow({ e, t, locale }: { e: Event; t: (k: string) => string; locale: string }) {
  const [open, setOpen] = useState(false);
  const title = eventTitle(t, e.eventType);
  const payload = formatPayload(e.payload);
  const clamped = payload != null && payload.length > PAYLOAD_CLAMP_THRESHOLD;
  const time = timeOf(e.occurredAt, locale);
  const user = e.userId && e.userId.trim() ? e.userId : t("animalDetailView.history.system");
  const meta = time ? `${time} · ${user}` : `${t("animalDetailView.history.by")} ${user}`;
  return (
    <div className="fawkes-history__event">
      <div className={`fawkes-history__icon fawkes-history__icon--${iconCircleVariant(e)}`} aria-hidden="true">
        {glyphFor(e)}
      </div>
      <div className="fawkes-history__body">
        <span className="fawkes-history__title">{title}</span>
        {payload && (
          <span className={`fawkes-history__payload${clamped ? " fawkes-history__payload--clamped" : ""}`}>
            {payload}
          </span>
        )}
        {clamped && (
          <button type="button" className="fawkes-history__more" onClick={() => setOpen(true)}>
            {t("animalDetailView.history.more")}
          </button>
        )}
        <span className="fawkes-history__meta">{meta}</span>
      </div>
      {/* "Toon meer" → the full payload in a modal dialog, mirroring the Jmix openDetailDialog. */}
      {clamped && payload && (
        <Modal
          open={open}
          title={title}
          onClose={() => setOpen(false)}
          footer={
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t("animalDetailView.history.close")}
            </Button>
          }
        >
          <div className="fawkes-history__dialog-meta">{meta}</div>
          <div className="fawkes-history__dialog-payload">{payload}</div>
        </Modal>
      )}
    </div>
  );
}

// Jmix buildEmptyState: a clock when there is simply nothing yet, a magnifier when a search term
// matched nothing — each beside its message inside the dashed empty card.
function HistoryEmpty({ filtered, t }: { filtered: boolean; t: (k: string) => string }) {
  return (
    <div className="fawkes-history__empty">
      {filtered
        ? <SearchIcon className="fawkes-history__empty-icon" aria-hidden="true" />
        : <ClockGlyph className="fawkes-history__empty-icon" />}
      <span>{t(filtered ? "animalDetailView.history.emptyFiltered" : "animalDetailView.history.empty")}</span>
    </div>
  );
}

export function HistoryTab({ animalId }: { animalId: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [query, setQuery] = useState("");
  const { data, isLoading, isError } = useTile<Event[]>(animalId, "history");
  const all = (data ?? []).filter((e) => !isHiddenEvent(e.eventType));
  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;

  // The api already serves the rows `order by occurred_at desc` (newest first); keep that order.
  const q = query.trim().toLowerCase();
  const filtered = q === ""
    ? all
    : all.filter((e) =>
        `${eventTitle(t, e.eventType)} ${formatPayload(e.payload) ?? ""} ${e.userId ?? ""}`.toLowerCase().includes(q));

  // Group by day, preserving the newest-first order.
  const groups: { key: string; events: Event[] }[] = [];
  const byDay = new Map<string, { key: string; events: Event[] }>();
  for (const e of filtered) {
    const k = dayKey(e.occurredAt);
    let g = byDay.get(k);
    if (!g) { g = { key: k, events: [] }; byDay.set(k, g); groups.push(g); }
    g.events.push(e);
  }

  return (
    <div className="fawkes-history">
      <div className="fawkes-history__search-wrap">
        <SearchIcon className="fawkes-history__search-icon" aria-hidden="true" />
        <input
          className="fawkes-history__search"
          type="search"
          autoComplete="off"
          value={query}
          onChange={(ev) => setQuery(ev.target.value)}
          placeholder={t("animalDetailView.history.search")}
          aria-label={t("animalDetailView.history.search")}
        />
      </div>
      <div className="fawkes-history__content">
        {all.length === 0 ? (
          <HistoryEmpty filtered={false} t={t} />
        ) : filtered.length === 0 ? (
          <HistoryEmpty filtered t={t} />
        ) : (
          groups.map((g) => (
            <section className="fawkes-history__day" key={g.key || "unknown"}>
              <h5 className="fawkes-history__day-header">{dayHeader(t, locale, g.key)}</h5>
              <div className="fawkes-history__items">
                {g.events.map((e) => (
                  <HistoryRow key={e.id} e={e} t={t} locale={locale} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

// ---- Chipregistratie (PetBase) -----------------------------------------------------------------
// PetBase chip-registration migration (Epic 2 — Story 2.3, Decision E): the per-animal status surface
// for the async chip_registration ledger/outbox. Lists each registration (intake / release) with a
// StatusBadge + last error, and offers "Registratie opnieuw versturen" for failed/dead-lettered rows —
// POST /api/chip-registrations/{id}/requeue, gated server-side by the originating wizard authority
// (ROLE_ANIMAL_INTAKE_EXECUTE / ROLE_ANIMAL_RELEASE_EXECUTE). Read feed: GET
// /api/animals/{id}/chip-registrations. Code→label mapping lives here (AR2); the presentational card +
// re-queue affordance are the reusable @fawkes/ui ChipRegistrationStatusCard (so the wizards can drop the
// same surface in); this wrapper only fetches, resolves the i18n labels, and wires the re-queue mutation.
interface ChipRegistrationStatusRow {
  id: string;
  vendor: string;
  flow: string;
  status: string;
  displayState: string;
  requeueable: boolean;
  externalRef: string | null;
  lastError: string | null;
  attemptCount: number;
  createdAt: string | null;
}

function chipRegTone(displayState: string): StatusTone {
  switch (displayState) {
    case "REGISTERED":
    case "COMPLETED":
      return "success";
    case "DEAD_LETTERED":
      return "danger";
    case "NOT_REGISTERED":
    case "FAILED_AFVOER":
    case "FAILED_AANVOER":
      return "warning";
    default:
      // QUEUED / AFVOER_DONE (in flight), SKIPPED_NO_CHIP / SKIPPED_OUTAGE (resolved, no action)
      return "neutral";
  }
}

export function ChipRegistrationTile({ animalId }: { animalId: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError, refetch } = useCustom({
    url: `animals/${animalId}/chip-registrations`,
    method: "get",
  });
  const rows = (data?.data as ChipRegistrationStatusRow[] | undefined) ?? [];
  const { mutate: requeue, isLoading: requeuing } = useCustomMutation();
  const [notice, setNotice] = useState<"requeued" | "error" | null>(null);

  const onRequeue = (id: string) => {
    setNotice(null);
    requeue(
      { url: `chip-registrations/${id}/requeue`, method: "post", values: {} },
      { onSuccess: () => { setNotice("requeued"); refetch(); }, onError: () => setNotice("error") },
    );
  };

  // Nothing registered → no tile at all. An empty "Nog niets vastgelegd" card said nothing and took
  // a slot on every animal that had never been near PetBase.
  if (!isLoading && !isError && rows.length === 0) {
    return null;
  }

  return (
    <Tile title={t("animalDetailView.stay.tile.chipRegistration")}>
    <div className="fawkes-chipreg">
      <Status isLoading={isLoading} isError={isError} empty={false} t={t} />
      {notice === "requeued" && (
        <p className="fawkes-chipreg__notice" role="status">{t("animalDetailView.chipReg.requeued")}</p>
      )}
      {notice === "error" && (
        <p className="fawkes-error" role="alert">{t("animalDetailView.chipReg.requeueFailed")}</p>
      )}
      {rows.map((row) => (
        <ChipRegistrationStatusCard
          key={row.id}
          flowLabel={t(`animalDetailView.chipReg.flow.${row.flow}`)}
          statusLabel={t(`animalDetailView.chipReg.status.${row.displayState}`)}
          statusTone={chipRegTone(row.displayState)}
          meta={
            <>
              {t("animalDetailView.chipReg.attempts")}: {row.attemptCount}
              {row.createdAt ? ` · ${formatDateTime(row.createdAt, locale)}` : ""}
              {row.externalRef ? ` · ${row.externalRef}` : ""}
            </>
          }
          errorLabel={t("animalDetailView.chipReg.lastError")}
          error={row.lastError}
          requeueLabel={t("animalDetailView.chipReg.requeue")}
          onRequeue={row.requeueable ? () => onRequeue(row.id) : undefined}
          requeueDisabled={requeuing}
        />
      ))}
    </div>
    </Tile>
  );
}

// ---- Medicijnen ------------------------------------------------------------------------------------
// The Medicijnen tile (GET /api/animals/{id}/medicijnen) — the Jmix Medicijnen-tile migration. Read-only
// Phase 1: lists the animal's prescriptions (most-recent first) with their derived status
// (Actief/Binnenkort/Historisch), the medication detail fields and the administration moments
// (toedienlijst), plus the KPI status counts. The add/edit/delete prescription dialog + the
// "Toediening registreren" flow are the follow-up write slices (mirroring how Bescherming was migrated).
interface MedicationAdministration {
  id: string;
  version: number | null;
  moment: string | null;
  momentIso: string | null;
  toegediend: boolean;
  toegediendDoor: string | null;
  /** De initialen van wie het gaf; het ingelogde account staat in toegediendDoor. */
  initialen: string | null;
  opmerking: string | null;
}
interface MedicationPrescription {
  id: string;
  version: number | null;
  naam: string;
  categorie: string | null;
  dosering: string | null;
  frequentie: string | null;
  toedieningsvorm: string | null;
  startDatum: string | null;
  startDatumIso: string | null;
  stopDatum: string | null;
  stopDatumIso: string | null;
  indicatie: string | null;
  voorgeschrevenDoor: string | null;
  actief: boolean;
  opmerkingen: string | null;
  status: string;
  toedieningen: MedicationAdministration[];
}
interface MedicationTile {
  kpi: { actief: number; binnenkort: number; historisch: number };
  prescriptions: MedicationPrescription[];
}
// A medication product option (GET .../medicijnen/producten) — picking one pre-fills the dialog.
interface MedicijnProduct {
  productId: number;
  naam: string;
  categorie: string | null;
  toedieningsvorm: string | null;
  dosering: string | null;
  freqAantal: number | null;
  freqEenheid: string | null;
  kuurDagen: number | null;
  indicatie: string | null;
  waarschuwingen: string | null;
  receptplichtig: boolean;
}
// AI-suggested standard fields (POST .../medicijnen/ai-suggestie). available=false → nothing applied.
interface MedicijnAiSuggestie {
  available: boolean;
  categorie: string | null;
  dosering: string | null;
  frequentie: string | null;
  toedieningsvorm: string | null;
  indicatie: string | null;
  opmerkingen: string | null;
  kuurDagen: number | null;
  toelichting: string | null;
}

type MedTab = "actueel" | "toedienlijst" | "historie";
// The Jmix tile caps each list at 3 cards behind a "toon alles" toggle.
const MED_PREVIEW_LIMIT = 3;

const MED_STATUS_TONE: Record<string, "success" | "warning" | "neutral"> = {
  ACTIEF: "success",
  BINNENKORT: "warning",
  HISTORISCH: "neutral",
};

type MedicijnDialogState = { mode: "create" | "edit"; row: MedicationPrescription | null };
type MedNotice = "saved" | "deleted" | "conflict" | "invalid" | "error" | null;

export function MedicijnenTile({ animalId }: { animalId: string }) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useTile<MedicationTile>(animalId, "medicijnen");
  const { mutate, isLoading: saving } = useCustomMutation();

  const { data: identity } = useGetIdentity<{ id?: string; name?: string }>();
  const currentUserName = identity?.name ?? identity?.id ?? "";
  // Product catalog for the dialog's picker (gated by ROLE_ANIMAL_MEDICATION_EDIT — empty when the
  // operator can't edit, which simply hides the picker options).
  const { data: productData } = useTile<MedicijnProduct[]>(animalId, "medicijnen/producten");
  const products = productData ?? [];

  const [dialog, setDialog] = useState<MedicijnDialogState | null>(null);
  const [confirmRow, setConfirmRow] = useState<MedicationPrescription | null>(null);
  const [adminDialog, setAdminDialog] = useState<string | null>(null);
  const [generateDialog, setGenerateDialog] = useState<string | null>(null);
  const [notice, setNotice] = useState<MedNotice>(null);
  // Jmix tile parity: a tab strip (Actueel = running/binnenkort, Toedienlijst = all moments, Historie =
  // stopped/inactive), a search box (naam/categorie/indicatie) and a 3-card preview with "toon alles".
  const [tab, setTab] = useState<MedTab>("actueel");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const run = (
    url: string,
    method: "post" | "put" | "delete",
    values: Record<string, unknown> | undefined,
    onOk?: () => void,
    okNotice: MedNotice = "saved",
  ) => {
    setNotice(null);
    mutate(
      { url: `animals/${animalId}/${url}`, method, values: values ?? {} },
      {
        onSuccess: () => { refetch(); onOk?.(); setNotice(okNotice); },
        onError: (err) => {
          const sc = (err as { statusCode?: number }).statusCode;
          setNotice(sc === 409 ? "conflict" : sc === 400 ? "invalid" : "error");
        },
      },
    );
  };

  const submit = (form: MedicijnForm) => {
    const editing = dialog?.mode === "edit" ? dialog.row : null;
    const values: Record<string, unknown> = {
      naam: form.naam.trim(),
      categorie: form.categorie.trim() || null,
      dosering: form.dosering.trim() || null,
      frequentie: form.frequentie.trim() || null,
      toedieningsvorm: form.toedieningsvorm.trim() || null,
      startDatum: form.startDatum || null,
      stopDatum: form.stopDatum || null,
      indicatie: form.indicatie.trim() || null,
      voorgeschrevenDoor: form.voorgeschrevenDoor.trim() || null,
      actief: form.actief,
      opmerkingen: form.opmerkingen.trim() || null,
    };
    if (editing) {
      run(`medicijnen/${editing.id}`, "put", { ...values, version: editing.version }, () => setDialog(null));
    } else {
      run("medicijnen", "post", values, () => setDialog(null));
    }
  };

  const confirmDelete = () => {
    if (!confirmRow) return;
    run(`medicijnen/${confirmRow.id}`, "delete", undefined, () => setConfirmRow(null), "deleted");
  };

  // Register an open ("gepland") moment as given: a PUT that flips toegediend + stamps the current user.
  const registerToediening = (medicijnId: string, a: MedicationAdministration) => {
    run(
      `medicijnen/${medicijnId}/toedieningen/${a.id}`,
      "put",
      { moment: a.momentIso, toegediend: true, toegediendDoor: currentUserName || null, version: a.version },
    );
  };
  const deleteToediening = (medicijnId: string, a: MedicationAdministration) => {
    run(`medicijnen/${medicijnId}/toedieningen/${a.id}`, "delete", undefined, undefined, "deleted");
  };
  const submitToediening = (medicijnId: string, form: AdministrationForm) => {
    run(
      `medicijnen/${medicijnId}/toedieningen`,
      "post",
      {
        moment: `${form.datum}T${form.tijd}:00`,
        toegediend: form.toegediend,
        toegediendDoor: form.door.trim() || null,
        opmerking: form.opmerking.trim() || null,
      },
      () => setAdminDialog(null),
    );
  };
  const submitGenerate = (medicijnId: string, form: GenerateForm) => {
    run(
      `medicijnen/${medicijnId}/toedieningen/genereer`,
      "post",
      { aantal: form.aantal, eenheid: form.eenheid },
      () => setGenerateDialog(null),
    );
  };

  const noticeText =
    notice === "saved" ? t("animalDetailView.medicijnen.saved")
    : notice === "deleted" ? t("animalDetailView.medicijnen.deleted")
    : notice === "conflict" ? t("animalDetailView.medicijnen.saveConflict")
    : notice === "invalid" ? t("animalDetailView.medicijnen.saveInvalid")
    : notice === "error" ? t("animalDetailView.medicijnen.saveError")
    : null;

  if (isLoading || isError) {
    return <Status isLoading={isLoading} isError={isError} t={t} />;
  }
  const kpi = data?.kpi ?? { actief: 0, binnenkort: 0, historisch: 0 };
  const prescriptions = data?.prescriptions ?? [];

  const q = search.trim().toLowerCase();
  const matchesSearch = (p: MedicationPrescription) =>
    !q || `${p.naam ?? ""} ${p.categorie ?? ""} ${p.indicatie ?? ""}`.toLowerCase().includes(q);

  // Actueel = running/upcoming; Historie = stopped/inactive. Both honour the search box.
  const actueel = prescriptions.filter((p) => p.status !== "HISTORISCH").filter(matchesSearch);
  const historie = prescriptions.filter((p) => p.status === "HISTORISCH").filter(matchesSearch);
  // Toedienlijst = every administration moment across the non-historic prescriptions, chronological.
  const toedienlijst = prescriptions
    .filter((p) => p.status !== "HISTORISCH")
    .flatMap((p) => p.toedieningen.map((a) => ({ p, a })))
    .sort((x, y) => (x.a.momentIso ?? "").localeCompare(y.a.momentIso ?? ""));

  const cards = tab === "historie" ? historie : actueel;
  const visibleCards = showAll ? cards : cards.slice(0, MED_PREVIEW_LIMIT);

  const tabs = [
    { key: "actueel", label: `${t("animalDetailView.medicijnen.tab.actueel")} (${actueel.length})` },
    { key: "toedienlijst", label: `${t("animalDetailView.medicijnen.tab.toedienlijst")} (${toedienlijst.length})` },
    { key: "historie", label: `${t("animalDetailView.medicijnen.tab.historie")} (${historie.length})` },
  ];

  const renderCards = (list: MedicationPrescription[]) => (
    <div className="fawkes-medication__list">
      {list.map((p) => (
        <MedicijnCard
          key={p.id}
          prescription={p}
          t={t}
          onEdit={() => setDialog({ mode: "edit", row: p })}
          onDelete={() => setConfirmRow(p)}
          onAddToediening={() => setAdminDialog(p.id)}
          onGenerate={() => setGenerateDialog(p.id)}
          onRegister={(a) => registerToediening(p.id, a)}
          onDeleteToediening={(a) => deleteToediening(p.id, a)}
        />
      ))}
    </div>
  );

  return (
    <div className="fawkes-medication">
      <div className="fawkes-medication__toolbar">
        {prescriptions.length > 0 && (
          <div className="fawkes-medication__kpis">
            <span className="fawkes-medication__kpi fawkes-medication__kpi--actief">
              <b>{kpi.actief}</b> {t("animalDetailView.medicijnen.status.ACTIEF")}
            </span>
            <span className="fawkes-medication__kpi fawkes-medication__kpi--binnenkort">
              <b>{kpi.binnenkort}</b> {t("animalDetailView.medicijnen.status.BINNENKORT")}
            </span>
            <span className="fawkes-medication__kpi fawkes-medication__kpi--historisch">
              <b>{kpi.historisch}</b> {t("animalDetailView.medicijnen.status.HISTORISCH")}
            </span>
          </div>
        )}
        <Button variant="primary" className="fawkes-medication__add" onClick={() => setDialog({ mode: "create", row: null })}>
          <PlusCircleSolidIcon width={18} height={18} /> {t("animalDetailView.medicijnen.add")}
        </Button>
      </div>

      {noticeText && (
        <p className={notice === "saved" || notice === "deleted" ? "fawkes-success" : "fawkes-error"}>{noticeText}</p>
      )}

      {prescriptions.length === 0 ? (
        <Status isLoading={false} isError={false} empty t={t} />
      ) : (
        <>
          <div className="fawkes-medication__filters">
            <Tabs
              tabs={tabs}
              activeKey={tab}
              onChange={(k) => { setTab(k as MedTab); setShowAll(false); }}
              ariaLabel={t("animalDetailView.tile.medicijnen")}
            />
            <div className="fawkes-products-search fawkes-medication__search">
              <SearchIcon className="fawkes-products-search__icon" width={16} height={16} />
              <input
                type="search"
                autoComplete="off"
                className="fawkes-products-search__input"
                placeholder={t("animalDetailView.medicijnen.searchPlaceholder")}
                value={search}
                aria-label={t("animalDetailView.medicijnen.searchPlaceholder")}
                onChange={(e) => { setSearch(e.target.value); setShowAll(false); }}
              />
            </div>
          </div>

          {tab === "toedienlijst" ? (
            toedienlijst.length === 0 ? (
              <p className="fawkes-medication__admin-empty">{t("animalDetailView.medicijnen.toediening.empty")}</p>
            ) : (
              <ul className="fawkes-medication__admin-list fawkes-medication__toedienlijst">
                {toedienlijst.map(({ p, a }) => (
                  <li key={a.id} className={`fawkes-medication__admin-row${a.toegediend ? " fawkes-medication__admin-row--done" : ""}`}>
                    <span className="fawkes-medication__admin-check" aria-hidden="true">
                      {a.toegediend ? <CheckIcon width={13} height={13} /> : <ClockIcon width={13} height={13} />}
                    </span>
                    <span className="fawkes-medication__admin-moment">{a.moment ?? "—"}</span>
                    <span className="fawkes-medication__tl-naam">{p.naam}</span>
                    <span className="fawkes-medication__admin-actions">
                      {!a.toegediend && (
                        <button type="button" className="fawkes-medication__admin-register" onClick={() => registerToediening(p.id, a)}>
                          <CheckIcon width={13} height={13} /> {t("animalDetailView.medicijnen.toediening.register")}
                        </button>
                      )}
                      <button type="button" className="fawkes-medication__row-btn fawkes-medication__row-btn--danger" aria-label={t("animalDetailView.medicijnen.toediening.delete")} onClick={() => deleteToediening(p.id, a)}>
                        <TrashIcon width={13} height={13} />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : cards.length === 0 ? (
            <p className="fawkes-medication__admin-empty">
              {q ? t("animalDetailView.medicijnen.noMatch") : t("animalDetailView.medicijnen.tabEmpty")}
            </p>
          ) : (
            <>
              {renderCards(visibleCards)}
              {cards.length > MED_PREVIEW_LIMIT && (
                <button type="button" className="fawkes-medication__showall" onClick={() => setShowAll((s) => !s)}>
                  {showAll
                    ? t("animalDetailView.medicijnen.showLess")
                    : t("animalDetailView.medicijnen.showAll").replace("%d", String(cards.length))}
                </button>
              )}
            </>
          )}
        </>
      )}

      <MedicijnDialog
        open={dialog != null}
        animalId={animalId}
        row={dialog?.row ?? null}
        products={products}
        saving={saving}
        onClose={() => setDialog(null)}
        onSubmit={submit}
        t={t}
      />

      <ConfirmDialog
        cancelLabel={t("common.cancel")}
        open={confirmRow != null}
        title={t("animalDetailView.medicijnen.deleteTitle")}
        confirmLabel={t("animalDetailView.medicijnen.deleteConfirm")}
        danger
        busy={saving}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmRow(null)}
      >
        {t("animalDetailView.medicijnen.deleteBody").replace("%s", confirmRow?.naam ?? "")}
      </ConfirmDialog>

      <AdministrationDialog
        open={adminDialog != null}
        saving={saving}
        defaultDoor={currentUserName}
        onClose={() => setAdminDialog(null)}
        onSubmit={(form) => { if (adminDialog) submitToediening(adminDialog, form); }}
        t={t}
      />

      <GenerateScheduleDialog
        open={generateDialog != null}
        saving={saving}
        onClose={() => setGenerateDialog(null)}
        onSubmit={(form) => { if (generateDialog) submitGenerate(generateDialog, form); }}
        t={t}
      />
    </div>
  );
}

interface GenerateForm {
  aantal: number;
  eenheid: "DAG" | "WEEK" | "MAAND" | "UUR";
}

function GenerateScheduleDialog({ open, saving, onClose, onSubmit, t }: {
  open: boolean; saving: boolean; onClose: () => void; onSubmit: (v: GenerateForm) => void; t: (k: string) => string;
}) {
  const [form, setForm] = useState<GenerateForm>({ aantal: 1, eenheid: "DAG" });
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setForm({ aantal: 1, eenheid: "DAG" });
  }, [open]);

  const submit = () => {
    if (!Number.isFinite(form.aantal) || form.aantal < 1) {
      setErr(t("animalDetailView.medicijnen.genereer.err.aantal"));
      return;
    }
    onSubmit(form);
  };

  const g = (k: string) => t(`animalDetailView.medicijnen.genereer.${k}`);
  const units: GenerateForm["eenheid"][] = ["DAG", "WEEK", "MAAND", "UUR"];

  return (
    <Modal
      open={open}
      title={g("title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={saving} onClick={submit}>{g("submit")}</Button>
        </>
      }
    >
      <div className="fawkes-medication-form">
        <FieldShell label={g("aantal")}>
          <input
            type="number"
            min={1}
            className="fui-field__input"
            value={form.aantal}
            aria-label={g("aantal")}
            onChange={(e) => setForm((f) => ({ ...f, aantal: Number(e.target.value) }))}
          />
        </FieldShell>
        <FieldShell label={g("eenheid")}>
          <select
            className="fui-field__input fui-select"
            value={form.eenheid}
            aria-label={g("eenheid")}
            onChange={(e) => setForm((f) => ({ ...f, eenheid: e.target.value as GenerateForm["eenheid"] }))}
          >
            {units.map((u) => (
              <option key={u} value={u}>{g(`eenheid.${u}`)}</option>
            ))}
          </select>
        </FieldShell>
        <p className="fawkes-medication__notes fawkes-medication-form__span2">{g("hint")}</p>
        {err && <p className="fawkes-error fawkes-medication-form__span2">{err}</p>}
      </div>
    </Modal>
  );
}

interface AdministrationForm {
  datum: string;
  tijd: string;
  door: string;
  toegediend: boolean;
  opmerking: string;
}

function AdministrationDialog({ open, saving, defaultDoor, onClose, onSubmit, t }: {
  open: boolean; saving: boolean; defaultDoor: string;
  onClose: () => void; onSubmit: (v: AdministrationForm) => void; t: (k: string) => string;
}) {
  const now = new Date();
  const today = localDateIso(now);
  const nowTime = now.toTimeString().slice(0, 5);
  const [form, setForm] = useState<AdministrationForm>({ datum: today, tijd: nowTime, door: defaultDoor, toegediend: true, opmerking: "" });
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setForm({ datum: today, tijd: nowTime, door: defaultDoor, toegediend: true, opmerking: "" });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<AdministrationForm>) => setForm((f) => ({ ...f, ...patch }));

  const submit = () => {
    if (!form.datum || !form.tijd) { setErr(t("animalDetailView.medicijnen.toediening.err.momentRequired")); return; }
    onSubmit(form);
  };

  const f = (k: string) => t(`animalDetailView.medicijnen.toediening.${k}`);

  return (
    <Modal
      open={open}
      title={f("addTitle")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={saving} onClick={submit}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-medication-form">
        <FieldShell label={f("datum")}>
          <DateField className="fui-field__input" value={form.datum} aria-label={f("datum")} onChange={(e) => set({ datum: e.target.value })} />
        </FieldShell>
        <FieldShell label={f("tijd")}>
          <input type="time" className="fui-field__input" value={form.tijd} aria-label={f("tijd")} onChange={(e) => set({ tijd: e.target.value })} />
        </FieldShell>
        <FieldShell label={f("door")} className="fawkes-medication-form__span2">
          <input className="fui-field__input" value={form.door} aria-label={f("door")} onChange={(e) => set({ door: e.target.value })} />
        </FieldShell>
        <FieldShell label={f("opmerking")} className="fawkes-medication-form__span2">
          <input className="fui-field__input" value={form.opmerking} aria-label={f("opmerking")} onChange={(e) => set({ opmerking: e.target.value })} />
        </FieldShell>
        <label className="fawkes-medication-form__toggle fawkes-medication-form__span2">
          <input type="checkbox" checked={form.toegediend} onChange={(e) => set({ toegediend: e.target.checked })} />
          <span>{f("toegediend")}</span>
        </label>
        {err && <p className="fawkes-error fawkes-medication-form__span2">{err}</p>}
      </div>
    </Modal>
  );
}

interface MedicijnForm {
  naam: string;
  categorie: string;
  dosering: string;
  frequentie: string;
  toedieningsvorm: string;
  startDatum: string;
  stopDatum: string;
  indicatie: string;
  voorgeschrevenDoor: string;
  actief: boolean;
  opmerkingen: string;
}

// Frequency label from the product config's aantal + eenheid (the Jmix freqLabel): "om de N uur" for
// hours, else "Nx per dag/week/maand".
function medicijnFreqLabel(aantal: number | null, eenheid: string | null): string {
  if (!aantal || !eenheid) return "";
  const e = eenheid.toLowerCase();
  if (e.startsWith("uur")) return `Om de ${aantal} uur`;
  return `${aantal}x per ${e}`;
}

function MedicijnDialog({ open, animalId, row, products, saving, onClose, onSubmit, t }: {
  open: boolean; animalId: string; row: MedicationPrescription | null; products: MedicijnProduct[]; saving: boolean;
  onClose: () => void; onSubmit: (v: MedicijnForm) => void; t: (k: string) => string;
}) {
  const today = todayIso();
  const [form, setForm] = useState<MedicijnForm>(emptyMedicijnForm(today));
  const [productId, setProductId] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const { mutate: aiMutate, isLoading: aiBusy } = useCustomMutation();

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setAiNote(null);
    setProductId("");
    setForm(row ? medicijnFormFromRow(row) : emptyMedicijnForm(today));
  }, [open, row?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<MedicijnForm>) => setForm((f) => ({ ...f, ...patch }));

  // "AI invullen": ask the model for standard fields for the typed name (species/age resolved
  // server-side). Applies the suggestion over the form (keeping the name the operator typed); when the
  // model is unavailable / abstains, the form is left unchanged and a hint is shown.
  const runAi = () => {
    if (!form.naam.trim()) return;
    setErr(null);
    setAiNote(null);
    aiMutate(
      { url: `animals/${animalId}/medicijnen/ai-suggestie`, method: "post", values: { naam: form.naam.trim() }, successNotification: false, errorNotification: false },
      {
        onSuccess: ({ data }) => {
          const s = data as unknown as MedicijnAiSuggestie;
          if (!s || !s.available) { setAiNote(t("animalDetailView.medicijnen.ai.unavailable")); return; }
          setForm((cur) => ({
            ...cur,
            categorie: s.categorie ?? cur.categorie,
            dosering: s.dosering ?? cur.dosering,
            frequentie: s.frequentie ?? cur.frequentie,
            toedieningsvorm: s.toedieningsvorm ?? cur.toedieningsvorm,
            indicatie: s.indicatie ?? cur.indicatie,
            opmerkingen: s.opmerkingen ?? cur.opmerkingen,
            stopDatum: s.kuurDagen && s.kuurDagen >= 1
              ? addDaysIso(cur.startDatum || today, s.kuurDagen)
              : cur.stopDatum,
          }));
          setAiNote(s.toelichting || t("animalDetailView.medicijnen.ai.done"));
        },
        onError: () => setAiNote(t("animalDetailView.medicijnen.ai.error")),
      },
    );
  };

  // Picking a product pre-fills the form from its standard config (the Jmix pasProductConfigToe);
  // existing field values are overwritten with the product defaults, the operator can still adjust.
  const pickProduct = (value: string) => {
    setProductId(value);
    if (!value) return;
    const p = products.find((pr) => String(pr.productId) === value);
    if (!p) return;
    setForm((f) => ({
      ...f,
      naam: p.naam ?? f.naam,
      categorie: p.categorie ?? "",
      dosering: p.dosering ?? "",
      toedieningsvorm: p.toedieningsvorm ?? "",
      frequentie: medicijnFreqLabel(p.freqAantal, p.freqEenheid) || f.frequentie,
      indicatie: p.indicatie ?? "",
      opmerkingen: p.waarschuwingen ?? "",
      stopDatum: p.kuurDagen && p.kuurDagen >= 1
        ? addDaysIso(f.startDatum || today, p.kuurDagen)
        : f.stopDatum,
    }));
  };

  const submit = () => {
    if (!form.naam.trim()) { setErr(t("animalDetailView.medicijnen.err.naamRequired")); return; }
    if (form.startDatum && form.stopDatum && form.stopDatum < form.startDatum) {
      setErr(t("animalDetailView.medicijnen.err.stopBeforeStart"));
      return;
    }
    onSubmit(form);
  };

  const f = (k: string) => t(`animalDetailView.medicijnen.field.${k}`);

  return (
    <Modal
      open={open}
      title={t(row ? "animalDetailView.medicijnen.editTitle" : "animalDetailView.medicijnen.addTitle")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={saving} onClick={submit}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-medication-form">
        {products.length > 0 && (
          <FieldShell label={t("animalDetailView.medicijnen.field.product")} className="fawkes-medication-form__span2">
            <select
              className="fui-field__input fui-select"
              value={productId}
              aria-label={t("animalDetailView.medicijnen.field.product")}
              onChange={(e) => pickProduct(e.target.value)}
            >
              <option value="">{t("animalDetailView.medicijnen.field.productAnders")}</option>
              {products.map((p) => (
                <option key={p.productId} value={p.productId}>{p.naam}</option>
              ))}
            </select>
          </FieldShell>
        )}
        <FieldShell label={f("naam")} required className="fawkes-medication-form__span2">
          <input className="fui-field__input" value={form.naam} aria-label={f("naam")} aria-required="true" onChange={(e) => set({ naam: e.target.value })} />
        </FieldShell>
        <div className="fawkes-medication-form__span2 fawkes-medication-form__ai">
          <button type="button" className="fawkes-medication__ai-btn" disabled={!form.naam.trim() || aiBusy} onClick={runAi}>
            <SparklesIcon width={14} height={14} /> {aiBusy ? t("animalDetailView.medicijnen.ai.busy") : t("animalDetailView.medicijnen.ai.button")}
          </button>
          {aiNote && <span className="fawkes-medication__ai-note">{aiNote}</span>}
        </div>
        <FieldShell label={f("dosering")}>
          <input className="fui-field__input" value={form.dosering} aria-label={f("dosering")} onChange={(e) => set({ dosering: e.target.value })} />
        </FieldShell>
        <FieldShell label={f("frequentie")}>
          <input className="fui-field__input" value={form.frequentie} aria-label={f("frequentie")} onChange={(e) => set({ frequentie: e.target.value })} />
        </FieldShell>
        <FieldShell label={f("toedieningsvorm")}>
          <input className="fui-field__input" value={form.toedieningsvorm} aria-label={f("toedieningsvorm")} onChange={(e) => set({ toedieningsvorm: e.target.value })} />
        </FieldShell>
        <FieldShell label={f("categorie")}>
          <input className="fui-field__input" value={form.categorie} aria-label={f("categorie")} onChange={(e) => set({ categorie: e.target.value })} />
        </FieldShell>
        <FieldShell label={f("startDatum")}>
          <DateField className="fui-field__input" value={form.startDatum} aria-label={f("startDatum")} onChange={(e) => set({ startDatum: e.target.value })} />
        </FieldShell>
        <FieldShell label={f("stopDatum")}>
          <DateField className="fui-field__input" value={form.stopDatum} aria-label={f("stopDatum")} onChange={(e) => set({ stopDatum: e.target.value })} />
        </FieldShell>
        <FieldShell label={f("voorgeschrevenDoor")} className="fawkes-medication-form__span2">
          <input className="fui-field__input" value={form.voorgeschrevenDoor} aria-label={f("voorgeschrevenDoor")} onChange={(e) => set({ voorgeschrevenDoor: e.target.value })} />
        </FieldShell>
        <FieldShell label={f("indicatie")} className="fawkes-medication-form__span2">
          <input className="fui-field__input" value={form.indicatie} aria-label={f("indicatie")} onChange={(e) => set({ indicatie: e.target.value })} />
        </FieldShell>
        <FieldShell label={t("animalDetailView.medicijnen.field.opmerkingen")} className="fawkes-medication-form__span2">
          <textarea className="fui-field__input" rows={2} value={form.opmerkingen} aria-label={t("animalDetailView.medicijnen.field.opmerkingen")} onChange={(e) => set({ opmerkingen: e.target.value })} />
        </FieldShell>
        <label className="fawkes-medication-form__toggle fawkes-medication-form__span2">
          <input type="checkbox" checked={form.actief} onChange={(e) => set({ actief: e.target.checked })} />
          <span>{t("animalDetailView.medicijnen.field.actief")}</span>
        </label>
        {err && <p className="fawkes-error fawkes-medication-form__span2">{err}</p>}
      </div>
    </Modal>
  );
}

function emptyMedicijnForm(today: string): MedicijnForm {
  return {
    naam: "", categorie: "", dosering: "", frequentie: "", toedieningsvorm: "",
    startDatum: today, stopDatum: "", indicatie: "", voorgeschrevenDoor: "", actief: true, opmerkingen: "",
  };
}

function medicijnFormFromRow(row: MedicationPrescription): MedicijnForm {
  return {
    naam: row.naam ?? "",
    categorie: row.categorie ?? "",
    dosering: row.dosering ?? "",
    frequentie: row.frequentie ?? "",
    toedieningsvorm: row.toedieningsvorm ?? "",
    startDatum: row.startDatumIso ?? "",
    stopDatum: row.stopDatumIso ?? "",
    indicatie: row.indicatie ?? "",
    voorgeschrevenDoor: row.voorgeschrevenDoor ?? "",
    actief: row.actief,
    opmerkingen: row.opmerkingen ?? "",
  };
}

function MedicijnCard({ prescription: p, t, onEdit, onDelete, onAddToediening, onGenerate, onRegister, onDeleteToediening }: {
  prescription: MedicationPrescription; t: (k: string) => string; onEdit: () => void; onDelete: () => void;
  onAddToediening: () => void;
  onGenerate: () => void;
  onRegister: (a: MedicationAdministration) => void;
  onDeleteToediening: (a: MedicationAdministration) => void;
}) {
  const tone = MED_STATUS_TONE[p.status] ?? "neutral";
  const rows: Array<{ key: string; value: string | null }> = [
    { key: "dosering", value: p.dosering },
    { key: "frequentie", value: p.frequentie },
    { key: "toedieningsvorm", value: p.toedieningsvorm },
    { key: "categorie", value: p.categorie },
    { key: "startDatum", value: p.startDatum },
    { key: "stopDatum", value: p.stopDatum },
    { key: "voorgeschrevenDoor", value: p.voorgeschrevenDoor },
    { key: "indicatie", value: p.indicatie },
  ].filter((r) => r.value);

  return (
    <div className="fawkes-medication__card">
      <div className="fawkes-medication__card-head">
        <span className="fawkes-medication__card-icon" aria-hidden="true">
          <DropletIcon width={16} height={16} />
        </span>
        <span className="fawkes-medication__card-name">{p.naam}</span>
        <StatusBadge tone={tone}>{t(`animalDetailView.medicijnen.status.${p.status}`)}</StatusBadge>
        <span className="fawkes-medication__card-actions">
          <button type="button" className="fawkes-medication__row-btn" aria-label={t("animalDetailView.medicijnen.edit")} onClick={onEdit}>
            <PencilIcon width={15} height={15} />
          </button>
          <button type="button" className="fawkes-medication__row-btn fawkes-medication__row-btn--danger" aria-label={t("animalDetailView.medicijnen.delete")} onClick={onDelete}>
            <TrashIcon width={15} height={15} />
          </button>
        </span>
      </div>

      {rows.length > 0 && (
        <div className="fawkes-medication__meta">
          {rows.map((r) => (
            <div className="fawkes-medication__meta-row" key={r.key}>
              <span className="fawkes-medication__meta-label">{t(`animalDetailView.medicijnen.field.${r.key}`)}</span>
              <span className="fawkes-medication__meta-value">{r.value}</span>
            </div>
          ))}
        </div>
      )}

      {p.opmerkingen && <p className="fawkes-medication__notes">{p.opmerkingen}</p>}

      <div className="fawkes-medication__admin">
        <div className="fawkes-medication__admin-head">
          <span className="fawkes-medication__admin-title">
            <ClockIcon width={13} height={13} /> {t("animalDetailView.medicijnen.toedienlijst")}
          </span>
          <span className="fawkes-medication__admin-tools">
            <button type="button" className="fawkes-medication__admin-add" onClick={onGenerate}>
              <RotateLeftIcon width={13} height={13} /> {t("animalDetailView.medicijnen.genereer.button")}
            </button>
            <button type="button" className="fawkes-medication__admin-add" onClick={onAddToediening}>
              <PlusCircleSolidIcon width={16} height={16} /> {t("animalDetailView.medicijnen.toediening.add")}
            </button>
          </span>
        </div>
        {p.toedieningen.length > 0 ? (
          <ul className="fawkes-medication__admin-list">
            {p.toedieningen.map((a) => (
              <li
                key={a.id}
                className={`fawkes-medication__admin-row${a.toegediend ? " fawkes-medication__admin-row--done" : ""}`}
              >
                <span className="fawkes-medication__admin-check" aria-hidden="true">
                  {a.toegediend ? <CheckIcon width={13} height={13} /> : <ClockIcon width={13} height={13} />}
                </span>
                <span className="fawkes-medication__admin-moment">{a.moment ?? "—"}</span>
                <span className="fawkes-medication__admin-state">
                  {a.toegediend
                    ? uitgevoerdDoor(a.toegediendDoor, a.initialen)
                      ? t("animalDetailView.medicijnen.toegediendDoor").replace(
                          "%s",
                          uitgevoerdDoor(a.toegediendDoor, a.initialen) as string,
                        )
                      : t("animalDetailView.medicijnen.toegediend")
                    : t("animalDetailView.medicijnen.gepland")}
                </span>
                <span className="fawkes-medication__admin-actions">
                  {!a.toegediend && (
                    <button type="button" className="fawkes-medication__admin-register" onClick={() => onRegister(a)}>
                      <CheckIcon width={13} height={13} /> {t("animalDetailView.medicijnen.toediening.register")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="fawkes-medication__row-btn fawkes-medication__row-btn--danger"
                    aria-label={t("animalDetailView.medicijnen.toediening.delete")}
                    onClick={() => onDeleteToediening(a)}
                  >
                    <TrashIcon width={13} height={13} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="fawkes-medication__admin-empty">{t("animalDetailView.medicijnen.toediening.empty")}</p>
        )}
      </div>
    </div>
  );
}
